import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { installFallbackZodErrorMap } from '@/lib/zod-error-map';

// i18n foundation — hu az alapértelmezett, en a másodlagos. A modulok saját kulcsokkal bővítik.
//
// RÉTEGES (i18next-namespace) felépítés — nyelvenként HÁROM namespace:
//   • `translation` (defaultNS) — MINDEN látható felirat (label, cím, oszlop, gomb, üres/hiba-állapot).
//       Hívás: `t('clients.name')` (namespace-prefix nélkül, mert ez a default).
//   • `validation` — ŰRLAP-MEZŐ hibaüzenetek (zod issue path → mező-hiba). Hívás: `t('validation:clients.email.invalid')`.
//   • `toast` — toast / siker- / hiba-üzenetek (mutáció-visszajelzés). Hívás: `t('toast:clients.saved')`.
// A namespace-prefix a `:` szeparátor (i18next default). A MEGLÉVŐ oldalak a `translation` réteget
// használják változatlanul (a kulcsaik nem törnek); a Clients a réteges minta referencia-fogyasztója.
/** A nyers szótár-objektum. EXPORTÁLT, mert a napló-falak (`*.wall.spec.ts`) EBBŐL ellenőrzik a kulcs-készlet
 *  teljességét és a hu/en szimmetriát — szöveg-parse-olás helyett a valódi adaton. */
export const resources = {
  hu: {
    translation: {
      // AUTOMATIKUS VÁZLAT (jegy-válasz, partner-válasz, üzenet) — `hooks/use-draft.ts` + `DraftStatus`.
      drafts: {
        saved: 'Vázlat mentve · {{time}}',
        discard: 'Vázlat elvetése',
        resume: 'Mentett vázlat — folytatás…',
      },
      app: { name: 'Atlas' },
      mo: {
        title: 'Mo',
        subtitle: 'Az állandó segítőd',
        close: 'Bezárás',
        empty:
          'Kérdezz bátran! Forrásokkal alátámasztva válaszolok, és ha valamire nincs pontos adatom, azt őszintén jelzem — nem találgatok.',
        noResult: 'Erre nincs pontos adat a tudástárban.',
        placeholder: 'Kérdés Mo-nak…',
        send: 'Küldés',
        errorNetwork: 'Hálózati hiba — Mo nem elérhető.',
        errorInvalid: 'Érvénytelen kérés.',
        errorHttp: 'Mo hiba (HTTP {{code}}).',
        colGroup: 'Csoport',
        colCount: 'Darab',
        colSum: 'Összeg',
      },
      // ── Értesítések („harang") ───────────────────────────────────────────────────────────────────────
      // A feed-sor SZÁNDÉKOSAN nem hordoz kész szöveget: a `type` + `data` párból a WEB rendereli (lásd
      // `features/notifications/render.ts`) — így a nyelvváltás a MÁR kiküldött értesítésekre is visszamenőleg hat.
      // A kulcs-tő a katalógus-típus (`apps/api/src/notify/events.ts` → `NOTIFY_EVENT_TYPES`); mivel a `.` az
      // i18next kulcs-szeparátora, a típusok BEÁGYAZOTT objektumként élnek (`ticket.assigned` → ticket → assigned).
      // A szövegek a legacy CRM `resources/lang/hu/notification.php`-jának parity-fordításai (ahol volt párja).
      // KÉP-NAGYÍTÓ (lightbox) — a beágyazott képek/videók teljes méretű megtekintése, léptetéssel.
      lightbox: {
        title: 'Melléklet megtekintése',
        untitled: 'melléklet',
        close: 'Bezárás',
        prev: 'Előző',
        next: 'Következő',
        openInNewTab: 'Megnyitás új lapon',
      },
      notify: {
        bell: {
          aria: 'Értesítések',
          // Az `{{unread}}` SZÁNDÉKOSAN nem `count`: az i18next a `count`-ra plural-variánsokat keresne.
          ariaWithCount: 'Értesítések — {{unread}} olvasatlan',
          liveCount: 'Olvasatlan értesítések: {{unread}}',
        },
        panel: {
          title: 'Értesítések',
          markAll: 'Mind olvasottnak',
          unreadMark: 'Olvasatlan',
          // A footer LINK-je: a panel a friss értesítéseket adja, a teljes üzenet-történet a modul lapján van.
          openMessages: 'Üzenetek megnyitása',
          emptyTitle: 'Nincs értesítésed',
          emptyDescription:
            'Itt jelennek meg a jegyekhez, megbeszélésekhez, eszköz-igényekhez és határidőkhöz kapcsolódó értesítések.',
          errorTitle: 'Az értesítések nem tölthetők be',
          errorDescription: 'Próbáld újra kicsit később.',
          loadMore: 'Továbbiak',
          loading: 'Betöltés…',
        },
        // Hiányzó/null payload-mező → ÉRTELMES helyettesítő (a szövegben SOHA nem jelenhet meg `undefined`).
        fallback: {
          title: 'Értesítés',
          body: 'Új értesítés érkezett.',
          actorName: 'Rendszer',
          ticketNumber: 'ismeretlen',
          accountName: 'ismeretlen',
          subject: 'nincs tárgy',
          deliveredCount: 'ismeretlen számú',
          droppedCount: 'néhány',
          meetingTitle: 'névtelen',
          startsAtLabel: 'nincs megadva',
          startsInMinutes: 'néhány',
          month: 'ismeretlen hónap',
          count: 'ismeretlen számú',
          assetName: 'eszköz',
          assetType: 'eszköz',
          team: 'ismeretlen csapat',
          netPriceLabel: 'nincs megadva',
          rejectReason: 'nincs megadva',
          cardMasked: 'ismeretlen',
          cardExpiryLabel: 'nincs megadva',
          expiryLabel: 'nincs megadva',
          clientName: 'ismeretlen',
          // A reakció JELE — jel nélküli (régi) sornál semleges szó, nem üres hely.
          emoji: 'egy',
          // A FÜGGŐ hozzárendelés hamarosan-indul értesítéséhez (`service_assignment.pending_start_soon`).
          serviceName: 'ismeretlen szolgáltatás',
          planName: 'ismeretlen konstrukció',
          startDate: 'nincs megadva',
        },
        types: {
          document: {
            // A dokumentumtár-modul ⛔ ELVETVE — ez a MIGRÁLT sor (mérve: 1 db) így is olvasható marad.
            alert: {
              title: 'Dokumentum-emlékeztető',
              body: 'Dokumentum-határidő értesítés — archív értesítés a korábbi CRM-ből (a modul elvetve).',
            },
          },
          ticket: {
            // ── MIGRÁLT LEGACY TÍPUSOK ─────────────────────────────────────────────────────────────────
            // A migrált CRM-értesítések kulcsai MÁSOK, mint a mai katalógusé (`open` ↔ `opened`, `comment`
            // ↔ `comment.external`, …), és a payloadjuk CSAK a legacy `model_id`-t hordozza — nincs benne
            // tárgy, jegyszám, cím. Kulcs nélkül MINDEGYIK a generikus „Értesítés / Új értesítés érkezett"
            // szövegre esett (mérve: 22 800 sorból ~15 800). Ezért kapnak SAJÁT, típus-specifikus szöveget,
            // SZÁNDÉKOSAN placeholder NÉLKÜL: kitalált részletet nem írunk oda, de a felhasználó legalább
            // tudja, MI történt. (Az ÚJ értesítések a mai katalógus-kulcsokon futnak, teljes adattal.)
            open: {
              title: 'Jegy megnyitva',
              body: 'Új támogatási jegy nyílt — archív értesítés a korábbi CRM-ből.',
            },
            close: {
              title: 'Jegy lezárva',
              body: 'Egy támogatási jegy lezárult — archív értesítés a korábbi CRM-ből.',
            },
            status_change: {
              title: 'Jegy státusza módosult',
              body: 'Egy támogatási jegy státusza megváltozott — archív értesítés a korábbi CRM-ből.',
            },
            assign: {
              title: 'Jegy hozzárendelve',
              body: 'Egy támogatási jegy hozzád lett rendelve — archív értesítés a korábbi CRM-ből.',
            },
            opened: {
              title: 'Jegy megnyitva',
              body: 'A(z) {{accountName}} fiók alatt új, {{ticketNumber}} számú jegy nyílt: {{subject}}',
            },
            assigned: {
              title: 'Új feladat',
              body: 'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegy hozzád lett rendelve.',
            },
            // A legacyhez hűen KÜLÖN szöveg a „vizsgálat alatt" / „újranyitva" átmenetre (a feed `data`-ja
            // hordozza a `status`-t, a variáns-kulcsot a `textVariant()` adja); a semleges pár a fallback,
            // ha egyszer új státusz-érték jönne a szervertől.
            status_changed: {
              title: 'Jegy státusza módosult',
              body: 'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegy státusza módosult.',
              title_in_progress: 'Jegy vizsgálat alatt',
              body_in_progress:
                'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegy vizsgálat alatt áll.',
              title_reopened: 'Jegy újranyitva',
              body_reopened:
                'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegy újranyitásra került.',
            },
            closed: {
              title: 'Jegy lezárva',
              body: 'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegy lezárásra került.',
            },
            resolved: {
              title: 'Jegy megoldva',
              body: 'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegy megoldásra került.',
            },
            comment: {
              // MIGRÁLT LEGACY kulcs (`ticket.comment`): a mai katalógus a külső/belső hozzászólást KÜLÖN
              // típusnak veszi (`comment.external`/`.internal`), a migrált sorok viszont az összevont
              // kulcson jönnek — ezért kap a `comment` KÖZVETLEN cím/törzs párt is.
              title: 'Új hozzászólás a jegyhez',
              body: 'Hozzászólás érkezett egy támogatási jegyhez — archív értesítés a korábbi CRM-ből.',
              external: {
                title: 'Komment érkezett',
                body: 'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegyhez új komment érkezett.',
              },
              internal: {
                title: 'Hozzászólás érkezett',
                body: 'A(z) {{accountName}} fiók alatt található {{ticketNumber}} számú jegyhez új belsős megjegyzés érkezett.',
              },
              // EMOJI-REAKCIÓ a hívó SAJÁT kommentjén (a címzett mindig a komment szerzője) — ezért szól a
              // szöveg második személyben („kommentedre"): itt biztosan a saját hozzászólásáról van szó.
              reaction: {
                title: 'Reakció érkezett',
                body: '{{actorName}} {{emoji}} reakciót fűzött a(z) {{ticketNumber}} számú jegyhez kapcsolódó kommentedre.',
              },
            },
            // A PARTNER-oldali megosztás értesítése: a címzett eddig NEM látta a jegyet, most igen.
            access_granted: {
              title: 'Hozzáférést kapott egy jegyhez',
              body: '{{actorName}} megosztotta Önnel a(z) {{ticketNumber}} számú jegyet — tárgy: {{subject}}',
            },
          },
          message: {
            new: {
              title: 'Új üzenet',
              body: 'Üzenet érkezett az üzenőfalra — archív értesítés a korábbi CRM-ből.',
            },
            received: {
              title: 'Üzenet érkezett',
              body: '{{actorName}} üzenetet küldött — tárgy: {{subject}}',
            },
            // A KÜLDŐNEK szóló üzemeltetési visszajelzés: a címzett-lista a biztonsági plafonnál csonkolt lett
            // (a küldő 201-et kapott, tehát máshonnan nem tudná meg). A két plafon két külön teendő → variáns.
            delivery_truncated: {
              title: 'Csonkolt címzett-lista',
              body: 'A(z) „{{subject}}" tárgyú üzenet {{deliveredCount}} címzetthez ment ki, de a címzett-lista csonkolt: {{droppedCount}} kimaradt.',
              body_recipient_cap:
                'A(z) „{{subject}}" tárgyú üzenet {{deliveredCount}} címzetthez ment ki, de {{droppedCount}} címzett kimaradt (túl széles címzés). Szűkítsd a címzést, vagy küldd el több részletben.',
              body_account_scan_cap:
                'A(z) „{{subject}}" tárgyú üzenet {{deliveredCount}} címzetthez ment ki, de {{droppedCount}} cél-fiók kapcsolattartói kimaradtak (túl sok fiókra címzett üzenet). Küldd el több részletben.',
              // A HARMADIK plafon: a TÁROLT címzés visszaolvasása állt le a lapozási plafonon, tehát a
              // címzés-mátrix egy része fel sem olvasódott. A `droppedCount` itt DIMENZIÓ-darabszám (nem
              // címzett és nem fiók) — a szöveg ezért nem ígér pontos számot, hanem ellenőrzésre kér.
              body_pivot_page_cap:
                'A(z) „{{subject}}" tárgyú üzenet {{deliveredCount}} címzetthez ment ki, de a címzett-lista {{droppedCount}} címzés-csoportnál nem volt teljesen visszaolvasható, így valaki kimaradhatott. Ellenőrizd a címzést, és szükség esetén küldd el újra egy szűkebb körnek.',
            },
          },
          meeting: {
            new: {
              title: 'Új megbeszélés',
              body: 'Megbeszélésre kaptál meghívást — archív értesítés a korábbi CRM-ből.',
            },
            reschedule: {
              title: 'Megbeszélés áthelyezve',
              body: 'Egy megbeszélés időpontja módosult — archív értesítés a korábbi CRM-ből.',
            },
            delete: {
              title: 'Megbeszélés törölve',
              body: 'Egy megbeszélés törlésre került — archív értesítés a korábbi CRM-ből.',
            },
            invited: {
              title: 'Meeting rögzítve',
              body: 'A(z) {{title}} elnevezésű megbeszélés rögzítésre került. Időpont: {{startsAtLabel}}',
            },
            rescheduled: {
              title: 'Meeting átütemezve',
              body: 'A(z) {{title}} elnevezésű megbeszélés átütemezésre került. Új időpont: {{startsAtLabel}}',
            },
            cancelled: {
              title: 'Meeting törölve',
              body: 'A(z) {{title}} elnevezésű megbeszélés törölve.',
            },
            removed: {
              title: 'Lekerültél egy meetingről',
              body: 'A(z) {{title}} elnevezésű megbeszélés résztvevői közül lekerültél.',
            },
            link_changed: {
              title: 'Megváltozott a meeting linkje',
              body: 'A(z) {{title}} elnevezésű megbeszélés csatlakozási linkje megváltozott.',
            },
            reminder: {
              title: 'Közelgő meeting',
              body: 'A megbeszélés hamarosan kezdődik!',
            },
          },
          asset: {
            request: {
              title: 'Eszköz-igénylés',
              body: 'Új eszköz-igénylés érkezett — archív értesítés a korábbi CRM-ből.',
            },
            approve: {
              title: 'Eszköz-igénylés jóváhagyva',
              body: 'Egy eszköz-igénylést jóváhagytak — archív értesítés a korábbi CRM-ből.',
            },
            reject: {
              title: 'Eszköz-igénylés elutasítva',
              body: 'Egy eszköz-igénylést elutasítottak — archív értesítés a korábbi CRM-ből.',
            },
            purchase: {
              title: 'Eszköz beszerezve',
              body: 'Egy igényelt eszköz beszerzése megtörtént — archív értesítés a korábbi CRM-ből.',
            },
            requested: {
              title: 'Új eszközigény',
              body: 'A(z) {{team}} csapat részére új {{assetType}} beszerzésére vonatkozó kérelem érkezett, nettó {{netPriceLabel}} értékben.',
            },
            approved: {
              title: 'Eszközigény jóváhagyva',
              body: 'A(z) {{team}} csapat részére, nettó {{netPriceLabel}} értékben igényelt {{assetName}} jóváhagyásra került.',
            },
            rejected: {
              title: 'Eszközigény elutasítva',
              body: 'A(z) {{team}} csapat részére igényelt {{assetName}} elutasításra került. Elutasítás oka: {{rejectReason}}',
            },
            purchased: {
              title: 'Eszköz beszerzés alatt',
              body: 'A(z) {{team}} csapat részére igényelt {{assetName}} megvásárlásra került.',
            },
            approved_info: {
              title: 'Eszközigény jóváhagyva',
              body: 'A(z) {{team}} csapat részére igényelt {{assetName}} ({{assetType}}) jóváhagyásra került — a beszerzést a beszerző intézi.',
            },
            request_updated: {
              title: 'Az eszközigényedet módosították',
              body: 'A(z) {{assetName}} ({{assetType}}) igényed adatait a bíráló módosította. Nézd át a változtatásokat.',
            },
            expiring: {
              title: 'Lejáró előfizetés',
              body: 'A(z) {{assetName}} ({{assetType}}) lejárati dátuma: {{expiryLabel}}.',
              title_month: 'Egy hónapon belül lejár',
              body_month:
                'A(z) {{assetName}} ({{assetType}}) 30 napon belül lejár: {{expiryLabel}}. Érdemes időben dönteni a megújításról.',
              title_week: 'Egy héten belül lejár',
              body_week: 'A(z) {{assetName}} ({{assetType}}) 7 napon belül lejár: {{expiryLabel}}.',
              title_due: 'Lejárt előfizetés',
              body_due:
                'A(z) {{assetName}} ({{assetType}}) elérte a lejárati dátumát ({{expiryLabel}}). Újítsd meg, vagy vezesd ki a leltárból.',
            },
          },
          payment_card: {
            expiring: {
              title: 'Lejáró bankkártya',
              body: 'A(z) {{clientName}} ügyfél {{cardMasked}} bankkártyájának lejárati dátuma: {{cardExpiryLabel}}',
            },
            expired: {
              title: 'Lejárt bankkártya',
              body: 'A(z) {{clientName}} ügyfél {{cardMasked}} bankkártyája lejárt ({{cardExpiryLabel}}).',
            },
          },
          // FÜGGŐ hozzárendelés, aminek az induló dátuma hamarosan esedékes (a napi cron ÉS a hozzárendelés-
          // lista figyelem-panelje ugyanezt a szabályt hajtja — `docs/modules/service-assignments.md`).
          service_assignment: {
            // ÖSSZESÍTŐ sor: a napi cron EGY értesítést ad az ÖSSZES esedékes hozzárendelésről (korábban
            // tételenként egyet — élesben 36 sor × 17 címzett = 612 levél és 612 panel-sor egy reggelen).
            // A tétel-lista a levélben van; a panel a darabszámot és a legkorábbi indulást mondja ki.
            pending_start_soon: {
              title: 'Hamarosan induló, függő hozzárendelések',
              body: '{{count}} függő hozzárendelés indul hamarosan — a legkorábbi: {{firstStartDate}}',
            },
          },
          // A KÉZZEL indított, háttérben futó havi generálás visszajelzése a kezdeményezőnek.
          invoice: {
            generation_finished: {
              title: 'A számla-generálás befejeződött',
              body: '{{month}} — {{invoices}} piszkozat, {{items}} tétel, {{skipped}} kihagyás',
            },
          },
        },
      },
      // Rich-text szerkesztő toolbar (üzenet-törzs · ticket-leírás · külső komment) — GLOBÁLIS komponens.
      richText: {
        bold: 'Félkövér',
        italic: 'Dőlt',
        underline: 'Aláhúzott',
        strike: 'Áthúzott',
        highlight: 'Kiemelés',
        code: 'Kód (soron belül)',
        codeBlock: 'Kód-blokk',
        blockquote: 'Idézet',
        horizontalRule: 'Elválasztó vonal',
        bulletList: 'Felsorolás',
        orderedList: 'Számozott lista',
        undo: 'Visszavonás',
        redo: 'Újra',
        clearFormat: 'Formázás törlése',
        // BLOKK-TÍPUS (bekezdés / címsorok) — a legacy „typography" legördülőjének párja.
        blockType: 'Blokk-típus',
        paragraph: 'Bekezdés',
        heading2: 'Címsor 1',
        heading3: 'Címsor 2',
        heading4: 'Címsor 3',
        // IGAZÍTÁS
        alignLeft: 'Balra',
        alignCenter: 'Középre',
        alignRight: 'Jobbra',
        alignJustify: 'Sorkizárt',
        // SZÍN + BETŰMÉRET (fix paletta/lista — szabad választó helyett, hogy a tartalom olvasható maradjon)
        color: 'Szöveg-szín',
        colorNone: 'Nincs szín',
        fontSize: 'Betűméret',
        fontSizeDefault: 'Alap',
        // LINK — beviteli mező (a korábbi `window.prompt` a beúszó panelben nem volt használható)
        link: 'Link',
        unlink: 'Link eltávolítása',
        linkUrl: 'Link URL',
        linkPlaceholder: 'https://…',
        linkApply: 'Beszúrás',
        linkRemove: 'Eltávolítás',
        linkInvalid: 'Érvényes URL-t adj meg (http:// vagy https://).',
        image: 'Kép beszúrása',
        pasteNotMedia:
          'A szövegbe csak kép vagy videó illeszthető be — más fájlt mellékletként csatolj.',
        video: 'Videó beszúrása',
        imageError: {
          type: 'Csak JPG, PNG, GIF, WEBP kép vagy MP4, WEBM, MOV videó tölthető fel.',
          size: 'A kép legfeljebb 5 MB, a videó legfeljebb 25 MB lehet.',
          failed: 'A feltöltés nem sikerült.',
        },
      },
      nav: {
        home: 'Ugrás a kezdőlapra',
        dashboard: 'Áttekintés',
        clients: 'Ügyfelek',
        accounts: 'Fiókok',
        contacts: 'Kapcsolattartók',
        services: 'Szolgáltatások',
        finance: 'Bevétel/kiadás',
        billingProfiles: 'Számlázási profilok',
        usageData: 'Használati adatok',
        paymentCards: 'Bankkártyák',
        invoices: 'Számlák',
        tickets: 'Ticketek',
        meetings: 'Megbeszélések',
        messages: 'Üzenetek',
        orgStructure: 'Szervezeti felépítés',
        employees: 'Munkatársak',
        supportTeams: 'Support-csapatok',
        campaignForecast: 'Kampány-előrejelzés',
        productUpdates: 'Fejlesztések',
        serviceCatalog: 'Katalógus',
        assets: 'Eszközök',
        accessRights: 'Jogosultságok',
        activityLog: 'Eseménynapló',
        comingSoon: 'Hamarosan',
        collapse: 'Összecsukás',
        menu: 'Menü',
        search: 'Keresés vagy ugrás…',
        searchPlaceholder: 'Globális keresés…',
        noResults: 'Nincs találat.',
        navigation: 'Navigáció',
        navigate: 'navigálás',
        open: 'megnyitás',
        searching: 'Keresés…',
        partialResults:
          'Lehet, hogy nem látsz mindent — pontosíts a keresésen, vagy nyisd meg az érintett listaoldalt.',
        results: 'Találatok',
        resultTypes: {
          client: 'Ügyfelek',
          account: 'Fiókok',
          contact: 'Kapcsolattartók',
          ticket: 'Ticketek',
          invoice: 'Számlák',
          expense: 'Kiadások',
          message: 'Üzenetek',
          meeting: 'Megbeszélések',
          employee: 'Munkatársak',
          supportTeam: 'Support-csapatok',
          asset: 'Eszközök',
          serviceAssignment: 'Szolgáltatás-hozzárendelések',
          serviceCatalog: 'Katalógus',
          campaign: 'Kampányok',
          productUpdate: 'Fejlesztések',
          department: 'Részlegek',
          team: 'Csapatok',
          position: 'Beosztások',
        },
        notifications: 'Értesítések',
        desc: {
          clients: 'Ügyfelek és adataik',
          accounts: 'Ügyfél-fiókok',
          contacts: 'Külsős felhasználók',
          tickets: 'Support-jegyek',
          messages: 'Személyes üzenetek és rendszerértesítések',
          meetings: 'Rögzített meetingek',
          invoices: 'Automatikusan generált számlák',
          serviceCatalog: 'Szolgáltatás-katalógus',
          orgStructure: 'Részlegek, csapatok és beosztások',
          employees: 'Belső felhasználók',
          supportTeams: 'Ügyfélszolgálati csapatok és tagjaik',
          campaignForecast: 'Kampányok fiókonként, idősávon',
          productUpdates: 'Útiterv és változásnapló — mit adtunk ki, és mi jön',
          assets: 'Belsős eszköz-leltár',
          accessRights: 'Felhasználók apponkénti szerepkörei és jogosultságai',
          activityLog: 'Az Odinra épülő consumer appok audit-naplója',
          billingProfiles: 'Ügyfelek számlázási adatai',
          usageData: 'Külső rendszer usage és árfolyamok',
          paymentCards: 'Ügyfél-kártyák',
          services: 'Szolgáltatás-hozzárendelések',
          finance: 'Mennyi jön be és mennyi megy ki',
        },
        group: {
          clientMgmt: 'Ügyfélkapcsolat',
          finance: 'Pénzügy',
          support: 'Támogatás és kommunikáció',
          organization: 'Szervezet',
          admin: 'Adminisztráció',
        },
        quickActions: 'Műveletek',
      },
      theme: {
        toggle: 'Téma váltása',
        title: 'Téma',
        light: 'Világos',
        dark: 'Sötét',
        system: 'Rendszer szerint',
      },
      /** Az összeg-blokk feliratai (bruttó nagyban, alatta nettó/áfa). */
      money: { net: 'nettó', vat: 'áfa', gross: 'bruttó' },
      // ── MEGSZEMÉLYESÍTÉS („belépés más nevében") ──
      impersonation: {
        rowAction: 'Belépés {{name}} nevében',
        // Jog nélkül a gomb TILTOTT (nem eltűnik) — a tooltip a hiányzó jogot mondja meg.
        noPermission: 'Nincs jogosultságod a megszemélyesítéshez',
        bannerTitle: '{{name}} nevében dolgozol',
        // A MÁSODIK tagmondat SZÁNDÉKOSAN a teljes profil-beállításokról szól, nem csak a jelszóról/2FA-ról:
        // az Odin a profil-írást és az avatart is falazza, tehát a szűkebb megfogalmazás félrevezetett
        // (user-döntés).
        bannerHint:
          'A műveletek {{actor}} néven naplózódnak, a profilbeállítások most nem módosíthatók.',
        bannerHintNoActor:
          'A műveletek a valódi nevedben naplózódnak, a profilbeállítások most nem módosíthatók.',
        stop: 'Vissza a saját fiókomra',
        unknownSubject: 'a kiválasztott személy',
        // A FELÜLETI FAL magyarázata (`ImpersonationFence`): a halványítás önmagában nem mondja meg, miért
        // tiltott — a szerver (Odin) a hitelesítés-kezelő self-végpontokat megszemélyesítés közben 403-cal
        // zárja, a felület pedig ezt kimondja, mielőtt a támogató kitöltené az űrlapot.
        fencedHint:
          'Megszemélyesítés közben ezek a beállítások nem módosíthatók — a saját fiókodra visszalépve elérhetők.',
      },
      // ── PARTNER-PORTÁL (a külsős kapcsolattartó felülete) ──────────────────────────────────────────────
      portal: {
        brand: 'Ügyfélportál',
        nav: {
          aria: 'Portál-navigáció',
          home: 'Kezdőlap',
          tickets: 'Jegyeim',
          messages: 'Üzenetek',
          invoices: 'Szolgáltatások és díjak',
          productUpdates: 'Fejlesztések',
          profile: 'Profil',
          imprint: 'Impresszum',
          gdpr: 'Adatkezelés',
        },
        // FEJLESZTÉSEK a partner-felületen — a hangnem ÖNÖZŐ, és a szavak nem belső zsargonból jönnek:
        // nincs „stage"/„publikálás", csak „Mi újság" és „Min dolgozunk".
        productUpdates: {
          subtitle: 'Mit adtunk ki, és min dolgozunk',
          changelogTitle: 'Mi újság',
          changelogEmpty: 'Még nincs kihirdetett fejlesztés',
          changelogEmptyDesc: 'Amint kiadunk valami újat, itt olvashatja.',
          roadmapTitle: 'Min dolgozunk',
          roadmapHelp:
            'A tervezett fejlesztések sorrendje tájékoztató jellegű — pontos határidőt nem ígérünk.',
          roadmapEmpty: 'Nincs megosztott terv',
          roadmapEmptyDesc: 'Amint van bejelenthető tervünk, itt megjelenik.',
          noDate: 'Ismeretlen időpont',
          columnEmpty: 'Most nincs itt semmi',
          noHorizon: 'Még nincs időzítve',
          unavailableTitle: 'A fejlesztések most nem érhetők el',
          unavailableDesc:
            'Technikai okból nem tudjuk betölteni a listát. Kollégáink már tudnak róla — kérjük, nézzen vissza később.',
        },
        // ── a KERET (shell) feliratai ──
        brandTag: 'Partner',
        shell: {
          myAccounts: 'Fiókjaim',
          allAccounts: 'Minden fiók',
          allAccountsHint: '{{count}} fiók összesítve',
          scopeLabel: 'Szűkítve: {{name}}',
          primaryContact: 'elsődleges kapcsolattartó',
          switchAccount: 'Fiókváltás',
          helpTitle: 'Segítségre van szüksége?',
          helpBody: 'Nyisson jegyet, és kollégáink munkanapokon 4 órán belül válaszolnak.',
          menu: 'Menü',
          breadcrumb: 'Morzsamenü',
          language: 'Nyelv',
          notifications_one: 'Értesítések ({{count}} olvasatlan)',
          notifications_other: 'Értesítések ({{count}} olvasatlan)',
          // A panel FEJLÉCE és a harang `title`-je szám nélkül (a számot az `aria-label` viszi).
          notificationsTitle: 'Értesítések',
          markAllRead: 'Mind olvasott',
          noNotifications: 'Nincs értesítése',
          noNotificationsDesc:
            'Itt jelezzük, ha komment érkezik a jegyére, üzenetet vagy meghívót kap.',
          notificationsErrorTitle: 'Az értesítéseket most nem tudtuk betölteni',
          notificationsErrorDesc:
            'Próbálja meg néhány másodperc múlva — a jegyei és üzenetei változatlanul elérhetők.',
          notificationsLoading: 'Töltés…',
          openMessages: 'Üzenetek megnyitása',
        },
        profile: {
          subtitle: 'Saját adatai, jelszava és értesítési beállításai.',
          primaryBadge: 'elsődleges kapcsolattartó',
          primaryShort: 'elsődleges',
          accountCount_one: '{{count}} fiók',
          accountCount_other: '{{count}} fiók',
          accountsHint:
            'A fiók-hozzáférések módosításához nyisson jegyet, vagy keresse ügyfélkapcsolati kollégáját.',
          legalTitle: 'Jogi tudnivalók',
          imprintSub: 'A szolgáltató adatai és elérhetőségei',
          gdprSub: 'Milyen adatokat kezelünk és milyen jogai vannak',
        },
        // ── JOGI LAPOK ──
        // A cég-adatok a LEGACY CRM impresszumából/adatkezelési tájékoztatójából jönnek — a tényadatok (bank,
        // NAIH-szám) üzleti/jogi jóváhagyást igényelnek
        // (nyitott tétel: `docs/ROADMAP.md` A-71). A szerkezet és a bekezdés-lista viszont a végleges.
        // ── JOGI LAPOK ──
        //
        // A TARTALOM A LEGACY CRM-BŐL jön (`../crm/resources/views/livewire/partner/{imprint,gdpr}.blade.php`),
        // ADATRA PONTOSAN — user-döntés: „minden más adat a régi". EGY dolog változott: a kimenő
        // e-mailt ma az Amazon SES kézbesíti, ezért került be az adatfeldolgozó-szakasz. Két, tudatos eltérés
        // a legacy szövegtől: (1) a hivatkozott weboldal `atlas.morgens.hu` (a leírt felület MA ez, a
        // `crm.morgens.hu` a kivezetett rendszer), (2) a megszólítás TEGEZŐ helyett ÖNÖZŐ — a portál egész
        // felülete önöz, és a regiszter felület-hang, nem adat. A legacy egy hiányos mondatát („keresd
        // munkatársainkat a e-mail címen") kiegészítettük a tényleges címmel.
        legal: {
          imprint: {
            title: 'Impresszum',
            updated: 'Frissítve: 2026. szeptember 2.',
            blocks: [
              {
                h: 'A szolgáltató',
                p: ['Az atlas.morgens.hu partner-felület üzemeltetője a MORGENS Design Kft.'],
              },
              {
                h: 'A cég adatai',
                p: [
                  'A cég elnevezése: MORGENS Design Marketing Tanácsadó és Informatikai Szolgáltató Kft.',
                  'A cég rövidített elnevezése: MORGENS Design Kft.',
                  'A cég székhelye: 8800 Nagykanizsa, Magyar utca 79.',
                  'A cég képviseletére jogosult személy neve: Katona Zoltán ügyvezető',
                  'A cég statisztikai számjele: 23964710-6201-113-20',
                  'A cég adószáma: 23964710-2-20',
                  'A cég cégjegyzékszáma: 20-09-072782 (nyilvántartásba vette a Zalaegerszegi Törvényszék Cégbírósága)',
                  'A cég pénzforgalmi jelzőszáma: 10104985-28145400-01005004',
                  'A pénzforgalmi jelzőszámot a Budapest Bank Zrt. kezeli',
                ],
              },
              {
                h: 'Kapcsolat',
                p: [
                  'Általános információ: +36 30 648 0047',
                  'E-mail cím: sales@morgens.hu',
                  'A cég elektronikus elérhetősége: sales@morgens.hu',
                ],
              },
              {
                h: 'E-mail kézbesítés',
                p: [
                  'A partner-felület kimenő leveleit (értesítések, jegy-válaszok, számla-értesítők) az Amazon Web Services EMEA SARL Amazon SES szolgáltatása kézbesíti, európai régióból.',
                ],
              },
            ],
          },
          gdpr: {
            title: 'Adatkezelés',
            updated: 'Frissítve: 2026. szeptember 2.',
            blocks: [
              {
                h: 'A MORGENS Design Kft. adatkezelési tájékoztatója',
                p: [
                  'Az adatokat csak meghatározott és törvényes célra tároljuk, és attól eltérő módon nem használjuk fel.',
                  'Az Ön személyes adatait tisztességesen és törvényesen dolgozzuk fel.',
                  'Megfelelő biztonsági intézkedéseket teszünk az adatállományokban tárolt személyes adatok védelme érdekében: a jogtalan hozzáférést, megváltoztatást vagy terjesztést megakadályozzuk.',
                ],
              },
              {
                h: 'Ennek keretében',
                ordered: true,
                p: [
                  'Az Ön személyes adatait az atlas.morgens.hu weboldal (a továbbiakban: Weboldal) üzemeltetője, a MORGENS Design Kft. (székhely: 8800 Nagykanizsa, Magyar utca 79.; Cg.: 20-09-072782; adószám: 23964710-2-20; a továbbiakban: Üzemeltető) jogosult kezelni; azokat harmadik fél részére kizárólag a jelen adatkezelési tájékoztatóban foglaltaknak megfelelően adjuk át. Adatkezelés nyilvántartási számunk: NAIH-85268/2015.',
                  'Az adatszolgáltatás önkéntes és az Ön hozzájárulásán alapul (jogalap). A regisztrációval az adatok kezeléséhez való írásbeli hozzájárulást megadottnak tekintjük. Az adatok kezelése és továbbítása automatikus, zárt rendszeren keresztül történik, emberi beavatkozás nélkül.',
                  'Az Ön által az űrlap kitöltése során megadott személyes adatok (az űrlap típusától függően: név, cégnév, telefonszám, e-mail cím) a rendszerünkben rögzítésre kerülnek. Az adatokat kizárólag ticketing célzattal rögzítjük.',
                  'Az adatkezelés célja a Weboldalon keresztül történő ajánlatkérés megválaszolásához tartozó kapcsolattartás és tájékoztatás, valamint a hírlevél kiküldésének alapjául szolgáló adatbázis kezelése és a hírlevél küldése. Az Ön által megadott személyes adatok kezelése mindaddig fennmarad, amíg kifejezetten nem kéri azok kezelésének megszüntetését.',
                  'A Weboldalt böngésző látogatókról, érdeklődőkről semmilyen személyes adatot nem gyűjtünk. Böngészéskor mindössze anonim információk kerülnek rögzítésre arról, hogy a látogatók mely aloldalakat tekintik meg, milyen funkciókat használnak vagy próbálnak meg használni. Ezen adatok segítséget nyújtanak az oldal üzemeltetéséhez és továbbfejlesztéséhez, valamint a zökkenőmentes működés fenntartásához.',
                  'A személyes adatok kezeléséről bármikor ingyenes írásbeli tájékoztatást kérhet az adatairól, azok forrásáról, az adatkezelés céljáról, jogalapjáról, időtartamáról, az adatfeldolgozó nevéről, címéről és az adatkezeléssel összefüggő tevékenységéről, továbbá — személyes adatainak továbbítása esetén — az adattovábbítás jogalapjáról és címzettjéről. Elérhetőségeink: MORGENS Design Kft. ügyfélszolgálat, e-mail: sales@morgens.hu, cím: 8800 Nagykanizsa, Magyar utca 79., telefon: +36 30 648 0047.',
                  'Az adatok törlését, zárolását vagy helyesbítését is a fenti elérhetőségeken kérheti, illetve ugyanitt van lehetősége az adatok kezelése ellen kifogást emelni, az adatkezelésről tájékoztatást kérni, továbbá tiltakozni a személyes adatok kezelése ellen. Tájékoztatjuk, hogy a tájékoztatási, illetve törlési, zárolási, helyesbítési kérelmét ezen igények beérkezésétől számított legrövidebb időn belül, de legkésőbb 10 munkanapon belül ingyenesen, írásban teljesítjük.',
                  'Tájékoztatjuk továbbá, hogy a személyes adatok védelméhez való jog érvényesülésének ellenőrzése és elősegítése a Nemzeti Adatvédelmi és Információszabadság Hatóság (1055 Budapest, Falk Miksa utca 9–11.) mint független, csak a törvénynek alárendelt Hatóság feladata.',
                  'A jogai megsértése esetén az Üzemeltető, illetőleg az adatátvevő ellen a Nemzeti Adatvédelmi és Információszabadság Hatósághoz (1055 Budapest, Falk Miksa utca 9–11.), illetve bírósághoz fordulhat. A bíróság az ügyben soron kívül jár el.',
                  'A személyes adatok nyilvántartásba vétele és kezelése az információs önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII. törvény, valamint a kutatás és a közvetlen üzletszerzés célját szolgáló név- és lakcímadatok kezeléséről szóló 1995. évi CXIX. törvény alapján történik.',
                  'A jelen adatkezelési szabályzat az Üzemeltető által bármikor módosítható, amelyről hírlevélben vagy belépéskor felugró ablakban tájékoztatjuk. A szolgáltatás további használatával a megváltozott adatkezelési szabályzatot automatikusan tudomásul veszi, ezen túlmenően a beleegyezésére nincs szükség.',
                ],
                links: [
                  {
                    label: '2011. évi CXII. törvény',
                    url: 'https://net.jogtar.hu/jogszabaly?docid=a1100112.tv',
                  },
                  {
                    label: '1995. évi CXIX. törvény',
                    url: 'https://net.jogtar.hu/jogszabaly?docid=99500119.tv',
                  },
                ],
              },
              {
                h: 'Adatfeldolgozó — e-mail kézbesítés',
                p: [
                  'A rendszer kimenő leveleit (jegy-értesítések, jegy-válaszok, számla-értesítők, hírlevél) az Amazon Web Services EMEA SARL Amazon SES szolgáltatása kézbesíti, európai régióból. Az adatfeldolgozó a kézbesítéshez szükséges adatokat (címzett e-mail címe, a levél tartalma) kezeli, más célra nem használja fel.',
                ],
              },
              {
                h: 'Kapcsolat',
                p: [
                  'Kérjük, hogy az adatkezeléssel kapcsolatos kérdéseivel, észrevételével keresse munkatársainkat a sales@morgens.hu e-mail címen.',
                ],
              },
              {
                h: 'Kelt',
                p: ['Nagykanizsa, 2018. május 3. · Frissítve: 2026. szeptember 2.'],
              },
            ],
          },
        },

        tickets: {
          title: 'Jegyeim',
          subtitle: 'A fiókjaid támogatási jegyei',
          tabs: { in_progress: 'Folyamatban', closed: 'Lezárt' },
          columns: {
            ticket: 'Jegy',
            account: 'Fiók',
            requester: 'Igénylő',
            assignee: 'Felelős',
            lastActivity: 'Utolsó aktivitás',
            status: 'Státusz',
            // A „Részletek" kibontó (és a mobil-kártya) sor-címkéje — a jegyszám itt már KÓD, nem horgony.
            ticketNumber: 'Jegyszám',
          },
          /** A „Részletek" kibontó másik sora: mikor adta be a partner a jegyet (user-döntés: „Leadva"). */
          submittedAt: 'Leadva',
          closedAt: 'Lezárva',
          columnsLastActivity: 'Utolsó aktivitás',
          searchPlaceholder: 'Keresés jegyszám, tárgy, igénylő…',
          searchLabel: 'Keresés a jegyek közt',
          filters: 'Szűrők',
          clearFilters: 'Törlés',
          allAccounts: 'Összes fiók',
          unread_one: '{{count}} új komment',
          unread_other: '{{count}} új komment',
          // A LISTA-jelvény rövid alakja: a cím mellett a szám + ikon elég (a „komment" szó a TÁRGYTÓL
          // vitte el a helyet). A hosszú alak a kezdőlapon és a részleten marad.
          // A SZÓHASZNÁLAT egységesen „komment" (user-döntés): a szálon a partner és a kolléga
          // hozzászólásai együtt állnak, tehát nem „válasz" — és a belső felület is kommentet mond.
          unreadShort_one: '{{count}} új',
          unreadShort_other: '{{count}} új',
          // A SZÁLON a partner és a kolléga hozzászólásai együtt állnak, ezért „komment" (nem „válasz"):
          // a listában jelvényként áll a komment-szám (user-döntés).
          commentCount_one: '{{count}} komment',
          commentCount_other: '{{count}} komment',
          countShort_one: '{{count}} jegy',
          countShort_other: '{{count}} jegy',
          empty: {
            in_progress: {
              title: 'Nincs folyamatban lévő jegye',
              desc: 'Ha kérdése vagy problémája van, nyisson új jegyet, és kollégáink jelentkeznek.',
            },
            closed: {
              title: 'Még nincs lezárt jegye',
              desc: 'A lezárt kérései itt lesznek elérhetők visszakereshetően.',
            },
          },
          emptyFiltered: {
            title: 'Nincs találat',
            desc: 'Módosítsa a keresést, vagy törölje a szűrőket.',
          },
          count: '{{shown}} / {{total}} jegy',
          // ── részlet ──
          backToList: 'Vissza a listához',
          detailFallback: 'Jegy',
          request: 'Kérés',
          history: 'Előzmény',
          onUs: 'rajtunk a sor',
          awaitingReply: 'válaszra vár',
          access: 'Jegy-hozzáférés',
          accessDisabled:
            'Hozzáférést a jegy nyitója és a fiók elsődleges kapcsolattartója oszthat meg.',
          accessHint:
            'Kiválaszthatja, mely kollégái férhetnek hozzá ehhez a jegyhez — értesítést is kapnak róla. A hozzáférés visszavonása azonnal életbe lép.',
          accessAlways: 'mindent lát',
          accessNoContacts: 'A fiókon nincs másik kapcsolattartó.',
          accessOnlyYou: 'Csak Ön látja ezt a jegyet.',
          // A blokkból CSAK a `assignee` van használatban: a fejléc 2. meta-sorának címkéje. A többi öt
          // kulcsot az „Adatok" kártya loopja hívta — a kártya kivezetésével elárvultak.
          field: {
            assignee: 'Felelős',
          },
          event: {
            opened: 'Jegy megnyitva',
            reply: 'Komment a szálon',
            status: 'Státusz megváltozott',
            closed: 'Jegy lezárva',
          },
          description: 'Leírás',
          attachments: 'Mellékletek',
          noAttachments: 'Nincs melléklet',
          download: 'Letöltés',
          downloadUnavailable: 'A fájl nem elérhető',
          attachFile: 'Fájl csatolása',
          attachUploading: 'Feltöltés…',
          attachmentsEmpty: 'Még nincs melléklet.',
          links: 'Fájl-megosztási linkek',
          linkLabel: {
            googleDrive: 'Google Drive',
            onedrive: 'OneDrive',
            dropbox: 'Dropbox',
          },
          conversation: 'Beszélgetés ({{count}})',
          conversationAria: 'Beszélgetés',
          noComments: 'Még nincs komment ezen a jegyen.',
          you: 'Ön',
          newReply: 'új',
          readOnly:
            'Ezen a fiókon csak megtekintési jogosultságod van, ezért nem tudsz kommentelni.',
          // A DÁTUM-TARTOMÁNY szűrő címkéi. SAJÁT kulcsok (nem a belső `tickets.filterFrom`): a partner-
          // felület kulcs-készlete önálló, és egy belső modul kulcsára hivatkozva egy ottani átfogalmazás
          // csendben átírná a portál feliratát is.
          filterFrom: 'Létrehozva ettől',
          filterTo: 'Létrehozva eddig',
          // a „Lezárt" fül lezárás-dátum tartománya (a `closedAt`-ra)
          filterClosedFrom: 'Lezárva ettől',
          filterClosedTo: 'Lezárva eddig',
          // KOMMENTEK — a „Válaszok" (legacy szóhasználat) helyett: a belső felület is kommentet mond,
          // egy fogalom, egy szó a két felületen (user-döntés).
          comments: 'Kommentek',
          // A PÁR PONTOSAN az adminé (user-kérés): az ÖSSZECSUKOTT sáv „Komment írása…", a kinyitott
          // szerkesztő placeholderje „Írj ide egy kommentet…". Két felület, egy szóhasználat.
          commentCollapsed: 'Komment írása…',
          commentPlaceholder: 'Írj ide egy kommentet…',
          commentLabel: 'Komment szövege',
          commentSend: 'Komment küldése',
          commentHint: 'A támogató csapatunk értesítést kap a kommentjéről.',
          // KI LÁTJA A JEGYET — a fejlécből kivett hozzáférés-gomb új helye, az ADAT mellett.
          visibilityTitle: 'Ki látja ezt a jegyet',
          visibilityHint:
            'A jegyet Ön, a fiók elsődleges kapcsolattartója és az itt felsorolt kollégák látják — rajtuk kívül csak a MORGENS támogató csapata.',
          visibilityUnknown:
            'A jegyhez hozzáféréssel rendelkezők névsorát a fiók elsődleges kapcsolattartója látja.',
          accessManage: 'Hozzáférés',
          noAssignee: 'még nincs kijelölve',
          sending: 'küldés…',
          // A SAJÁT válasz szerkesztése (a szerver soronként adja meg a jogot: `canEdit`).
          editReply: 'Szerkesztés',
          editReplyLabel: 'A komment szerkesztése',
          cancel: 'Mégsem',
          save: 'Mentés',
          saving: 'mentés…',
          notFound: {
            title: 'A jegy nem található',
            desc: 'Lehet, hogy törölték, vagy nincs hozzáférésed ehhez a jegyhez.',
          },
        },
        // ── SZÁMLÁIM ──
        invoices: {
          subtitle: 'Kiállított számlái és aktív szolgáltatásai a következő havi várható díjjal.',
          tabs: { issued: 'Kiállított', paid: 'Kiegyenlített', storno: 'Érvénytelenített' },
          columns: {
            number: 'Számlaszám',
            buyer: 'Számlázási adatok',
            amount: 'Összeg',
            dates: 'Dátumok',
            pdf: 'PDF',
          },
          status: {
            draft: 'piszkozat',
            issued: 'kiállított',
            paid: 'fizetett',
            overdue: 'lejárt',
            cancelled: 'érvénytelenített',
            storno: 'sztornó',
          },
          issued: 'Kelt:',
          due: 'Határidő:',
          paid: 'Fizetve:',
          searchPlaceholder: 'Számlaszám vagy vevő…',
          searchLabel: 'Keresés a számlák közt',
          count_one: '{{count}} számla',
          count_other: '{{count}} számla',
          downloadAria: '{{number}} letöltése PDF-ben',
          overdueTitle_one: '{{count}} lejárt számlája van',
          overdueTitle_other: '{{count}} lejárt számlája van',
          overdueTotal:
            '— összesen {{amounts}}. Kérjük rendezze, vagy jelezze, ha eltérést tapasztal.',
          showOverdue: 'Megnézem',
          overdueSince_one: '{{count}} napja lejárt',
          overdueSince_other: '{{count}} napja lejárt',
          dueIn_one: '{{count}} nap múlva esedékes',
          dueIn_other: '{{count}} nap múlva esedékes',
          empty: {
            title: 'Nincs számla ebben a nézetben',
            issued: 'Ha új számla készül, itt fog megjelenni, és e-mailben is értesítjük.',
            paid: 'Kiegyenlített számla még nem szerepel ebben a nézetben.',
            storno: 'Nem volt érvénytelenített számlája — ez jó hír.',
          },
          myServices: 'Szolgáltatásaim',
          itemCount_one: '{{count}} tétel',
          itemCount_other: '{{count}} tétel',
          activeAssignments: 'Aktív hozzárendelések',
          pending: 'függőben',
          usageBased: 'használat-alapú',
          usageAmount: 'használat szerint',
          period: { monthly: 'hó', yearly: 'év', one_time: 'egyszeri', recurring: 'hó' },
          noServices: {
            title: 'Nincs aktív szolgáltatása',
            desc: 'Ha szolgáltatást indítunk a fiókján, itt fog megjelenni.',
          },
          nextMonth: 'Következő havi várható díj',
          nextMonthOverline: '{{month}} · várható',
          noExpected: 'A következő hónapra nincs esedékes, fix díjas tétele.',
          variableWarn:
            'Az összeg használat alapján változhat — a végleges érték a hónap végén dől el.',
          fxNote:
            'A devizás tételek az MNB középárfolyamán, tájékoztató jelleggel szerepelnek forintban.',
        },
        // ── SZÁMLÁZÁS (havi áttekintés · számláim · szolgáltatásaim) ──
        billing: {
          title: 'Szolgáltatások és díjak',
          subtitle:
            'Havi bontásban látja, mit számláztunk már (tény) és mi esedékes még (terv) — tételesen.',
          views: {
            aria: 'Számlázási nézetek',
            month: 'Havi áttekintés',
            invoices: 'Számlák',
            services: 'Szolgáltatások',
          },
          month: {
            picker: 'Hónap-választó',
            basis: { actual: 'Tény', plan: 'Terv', mixed: 'Vegyes' },
            basisHint: {
              actual: 'Ebben a hónapban minden tétel ki van számlázva.',
              plan: 'Ebben a hónapban még nincs számla — a tételek tervezettek.',
              mixed: 'Ebben a hónapban részben kiszámlázott, részben még tervezett tételek vannak.',
            },
            totalsTitle: 'A hónap nettó összege',
            invoicesTitle: 'Számlák',
            planTitle: 'Még nem számlázott (terv)',
            planHint:
              'Ezekre a tételekre ebben a hónapban várható számla — a számlázás hónapjában fizeti őket.',
            noInvoices: 'Ebben a hónapban nincs számla.',
            empty: {
              title: 'Ebben a hónapban nincs számlázás',
              desc: 'Nincs sem kiállított számla, sem esedékes tétel erre a hónapra.',
            },
          },
          invoice: {
            draft: 'Előkészítés alatt',
            draftHint: 'A számla még készül — a PDF a kiállítás után tölthető le.',
            showItems: 'Tételek és megjegyzés',
            hideItems: 'Tételek elrejtése',
            noItems: 'Ehhez a számlához nincs tétel.',
            note: 'Megjegyzés a számlán',
            itemsAria: 'A számla tételei',
            quantity: 'Mennyiség',
            unitPrice: 'Egységár',
            period: 'Időszak',
            details: 'Tételek',
            detailsAria: '{{number}} tételei',
            sheetTitle: 'Számla tételei',
          },
          plan: {
            usageAmount: 'használat szerint',
            estimate: 'becslés',
            estimateHint: 'az előző számla alapján',
            prorated: 'arányosított',
          },
          hiddenShared: {
            notice_one:
              '{{count}} további csoportos tétel is vonatkozik a fiókodra, de olyan fiókokat is érint, amelyekhez nincs díj-hozzáférésed, ezért nem tekintheted meg.',
            notice_other:
              '{{count}} további csoportos tétel is vonatkozik a fiókodra, de olyan fiókokat is érint, amelyekhez nincs díj-hozzáférésed, ezért nem tekintheted meg.',
            noticeInvoices_one:
              '{{count}} csoportos számla is vonatkozik a fiókodra, de olyan fiókokat is érint, amelyekhez nincs díj-hozzáférésed, ezért nem tekintheted meg.',
            noticeInvoices_other:
              '{{count}} csoportos számla is vonatkozik a fiókodra, de olyan fiókokat is érint, amelyekhez nincs díj-hozzáférésed, ezért nem tekintheted meg.',
            totalsVisible: 'A megtekinthető tételek nettó összege',
            restrictedTitle: 'Csoportosan számlázva',
            restrictedAmount: 'Csoportosan számlázva',
            restrictedHint:
              'A díj nem látható, mert a szolgáltatás olyan fiókokat is érint, amelyekhez nincs díj-hozzáférésed.',
          },
          shared: {
            title_one: 'Közös számlázás — {{count}} fiókra vonatkozik együtt',
            title_other: 'Közös számlázás — {{count}} fiókra vonatkozik együtt',
            accounts: 'Fiókok: {{names}}',
            other_one: '+{{count}} további fiók',
            other_other: '+{{count}} további fiók',
          },
          frequency: {
            monthly: 'havi',
            quarterly: 'negyedéves',
            semi_annual: 'féléves',
            annual: 'éves',
            one_time: 'egyszeri',
          },
          per: {
            monthly: '/hó',
            quarterly: '/negyedév',
            semi_annual: '/félév',
            annual: '/év',
            one_time: 'egyszeri',
          },
          services: {
            countBadge_one: '{{count}} szolgáltatás',
            countBadge_other: '{{count}} szolgáltatás',
            usageBased: 'használat-alapú',
            pending: 'függőben',
            usageAmount: 'használat szerint',
            empty: {
              title: 'Nincs szolgáltatása',
              desc: 'Ha szolgáltatást indítunk a fiókján, itt fog megjelenni.',
            },
            periodsAria: '{{name}} időszakai',
          },
        },
        // ── KEZDŐLAP ──
        home: {
          greeting: 'Üdvözöljük, {{name}}!',
          greetingNoName: 'Üdvözöljük!',
          waiting_one: '{{count}} jegyén új komment vár Önre.',
          waiting_other: '{{count}} jegyén új komment vár Önre.',
          allClear:
            'Nincs teendője — minden jegye naprakész. Ha kérdése van, nyisson egy új jegyet.',
          tiles: {
            openTickets: 'Folyamatban lévő jegyem',
            unreadTickets: 'Új komment a jegyeimen',
            messages: 'Üzenet és meghívó',
            overdueInvoices: 'Lejárt számla',
          },
          recentTickets: 'Legutóbbi jegyeim',
          viewAll: 'Összes',
          meetings: 'Közelgő megbeszélések',
          // JELZÉS szűkített nézetben: ez a blokk SZÁNDÉKOSAN nem szűkül a kiválasztott fiókra.
          allAccountsNote: 'minden fiók',
          unreadMessages: 'Olvasatlan üzenetek',
          feeUnavailable: 'A várható díj most nem érhető el.',
          feeItems_one: '{{count}} esedékes tétel',
          feeItems_other: '{{count}} esedékes tétel',
          noOpen: {
            title: 'Nincs folyamatban lévő jegye',
            desc: 'Minden kérése lezárult.',
          },
          noUnread: {
            title: 'Nincs olvasatlan üzenete',
            // SZÁNDÉKOSAN nem említ meghívót: azok a SZOMSZÉDOS „Válaszra vár" kártyán jelennek meg, tehát a
            // korábbi szöveg („körüzenetek és meghívók") félrevezető volt (user-lelet).
            desc: 'A korábbi körüzenetek az Üzenetek lapon maradnak elérhetők.',
          },
          noMeetings: {
            title: 'Nincs közelgő megbeszélése',
            desc: 'Ha megbeszélést egyeztetünk Önnel, itt fog megjelenni — és itt tud rá válaszolni.',
          },
        },
        // ── JEGY-NYITÁS (3-lépéses, vezetett űrlap) ──
        newTicket: {
          action: 'Új jegy',
          title: 'Jegy nyitása',
          subtitle:
            'Három rövid lépés — minél pontosabb a leírás, annál gyorsabban tudunk segíteni.',
          stepsAria: 'Lépések',
          steps: { where: 'Hol?', what: 'Miről?', details: 'Részletek' },
          client: 'Ügyfél',
          clientHint: 'A képviselt ügyfél — a jegy ehhez az ügyfélhez tartozik.',
          hasAccess: 'hozzáférése van',
          whichAccount: 'Melyik fiókot érinti?',
          youArePrimary: 'Ön az elsődleges kapcsolattartó',
          viewerOnly: 'csak megtekintés',
          servicesOfAccount: 'Szolgáltatásaim',
          otherServices: 'További szolgáltatásaink',
          activeService: 'aktív',
          noServices: 'Ehhez a fiókhoz nem találtunk választható szolgáltatást.',
          type: 'Jegytípus',
          subject: 'Tárgy',
          subjectPlaceholder: 'Rövid, beszédes összefoglaló',
          description: 'Kérés',
          descriptionTip:
            'Írja le minél részletesebben, mi történt, milyen lépések után, és milyen eszközön. Képernyőfotó csatolása sokat gyorsít a megoldáson.',
          descriptionPlaceholder: 'Mi történt? Milyen lépések után? Melyik eszközön / böngészőben?',
          descriptionOk: 'Köszönjük, ez elég részletes.',
          descriptionRemaining_one: 'Még {{count}} karakter szükséges',
          descriptionRemaining_other: 'Még {{count}} karakter szükséges',
          attachmentsAdd: 'Fájl csatolása',
          attachmentsRemove: '{{name}} eltávolítása',
          linksHint: 'Ha nagy fájlokat oszt meg, illessze be a linket.',
          linkPlaceholder: 'https://…',
          prev: 'Vissza',
          next: 'Tovább',
          submit: 'Jegy létrehozása',
          errors: {
            service: 'Válasszon szolgáltatást.',
            subject: 'A tárgy megadása kötelező.',
            subjectMax: 'Legfeljebb {{max}} karakter.',
          },
          readOnly: {
            title: 'Nem tud jegyet nyitni',
            desc: 'A fiókjain csak megtekintési jogosultsága van. Kérje meg a fiók elsődleges kapcsolattartóját, hogy adjon szerkesztési jogot.',
          },
          // KONFIGURÁCIÓS akadály (nem átmeneti hiba): a lap ezért magyarázatot mutat, nem „Újra" gombot.
          unavailable: {
            title: 'A jegynyitás most nem érhető el',
            desc: 'Technikai okból nem tudjuk betölteni a szolgáltatásait, ezért új jegyet most nem lehet nyitni. Dolgozunk rajta — kérjük, próbálja meg később, vagy írjon nekünk e-mailben.',
          },
        },
        status: {
          open: 'Nyitott',
          in_progress: 'Folyamatban',
          resolved: 'Megoldva',
          // a belső felülettel egységesen „Lezárva" (Évi #86)
          closed: 'Lezárva',
          reopened: 'Újranyitva',
        },
        // a státuszok rövid SÚGÓJA a jegy státusz-jelvényén (Évi #86)
        statusHelp: {
          open: 'Megkaptuk a jegyet, hamarosan foglalkozunk vele.',
          in_progress: 'Kollégánk dolgozik rajta.',
          resolved: 'Megoldottuk: a hibát javítottuk, illetve a kérést teljesítettük.',
          closed: 'Megoldás nélkül lezártuk (pl. ismétlődő vagy nem reprodukálható bejelentés).',
          reopened: 'A jegyet újranyitottuk, ismét foglalkozunk vele.',
        },
        type: {
          support: 'Támogatás',
          bug: 'Hibabejelentés',
          feature_request: 'Új funkció igénylése',
          billing: 'Számlázás',
          other: 'Egyéb',
        },
        // ── ÜZENETEK ÉS MEGHÍVÓK ──
        messages: {
          subtitle: 'Tőlünk kapott üzenetei és a közelgő megbeszélés-meghívói.',
          // „MEGBESZÉLÉSEIM", nem „meghívóim" (user-döntés): a blokk a MEGBESZÉLÉSEKET sorolja — a meghívó
          // csak az értesítés formája —, és a rendszer modul-neve is „Megbeszélések".
          meetingsTitle: 'Közelgő megbeszéléseim',
          awaitingAnswer_one: '{{count}} válaszra vár',
          awaitingAnswer_other: '{{count}} válaszra vár',
          inboxTitle: 'Bejövő üzenetek',
          unreadCount_one: '{{count}} olvasatlan',
          unreadCount_other: '{{count}} olvasatlan',
          scope: { broadcast: 'körlevél', account: 'a fiókjának', contact: 'személyesen Önnek' },
          organizer: 'Szervező:',
          minutes_one: '{{count}} perc',
          minutes_other: '{{count}} perc',
          rsvp: { pending: 'válaszra vár', confirmed: 'ott leszek', declined: 'nem megyek' },
          willAttend: 'Ott leszek',
          wontAttend: 'Nem megyek',
          changeAnswer: 'Válasz módosítása',
          markAllRead: 'Mind olvasott',
          noMeetings: {
            title: 'Nincs közelgő megbeszélése',
            desc: 'Ha megbeszélést egyeztetünk Önnel, itt fog megjelenni.',
          },
          noMessages: {
            title: 'Nincs üzenete',
            desc: 'Az e-mailben kiküldött tájékoztatóink itt is megjelennek.',
          },
          // OLVASÓ-OLDALI ARCHÍVUM — a saját nézetből tünteti el az üzenetet (a többi címzettnél megmarad)
          archivedView: 'Archivált',
          archivedTitle: 'Archivált üzenetek',
          archive: 'Archiválás',
          unarchive: 'Visszaállítás',
          noArchived: {
            title: 'Nincs archivált üzenete',
            desc: 'Az archivált üzenetek itt jelennek meg, és innen visszaállíthatók.',
          },
          unavailable: {
            title: 'Az üzenetek most nem érhetők el',
            desc: 'Technikai okból nem tudjuk betölteni az üzeneteit. Dolgozunk rajta — próbálja meg később.',
          },
        },
        // A hiba-állapot PARTNER-hangnemben (a belső felület tegez — lásd `PortalErrorState`).
        loadError: {
          title: 'Nem sikerült betölteni',
          desc: 'Átmeneti hiba történt a kapcsolatban. Kérjük, próbálja újra.',
        },
        noAccess: {
          title: 'Nincs portál-hozzáférésed',
          desc: 'Kérd meg a fiókod elsődleges kapcsolattartóját, hogy adjon hozzáférést.',
          pageTitle: 'Nincs hozzáférésed ehhez az oldalhoz',
          pageDesc:
            'A szereped nem tartalmazza ezt a funkciót. Kérd meg a fiókod elsődleges kapcsolattartóját, hogy adjon hozzáférést.',
          scopeDesc:
            'A kiválasztott fiókon nincs hozzáférésed ehhez az oldalhoz. Válassz másik fiókot, vagy kérd meg a fiók elsődleges kapcsolattartóját.',
        },
      },
      auth: { logout: 'Kijelentkezés' },
      usageData: {
        // A „Megjegyzés" oszlop: lesz-e a mérésből számla-tétel, és ha nem, MIÉRT (badge + magyarázó mondat).
        billingNote: {
          label: {
            billed: 'Ebből számlázunk',
            billed_inactive: 'Inaktív — nem számlázunk',
            other_variant: 'Másik sorból számlázunk',
            tracking: 'Nyilvántartásos hozzárendelés',
            bound_elsewhere: 'Másik azonosítóra kötve',
            ended: 'A szolgáltatás lezárult',
            not_started: 'A szolgáltatás később indul',
            no_assignment: 'Mért érték, nincs szolgáltatás',
            unused: 'Nem igénybe vett szolgáltatás',
            no_account: 'Nincs fióknál beállítva',
            no_account_unused: 'Nincs fióknál beállítva',
            not_final: 'HotelCMS-ben nincs lezárva',
            disabled: 'HotelCMS-ben kikapcsolva',
            combined_minimum: 'HotelCMS-ben összevont minimum',
          },
          desc: {
            billed:
              'Ezt az adatot egy élő szolgáltatás-hozzárendelés használja — ebből készül a számla-tétel.',
            // LEZÁRT, azóta inaktív sor: a záró dátumig számlázunk (a generátorral azonos szabály)
            billed_closed:
              'A(z) „{{sourceType}}" szolgáltatás {{date}}-ig élt, és azóta {{inactive}} inaktív — erre a hónapra még számlázunk belőle (a záró dátumig).',
            other_variant:
              'A szálláshely szerződése a(z) „{{usedSourceType}}" sorból számláz; ez a sor csak tájékoztató bontás, ebből külön tétel nem készül.',
            billed_inactive:
              'Erre a hónapra van „{{sourceType}}" szolgáltatás-hozzárendelés, de {{inactive}} — záró dátum nélkül ebből nem készül számla. Ha a szolgáltatás megszűnt, add meg a záró dátumot: addig számlázunk.',
            ended:
              'A(z) {{ref}} azonosítójú fiók „{{sourceType}}" szolgáltatása {{date}}-ig élt, ezért erre a hónapra nem számlázunk belőle.',
            not_started:
              'A(z) {{ref}} azonosítójú fiók „{{sourceType}}" szolgáltatása {{date}}-tól indul, ezért erre a hónapra még nem számlázunk belőle.',
            tracking:
              'Van hozzárendelés erre az azonosítóra („{{sourceType}}"), de nyilvántartásos, ezért nem készül belőle számlatétel. Ha számlázni kellene, a hozzárendelés típusát állítsd számlázásra.',
            bound_elsewhere:
              'A(z) {{ref}} azonosító a fiókhoz tartozik, és a fióknak van erre a hónapra „{{sourceType}}" hozzárendelése — de az egy MÁSIK azonosítóra van kötve, ezért ebből a sorból nem készül számlatétel. Ellenőrizd a hozzárendelés „Forrás azonosító" mezőjét.',
            no_assignment:
              'Erre az adatra van mért érték, de a(z) {{ref}} azonosítójú fióknak nincs „{{sourceType}}" szolgáltatás-hozzárendelése erre a hónapra, ezért nem készül belőle számla. Ha számlázni kellene, vegyél fel hozzárendelést.',
            unused:
              'A(z) {{ref}} azonosítójú fiók nem használja a(z) „{{sourceType}}" szolgáltatást (a mért érték 0, és nincs rá hozzárendelés) — ez nem hiány.',
            no_account:
              'Egyik fiókon sincs a(z) {{ref}} azonosító, pedig van rá mért érték — az adat nem köthető ügyfélhez. Vidd fel az azonosítót a megfelelő fiók adatlapján.',
            no_account_unused:
              'Egyik fiókon sincs a(z) {{ref}} azonosító; a mért érték 0, így most nem vész el semmi. Ha a szálláshely a miénk, vidd fel az azonosítót a fiók adatlapján.',
            not_final:
              'A HotelCMS-ben ez a hónap még nincs lezárva, ezért a részleges összeget nem számlázzuk. Lezárás után az Atlas a következő hajnali lekéréskor magától beírja.',
            disabled:
              'A HotelCMS-ben ez az ág / modul ki van kapcsolva, ezért nem ad összeget. Ha a szolgáltatás él, kapcsolják be a HotelCMS-ben; ha nem, a hozzárendelést le kell zárni.',
            combined_minimum:
              'A szálláshely a HotelCMS-ben összevont minimummal zár: a direkt és az ajánlatkérés ág itt nyers összeg, a számlázandó a nagyobbik ág vagy a közös minimum — ezt csak a „Direkt + AK" (modul-végösszeg) sor hordozza. Ágankénti hozzárendelésből ezért nem készül számla: állítsd át a hozzárendelést Direkt + AK-ra.',
          },
          // a `billed_inactive` mondat {{inactive}} helye: MI inaktív
          // a mondatok {{inactive}} helye: MI nem aktív (a `billed_inactive` mondatban az állapottal együtt)
          inactive: {
            assignment: 'a szolgáltatás-hozzárendelés',
            account: 'a fiók',
            client: 'az ügyfél',
          },
          inactiveState: {
            assignment: 'a szolgáltatás-hozzárendelés inaktív',
            pending: 'a szolgáltatás-hozzárendelés függő (még nem indult)',
            account: 'a fiók inaktív',
            client: 'az ügyfél inaktív',
          },
        },
        // A KOMBINÁLT RoomSome-sor ág-bontása: a lap ezt az egy sort mutatja a két ág sora HELYETT, ha a
        // szerződés a modul-végösszegre szól — a bontás mondja meg, miből áll a szám.
        branch: {
          direct: 'ebből direkt: {{amount}} {{currency}} · {{count}} foglalás',
          inquiry: 'ebből ajánlatkérés: {{amount}} {{currency}} · {{count}} foglalás',
        },
        prevMonth: 'Előző hónap',
        nextMonth: 'Következő hónap',
        filterType: 'Típus',
        filterAssignment: 'Hozzárendelés',
        assignmentFilter: {
          // a mért, de be nem sorolt sorok: az azonosító egyik fiókon sincs, vagy nincs ilyen hozzárendelés
          missing: 'Hiányzó hozzárendelés',
        },
        // ALAPDÍJ + SÁVOS tétel: a bizonylat két tétele egy sorban (alapdíj + a mérésből számolt rész)
        amountParts: {
          registrants: 'alapdíj {{base}} + regisztráltak után {{measured}}',
          measured: 'alapdíj {{base}} + mérés után {{measured}}',
        },
        // MEGOSZTOTT HASZNÁLAT: miből jön a rész
        amountShared: 'a regisztráltak utáni {{total}} {{count}} hotel között osztva',
        consumers: {
          // több hozzárendelés is fogyasztja a mérést — mindegyik a saját összegével
          title: '{{count}} hozzárendelés használja',
          unknownOwner: 'ismeretlen fiók',
        },
        splitMismatch:
          'A regisztráltak utáni díj megosztása nincs minden hotelen bekapcsolva — a rész kevesebb felé oszlik.',
        loading: 'betöltés…',
        countOf: '{{shown}} / {{total}} sor',
        noAccount: 'nincs feloldott fiók',
        more: '+{{count}}',
        moreItems: 'További fiókok megjelenítése',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'A szűrőknek nincs megfelelő használati sor. Módosítsd a szűrőket.',
        empty: 'Nincs használati adat',
        emptyDesc:
          'Erre a hónapra nincs külső-rendszer használati adat. A usage-lekérés (külső integráció) még nem aktív — a historikus (ETL) sorok jelennek meg.',
        columns: {
          sourceType: 'Típus',
          source: 'Forrás-azonosító / fiók',
          period: 'Időszak',
          // „Kapott adat", nem „Mennyiség" és nem „Mért érték" (user-döntés): az adatot KÜLSŐ rendszertől
          // kapjuk, nem mi mérjük, és forrás-típusonként mást jelent — hírlevél-darabot, regisztráló főt,
          // illetve jutalék-összeget. A mértékegység a cellában áll.
          measured: 'Kapott adat',
          note: 'Megjegyzés',
        },
        /** A mért érték MÉRTÉKEGYSÉGE (a pénz-devizát a szerver adja, az nem itt él). */
        unit: {
          count: 'db',
          person: 'fő',
        },
        /** Miért NINCS számolt összeg a soron — csak ott, ahol a hiány kimondható. */
        amountNote: {
          no_rate: 'nincs árfolyam a hónapra',
          aggregated: 'összevont hozzárendelés',
          // A mért adat MEGVAN, de ez a hónap EGYETLEN számlázási periódusba sem esik (a szerződés kezdete
          // előtt vagy a lezárása után). A periódus hónapjai MIND számlára kerülnek — mindegyik a saját
          // árával —, tehát ez a jelzés NEM a periódus 2.–12. hónapját jelöli.
          not_due: 'nincs rá számlázási időszak',
          auto_billing_off: 'nincs automata számlázás',
          // KÜLÖN ok a kapcsolótól — ugyanaz a bontás, mint a generátor kihagyás-naplójában (más a teendő).
          owner_inactive: 'az ügyfél / fiók inaktív',
        },
        /**
         * A HASZNÁLAT-FORRÁS NYOLC TÍPUSA — a rendszer EGYETLEN készlete.
         *
         * MIÉRT EGY HELYEN: ugyanez a nyolc kulcs jelenik meg a hozzárendelés-űrlapon, a szolgáltatás-terv
         * szerkesztőjében, a számlák kihagyás-naplójában és ezen a lapon. HÁROM külön készlet élt belőle, és
         * el is tértek egymástól: ugyanaz a forrás „RoomSome hűség" volt az egyik lapon és „Loyalty
         * regisztráció" a másikon — a kezelő nem tudta, ugyanarról beszélnek-e. (Angolul a két másolat is
         * elvált: „referral" az egyikben, „inquiry" a másikban.)
         *
         * A SZÓHASZNÁLAT a legacy CRM-é (user-döntés): azt ismeri a csapat, és az áll a partner-levelezésben.
         * A MÁRKANEVEKET NEM FORDÍTJUK — a `Loyalty`, a `Voucher` és a `Ticket` RoomSome termék-nevek, a
         * „hűség" fordítás félrevezető volt. Az „AK" rövidítés KIÍRVA: „ajánlat" (user-döntés).
         */
        sourceType: {
          newsletter: 'Hírlevél',
          loyalty_newsletter: 'Loyalty hírlevél',
          rs_loyalty: 'RoomSome Loyalty',
          rs_direct_referral: 'RoomSome jutalék (direkt + ajánlat)',
          rs_direct: 'RoomSome jutalék (direkt)',
          rs_referral: 'RoomSome jutalék (ajánlat)',
          rs_voucher: 'RoomSome Voucher jutalék',
          ticket: 'Ticket jutalék',
        },
        // A forrás-oszlop azonosító-chipje („MailWizz ID 132") — a RENDSZER-NEVEK nem fordulnak, de
        // i18n-kulcsból jönnek (nincs hardcode). Ismeretlen forrásnál a semleges „ID".
        idChip: 'ID',
        sourceSystemChip: {
          mailwizz: 'MailWizz ID',
          hotelcms: 'HotelCMS ID',
          zadircms: 'ZadirCMS ID',
        },
        // A nem-HAVI hozzárendelés összege a TELJES periódus díja — a szám különben havi díjnak látszana.
        amountPeriod: {
          quarterly: 'negyedéves díj',
          semi_annual: 'féléves díj',
          annual: 'éves díj',
        },
        // Az ELSŐ számla arányosított tétele (a periódus első hónapjának fele hiányzik).
        amountProrated: 'arányosított',
        // A számlázott időszak, amit az összeg fedez (tooltip az összegen).
        amountPeriodRange: 'Számlázott időszak: {{start}} – {{end}}',
        // HAVI LEKÉRÉS a külső rendszerekből (a cron mellett kézi indítás a látott hónapra).
        fetch: {
          action: 'Lekérés',
          running: 'Lekérés…',
          title: 'Használati adatok lekérése a külső rendszerekből',
          done: '{{month}}: {{written}} sor frissítve',
          empty: '{{month}}: nem érkezett új adat',
          // A BUKOTT ÍRÁSOK külön üzenete: ÚJRAPRÓBÁLHATÓK, és a „{{written}} sor frissítve" mögött
          // elvesznének — pedig épp ezek igénylik, hogy valaki visszajöjjön.
          failedRows: '{{count}} sor írása nem sikerült — a következő lekérés pótolja',
          skipped: 'Kihagyva: {{list}}',
          reason: {
            no_source_api: 'nincs adatforrás',
            not_configured: 'nincs beállítva',
            upstream_error: 'a külső rendszer nem válaszolt',
            period_unsupported:
              'csak az aktuális vagy az előző hónapra kérhető (régebbire a forrás már nem a valós adatot adja)',
          },
        },
        rates: {
          title: 'Havi manuális árfolyam',
          subtitle: 'A hó 1-jére érvényes EUR/USD → HUF (a generátor ezt használja).',
          none: 'Árfolyam',
          missing: 'nincs megadva',
          base: 'Deviza',
          rate: 'Árfolyam',
          ratePlaceholder: 'pl. 398,5',
          save: 'Mentés',
          saving: 'Mentés…',
          // Sor-szintű műveletek: minden deviza a SAJÁT sorában szerkeszthető/törölhető (nincs külön
          // „felvitel" blokk — az volt a félreértés forrása: a felhasználó nem találta, hol írhat át egy
          // meglévő kurzust).
          editRate: '{{base}} árfolyam szerkesztése',
          deleteRate: '{{base}} árfolyam törlése',
          addRate: '{{base}} árfolyam megadása',
          cancel: 'Mégse',
          deleting: 'Törlés…',
          // A tizedes VESSZŐ is elfogadott (magyar bevitel) — a mentés pontra normalizál.
          invalidHint: 'Csak szám, tizedes vesszővel vagy ponttal (max. 8 tizedes).',
          hint: 'A hó 1-jére, `manual` forrással mentjük — a generátor ezt olvassa.',
          readOnly: 'Az árfolyam módosítása számlázás-kezelő jogot igényel (megtekintés szabadon).',
        },
      },
      common: {
        // MENTETLEN VÁLTOZÁS a szerkesztő-sávban: mellékattintás / Esc / X előtt rákérdezünk (`FormSheet` `dirty`)
        unsavedChanges: {
          title: 'Elveted a változtatásokat?',
          desc: 'Az űrlapon mentetlen módosítás van. Ha most bezárod, elvész.',
          discard: 'Elvetés',
          keepEditing: 'Szerkesztés folytatása',
        },
        // TÖRÖLT (offboardolt) személy megjelenítése: a tartalma (komment, üzenet, hozzárendelés) megmarad,
        // a neve viszont már nem oldható fel — SOHA nem nyers UUID és nem hazug fallback („Nincs felelős").
        deletedUser: 'Törölt felhasználó',
        // Élő portál-grant törölt fiókon (adathiba-jelzés a jogosultság-áttekintőn).
        deletedAccount: '(törölt fiók)',
        // A veszélyzóna CÍME közös (minden detail-oldalon ugyanaz); a benne lévő művelet szövege modul-specifikus.
        dangerZone: 'Veszélyzóna',
        // A lap-szintű jelzések lenyitható sora (`PageNotices`): a zárt sor tónusonként darabszámot mutat.
        pageNotices: {
          title: 'Információk',
          count: {
            danger_one: '{{count}} kritikus',
            danger_other: '{{count}} kritikus',
            warning_one: '{{count}} figyelmeztetés',
            warning_other: '{{count}} figyelmeztetés',
            neutral_one: '{{count}} megjegyzés',
            neutral_other: '{{count}} megjegyzés',
          },
        },
        searchPlaceholder: 'Keresés…',
        appSelector: 'Alkalmazás',
        error: 'Hiba',
        notAuthorized: 'Nincs jogosultság',
        edit: 'Szerkesztés',
        details: 'Részletek',
        // Igen/nem — az eseménynapló diff-panelje a legacy STRING-boolean értékeit (`'true'`/`'enabled'`)
        // ezekkel írja ki (a nyers `true` szó helyett); lásd `features/activity-log/value-label.ts`
        yes: 'Igen',
        no: 'Nem',
        // A lista-csonkolás jelzése (a BFF `truncated` mezője): a védő-plafon elérésekor a lista és a
        // darabszám sem teljes — ezt KI KELL írni, mert egy csendben elvágott lista hibás döntést alapoz meg.
        listTruncated: 'A lista csonkolt (túl sok találat) — szűkíts a szűrőkkel!',
        save: 'Mentés',
        saving: 'Mentés…',
        cancel: 'Mégse',
        // A SOR-műveletek közös szavai (a listák akció-oszlopa és a törlés-megerősítők) — modulonként
        // ugyanaz a szó, ezért a közös rétegben él.
        delete: 'Törlés',
        actions: 'Műveletek',
        // A bezáró vezérlők (modál/beúszó/kártya) KÉPERNYŐOLVASÓ-címkéje is ebből jön — korábban három
        // helyen HARDCODE-olt magyar `sr-only` szöveg volt, tehát angol felületen is magyarul hangzott el.
        close: 'Bezárás',
        comingSoon: 'Hamarosan',
        selected: 'kijelölve',
        all: 'Mind',
        slugHint: 'Azonosító (slug)',
        none: 'Egyik sem',
        select: 'Válassz…',
        clear: 'Törlés',
        edited: 'Szerkesztve',
        editedAt: 'Szerkesztve: {{date}}',
        time: 'Idő',
        // A DÁTUM-/IDŐ-mezők placeholderei: a form-mezők FELETT már ott a címke, ezért a placeholder a VÁRT
        // FORMÁTUMOT mutatja (best practice), nem a címkét ismétli meg. A megjelenítés locale-hű: HU-ban
        // „ÉÉÉÉ. HH. NN.", EN-ben „YYYY-MM-DD" — ugyanaz, amit a kiválasztás után ki is ír a mező.
        datePlaceholder: 'ÉÉÉÉ. HH. NN.',
        timePlaceholder: 'ÓÓ:PP',
        // JELSZÓ-POLICY (a szerver az autoritás: min. 12 karakter, kis+nagy+szám) — KÖZÖS kulcsok, mert két
        // felület mutatja: a jelszó-beállító lap és az „Új munkatárs" kezdő-jelszó mezője. Ismeretlen kód →
        // `invalid` (nyers kód SOHA nem jelenhet meg).
        passwordPolicyHint: 'Legalább 12 karakter, kis- és nagybetűvel, valamint számmal.',
        passwordPolicy: {
          too_short: 'A jelszó túl rövid — legalább 12 karakter kell.',
          too_long: 'A jelszó túl hosszú (legfeljebb 200 karakter).',
          missing_uppercase: 'Legalább egy nagybetű kell.',
          missing_lowercase: 'Legalább egy kisbetű kell.',
          missing_digit: 'Legalább egy szám kell.',
          invalid: 'A jelszó nem felel meg a biztonsági szabályoknak.',
        },
        // A globális naptár (Calendar) drill-downja: a fejléc-címke nap → hónap → év rácsot nyit; a nyilak
        // a11y-neve a NÉZETHEZ igazodik (különben egy hónap-rácson „Előző hónap" hangzana el).
        calendar: {
          prevMonth: 'Előző hónap',
          nextMonth: 'Következő hónap',
          prevYear: 'Előző év',
          nextYear: 'Következő év',
          prevYears: 'Előző évek',
          nextYears: 'Következő évek',
          pickMonth: 'Hónap választása',
          pickYear: 'Év választása',
          pickDay: 'Nap választása',
        },
        loading: 'Betöltés…',
        noResults: 'Nincs találat',
        noAccessTitle: 'Nincs jogosultságod',
        noAccessDesc:
          'Nincs jogosultságod ennek a megtekintéséhez. Ha szükséged van rá, szólj a rendszergazdának.',
        loadErrorTitle: 'Nem sikerült betölteni',
        loadErrorDesc: 'Váratlan hiba történt. Próbáld újra.',
        retry: 'Újra',
        filters: 'Szűrők',
        clearFilters: 'Szűrők törlése',
        // A detail-oldalak kapcsolódó szekcióinak LÁB-LINKJE: a szűrt lista-oldalra visz (a szekció a
        // teljes halmazt kiírja, a szűrők/oszlopok/tömeges műveletek viszont a lista-oldalon élnek).
        openFilteredList: 'Részletes megtekintés',
        activeFilters: '{{count}} aktív szűrő',
        // A KÖZÖS több-választós picker összefoglaló felirata (a nevek helyett, ha sok van kijelölve).
        selectedCount: '{{count}} kiválasztva',
        // A KÖZÖS hónap-léptető (`MonthStepper`) a11y-nevei — három fogyasztó (használati adatok,
        // bevétel-előrejelzés, lista-eszköztár `month` szűrő) osztja őket.
        prevMonth: 'Előző hónap',
        nextMonth: 'Következő hónap',
        month: 'Hónap',
        noCreatePermission: 'Nincs jogosultság a létrehozáshoz.',
        // LISTA-EXPORT (a legacy „Export" gombja) — a hat érintett lista-oldal KÖZÖS feliratai.
        export: {
          label: 'Export',
          xlsx: 'Excel (.xlsx)',
          csv: 'CSV (.csv)',
          json: 'JSON (.json)',
          pending: 'Exportálás…',
          pendingHint: 'Az export folyamatban van',
          emptyHint: 'Nincs exportálható sor',
          // A darabszám KIMONDÁSA szándékos: a legacy csendben csak az aktuális lapot exportálta.
          done: '{{count}} sor exportálva',
          partial: 'Csak {{count}} sor exportálva — a lista nem tölthető be teljesen',
          empty: 'Nincs exportálható sor',
          failed: 'Az exportálás nem sikerült',
        },
        // A kétnyelvű mezők nyelv-toldaléka (sr-only, a nyelv-zászló mellett). A hu/en itt a MEZŐ nyelvét jelöli.
        langSuffix: { hu: '(magyar)', en: '(angol)' },
      },
      login: {
        title: 'Üdv újra!',
        subtitle: 'Jelentkezz be az Atlas fiókodba.',
        email: 'Email cím',
        password: 'Jelszó',
        forgot: 'Elfelejtett jelszó?',
        remember: 'Emlékezz rám',
        submit: 'Belépés',
        submitting: 'Belépés…',
        errInvalid: 'Hibás e-mail vagy jelszó',
        errFailed: 'Nem sikerült a bejelentkezés',
        // A jelszó JÓ, de a fiókhoz még nincs Atlas-hozzáférés (nem app-tag) — pl. frissen felvett munkatárs.
        errNoAccess:
          'A jelszó helyes, de ehhez a fiókhoz még nincs Atlas-hozzáférés. Kérj jogosultságot a rendszergazdától.',
        // A 401-től KÜLÖNVÁLASZTVA: ezek nem a jelszóról szólnak.
        errThrottled: 'Túl sok próbálkozás. Várj néhány percet, és próbáld újra.',
        errUnavailable:
          'A bejelentkezés átmenetileg nem elérhető (a kiszolgáló nem válaszol). Próbáld újra pár másodperc múlva.',
        mfaLabel: 'Kétlépcsős kód',
        mfaPlaceholder: '123456',
        mfaHint: 'Add meg a hitelesítő appod 6 jegyű kódját vagy egy recovery-kódot.',
        mfaInvalid: 'Hibás vagy lejárt kód',
        heroEyebrow: 'Belső platform',
        heroLine1: 'Egyetlen platform',
        heroLine2: 'minden ügyfélinterakcióhoz.',
        heroBody: 'CRM, ticketing, eszközleltár, számlázás és integrációk — mindezt egy helyen.',
      },
      // ── Jelszó-visszaállítás (publikus, kijelentkezett folyamat) ──
      // A szövegek KÖTELEZŐEN FELTÉTELESEK („ha van fiók…"): a lap SOSEM árulhatja el, létezik-e a cím —
      // ez az anti-enumeráció UI-oldali fele (a BFF válasza is azonos létező és nem létező címre).
      forgotPassword: {
        title: 'Elfelejtett jelszó',
        subtitle: 'Add meg az e-mail-címed, és küldünk egy jelszó-beállító linket.',
        email: 'E-mail cím',
        submit: 'Link kérése',
        submitting: 'Küldés…',
        backToLogin: 'Vissza a bejelentkezéshez',
        // A SIKER-állapot: generikus, megnyugtató, és NEM állítja, hogy a cím létezik.
        sentTitle: 'Elküldtük a levelet',
        sentSubtitle: 'Nézd meg a postafiókodat.',
        sentBody:
          'Ha van fiók a megadott e-mail-címmel, elküldtük rá a jelszó-beállító linket. A link 60 percig érvényes.',
        sentHint:
          'Ha pár percen belül nem érkezik meg, ellenőrizd a levélszemét mappát, és nézd meg, jól írtad-e be a címet.',
        retry: 'Másik e-mail-címmel próbálom',
        errRateLimited: 'Túl sok próbálkozás. Kérlek, várj néhány percet, és próbáld újra.',
        errUnavailable:
          'A jelszó-visszaállítás most nem érhető el. Kérlek, próbáld újra később, vagy jelezd egy kollégának.',
      },
      resetPassword: {
        title: 'Jelszó beállítása',
        subtitle: 'Adj meg egy új jelszót a fiókodhoz.',
        newPassword: 'Új jelszó',
        confirm: 'Új jelszó megerősítése',
        // A `policyHint` és a `policy.*` a `common.passwordPolicy*`-ba promotálva (két felület használja).
        mismatch: 'A két jelszó nem egyezik.',
        submit: 'Jelszó beállítása',
        submitting: 'Beállítás…',
        success: 'A jelszavad beállítottuk — jelentkezz be az új jelszóval.',
        backToLogin: 'Vissza a bejelentkezéshez',
        // GENERIKUS link-hiba: a „nincs ilyen" / „lejárt" / „már felhasználták" eset SZÁNDÉKOSAN azonos.
        invalidTitle: 'Ez a link már nem érvényes',
        invalidSubtitle: 'Kérj egy újat — egy perc az egész.',
        invalidBody:
          'A jelszó-beállító link érvénytelen vagy lejárt. Ez akkor is előfordul, ha már felhasználtad, vagy ha közben újabb linket kértél. Kérj egy új levelet, és próbáld újra.',
        requestNew: 'Új link kérése',
        errRateLimited: 'Túl sok próbálkozás. Kérlek, várj néhány percet, és próbáld újra.',
        errUnavailable: 'A jelszó beállítása most nem érhető el. Kérlek, próbáld újra később.',
      },
      dashboard: {
        greeting: {
          morning: 'Jó reggelt!',
          afternoon: 'Jó napot!',
          evening: 'Jó estét!',
          morningNamed: 'Jó reggelt, {{name}}!',
          afternoonNamed: 'Jó napot, {{name}}!',
          eveningNamed: 'Jó estét, {{name}}!',
        },
        kpi: {
          clients: 'Ügyfelek',
          accounts: 'Fiókok',
          openTickets: 'Nyitott jegyek',
          mrr: 'Havi bevétel (MRR)',
          // A változás az ELŐZŐ hónaphoz mérve (a bevétel-riport `mrrChangePct`-je).
          mrrChange: '{{pct}} az előző hónaphoz',
        },
        openAll: 'mind megnyitása',
        // KITŰZÖTT KÖZLEMÉNY (T15) — csak ha egy kezelő szándékosan kitűzött valamit.
        pinnedMessage: {
          title: 'Kitűzött közlemény',
          pinnedAt: 'kitűzve: {{date}}',
          // a kártya legfeljebb 3-at mutat; a többi a kitűzött-szűrős üzenetlistán
          more: '+{{count}} további kitűzött',
        },
        // „RÁM VÁR" — a bejelentkezett felhasználó nyitott teendői, jog szerint szűrve.
        myWork: {
          title: 'Rám vár',
          empty: 'Nincs rád várakozó teendő.',
          items: {
            myTickets: 'Rám osztott, nyitott jegy',
            rsvp: 'Válaszra váró megbeszélés',
            assetRequests: 'Döntésre váró eszköz-igény',
            draftInvoices: 'Kiállításra váró piszkozat',
          },
        },
        // KEZDŐLAP-CSEMPÉK (a katalógus: `@atlas/shared` `dashboard-tiles.ts`) — cím + egysoros leírás a szerkesztőhöz és a galériához.
        tiles: {
          // KÖTELEZŐ csempe (nincs a galériában, nem levehető) — a szöveg a szerkesztő-keretben látszik.
          pinnedMessages: {
            title: 'Kitűzött közlemény',
            description: 'A nekem szóló kitűzött közlemények. Kötelező: csak mozgatható.',
          },
          kpiClients: {
            title: 'Ügyfelek',
            description: 'Az ügyfelek száma, a lista egy kattintásra.',
          },
          kpiAccounts: { title: 'Fiókok', description: 'A fiókok száma, a lista egy kattintásra.' },
          kpiOpenTickets: {
            title: 'Nyitott jegyek',
            description: 'Az aktív jegyek száma, a „Folyamatban" listára visz.',
          },
          kpiMrr: {
            title: 'Havi bevétel (MRR)',
            description: 'A havi ismétlődő bevétel, változással és 12 hónapos trenddel.',
          },
          finance: {
            title: 'Pénzügy',
            description: 'Lejárt és fizetésre váró számlák, a hónap kiállításai.',
          },
          meetings: {
            title: 'Aktuális megbeszélések',
            description: 'A mai naptól induló megbeszélések agenda-nézetben, válaszadással.',
          },
          celebrations: {
            title: 'Ünnepeltek',
            description: 'Születésnapok, névnapok, évfordulók és az új kollégák üdvözlése.',
          },
          attention: {
            title: 'Figyelmet kér',
            description:
              'Lejáró kártyák, hiányzó használati adat, induló hozzárendelések, generálási kihagyások.',
          },
          myTickets: {
            title: 'Saját jegyeim',
            description: 'A rád osztott, aktív jegyek listája — melyik vár rád.',
          },
          notes: {
            title: 'Jegyzet',
            description: 'Személyes szöveges jegyzet, automatikusan mentve.',
          },
        },
        // A fejléc „Új…" menüje (a régi „Gyors műveletek" csempe utódja).
        newMenu: {
          label: 'Új …',
          items: {
            client: 'Új ügyfél',
            account: 'Új fiók',
            ticket: 'Új jegy',
            meeting: 'Új megbeszélés',
            invoice: 'Új számla',
          },
        },
        // SZERKESZTŐ-MÓD („Testreszabás")
        editor: {
          customize: 'Testreszabás',
          unavailable: 'Az elrendezés most nem tölthető be, ezért nem szabható testre.',
          hint: 'Húzd a csempéket a fogantyúnál fogva, vagy használd a nyilakat. A Mégse mindent visszavon.',
          draggable: 'húzható csempe',
          dragHandle: '{{tile}} áthelyezése',
          sizeLabel: '{{tile}} mérete',
          sizeSmall: 'Kicsi',
          sizeMedium: 'Közepes',
          sizeWide: 'Széles',
          moveUp: '{{tile}} feljebb',
          moveDown: '{{tile}} lejjebb',
          remove: '{{tile}} eltávolítása',
          mandatoryHint: 'Kötelező csempe: mozgatható, de nem távolítható el.',
          addTile: 'Csempe hozzáadása',
          reset: 'Alapértelmezés visszaállítása',
          cancel: 'Mégse',
          save: 'Mentés',
          moreActions: 'További műveletek',
          emptyTitle: 'Nincs kirakott csempe',
          emptyDescription: 'Adj hozzá csempét a „Csempe hozzáadása" gombbal.',
          listLabel: 'A kezdőlap csempéi',
          resetTitle: 'Alapértelmezés visszaállítása?',
          resetDescription:
            'Az elrendezésed törlődik, és újra az alapértelmezett csempék jelennek meg — az újonnan megjelenő alap-csempéket is megkapod.',
          resetConfirm: 'Visszaállítás',
          leaveTitle: 'Elveted a módosításokat?',
          leaveDescription: 'A csempék átrendezése még nincs elmentve.',
          leaveConfirm: 'Elvetés és kilépés',
          leaveStay: 'Maradok',
          a11y: {
            instructions:
              'A csempe felemeléséhez nyomd meg a szóközt, a nyilakkal mozgasd, a szóközzel tedd le, az Esc visszavon.',
            lifted: '{{tile}} felemelve, {{position}}. hely.',
            moved: '{{tile}} áthelyezve a(z) {{position}}. helyre.',
            dropped: '{{tile}} letéve a(z) {{position}}. helyen.',
            cancelled: '{{tile}} visszatéve az eredeti helyére.',
          },
        },
        // CSEMPE-GALÉRIA
        gallery: {
          title: 'Csempe hozzáadása',
          emptyTitle: 'Minden elérhető csempe kint van',
          emptyDescription: 'Nincs több csempe, amit hozzáadhatnál.',
          categories: {
            kpi: 'Mutatók',
            work: 'Munka',
            finance: 'Pénzügy',
            team: 'Csapat',
            personal: 'Személyes',
          },
          new: 'új',
          add: 'Hozzáadás',
          addTile: '{{tile}} hozzáadása',
        },
        // ÜNNEPELTEK — születésnap/névnap/évforduló/új belépő/saját gyerekek (ÉV és ÉLETKOR nélkül).
        celebrations: {
          window: '{{count}} nap',
          windowLabel: 'Előretekintés',
          calendarToday: 'Ma névnapját ünnepli: {{names}}',
          calendarMore: '+{{count}}',
          empty: 'A következő {{count}} napban nincs ünnepelt.',
          today: 'Ma',
          upcoming: 'Közelgő',
          tomorrow: 'holnap',
          showAll: 'Mind a(z) {{count}} mutatása',
          showLess: 'Kevesebb',
          kind: {
            birthdayToday: 'Boldog születésnapot!',
            birthday: 'Születésnap',
            nameDayToday: 'Boldog névnapot!',
            nameDay: 'Névnap',
            anniversaryToday_one: '{{count}}. évforduló — köszönjük, hogy velünk vagy!',
            anniversaryToday_other: '{{count}}. évforduló — köszönjük, hogy velünk vagy!',
            anniversary_one: '{{count}}. évforduló',
            anniversary_other: '{{count}}. évforduló',
            newJoinerToday: 'Új kolléga — üdvözöljük!',
            newJoinerPast_one: '{{count}} napja csatlakozott',
            newJoinerPast_other: '{{count}} napja csatlakozott',
            childBirthdayToday: 'Ma van {{child}} születésnapja',
            childBirthday: '{{child}} születésnapja',
          },
        },
        myTickets: { empty: 'Nincs rád osztott, aktív jegy.' },
        // JEGYZET-csempe (automatikus mentés)
        notes: {
          placeholder: 'Ide jegyzetelhetsz — csak te látod.',
          unavailableImpersonated: 'A jegyzet megszemélyesítés közben nem elérhető.',
          saving: 'Mentés…',
          saved: 'Mentve',
          saveFailed: 'A mentés nem sikerült',
          saveError: 'A jegyzet mentése nem sikerült — próbáld újra.',
          forbidden: 'Megszemélyesítés alatt a jegyzet nem menthető.',
          conflict: 'Máshol módosult a jegyzeted.',
          keepMine: 'Az enyém marad',
          loadTheirs: 'Betöltöm a másikat',
          counter: '{{count}} / {{max}}',
        },
        // FIGYELMET KÉR — a csendben elromló dolgok (lejáró kártya · elmaradt havi usage-lekérés).
        attention: {
          title: 'Figyelmet kér',
          expiringCards_one: '{{count}} bankkártya 60 napon belül lejár',
          expiringCards_other: '{{count}} bankkártya 60 napon belül lejár',
          usageMissing: 'A(z) {{month}} hónapra nincs használati adat lekérve',
          // Hamarosan induló, függő hozzárendelés (a hozzárendelés-lista figyelem-paneljének ugyanaz a szabálya).
          pendingStartingSoon_one: '{{count}} függő hozzárendelés hamarosan indul',
          pendingStartingSoon_other: '{{count}} függő hozzárendelés hamarosan indul',
          // NYITOTT generálási kihagyás az AKTUÁLIS hónapban (a számla-lista „Generálási kihagyások" füle).
          generationSkipsOpen_one: '{{count}} generálási kihagyás ebben a hónapban',
          generationSkipsOpen_other: '{{count}} generálási kihagyás ebben a hónapban',
        },
        // PÉNZÜGY — a számlázás három kérdése (lejárt / fizetésre vár / e hónapban kiállítva).
        finance: {
          title: 'Pénzügy',
          overdue: 'Lejárt számla',
          awaiting: 'Fizetésre vár',
          issuedThisMonth: 'E hónapban kiállítva',
          count: '{{count}} db',
        },
        // A legacy dashboard központi blokkja — nálunk AGENDA-nézetben (nap-szeparátor + idő-oszlop).
        meetings: {
          title: 'Aktuális megbeszélések',
          new: 'Új megbeszélés',
          empty: 'Nincs közelgő megbeszélés.',
          untitled: 'Cím nélkül',
          untitledDay: 'Időpont nélkül',
          today: 'Ma',
          tomorrow: 'Holnap',
          now: 'most',
          participantCount: '{{count}} résztvevő',
        },
      },
      clients: {
        // Űrlap-mező feliratok
        name: 'Név',
        email: 'E-mail',
        phone: 'Telefon',
        status: 'Státusz',
        autoBilling: 'Automata számlázás',
        // A számla-generátor MÁR él és nézi ezt a kapcsolót (`invoice-generation.service.ts`: csak
        // `autoBilling && status=active` ügyfél kerül bele). A FUTTATÁS viszont ma manuális (nincs cron) —
        // a szöveg ezt mondja meg, nem azt, hogy „majd egyszer".
        autoBillingHint:
          'Bevonja az ügyfelet a havi számla-generálásba. Elsődleges számlázási profil szükséges hozzá.',
        // A ZÁROLT kapcsoló indoklása — sosem néma szürkeség: megmondjuk, MIÉRT nem kapcsolható, és MI kell hozzá.
        autoBillingBlocked: {
          create:
            'Az ügyfél létrehozása után kapcsolható be: számla elsődleges számlázási profilról áll ki, profilt viszont csak meglévő ügyfélhez lehet rögzíteni.',
          noPrimaryProfile:
            'Nincs elsődleges számlázási profil — előbb vegyél fel egyet lent a „Számlázási profilok" szekcióban, utána kapcsolható be.',
        },
        // Kisbetűs kezdet — a `serviceAssignments.statusLabel` bevált mintája (mindkét nyelven), mert ezek
        // állapot-ÉRTÉKEK (pill/badge felirata), nem mondat- vagy cím-kezdetek.
        statusLabel: { active: 'aktív', pending: 'függőben', inactive: 'inaktív' },
        // Oszlop-fejlécek (lista)
        columns: {
          client: 'Ügyfél',
          email: 'E-mail',
          phone: 'Telefon',
          accounts: 'Fiókok',
          billingData: 'Számlázási adatok',
          autoBilling: 'Automata számlázás',
          status: 'Státusz',
          actions: 'Műveletek',
        },
        // Cellán belüli legördülő (Fiókok / Számlázási adatok)
        more: '+{{count}}',
        moreItems: 'További elemek megjelenítése',
        // Eszköztár / lista
        filterStatus: 'Státusz',
        loading: 'betöltés…',
        countOf: '{{shown}} / {{total}} ügyfél',
        // Állapotok
        empty: 'Nincs ügyfél',
        emptyDesc: 'Még nincs betöltve egyetlen ügyfél sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek megfelelő ügyfél.',
        // Akciók / gombok
        new: 'Új ügyfél',
        create: 'Ügyfél létrehozása',
        createSubtitle: 'Adj meg egy új ügyfelet az alapadataival.',
        // Létrehozáskor ZÁROLT, kapcsolódó szekciók (a régi CRM tabjai; a dokumentumok tudatosan kimaradnak)
        pendingAccounts: 'Fiókok',
        pendingAccountsDesc: 'Az ügyfélhez tartozó szállodák és egyéb fiókok.',
        pendingServices: 'Szolgáltatások',
        pendingServicesDesc: 'Az ügyfél szintjén hozzárendelt szolgáltatások és áraik.',
        pendingPaymentCards: 'Bankkártyák',
        pendingPaymentCardsDesc: 'A számlázáshoz rögzített kártyák (csak az utolsó 4 számjegy).',
        pendingBillingProfiles: 'Számlázási profilok',
        pendingBillingProfilesDesc: 'Számlázási nevek és címek; egyikük az elsődleges.',
        pendingHint: 'Elérhető lesz, amint létrehoztad az ügyfelet.',
        edit: 'Szerkesztés',
        editSubtitle: 'Az ügyfél alapadatainak módosítása.',
        view: 'Megtekintés',
        save: 'Mentés',
        saving: 'Mentés…',
        back: 'Vissza az ügyfelekhez',
        cancel: 'Mégse',
        delete: 'Törlés',
        // Detail / szekciók
        sectionData: 'Alapadatok',
        sectionBilling: 'Számlázás',
        billingPlaceholder: 'A számlázási beállítások a billing-modul landolásakor jelennek meg.',
        // A NÉGY kapcsolódó szekció a detail-oldalon — a létrehozás-oldali zárolt szekciókkal AZONOS
        // sorrendben és címekkel (Fiókok · Szolgáltatások · Számlázási profilok · Bankkártyák).
        accountsTitle: 'Fiókok',
        addAccount: 'Fiók hozzáadása',
        accountsEmpty: 'Ehhez az ügyfélhez még nincs fiók.',
        /**
         * INAKTIVÁLÁS-FIGYELMEZTETÉS (ügyfél ÉS fiók — a szöveg mindkettőre áll). A mentés a nyitott
         * számlázás-sorokat LEZÁRJA a mai nappal, így az utólag fizetett sorok utolsó, már teljesített
         * időszaka még számlára kerül — enélkül csendben elveszne.
         */
        deactivationNotice: {
          title_one: '{{count}} nyitott számlázás-sor lezárul a mai nappal.',
          title_other: '{{count}} nyitott számlázás-sor lezárul a mai nappal.',
          postpaid_one:
            'Ebből {{count}} utólag fizetett: annak az utolsó, már teljesített időszakáról még kimegy a számla a következő generálási körben.',
          postpaid_other:
            'Ebből {{count}} utólag fizetett: azoknak az utolsó, már teljesített időszakáról még kimegy a számla a következő generálási körben.',
          prepaidOnly:
            'Mind előre fizetett, tehát a lezárás hónapjáig ki van számlázva — több számla nem megy ki.',
        },
        accountsEmptyDesc: 'Ehhez az ügyfélhez még nincs fiók rögzítve.',
        servicesTitle: 'Szolgáltatások',
        addService: 'Szolgáltatás hozzáadása',
        servicesEmpty: 'Ehhez az ügyfélhez még nincs szolgáltatás rendelve.',
        billingProfilesTitle: 'Számlázási profilok',
        addBillingProfile: 'Profil hozzáadása',
        billingProfilesEmpty: 'Ehhez az ügyfélhez még nincs számlázási profil.',
        paymentCardsTitle: 'Bankkártyák',
        addPaymentCard: 'Kártya hozzáadása',
        paymentCardsEmpty: 'Ehhez az ügyfélhez még nincs bankkártya rögzítve.',
        // A kapcsolódó szekciók KÖZÖS hiba-szövegei (mind a négy ugyanazt a `RelatedList`-et használja).
        relatedError: 'Nem sikerült betölteni.',
        relatedForbidden: 'Nincs jogosultságod megtekinteni.',
        relatedRetry: 'Újra',
        // CSONKOLÁS-sáv: a szekció max 8 sort mutat. A `ShowAll` kattintható (a szűrt lista-oldalra visz),
        // a `Total` csak jelzés — ott, ahol a lista-oldalnak MÉG NINCS ügyfél-szűrője.
        sectionNavLabel: 'Szekciók ezen az oldalon',
        relatedShowAll: 'Mind a {{count}} megtekintése',
        relatedTotal: 'Összesen {{count}} — az első 8 látszik',
        primaryBadge: 'elsődleges',
        notFound: 'Az ügyfél nem található',
        notFoundDesc: 'Lehet, hogy törölték, vagy nincs jogosultságod megtekinteni.',
        // Törlés-megerősítés
        deleteTitle: 'Ügyfél törlése',
        deleteDesc:
          'Biztosan törlöd a(z) „{{name}}" ügyfelet? A fiókok, számlázási profilok és szolgáltatás-hozzárendelések is törlődnek. Ez a művelet nem vonható vissza.',
        deleting: 'Törlés…',
        // Törlés-szekció a detail-oldal alján (a fejlécből ide költözött — a visszafordíthatatlan műveletnek
        // saját, magyarázott helye van, nem a cím mellett lapul).
        dangerTitle: 'Ügyfél törlése',
        dangerDesc:
          'A fiókok, számlázási profilok és szolgáltatás-hozzárendelések is törlődnek. A művelet nem vonható vissza.',
        // Jog-tiltás tooltip (a törlés jog nélkül REJTVE van, ezért ahhoz nincs külön kulcs)
        noManagePermission:
          'Nincs jogosultságod ügyfelet létrehozni vagy szerkeszteni (clients.manage).',
      },
      // közös SZEMÉLY-adatok (a munkatárs-adatlap és a belső Profilom is használja)
      people: {
        children: {
          title: 'Gyerekek',
          empty: 'Nincs rögzített gyerek.',
          fullName: 'Teljes név',
          birthDate: 'Születési dátum',
          add: 'Gyerek hozzáadása',
          remove: 'Gyerek törlése',
        },
      },
      employees: {
        name: 'Név',
        email: 'E-mail',
        role: 'Szerepkör',
        status: 'Státusz',
        // Oszlop-fejlécek (lista)
        columns: {
          employee: 'Munkatárs',
          position: 'Beosztás / Csapat',
          supportTeams: 'Ügyfélszolgálat',
          assets: 'Eszközök',
          status: 'Státusz',
        },
        // Státusz-címkék (Odin identity-státusz). Az `archived` a munkatárs-oldalon INAKTIVÁLÁST jelent — a lap
        // mindenhol ezt a szót használja (a gomb is „Inaktiválás"); a „Kivezetett" eszközre illett, személyre nem
        // (Andi #94).
        statusLabel: { active: 'Aktív', suspended: 'Felfüggesztve', archived: 'Inaktív' },
        loading: 'betöltés…',
        countOf: '{{shown}} / {{total}} munkatárs',
        empty: 'Nincs munkatárs',
        emptyDesc: 'Még nincs betöltve egyetlen belső munkatárs sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek megfelelő munkatárs.',
        moreSupportTeams: '+{{count}}',
        moreSupportTeamsAria: 'További csapatok',
        moreAssets: '+{{count}}',
        moreAssetsAria: 'További eszközök',
        filterPosition: 'Beosztás',
        filterTeam: 'Csapat',
        filterDepartment: 'Részleg',
        filterStatus: 'Státusz',
        filterAll: 'Összes {{label}}',
        // Sor-akció + create/edit sheet + törlés
        actions: 'Műveletek',
        edit: 'Szerkesztés',
        editTitle: 'Munkatárs szerkesztése',
        // ÜDVÖZLŐ LEVÉL (a legacy manuális gombja). A gomb MINDIG elérhető: az újraküldés legitim (elveszett/
        // lejárt link), és nincs app-oldali „már ment" flag — a kiküldés nyoma az Odin email_log-jában van.
        welcome: 'Üdvözlő levél küldése',
        welcomeConfirmTitle: 'Üdvözlő levél küldése',
        welcomeConfirmDesc:
          '„{{name}}" e-mail-címére küldünk egy köszöntő levelet, benne egy 7 napig érvényes linkkel, amivel a munkatárs beállíthatja a saját jelszavát. Jelszót SOHA nem küldünk e-mailben. A levél újraküldhető, ha a link lejárt vagy elveszett.',
        welcomeConfirm: 'Levél küldése',
        new: 'Új munkatárs',
        createTitle: 'Új munkatárs',
        createDesc:
          'E-mail-cím alapján jön létre a munkatárs a cég közös címtárában. Ha már létező személy, csak az alapadatait frissítjük (a nevét nem írjuk felül). Az app-hozzáférést és a szerepet a Jogosultságok oldalon állíthatod be.',
        // INAKTIVÁLÁS (Andi #96): a szöveg kimondja, MIKOR történik (a megerősítés után azonnal), MI szűnik meg és
        // MI marad meg — a korábbi „offboarding / archiválódik / globális művelet" nem mondta meg, töröl-e.
        delete: 'Inaktiválás',
        deleting: 'Inaktiválás…',
        deleteTitle: 'Munkatárs inaktiválása',
        deleteDesc:
          '„{{name}}" inaktiválása — a megerősítés után azonnal: nem tud többé belépni, és minden MORGENS-alkalmazásban (nem csak az Atlasban) megszűnik minden hozzáférése és szerepe. Az adatai, a hozzászólásai és az előzmények megmaradnak, a listán az „Inaktív" szűrővel továbbra is megtalálod — a személyes adatai (születésnap, névnap, gyerekek) viszont adatvédelmi okból 30 nap múlva törlődnek. Visszaállítani kérésre lehet; a hozzáféréseit ilyenkor újra ki kell osztani.',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        roles: { member: 'Tag', admin: 'Adminisztrátor', superadmin: 'Szuperadmin' },
        // #43 — foglalkoztatási forma és munkáltató (a munkáltató a számlázási kiállító kulcsa)
        employmentTypes: { employee: 'Alkalmazott', contractor: 'Vállalkozó' },
        employers: { MOR: 'MORGENS Design Kft.', MDIG: 'MORGENS Digital Kft.' },
        fields: {
          email: 'E-mail cím',
          emailHint: 'A belépési e-mail — ez a személy egyedi azonosítója az Odinban.',
          givenName: 'Keresztnév',
          familyName: 'Vezetéknév',
          nickname: 'Becenév',
          // a Profilom súgójával egyező tartalom (ott tegező, itt egy másik személyről szól)
          nicknameHint: 'Ahogy a rendszer megszólítja. Üresen hagyva a keresztnevét használja.',
          phone: 'Telefonszám',
          role: 'Atlas-szerepkör',
          roleHint: 'A kezdő szerep az Atlasban (később a Jogosultságok oldalon módosítható).',
          position: 'Beosztás',
          positionPlaceholder: 'Válassz beosztást…',
          // A beosztás-név CSAK CSAPATON BELÜL egyedi (egy szerep több csapatban is élhet), ezért a
          // választóban a csapat is látszik — enélkül két azonos címke állna egymás mellett.
          positionOptionWithTeam: '{{name}} – {{team}}',
          birthDate: 'Születésnap',
          employmentStartDate: 'Munkába lépés',
          employmentEndDate: 'Kilépés napja',
          nameDay: 'Névnap',
          employmentType: 'Foglalkoztatási forma',
          employer: 'Munkáltató',
          probation: 'Próbaidős',
          probationStartDate: 'Próbaidő kezdete',
          probationEndDate: 'Próbaidő lejárata',
          // KEZDŐ JELSZÓ (csak felvételnél, opcionális) — a Discord-azonosító és az Asana PAT helyére került:
          // azok SZEMÉLYES integrációs adatok, a saját „Profilom" oldalon állíthatók.
          password: 'Kezdő jelszó',
          passwordHint:
            'Nem kötelező. Üresen hagyva a kolléga a köszöntő levél linkjén állítja be a sajátját.',
        },
      },
      // FEJLESZTÉSEK (útiterv + változásnapló) — a lap két füle ugyanazt az entitást mutatja két
      // életszakaszban. A HÁROM DIMENZIÓ szótára szándékosan különböző szó-készlet: a `stage` a HALADÁSRÓL,
      // a `status` a KIHIRDETÉSRŐL, a `visibility` a CÍMZETTRŐL beszél — ha ugyanazokat a szavakat
      // használnánk (pl. „publikus" mindkettőre), a felhasználó összemosná őket.
      // KAMPÁNY-ELŐREJELZÉS. A STÁTUSZ-szavak a dátumokból SZÁMÍTOTT állapotot írják le (tervezett / fut /
      // lezárt) — nem kézi kapcsolók, ezért nincs „aktiválás"/„lezárás" ige sehol a felületen.
      campaignForecast: {
        title: 'Kampány-előrejelzés',
        status: {
          planned: 'tervezett',
          running: 'fut',
          closed: 'lezárt',
        },
        filters: {
          client: 'Ügyfél',
          account: 'Fiók',
          owner: 'Felelős',
          status: 'Státusz',
        },
        zoom: {
          // A NAGYÍTÁS-VÁLTÓ és a „ma" feliratai KISBETŰSEK — ugyanúgy, ahogy a megbeszélés-naptár
          // `calendarButtons`-ai (ott is i18n-értékben, nem CSS-sel): a nézet-váltó sáv így nyugodt marad.
          month: 'hónap',
          quarter: 'negyedév',
          year: 'év',
          // A navigáció-sor KÖZEPÉN álló időszak-felirat negyedév-szava („2026. III. negyedév").
          // A római szám nyelv-független, ezért csak a szó jön az i18n-ből.
          quarterWord: 'negyedév',
          // a szegmens-vezérlő képernyőolvasó-neve (nem látszik a felületen)
          label: 'Nagyítás',
          today: 'ma',
          previous: 'Előző időszak',
          next: 'Következő időszak',
        },
        new: 'Új kampány',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        deleting: 'Törlés…',
        deleteTitle: 'Törlöd a kampányt?',
        deleteDesc: 'A kampány eltűnik az idősávról. A művelet nem vonható vissza.',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        createTitle: 'Új kampány',
        createDesc: 'Több fiókot is kijelölhetsz — fiókonként külön kampány jön létre.',
        editTitle: 'Kampány szerkesztése',
        loading: 'Kampányok betöltése…',
        countOf: '{{shown}} / {{total}} kampány',
        refsUnavailable:
          'Az ügyfél-, fiók- és kolléga-lista most nem érhető el — próbáld újra kicsit később.',
        timelineEmpty: 'Ebben az időszakban nincs kampány',
        timelineEmptyDesc: 'Lépj másik időszakra, vagy tágítsd a nagyítást.',
        partialTitle: 'Nem minden fiókra sikerült',
        partialDesc: '{{count}} fiókra nem jött létre kampány:',
        fields: {
          client: 'Ügyfél',
          clientHint: 'Az ügyfél választása szűkíti a fiók-listát.',
          accounts: 'Fiókok',
          accountsHint: 'Fiókonként külön kampány jön létre, saját azonosítóval és büdzsével.',
          account: 'Fiók',
          name: 'Kampány neve',
          code: 'Azonosító',
          codeHint: 'A rendszer generálja, nem szerkeszthető.',
          owner: 'Felelős',
          startsOn: 'Kezdés',
          endsOn: 'Zárás',
          description: 'Megjegyzés / leírás',
          plannedBudget: 'Tervezett budget',
          actualBudget: 'Tényleges budget',
          currency: 'Deviza',
          asanaTaskUrl: 'Asana-hivatkozás',
          asanaTaskUrlHint: 'Opcionális, az app.asana.com egy feladatára mutató link.',
        },
      },
      productUpdates: {
        boardTab: 'Board',
        timelineTab: 'Idővonal',
        board: {
          emptyColumn: 'Nincs tétel',
          offBoard: '{{count}} tétel a boardon kívül:',
          offBoardView: 'Ezek a tételek a boardon kívül vannak.',
          backToBoard: 'Vissza a boardra',
        },
        timeline: {
          noDate: 'Ismeretlen időpont',
          count: '{{count}} tétel',
        },
        // A TÍPUS nem kap oszlopot: a jelvény a cím alatt áll, a láthatóság-jelvény mellett. A `stage` (Fázis)
        // és a `status` (Státusz) SZÁNDÉKOSAN két külön oszlop és két külön szó — független dimenziók.
        columns: {
          item: 'Fejlesztés',
          stage: 'Fázis',
          status: 'Státusz',
          shippedAt: 'Kiadás',
          target: 'Időzítés',
        },
        stage: {
          idea: 'Ötlet',
          planned: 'Tervezett',
          in_progress: 'Fejlesztés alatt',
          shipped: 'Kiadva',
          on_hold: 'Szüneteltetve',
          cancelled: 'Elvetve',
        },
        type: {
          feature: 'Új funkció',
          improvement: 'Fejlesztés',
          fix: 'Hibajavítás',
          security: 'Biztonság',
          performance: 'Teljesítmény',
        },
        status: { draft: 'Piszkozat', published: 'Kihirdetve', archived: 'Archív' },
        // A jelvény a MEZŐT nevezi meg (privát/publikus); a publikálás-dialógus ennél beszédesebb, saját
        // kulcsokon („Csak belsősöknek" / „Partnereknek is") — ott a következmény a fontos, nem a mező.
        visibility: { internal: 'Privát', public: 'Publikus' },
        horizon: { now: 'Most', next: 'Következik', later: 'Később' },
        // A SZŰRŐK SORRENDJE a lapon: Típus → Láthatóság → Fázis → Státusz (a záró oszloppal egyező rend).
        filters: {
          stage: 'Fázis',
          type: 'Típus',
          status: 'Státusz',
          visibility: 'Láthatóság',
        },
        loading: 'betöltés…',
        countOf: '{{shown}} / {{total}} fejlesztés',
        new: 'Új fejlesztés',
        actions: 'Műveletek',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        deleting: 'törlés…',
        deleteTitle: 'Fejlesztés törlése',
        deleteDesc: 'Biztosan törli a(z) „{{name}}" bejegyzést? A csatolmányai is törlődnek.',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'mentés…',
        createTitle: 'Új fejlesztés',
        createDesc: 'A bejegyzés piszkozatként jön létre — a kihirdetés külön lépés.',
        editTitle: 'Fejlesztés szerkesztése',
        emptyBoard: 'Még nincs fejlesztés a boardon',
        emptyTimeline: 'Még nincs kiadott fejlesztés',
        emptyDesc: 'Az első bejegyzést az „Új fejlesztés" gombbal veheti fel.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő fejlesztés.',
        // A LISTA sor-akciójának tooltipje — rövid, ige-alak (a dialógus feliratai ennél beszédesebbek).
        publish: 'Publikálás',
        revoke: 'Visszavonás',
        unpublish: 'Kihirdetés visszavonása',
        publishTitle: 'Fejlesztés kihirdetése',
        publishHelp:
          'A belsős kihirdetés csak a munkatársaknak látszik. A partneres kihirdetés a partner-portálra is kikerül — ott az összefoglaló jelenik meg.',
        publishInternal: 'Csak belsősöknek',
        publishPublic: 'Partnereknek is',
        publishCurrentInternal: 'Jelenleg: kihirdetve, csak belsősöknek.',
        publishCurrentPublic: 'Jelenleg: kihirdetve, a partnereknek is.',
        fields: {
          title: 'Cím',
          // Egy fogalom = egy szó: a szerkesztőben is „Fázis", ahogy a lista oszlopában és a szűrőben.
          stage: 'Fázis',
          type: 'Jelleg',
          horizon: 'Időhorizont',
          horizonPlaceholder: 'Nincs megadva',
          targetPeriod: 'Cél-időszak',
          targetPeriodHint: 'Pl. 2026-Q4 — durva, vállalható időszak (nem határidő).',
          shippedAt: 'Kiadás dátuma',
          shippedAtHint: 'A változásnapló ez szerint rendez.',
          bodyHu: 'Leírás (magyar)',
          bodyEn: 'Leírás (angol)',
          bodyHint: 'Formázható szöveg, kép is beszúrható.',
          bodyEnHint: 'Nem kötelező — ha üres, a partner a magyar szöveget látja.',
          slug: 'Azonosító (slug)',
          slugHint: 'Üresen hagyva a címből képződik — a partner-linkben ez jelenik meg.',
        },
      },
      supportTeams: {
        // Oszlop-fejlécek (lista) — a Csapat oszlop hordozza a nevet ÉS a leírást (halvány másodlagos sor).
        columns: {
          // NÉV-EGYÉRTELMŰSÍTÉS: „Support-csapat" (nem sima „Csapat") — a szervezeti hierarchia bevezetése óta
          // van egy MÁSIK, színes „csapat" fogalom is (részleg → csapat → beosztás), ez a kettő ne keveredjen.
          team: 'Support-csapat',
          members: 'Tagok',
          services: 'Szolgáltatások',
        },
        loading: 'betöltés…',
        countOf: '{{shown}} / {{total}} support-csapat',
        empty: 'Nincs support-csapat',
        emptyDesc: 'Még nincs betöltve egyetlen support-csapat sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek megfelelő support-csapat.',
        moreMembers: '+{{count}}',
        moreMembersAria: 'További tagok',
        moreServices: '+{{count}}',
        moreServicesAria: 'További szolgáltatások',
        // Sor-akció + create/edit sheet + törlés
        actions: 'Műveletek',
        edit: 'Szerkesztés',
        editTitle: 'Support-csapat szerkesztése',
        new: 'Új support-csapat',
        createTitle: 'Új support-csapat',
        createDesc: 'Ügyfélszolgálati csapat — kétnyelvű név, kapcsolattartó e-mail és tagok.',
        delete: 'Törlés',
        deleting: 'Törlés…',
        deleteTitle: 'Support-csapat törlése',
        deleteDesc:
          'Biztosan törlöd a(z) „{{name}}" support-csapatot? A tagságok is megszűnnek. (Ha aktív jegy hivatkozik rá, nem törölhető.)',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        fields: {
          name: 'Név',
          email: 'Kapcsolattartó e-mail',
          emailHint: 'A support-csapat közös e-mail címe.',
          description: 'Leírás',
          members: 'Tagok',
          membersPlaceholder: 'Válassz munkatársakat…',
          membersCount: '{{count}} kiválasztva',
          services: 'Szolgáltatások',
          servicesPlaceholder: 'Válassz szolgáltatásokat…',
          servicesCount: '{{count}} kiválasztva',
          servicesNoPermission: 'Ehhez nincs jogosultságod.',
        },
      },
      contacts: {
        // Oszlop-fejlécek (lista)
        columns: {
          contact: 'Kapcsolattartó',
          contactInfo: 'Elérhetőségek',
          accounts: 'Fiók(ok)',
          clientAdmins: 'Ügyféladminisztrátorok',
          status: 'Státusz',
        },
        // Identity-státusz címkék (a dedikált Státusz-oszlophoz). Az `archived` a KIVEZETÉS állapota — a
        // lap mindenhol ezt a szót használja, ezért a címke is ez.
        statusLabel: { active: 'Aktív', suspended: 'Felfüggesztve', archived: 'Kivezetett' },
        // Partner-portál jogosultsági szint (a fiók-ikon tooltipje)
        levelLabel: { admin: 'Admin', member: 'Tag', viewer: 'Megtekintő' },
        filterStatus: 'Státusz',
        // Sor-szintű jelzők (a fiók-kötődésekből aggregálva): elsődleges kapcsolattartó / van portál-hozzáférés
        primaryBadge: 'elsődleges',
        portalBadge: 'Portál',
        // ExpandableCell (fiók-lista): a `+N` chip + aria
        more: '+{{count}}',
        moreItems: 'További elemek megjelenítése',
        loading: 'betöltés…',
        countOf: '{{shown}} / {{total}} kapcsolattartó',
        empty: 'Nincs kapcsolattartó',
        emptyDesc: 'Még nincs betöltve egyetlen ügyfél-kapcsolattartó sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek megfelelő kapcsolattartó.',
        // Sor-akció + create/edit sheet + törlés
        actions: 'Műveletek',
        new: 'Új kapcsolattartó',
        edit: 'Szerkesztés',
        editTitle: 'Kapcsolattartó szerkesztése',
        welcome: 'Üdvözlő levél küldése',
        welcomeResend: 'Üdvözlő levél újraküldése',
        welcomeConfirmTitle: 'Üdvözlő levél küldése',
        welcomeConfirmDesc:
          '„{{name}}" e-mail-címére küldünk egy köszöntő levelet: bemutatjuk a rendszert, és mellékelünk egy 7 napig érvényes linket, amivel beállíthatja a saját jelszavát. Jelszót SOHA nem küldünk e-mailben. A levél újraküldhető, ha a link lejárt vagy elveszett.',
        welcomeConfirm: 'Levél küldése',
        createTitle: 'Új kapcsolattartó',
        createDesc:
          'E-mail-cím alapján jön létre a kapcsolattartó. Ha már létező személy, csak az adatait frissítjük; a fiókhoz rendelés a fiók oldalán történik.',
        // „KIVEZETÉS", nem „törlés": a művelet sosem törölt fizikailag — a kötődések és a portál-hozzáférés
        // szűnik meg, az identity pedig archiválódik. A régi szó azt ígérte, hogy a sor eltűnik, és emiatt
        // látszott ellentmondásnak, hogy a státusz-oszlopban „archivált" áll.
        delete: 'Kivezetés',
        deleting: 'Kivezetés…',
        deleteTitle: 'Kapcsolattartó kivezetése',
        deleteDesc:
          '„{{name}}" kivezetése: minden fiók-kötődése és portál-hozzáférése megszűnik, az identitása archiválódik. Később visszavehető — a hozzáférését viszont újra ki kell osztani.',
        restore: 'Visszavétel',
        restoring: 'Visszavétel…',
        restoreTitle: 'Kapcsolattartó visszavétele',
        restoreDesc:
          '„{{name}}" visszakerül a címtárba, és az e-mail címe újra használható. A belépéshez ÚJ jelszót kell beállítania (a régi megszűnik), a fiók-kötődéseit és a portál-hozzáférését pedig külön kell újra kiosztani.',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        fields: {
          email: 'E-mail cím',
          emailHint: 'A kapcsolattartó egyedi azonosítója — a partner-portál belépési e-mailje.',
          givenName: 'Keresztnév',
          familyName: 'Vezetéknév',
          phone: 'Telefon',
          nickname: 'Becenév',
          // a munkatárs-űrlap és a Profilom súgójával egyező tartalom
          nicknameHint: 'Ahogy a rendszer megszólítja. Üresen hagyva a keresztnevét használja.',
          jobTitle: 'Beosztás',
          accountAssignmentHint:
            'A kapcsolattartót a FIÓK oldalán, a „Kapcsolattartók" szekcióban lehet fiókhoz rendelni — ott adható meg a portál-hozzáférés, a szerepkör és az elsődleges jelölés is. Egy kapcsolattartó több fiókhoz is tartozhat.',
        },
      },
      tickets: {
        filterStatus: 'Státusz',
        filterMine: 'Hozzám rendelt',
        filterMineOn: 'csak a hozzám rendelt',
        filterTeam: 'Support-csapat',
        filterFrom: 'Létrehozva ettől',
        filterTo: 'Létrehozva eddig',
        // a „Lezárt" fül lezárás-dátum tartománya (a `closedAt`-ra)
        filterClosedFrom: 'Lezárva ettől',
        filterClosedTo: 'Lezárva eddig',
        // A lista SOR-AKCIÓJA: megnyitás a részletes felületre (a szerkesztő-sheet kivezetve).
        open: 'Jegy megnyitása',
        detailsNumber: 'Jegyszám',
        // A LEADÁS dátuma és a SUPPORT-CSAPAT a „Részletek" alatt: korábban az Igénylő, illetve a
        // Felelős oszlop második sorában álltak — egy személy-oszlopban a második sor a szkennelést zavarja.
        detailsCreated: 'Leadva',
        detailsClosed: 'Lezárva',
        detailsSupportTeam: 'Support-csapat',
        detailsComments: 'Kommentek',
        detailsInternalNotes: 'Ebből belső jegyzet',
        internalNotesBadge_one: '{{count}} belső jegyzet',
        internalNotesBadge_other: '{{count}} belső jegyzet',
        filterType: 'Típus',
        filterAll: '{{label}}: mind',
        loading: 'betöltés…',
        columns: {
          ticket: 'Jegy',
          account: 'Fiók',
          requester: 'Igénylő',
          assignee: 'Felelős',
          status: 'Státusz',
          actions: 'Műveletek',
        },
        edit: 'Szerkesztés',
        new: 'Új jegy',
        create: 'Jegy megnyitása',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        editTitle: 'Jegy szerkesztése',
        createTitle: 'Új jegy',
        createDesc: 'A jegy alapadatai — a válaszok és értesítés a megnyitás után jönnek.',
        fields: {
          status: 'Státusz',
          closeReason: 'Lezárás oka',
          closeReasonHint: 'Kötelező a jegy „Lezárva" státuszba állításához.',
          shareCloseReason: 'A lezárás okát a partner is megkapja e-mailben',
          shareCloseReasonHint:
            'Alapértelmezetten NEM küldjük el — a lezárás oka gyakran belső megjegyzés.',
          shareCloseReasonClosedHint:
            'A lezárás oka csak a lezárás pillanatában küldhető el — ez a jegy már lezárt.',
          assignee: 'Felelős',
          unassigned: 'Nincs felelős',
          supportTeam: 'Ügyfélszolgálati csapat',
          noTeam: 'Nincs csapat',
          account: 'Fiók',
          // A fiók weboldala (a detail „Alapadatok" szekciójában link) — a fiók-sorból dúsítva.
          website: 'Weboldal',
          service: 'Szolgáltatás',
          // A „(kapcsolattartó)" zárójeles pontosítás KIVEZETVE (user-döntés): a lista-oszlop is „Igénylő", a
          // választó tartalma pedig magától megmondja, kik közül választhatunk.
          requester: 'Igénylő',
          type: 'Típus',
          subject: 'Tárgy',
          description: 'Leírás',
          descriptionHint: 'Legalább 25 karakter.',
          createdAt: 'Létrehozva',
        },
        // Az ÚJ JEGY form szakaszai és élő visszajelzései.
        form: {
          step1: 'Kontextus',
          step1Hint: 'A fiók választása után töltjük a szolgáltatásokat és a kapcsolattartókat.',
          step2: 'A jegy',
          step3: 'Fájl-megosztási linkek',
          step3Hint: 'Google Drive · OneDrive · Dropbox — opcionális.',
          chooseAccountFirst: 'Előbb válassz fiókot',
          chooseService: 'Válassz szolgáltatást…',
          chooseRequester: 'Válassz kapcsolattartót…',
          servicesOfAccount: 'A fiók szolgáltatásai',
          otherServices: 'Egyéb szolgáltatás',
          routesTo: 'A jegy ide kerül: {{team}}',
          routesToUnknown: 'Ehhez a szolgáltatáshoz nincs csapat rendelve — így nem nyitható jegy.',
          noContacts: 'Nincs kapcsolattartó',
          noContactsHint: 'Ehhez a fiókhoz nincs kapcsolattartó — előbb rögzíts egyet.',
          subjectPlaceholder: 'Rövid, beszédes összefoglaló',
          descriptionPlaceholder: 'Mi a kérés vagy a hiba? Mit láttál, mit vártál?',
          descriptionRemaining_one: 'Még {{count}} karakter kell',
          descriptionRemaining_other: 'Még {{count}} karakter kell',
          descriptionOk_one: '{{count}} karakter',
          descriptionOk_other: '{{count}} karakter',
        },
        unassigned: 'Nincs felelős',
        unknownRequester: 'Ismeretlen igénylő',
        statusLabel: {
          open: 'Nyitott',
          in_progress: 'Folyamatban',
          resolved: 'Megoldva',
          closed: 'Lezárva',
          reopened: 'Újranyitva',
        },
        // a státuszok rövid SÚGÓJA (Évi #86) — a státusz-menü soraiban
        statusHelp: {
          open: 'Új jegy, még senki nem kezdett vele foglalkozni.',
          in_progress: 'Valaki dolgozik rajta.',
          resolved: 'Megoldottuk: a hibát javítottuk, illetve a kérést teljesítettük.',
          closed:
            'Megoldás nélkül lezárva (pl. duplikátum, nem reprodukálható) — mindig van lezárási ok.',
          reopened: 'Lezárás után újra megnyitották — ismét teendő van vele.',
        },
        typeLabel: {
          support: 'Support',
          bug: 'Hiba',
          feature_request: 'Fejlesztési kérés',
          billing: 'Számlázás',
          other: 'Egyéb',
        },
        // Életciklus-fülek (KETTŐ: aktív · lezárt — a „Mind" és a „Nyitott" fül is kivezetve) + a „csak az
        // enyém" alap-szűrő. A `tabAll` kulcs SZÁNDÉKOSAN marad: a teszt azt falazza, hogy a fül NINCS ott.
        tab: {
          in_progress: 'Folyamatban',
          closed: 'Lezárt',
        },
        tabAll: 'Mind',
        onlyMine: 'Csak az enyém',
        countOf: '{{shown}} / {{total}} jegy',
        empty: 'Nincs jegy',
        emptyDesc: 'Még nincs betöltve egyetlen support-jegy sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő jegy.',
        // Lista: komment-darabszám badge.
        commentCount_one: '{{count}} komment',
        commentCount_other: '{{count}} komment',
        // Detail (full-page) — navigáció + jog-/hiba-állapotok.
        back: 'Vissza a jegyekhez',
        notFound: 'A jegy nem található',
        notFoundDesc: 'Ez a jegy nem létezik, vagy már nem érhető el.',
        forbidden: 'Nincs hozzáférésed',
        forbiddenDesc: 'Nincs jogosultságod ennek a jegynek a megtekintéséhez.',
        // A ragadós szekció-navigáció a11y-neve (a detail-oldalak railje).
        sectionNavLabel: 'Jegy-szekciók',
        sections: {
          // A jegy-detail szekció-címei (a lap FORM-oldal → `FormSection`-ök + ragadós rail).
          data: 'Alapadatok',
          people: 'Résztvevők',
          links: 'Kapcsolódó linkek',
        },
        links: {
          // A BLOKK címkéje (a három mező EGY címke alatt, mint az üzenet-írásban) — a per-mező nevek
          // `aria-label`-ként élnek tovább (a mezőt a belső ikon azonosítja).
          title: 'Fájl-megosztási linkek',
          googleDrive: 'Google Drive',
          onedrive: 'Microsoft OneDrive',
          dropbox: 'Dropbox',
          asana: 'Asana-feladat',
          placeholder: 'https://…',
        },
        // ASANA-FELADAT a jegyből (a legacy „Asana" gombja). A szövegek KIMONDJÁK, ami a felhasználó
        // szempontjából számít: a feladat AZ Ő nevében jön létre, a leválasztás pedig NEM töröl az Asanában.
        asana: {
          create: 'Asana-feladat létrehozása',
          creating: 'Létrehozás…',
          confirmTitle: 'Létrehozod az Asana-feladatot?',
          confirmDescription:
            'A feladat a központi CRM-jegyek projektben jön létre, a te nevedben, és téged jelöl felelősnek. A jegy tárgya, leírása és a megadott linkek átkerülnek. Ehhez be kell állítanod a személyes Asana hozzáférési tokenedet a Profilom oldalon.',
          confirm: 'Létrehozás',
          unlink: 'Leválasztás',
          unlinking: 'Leválasztás…',
          unlinkTitle: 'Leválasztod a hivatkozást?',
          unlinkDescription:
            'A jegyről eltűnik az Asana-feladat hivatkozása. MAGA A FELADAT az Asanában megmarad — ott kell törölnöd, ha arra is szükség van.',
        },
        // Inline „Kezelés" kártya (státusz/felelős/csapat/lezárás-ok a detailen).
        // A LEZÁRÁS-DIALÓGUS (a státusz-menü „Lezárva" ága nyitja): a lezárás-ok KÖTELEZŐ, és a
        // „partner is megkapja" döntés ehhez a pillanathoz tartozik. A korábbi `manage.*` blokk (a kivezetett
        // „Kezelés" szekció feliratai) helyére került.
        close: {
          title: 'Jegy lezárása',
          // MÁR LEZÁRT jegynél a dialógus nem lezár, hanem az OKOT szerkeszti — külön cím kell, különben a
          // dialógus címe és a mező-címke bájtra ugyanaz („Lezárás oka"), ami duplán olvassa fel magát.
          editTitle: 'Lezárás okának szerkesztése',
          confirm: 'Lezárás',
        },
        // Komment-szál.
        comments: {
          title: 'Kommentek',
          empty: 'Még nincs komment ehhez a jegyhez.',
          unknownAuthor: 'Ismeretlen szerző',
          kindLabel: 'Komment típusa',
          bodyLabel: 'Komment szövege',
          // A SZERKESZTŐ-mező külön a11y-neve: a lapon a composer és az inline szerkesztő egyszerre látszik.
          editBodyLabel: 'Komment szerkesztése',
          // A KOMMENT-FAJTA két néven él, mert két szerepben jelenik meg (ugyanaz a minta, mint a
          // megbeszélés-RSVP-nél): a `kind.*` a THREAD-en lévő BADGE felirata (badge-konvenció: kisbetűs), a
          // `kindTab.*` pedig a szekció-fejléc SZEGMENTÁLT választójának címkéje (mondat-kezdő nagybetű).
          // Egy kulcson tartva a kettő vagy a badge-konvenciót, vagy a fejléc-tipográfiát sértette meg
          // (mérve: „külső válasz" | „Belső jegyzet" egymás mellett a fejlécben — vegyes kezdőbetű).
          kind: {
            internal: 'belső jegyzet',
            // „komment", nem „külső válasz" (user-döntés): a partnerrel megosztott bejegyzés a jegy
            // BESZÉLGETÉSE. A `kindTab` már korábban átváltott, ez a badge-felirat volt a maradék — a
            // soronkénti fajta-badge maga kivezetve, de a fajta→felirat leképezés forrása ez.
            external: 'komment',
          },
          // A KÜLSŐ fül neve „Kommentek" (user-döntés): a partnerrel megosztott szál a jegy
          // BESZÉLGETÉSE — a „külső válasz" a rendszer belső szóhasználata volt, nem a felhasználó nyelve.
          // A belső fül marad „Belső jegyzet": ott épp az a lényeg, hogy NEM megy ki.
          kindTab: {
            internal: 'Belső jegyzet',
            external: 'Kommentek',
          },
          // RÖVID placeholder FAJTÁNKÉNT (user-döntés): a hosszú „(a partnerrel is megosztott)"
          // magyarázat zaj volt, de a megnevezés nem mindegy — a komment kimegy a partnerhez, a megjegyzés
          // nem. A `composerCollapsed*` az ÖSSZECSUKOTT sáv felirata (Asana/Jira/Linear-minta).
          placeholderExternal: 'Írj ide egy kommentet…',
          placeholderInternal: 'Írj ide megjegyzést…',
          // EMOJI-REAKCIÓK (a komment alatti sáv). A `{{emoji}}` maga a jel — a képernyőolvasó a
          // FELIRATBÓL tudja meg, mi történik (az emoji az ikon-szerepben `aria-hidden`).
          reactions: {
            addLabel: 'Reakció hozzáadása',
            pickLabel: '{{emoji}} reakció',
            toggleLabel_one: '{{emoji}} — {{count}} reakció (kattints a be/kikapcsoláshoz)',
            toggleLabel_other: '{{emoji}} — {{count}} reakció (kattints a be/kikapcsoláshoz)',
          },
          composerCollapsedExternal: 'Komment írása…',
          composerCollapsedInternal: 'Megjegyzés írása…',
          send: 'Küldés',
          sending: 'Küldés…',
          saveNote: 'Mentés',
          savingNote: 'Mentés…',
          edit: 'Szerkesztés',
          loadTemplate: 'Sablon betöltése',
          saveAsTemplate: 'Mentés sablonként',
          saveTemplateTitle: 'Komment-sablon mentése',
          templateTitleLabel: 'Sablon neve',
          templateContentLabel: 'Sablon tartalma',
        },
        // Best-practice (INTERNAL tudásbázis).
        // Csatolmány-kezelés.
        attachments: {
          // EGYSÉGES SZÓ az egész appban: „Mellékletek" (user-döntés) — a jegynél is ez, mint az
          // üzeneteknél. A „csatolmány" a legacy szava volt; egy fogalom = egy szó.
          title: 'Mellékletek',
          empty: 'Még nincs melléklet ehhez a jegyhez.',
          // EGYSÉGES SZÓHASZNÁLAT (user-döntés): mindenhol „Fájl csatolása" — a létrehozó
          // formon ÉS a részlet-panelen is. Korábban a panel „Feltöltés"-t írt, a form „Fájl csatolása"-t;
          // az entitás neve pedig csatolmány/melléklet, tehát a CSATOLÁS a helyes ige.
          upload: 'Fájl csatolása',
          uploading: 'Csatolás…',
          // MIGRÁLT sor tartalom nélkül: a metaadat átjött, a BÁJTOK nem (a régi szerver fájlrendszerén
          // élnek). Kimondjuk, különben egy néma, kattinthatatlan fájlnév „elromlott felület"-nek látszik.
          unavailable: 'a fájl tartalma nem érhető el (migrált melléklet)',
          download: 'Letöltés',
          delete: 'Törlés',
          deleting: 'Törlés…',
          deleteTitle: 'Melléklet törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" mellékletet?',
          limitReached: 'Elérted a jegyenkénti {{max}} melléklet korlátot.',
          // A NYITÓ-FORM csatolmány-blokkja (a fájlok a jegy létrejötte után mennek fel).
          add: 'Fájl csatolása',
          remove: '„{{name}}" eltávolítása a listáról',
        },
      },
      assets: {
        filterHolder: 'Munkatárs',
        catalog: {
          button: 'Kategóriák',
          title: 'Eszköz-kategóriák és típusok',
          desc: 'Az űrlapokon és a szűrőkben megjelenő típusok. Az átnevezés mindenhol azonnal látszik.',
          empty: 'Még nincs kategória.',
          kind: 'Mit veszel fel',
          kindCategory: 'Kategória',
          kindType: 'Típus',
          name: 'Név',
          numberPrefix: 'Leltári szám előtagja',
          numberPrefixHint: '2–8 nagybetű vagy szám — csak az ÚJ leltári számokra hat.',
          isSoftware: 'Szoftver-jellegű',
          isSoftwareHint: 'Licenckulcs és lejárat (lejárat-figyelés) tartozik hozzá.',
          category: 'Kategória',
          add: 'Felvétel',
          saveEdit: 'Mentés',
          newTitle: 'Új kategória vagy típus',
          editTitle: 'Szerkesztés',
          editAria: '{{name}} szerkesztése',
          deleteAria: '{{name}} törlése',
          deleteCategoryTitle: 'Kategória törlése',
          deleteTypeTitle: 'Típus törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" tételt? A művelet nem vonható vissza.',
          typeInUse: 'Nem törölhető: eszköz vagy igény még hivatkozik erre a típusra.',
          typeInUseMove: 'A használatban lévő típus nem helyezhető át másik kategóriába.',
          categoryHasTypes: 'Nem törölhető: előbb töröld a kategória típusait.',
        },
        filterCategory: 'Kategória',
        filterType: 'Típus',
        filterTeam: 'Csapat',
        filterStatus: 'Státusz',
        filterAll: '{{label}}: mind',
        categoryLabel: {
          hardware: 'Hardver',
          software: 'Szoftver',
        },
        loading: 'betöltés…',
        name: 'Eszköz',
        type: 'Típus',
        status: 'Státusz',
        holder: 'Munkatárs',
        usageLocationCol: 'Felhasználás helye',
        department: 'Részleg',
        netPrice: 'Nettó ár',
        purchaseDate: 'Beszerzés',
        purchaseInfo: 'Vásárlás adatai',
        price: 'Ár',
        priceNet: 'nettó',
        priceVat: 'áfa',
        priceGross: 'bruttó',
        requesterCol: 'Igénylő',
        tabInventory: 'Meglévő eszközök',
        tabRequests: 'Igényelt eszközök',
        assetNumber: 'Nyilvántartási szám',
        serialNumber: 'Sorozatszám',
        licenseKey: 'Termékkulcs',
        purchaseLocation: 'Beszerzés helye',
        purchaseDateLabel: 'Vásárlás dátuma',
        invoiceNumber: 'Számlaszám',
        retirementDate: 'Selejtezés dátuma',
        requester: 'igénylő',
        noHolder: 'Nincs birtokló',
        office: 'iroda',
        /** A csapat-választó gyűjtő-csoportja a részleg NÉLKÜLI csapatoknak (a select `groups`-csoportosítása). */
        otherTeamsGroup: 'Egyéb csapatok',
        usageLabel: {
          office: 'Iroda',
          home: 'Otthon',
          social: 'Közösségi',
          mixed: 'Vegyes',
        },
        statusLabel: {
          requested: 'Igényelve',
          rejected: 'Elutasítva',
          approved: 'Jóváhagyva',
          being_procured: 'Beszerzés alatt',
          purchased: 'Megvásárolva',
          received: 'Beérkezett',
          spare: 'Tartalék',
          in_use: 'Használatban',
          retired: 'Kivezetve',
        },
        typeLabel: {
          desktop_pc: 'Asztali gép',
          laptop: 'Laptop',
          monitor: 'Monitor',
          mouse: 'Egér',
          keyboard: 'Billentyűzet',
          camera: 'Kamera',
          headphone: 'Fejhallgató',
          tablet: 'Tablet',
          mobile_phone: 'Mobiltelefon',
          sim_card: 'SIM-kártya',
          router: 'Router',
          security_camera: 'Biztonsági kamera',
          other_hardware: 'Egyéb hardver',
          operating_system: 'Operációs rendszer',
          antivirus: 'Vírusirtó',
          document_management: 'Dokumentumkezelő',
          design_creative: 'Design / kreatív',
          ide_code_editor: 'IDE / kódszerkesztő',
          subscription: 'Előfizetés (AI, SaaS)',
          other_software: 'Egyéb szoftver',
        },
        countOf: '{{shown}} / {{total}} eszköz',
        empty: 'Nincs eszköz',
        emptyDesc: 'Még nincs betöltve egyetlen eszköz sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő eszköz.',
        actions: 'Műveletek',
        new: 'Új eszköz',
        edit: 'Szerkesztés',
        editWorkflowDisabled: 'Igény-workflow alatt álló eszköz — a szerkesztés itt nem elérhető.',
        delete: 'Törlés',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        deleting: 'Törlés…',
        createTitle: 'Új eszköz',
        createDesc: 'A leltári eszköz adatai — a nyilvántartási szám automatikusan generálódik.',
        editTitle: 'Eszköz szerkesztése',
        deleteTitle: 'Eszköz törlése',
        deleteDesc: 'Biztosan törlöd a(z) „{{name}}" eszközt? A művelet nem vonható vissza.',
        fields: {
          officeUse: 'Iroda (általános használat)',
          officeUseHint: 'Közös, irodai eszköz — nincs személy, a hely fixen közösségi.',
          usageOfficeFixed: 'Irodai eszköznél fix.',
          teamComputed: 'Csapat (a birtokos beosztásából számolt): {{team}}',
          name: 'Megnevezés',
          namePlaceholder: 'Az eszköz megnevezése',
          type: 'Típus',
          status: 'Státusz',
          serialNumber: 'Sorozatszám',
          licenseKey: 'Termékkulcs',
          netPrice: 'Nettó ár',
          netPriceHint: 'Szám formában (pl. 150000) — a pénznem HUF.',
          vatRate: 'ÁFA-kulcs',
          purchaseDate: 'Beszerzés dátuma',
          website: 'Weboldal',
          purchaseLocation: 'Beszerzés helye',
          invoiceNumber: 'Számla-szám',
          team: 'Csapat',
          teamNone: 'Iroda (nincs csapat)',
          holder: 'Birtokló',
          holderPlaceholder: 'Keresés a munkatársak közt…',
          usageLocation: 'Felhasználás helye',
          usageNone: 'Nincs megadva',
          retirementDate: 'Selejtezés dátuma',
          retirementDateHint: 'Kivezetett státusznál kötelező.',
          expiryDate: 'Lejárat / megújítás',
          expiryDateHint:
            'Előfizetésnél, licencnél — 30 és 7 nappal előtte, majd a napján értesítés megy.',
        },
        countShown: '{{shown}} eszköz',
        views: {
          inventory: 'Leltár',
          requests: 'Igénylések',
        },
        request: {
          officeRecipient: 'Iroda (általános használat)',
          kinekCol: 'Kinek',
          view: 'Igény megnyitása',
          viewTitle: 'Eszköz-igény',
          viewDesc: 'Az igény adatai — amíg az igény nyitott, szerkeszthető.',
          editTitle: 'Igény szerkesztése',
          close: 'Bezárás',
          unavailable: 'Az igény nem érhető el (törölték, vagy nincs hozzáférésed).',
          new: 'Igénylés beadása',
          createTitle: 'Új eszköz-igénylés',
          createDesc: 'Az igényelt eszköz adatai — a döntés után indul a beszerzés.',
          submit: 'Beadás',
          submitting: 'Beadás…',
          category: {
            hardware: 'Hardver',
            software: 'Szoftver',
          },
          fields: {
            category: 'Kategória',
            categoryHint: 'Csak a típus-listát szűkíti.',
            namePlaceholder: 'Az igényelt eszköz megnevezése',
            vatRateHint: 'Százalék, szám formában (pl. 27).',
            website: 'Weboldal',
            websiteHint: 'A termék linkje (opcionális).',
            requestedFor: 'Kinek igénylem',
            requestedForPlaceholder: 'Magamnak',
            requestedForHint:
              'Üresen hagyva magadnak igényled; átvételkor ő lesz az eszköz birtokosa.',
            note: 'Megjegyzés',
            notePlaceholder: 'Indoklás, konfiguráció, határidő… (opcionális)',
          },
        },
        workflow: {
          working: 'Mentés…',
          approve: {
            action: 'Jóváhagyás',
            title: 'Igénylés jóváhagyása',
            desc: 'Jóváhagyod a(z) „{{name}}" igénylést? A jóváhagyás után indítható a beszerzés.',
            confirm: 'Jóváhagyás',
          },
          reject: {
            action: 'Elutasítás',
            title: 'Igénylés elutasítása',
            desc: 'Elutasítod a(z) „{{name}}" igénylést? Az indok megadása nem kötelező.',
            reasonLabel: 'Elutasítás indoka',
            reasonPlaceholder: 'Miért utasítod el? (opcionális)',
            confirm: 'Elutasítás',
          },
          being_procured: {
            action: 'Beszerzés indítása',
            title: 'Beszerzés indítása',
            desc: 'Elindítod a(z) „{{name}}" beszerzését?',
            confirm: 'Indítás',
          },
          purchased: {
            action: 'Megvásárolva',
            title: 'Megvásárolva',
            desc: 'Megvásároltnak jelölöd a(z) „{{name}}" eszközt? A beszerzés dátuma a mai nap lesz.',
            confirm: 'Megjelölés',
          },
          received: {
            action: 'Beérkezett',
            title: 'Beérkezett',
            desc: 'Beérkezettnek jelölöd a(z) „{{name}}" eszközt? Az eszköz ahhoz kerül, akinek igényelték (ennek híján az igénylőhöz), és átkerül a leltárba.',
            confirm: 'Megjelölés',
          },
        },
      },
      meetings: {
        from: 'Ettől',
        to: 'Eddig',
        loading: 'betöltés…',
        columns: {
          meeting: 'Megbeszélés',
          location: 'Helyszín',
          time: 'Időpont',
          organizer: 'Szervező',
          participants: 'Résztvevők',
          actions: 'Műveletek',
        },
        new: 'Új megbeszélés',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        deleting: 'Törlés…',
        createTitle: 'Új megbeszélés',
        createDesc: 'A megbeszélés alapadatai és résztvevői — a meghívó-értesítő később.',
        editTitle: 'Megbeszélés szerkesztése',
        // ÁTÜTEMEZÉS (drag & drop a naptáron) — megerősítés a legacy folyamat szerint.
        // A naptár hétvége-kapcsolója (a legacy „hétvégék megjelenítése" gombjának párja).
        // A naptár-gombok feliratai KISBETŰSEK (a `design.md` badge/érték-szabályának szellemében: ezek
        // értékek/választók, nem mondat-kezdetek), és a FullCalendar locale-jét is felülírjuk (lásd
        // `calendarButtons`), hogy „Hónap/Hét/Nap/Lista" helyett egységesen kisbetűs legyen minden.
        // A gomb FELIRATA egy szó; az állapotot a szem-ikon mutatja, az irányt a tooltip (`hide`/`show`).
        weekendLabel: 'hétvége',
        hideWeekends: 'hétvége el',
        showWeekends: 'hétvége be',
        calendarButtons: {
          today: 'ma',
          month: 'hónap',
          week: 'hét',
          day: 'nap',
        },
        rescheduleTitle: 'Megbeszélés átütemezése',
        rescheduleDesc: '„{{title}}" új időpontja: {{start}}. Átütemezed?',
        rescheduleConfirm: 'Átütemezés',
        // ISMÉTLŐDŐ megbeszélés (sorozat) — az űrlap blokkja, az előnézet és az összefoglaló.
        recurrence: {
          label: 'Ismétlődés',
          frequency: {
            none: 'Nem ismétlődik',
            weekly: 'Hetente',
            biweekly: 'Kéthetente',
            monthly: 'Havonta',
            workdays: 'Munkanaponként',
          },
          weekdays: 'Napok',
          end: 'Vége',
          endCount: 'alkalomszám',
          endUntil: 'dátumig',
          countLabel: 'Alkalmak száma',
          countHint: '2–52 alkalom (az első maga a kezdés).',
          untilLabel: 'Utolsó nap',
          preview_one: '{{count}} alkalom: {{first}}',
          preview_other: '{{count}} alkalom: {{first}} – {{last}}',
          count_one: '{{count}} alkalom',
          count_other: '{{count}} alkalom',
          until: '{{date}}-ig',
          seriesBadge: 'Ismétlődő',
        },
        // Sorozat-alkalom szerkesztése / törlése: mire vonatkozzon.
        seriesScope: {
          edit: {
            label: 'A módosítás hatóköre',
            title: 'Ismétlődő megbeszélés módosítása',
            desc: 'Ez egy sorozat alkalma. Mire vonatkozzon a módosítás?',
            this: 'Csak ez az alkalom',
            following: 'Ez és a következő alkalmak',
            all: 'A sorozat összes alkalma',
          },
          delete: {
            label: 'A törlés hatóköre',
            this: 'Csak ez az alkalom',
            following: 'Ez és a következő alkalmak',
            all: 'A sorozat összes alkalma',
          },
          hint: {
            this: 'A sorozat többi alkalma változatlan marad.',
            following: 'A korábbi alkalmak nem változnak.',
            all: 'A már elkezdődött alkalmak nem változnak.',
          },
          rescheduleOnlyThis:
            'Ez egy ismétlődő sorozat alkalma — az áthúzás csak ezt az alkalmat helyezi át.',
        },
        // RÉSZTVEVŐ-ÜTKÖZÉS — átléphető figyelmeztetés (nem tiltás) mentés / átütemezés előtt.
        participantConflict: {
          title: 'Résztvevő-ütközés',
          desc_one:
            'Egy résztvevő ebben az időpontban már egy másik megbeszélésen szerepel. Mented mégis?',
          desc_other:
            '{{count}} esetben szerepel egy résztvevő ebben az időpontban már egy másik megbeszélésen. Mented mégis?',
          proceed: 'Mentés mégis',
          back: 'Vissza',
          listLabel: 'Ütköző megbeszélések',
          untitled: '(cím nélkül)',
        },
        deleteTitle: 'Megbeszélés törlése',
        deleteDesc: 'Biztosan törlöd a(z) „{{title}}" megbeszélést? A művelet nem vonható vissza.',
        fields: {
          title: 'Cím',
          titlePlaceholder: 'A megbeszélés címe',
          description: 'Leírás',
          location: 'Helyszín',
          startsAt: 'Kezdés',
          endsAt: 'Vége',
          zoomLink: 'Zoom-link',
          zoomAccount: 'Zoom-fiók',
          zoomAccountNone: 'Nincs',
          googleMeetLink: 'Google Meet-link',
          microsoftTeamsLink: 'Microsoft Teams-link',
          employees: 'Munkatársak',
          employeesPlaceholder: 'Válassz munkatársakat…',
          employeesUngrouped: 'Csapat nélkül',
          contacts: 'Kapcsolattartók',
          contactsPlaceholder: 'Válassz kapcsolattartókat…',
          participantsCount: '{{count}} kiválasztva',
        },
        zoomAccountLabel: { zoom_account_1: 'Zoom-fiók 1', zoom_account_2: 'Zoom-fiók 2' },
        online: 'Online',
        noOrganizer: 'Nincs szervező',
        noParticipants: 'Nincs résztvevő',
        more: '+{{count}}',
        moreParticipants: 'További résztvevők',
        participants: {
          title: 'Résztvevők',
        },
        // A naptár RÉSZLET-KÁRTYÁJA (peek): az eseményre kattintva nyíló panel feliratai.
        peek: {
          join: 'Csatlakozás',
          notOwnHint: 'Csak a saját megbeszélésedet szerkesztheted.',
        },
        rsvp: {
          // BADGE-feliratok (HARMADIK személy): mások állapotát is ezek írják a résztvevő-listán.
          pending: 'Talán',
          confirmed: 'Ott lesz',
          declined: 'Nem megy',
          // A SAJÁT RSVP-vezérlő AKCIÓ-feliratai (ELSŐ személy): a gomb azt mondja, mit teszek — nem azt,
          // milyen állapotban vagyok (user-döntés).
          actionConfirm: 'Ott leszek',
          actionDecline: 'Nem megyek',
          myResponse: 'A te részvételed:',
        },
        locationLabel: {
          meeting_room: 'Tárgyaló',
          breakout_room: 'Breakout',
          silent_box_1: 'Silent box 1',
          silent_box_2: 'Silent box 2',
          dining_room: 'Étkező',
          keszthely_office: 'Keszthelyi iroda',
          other: 'Egyéb',
        },
        countOf: '{{shown}} / {{total}} megbeszélés',
        countInRange: '{{count}} megbeszélés a nézetben',
        filterLocation: 'Helyszín',
        filterParticipant: 'Kolléga',
        busyLegend: 'sávozott: a kiválasztott kollégák foglalt ideje',
        empty: 'Nincs megbeszélés',
        emptyDesc: 'Még nincs betöltve egyetlen megbeszélés sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő megbeszélés.',
      },
      messages: {
        filterType: 'Típus',
        filterAll: '{{label}}: mind',
        loading: 'betöltés…',
        columns: {
          subject: 'Tárgy',
          recipients: 'Címzett',
          enclosures: 'Mellékletek',
          sender: 'Küldő',
          date: 'Küldve',
        },
        typeLabel: { internal: 'Belső', external: 'Külső' },
        statusLabel: { draft: 'Piszkozat', scheduled: 'Ütemezett', sent: 'Elküldve' },
        audience: { everyone: 'mindenki', allClients: 'minden ügyfél' },
        recipientGroup: {
          account: '{{count}} fiók',
          service: '{{count}} szolgáltatás',
          team: '{{count}} csapat',
          department: '{{count}} részleg',
        },
        attachmentCount: '{{count}} melléklet',
        // KATEGÓRIA: a lap két tartalom-fajtája. `message` = ember írta · `system` = rendszer-generált
        // (gépi/service identity vagy nincs feladó). A besorolás SZERVER-oldali (a feladó identity típusából).
        category: {
          label: 'Kategória',
          // NAGY kezdőbetű: ezek MEZŐ-ÉRTÉKEK (a „Részletek" rács és a szűrő-legördülő), nem badge-ek — a
          // szomszédos „Típus" értéke (Belső/Külső) is így áll. A csupa-kisbetűs alak a badge-konvenció,
          // ami itt nem érvényes.
          message: 'Üzenet',
          system: 'Rendszerértesítés',
        },
        detailsType: 'Típus',
        detailsCategory: 'Kategória',
        detailsAttachments: 'Mellékletek',
        detailsLinks: 'Linkek',
        detailsReads: 'Olvasás',
        // A melléklet-panel (a KÖZÖS `AttachmentPanel` feloldott feliratai) — a jegy-csatolmány párja.
        attachments: {
          title: 'Mellékletek',
          empty: 'Ehhez az üzenethez nincs melléklet.',
          upload: 'Fájl csatolása',
          uploading: 'Csatolás…',
          // MIGRÁLT sor tartalom nélkül: a metaadat átjött, a BÁJTOK nem (a régi szerver fájlrendszerén
          // élnek). Kimondjuk, különben egy néma, kattinthatatlan fájlnév „elromlott felület"-nek látszik.
          unavailable: 'a fájl tartalma nem érhető el (migrált melléklet)',
          download: 'Letöltés',
          delete: 'Törlés',
          deleting: 'Törlés…',
          deleteTitle: 'Melléklet törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" mellékletet?',
          limitReached: 'Elérted az üzenetenkénti {{max}} melléklet korlátot.',
          // Az ÍRÁS-form melléklet-blokkja (a fájlok az üzenet létrejötte UTÁN mennek fel — mint a jegynél).
          add: 'Fájl csatolása',
          remove: '„{{name}}" eltávolítása a listáról',
        },
        linkCount: '{{count}} link',
        readCount: '{{count}} olvasás',
        unknownSender: 'Ismeretlen küldő',
        more: '+{{count}}',
        moreRecipients: 'További címzettek',
        countOf: '{{shown}} / {{total}} üzenet',
        empty: 'Nincs üzenet',
        emptyDesc: 'Még nincs betöltve egyetlen üzenet sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek megfelelő üzenet.',
        // --- Write-vertikum (compose/send/schedule/edit/read) ---
        new: 'Új üzenet',
        // Irány-fülek (a legacy „Bejövő"/„Kimenő" tabjai) + az Atlas összesített füle.
        tabIncoming: 'Bejövő',
        tabOutgoing: 'Kimenő',
        tabAll: 'Mind',
        markAllRead: 'Mind olvasottnak jelöl',
        unread: 'Olvasatlan',
        cancel: 'Mégse',
        fields: {
          type: 'Típus',
          audience: 'Címzés',
          subject: 'Tárgy',
          subjectPlaceholder: 'Az üzenet tárgya…',
          body: 'Üzenet',
          bodyPlaceholder: 'Írd ide az üzenetet…',
          bodyHint: 'Legalább {{count}} karakter (a formázás nem számít bele).',
          // A három link-mező EGY közös címke alatt áll (a mezőt a belső ikon + a placeholder azonosítja); a
          // per-mező címkék `aria-label`-ként élnek tovább (nem törölhetők — azok a mező akadálymentes nevei).
          links: 'Linkek',
          googleDriveLink: 'Google Drive link',
          googleMeetLink: 'Google Meet link',
          zoomLink: 'Zoom link',
          scheduledAt: 'Küldés időpontja',
          scheduledAtHint: 'Helyi idő szerint — jövőbeli időpont kell.',
        },
        audienceKind: {
          everyone: 'Mindenki',
          departments: 'Részlegek',
          teams: 'Csapatok',
          colleagues: 'Munkatársak',
          all_clients: 'Minden ügyfél',
          accounts: 'Fiókok',
          contacts: 'Kapcsolattartók',
          services: 'Szolgáltatások',
        },
        compose: {
          title: 'Új üzenet',
          desc: 'Belső vagy külső körüzenet írása.',
          scheduleToggle: 'Időzítés…',
          scheduleCancel: 'Időzítés elvetése',
          submitSend: 'Küldés',
          submitSchedule: 'Időzítés',
          submitting: 'Mentés…',
          targetsPlaceholder: 'Válassz címzetteket…',
          targetsCount: '{{count}}',
          flagHint: {
            everyone: 'Az üzenetet minden kolléga megkapja.',
            all_clients: 'Az üzenetet minden ügyfél-kapcsolattartó megkapja.',
          },
          confirmBroad: {
            title: {
              everyone: 'Biztosan az összes kollégának küldöd?',
              all_clients: 'Biztosan minden ügyfélnek küldöd?',
            },
            desc: {
              everyone: 'Az üzenetet az összes kolléga megkapja ({{count}} fő).',
              all_clients: 'Az üzenetet minden ügyfél-kapcsolattartó megkapja ({{count}} fő).',
            },
            descNoCount: {
              everyone: 'Az üzenetet az összes kolléga megkapja.',
              all_clients: 'Az üzenetet minden ügyfél-kapcsolattartó megkapja.',
            },
            back: 'Vissza',
          },
          /** A RÉSZLEG-célzás magyarázata (a részlegnek nincs saját tagsága — a csapatain át ér el mindenkit). */
          departmentsHint: 'A kiválasztott részlegek CSAPATAIN keresztül ér el mindenkit.',
          targetsLabel: {
            departments: 'Részlegek',
            teams: 'Csapatok',
            colleagues: 'Munkatársak',
            accounts: 'Ügyfélfiókok',
            contacts: 'Kapcsolattartók',
            services: 'Szolgáltatások',
          },
        },
        edit: {
          title: 'Üzenet szerkesztése',
          save: 'Mentés',
          saving: 'Mentés…',
          sentWarning:
            'Elküldött üzenetet szerkesztesz: a címzettek NEM kapnak értesítést a módosításról, az üzeneten „szerkesztve" jelzés lesz.',
          loadError: 'Az üzenet tartalma nem tölthető be.',
        },
        detail: {
          edited: 'szerkesztve: {{date}}',
          loadError: 'Az üzenet tartalma nem tölthető be.',
        },
        actions: {
          label: 'Műveletek',
          sendNow: 'Küldés most',
          schedule: 'Időzítés',
          reschedule: 'Átidőzítés',
          unschedule: 'Időzítés visszavonása',
          edit: 'Szerkesztés',
          pin: 'Kitűzés a kezdőlapra',
          unpin: 'Kitűzés levétele',
          archive: 'Archiválás',
          unarchive: 'Visszaállítás',
        },
        // KITŰZÉS (a kezdőlap kiemelt körüzenete) + OLVASÓ-OLDALI ARCHÍVUM-nézet
        pinned: 'Kitűzve a kezdőlapra',
        archivedView: 'Archivált',
        pinnedView: 'Kitűzött',
        sendNow: {
          title: 'Küldés most',
          desc: 'Elküldöd most a(z) „{{subject}}" üzenetet?',
          confirm: 'Küldés',
          working: 'Küldés…',
        },
        schedule: {
          title: 'Üzenet időzítése',
          desc: 'A(z) „{{subject}}" üzenet a megadott időpontban kerül kiküldésre.',
          confirm: 'Időzítés',
          working: 'Mentés…',
        },
        unschedule: {
          title: 'Időzítés visszavonása',
          desc: 'A(z) „{{subject}}" üzenet nem kerül kiküldésre — piszkozatként marad meg.',
          confirm: 'Visszavonás',
          working: 'Visszavonás…',
        },
      },
      invoices: {
        // Számlagenerálás-dialógus (a havi generátor manuális triggere).
        generate: {
          action: 'Számlagenerálás',
          title: 'Havi számlagenerálás',
          description:
            'Esedékes recurring hozzárendelésekből DRAFT-számlák előállítása a kiválasztott hónapra. Idempotens (a már számlázott periódus kimarad).',
          month: 'Számlázási hónap',
          prevMonth: 'Előző hónap',
          nextMonth: 'Következő hónap',
          types: 'Árazási típusok',
          typesHint: 'Üresen hagyva minden típus fut.',
          pricingType: {
            flat: 'Fix díjas',
            tiered: 'Sávos',
            base_tiered: 'Alap + sávos',
            commission: 'Jutalékos',
          },
          dryRun: 'Szimuláció (dry-run)',
          dryRunHint: 'Csak becslés — nem hoz létre számlát a buszon.',
          simulate: 'Szimuláció',
          generate: 'Generálás',
          generating: 'Feldolgozás…',
          close: 'Bezárás',
          dryRunResult: 'A szimuláció szerint ennyi jönne létre:',
          liveResult: 'A generálás elkészült:',
          dueAssignments: 'Esedékes',
          groups: 'Csoportok',
          invoices: 'Új számlák',
          // A napi futás a MÁR NYITOTT piszkozathoz fűz, ha a csoportnak van ilyenje — új bizonylat csak
          // akkor nyílik, ha nincs, vagy a meglévő már ki van állítva.
          appended: 'Kiegészített',
          items: 'Tételek',
          skipped: 'Kihagyva ({{count}})',
          // OKONKÉNTI darabszám a próba-futás eredmény-panelén.
          skippedCount_one: '{{count}} tétel',
          skippedCount_other: '{{count}} tétel',
          skipReason: {
            invalid_period: 'Érvénytelen periódus',
            already_invoiced: 'Már számlázva',
            no_rate: 'Hiányzó manuális árfolyam',
            eu_tax_number_required: 'EU-adószám hiányzik',
            issuer_missing: 'Nincs kibocsátó a katalógusban',
            no_price: 'Nincs ár',
            no_usage_data: 'Nincs használati adat',
            no_usage_source: 'Nincs használat-forrás',
            zero: 'Nulla összeg',
            write_failed: 'Busz-írás sikertelen',
            rate_limited: 'Busz fojtva (429)',
            // A MÉRT adat devizája eltér a számla célydevizájától — átváltani nem tudunk (nincs rá árfolyam
            // a szerződésben), ezért a tétel kimarad, nem pedig átcímkézve kimegy.
            currency_mismatch: 'Deviza-eltérés (nem váltunk át)',
            auto_billing_off: 'Automata számlázás kikapcsolva',
            manually_removed: 'Kézzel törölt tétel (zár)',
            assignment_not_active: 'A hozzárendelés nem aktív',
            owner_inactive: 'Az ügyfél / fiók inaktív',
            // A „nincs használati adat" három pontosabb oka (user-döntés) — mindhárom mást kér a kollégától.
            usage_not_final: 'HotelCMS-ben nincs lezárva',
            usage_source_disabled: 'HotelCMS-ben kikapcsolva',
            usage_combined_minimum: 'HotelCMS-ben összevont minimum',
            missing_usage_ref: 'Nincs integrációs azonosító',
          },
          // Tétel-szintű kihagyásnál a tétel kulcsa; `itemKey === null` → a teljes számla maradt ki.
          skippedItem: 'Tétel: {{itemKey}}',
          // Az ÉLES (nem dry-run) futás után, ha volt kihagyás — a napló-fülre mutató link-sor.
          skipsHint: 'A kihagyásokat a „Generálási kihagyások" fülön tekintheted át.',
          skipsHintLink: 'Generálási kihagyások megnyitása',
        },
        // A számla-lista FÜL-sávja: számlák · generálási kihagyások (a napló, a számla-generátor
        // kihagyás-listája ugyanazokkal az ok-kódokkal, `invoices.generate.skipReason.*`).
        tabs: {
          invoices: 'Számlák',
          skips: 'Generálási kihagyások',
        },
        // A „Generálási kihagyások" fül tartalma (a `billing/invoice-generation-skips` napló).
        skips: {
          columns: {
            occurredAt: 'Időpont',
            client: 'Ügyfél / fiók',
            service: 'Szolgáltatás',
            item: 'Tétel',
            reason: 'Ok',
            trigger: 'Indító',
            state: 'Állapot',
            details: 'Részletek',
          },
          itemWholeInvoice: 'teljes számla',
          triggerCron: 'ütemezett',
          triggerManual: 'kézi',
          stateOpen: 'nyitott',
          stateResolved: 'lezárva',
          resolvedAt: 'Lezárva: {{date}} (futás: {{runId}})',
          runIdLabel: 'Futás azonosítója',
          // A `details` payload hiányzó / üres mezőinek helykitöltői — a magyarázó mondatban SOHA nem
          // jelenhet meg `undefined`.
          detailNone: '—',
          detailUnknown: 'ismeretlen',
          // A `details` NYERS kulcsainak emberi címkéi (a kibontóban). Ismeretlen kulcs → a nyers kulcs marad.
          detailLabel: {
            pricingType: 'Árazás',
            usageSourceType: 'Használat forrása',
            currency: 'Deviza',
            rateMonth: 'Árfolyam-hónap',
            vatRate: 'ÁFA-kulcs',
            issuerKey: 'Kibocsátó',
            status: 'Busz-válasz',
            targetCurrency: 'Számla devizája',
            usageCurrencies: 'Mért devizák',
            // A kézi törlés ZÁRA: melyik piszkozatról törölték a tételt.
            invoiceNumber: 'Számlaszám',
            // Automata számlázás kikapcsolva: ügyfél- vagy fiók-szinten.
            level: 'Kikapcsolva',
            assignmentStatus: 'Hozzárendelés állapota',
            usageMonth: 'Mért hónap',
            idField: 'Hiányzó azonosító',
          },
          // AZ OK EMBERI MAGYARÁZATA — mi történt ÉS hol kell javítani. A láthatatlan rész (melyik forrás,
          // melyik hónap, melyik deviza) a sor saját `details`-éből töltődik ki.
          reasonDesc: {
            invalid_period:
              'A számlázási periódus nem képezhető: a hozzárendelésen a számlázás kezdete vagy a gyakoriság hiányzik, illetve ellentmond egymásnak. Nézd meg a hozzárendelés számlázási adatait.',
            already_invoiced:
              'A periódus már ki van számlázva — a generátor nem állít ki másodszor. Ez nem hiba, ezért a naplóba sem kerül.',
            no_usage_source:
              '{{pricingType}} árazás, de a hozzárendelésen nincs használat-forrás beállítva, így nincs miből mennyiséget számolni. Állítsd be a használat forrását a hozzárendelésen.',
            no_usage_data:
              'A használat-forrás ({{usageSourceType}}) be van állítva, de a periódus hónapjára még nem érkezett mért adat. A havi használat-lekérés pótolhatja; addig a tétel kimarad.',
            no_rate:
              'Hiányzik a kézi {{currency}} árfolyam a(z) {{rateMonth}} hónapra, ezért a sávos ár nem számolható ki. Vidd fel az árfolyamot arra a hónapra.',
            no_price:
              'A hozzárendelésen nincs meg a(z) {{pricingTypeLower}} árazáshoz szükséges ársor (egységár, alapdíj vagy passzív díj). Vidd fel a hiányzó ársort.',
            zero: 'A hónapra számított összeg nulla — érvényes üzleti kimenet (nem volt forgalom), nem hiba. Nulla összegű tétel nem kerül számlára.',
            eu_tax_number_required:
              'A számla fordított adózású ({{vatRate}}), ehhez a vevőnek EU-adószám kell, de nincs rögzítve. Vidd fel az EU-adószámot a fiók számlázási adatainál.',
            issuer_missing:
              'A kibocsátó ({{issuerKey}}) nincs a számlatömb-katalógusban, így nincs mire kiállítani a bizonylatot. Vedd fel a kibocsátót a katalógusba.',
            write_failed:
              'Az Odin busz elutasította a számla írását ({{status}}). Átmeneti hiba is lehet — a következő futás újrapróbálja.',
            rate_limited:
              'Az Odin busz fojtott (429) a futás közben, ezért a bizonylat nem íródott ki. A következő futás pótolja; ha ismétlődik, a kötegelt írás ütemezését kell nézni.',
            currency_mismatch:
              'A mért használati adat devizája ({{usageCurrencies}}) eltér a számla cél-devizájától ({{targetCurrency}}). Átváltani nem tudunk, mert a szerződés nem ad rá árfolyamot — ezért a tétel kimarad.',
            auto_billing_off:
              'A sor esedékes lett volna, de az automata számlázás ki van kapcsolva ({{level}}). Ha ez szándékos, zárd le a kivételt; ha nem, kapcsold be, és a következő futás kiszámlázza.',
            manually_removed:
              'Ezt a tételt valaki KÉZZEL törölte a(z) {{invoiceNumber}} piszkozatról, ezért a napi generálás nem állítja elő újra. Ha mégis kellene, oldd fel a zárat — a következő futás kiszámlázza.',
            assignment_not_active:
              'A hozzárendelés státusza „{{assignmentStatus}}", ezért nem számlázzuk. Ha a szolgáltatás már fut, állítsd aktívra; ha véget ért, zárd le — a lezárás az utolsó, már teljesített időszakot még kiszámlázza.',
            owner_inactive:
              'A sor esedékes lett volna, de a tulajdonos ({{level}}) INAKTÍV — ez nem az automata-számlázás kapcsolója, hanem a CRM-státusz. Ha a kapcsolat véget ért, a hozzárendelést le kell zárni; az inaktiválás ezt ma már magától megteszi, ez a sor a korábbi állapotot mutatja.',
            usage_not_final:
              'A(z) {{usageMonth}} hónap {{usageSourceType}} elszámolása a HotelCMS-ben még NINCS LEZÁRVA, ezért a részleges összeget nem számlázzuk. Zárják le a hónapot a HotelCMS-ben — utána az Atlas a következő hajnali lekéréskor magától beírja, és a generálás pótolja.',
            usage_source_disabled:
              'A HotelCMS-ben ez az ág / modul ({{usageSourceType}}) KI VAN KAPCSOLVA, ezért a(z) {{usageMonth}} hónapra nem ad összeget. Ha a szolgáltatás él, kapcsolják be a HotelCMS-ben; ha nem, zárd le a hozzárendelést.',
            usage_combined_minimum:
              'A szálláshely a HotelCMS-ben ÖSSZEVONT minimummal zár, ezért a(z) {{usageMonth}} hónapra az ágankénti tétel ({{usageSourceType}}) összege nem állítható elő: a két ág nyers összeg, a számlázandó a nagyobbik ág vagy a közös minimum. Állítsd át a hozzárendelést Direkt + AK-ra — utána a generálás a modul-végösszeget számlázza.',
            missing_usage_ref:
              'A hozzárendeléshez nincs mérhető {{idField}}: vagy nincs kiválasztva a hozzárendelésen, vagy a kiválasztottat időközben törölték a fiókról, vagy (a még nem kötött soron) a fiókon nincs ilyen azonosító. Ezért a mért adat ({{usageSourceType}}) nem köthető hozzá. Vidd fel az azonosítót a fiók adatlapján, és válaszd ki a hozzárendelésen (Forrás azonosító) — a következő lekérés és generálás utána pótolja.',
          },
          // Melyik azonosító hiányzik (`details.idField`) — mondat-közepi alakban.
          refField: {
            hotelcmsId: 'HotelCMS-azonosító',
            mailwizzId: 'MailWizz-azonosító',
            hotelcmsOrZadircmsId: 'HotelCMS- vagy ZadirCMS-azonosító',
          },
          // HOL van kikapcsolva a kapcsoló — a `details.level` lokalizálása.
          autoBillingLevel: { client: 'az ügyfélen', account: 'a fiókon' },
          filterReason: 'Ok',
          filterClient: 'Ügyfél',
          filterState: 'Állapot',
          filterStateOpen: 'nyitott',
          filterStateResolved: 'csak a lezártak',
          openAssignment: 'Hozzárendelés megnyitása',
          // A KÉZI TÖRLÉS ZÁRÁNAK feloldása — innentől a napi generálás újra előállítja a tételt.
          release: 'Zár feloldása',
          releaseSuccess: 'A zár feloldva — a következő generálás újra előállítja a tételt.',
          releaseConflict: 'Ez a sor már nem feloldható zár (időközben megváltozott).',
          loading: 'betöltés…',
          countOf: '{{shown}} / {{total}} kihagyás',
          empty: 'Nincs kihagyás ebben a hónapban',
          emptyDesc: 'A számla-generátor egyetlen problémát sem naplózott erre a hónapra.',
          noMatches: 'Nincs találat',
          noMatchesDesc: 'Nincs a szűrőknek megfelelő kihagyás.',
        },
        filterStatus: 'Státusz',
        filterType: 'Típus',
        filterIssuedFrom: 'Kiállítva ettől',
        filterIssuedTo: 'Kiállítva eddig',
        filterDueFrom: 'Határidő ettől',
        filterDueTo: 'Határidő eddig',
        filterIssuer: 'Kibocsátó',
        filterAll: '{{label}}: mind',
        loading: 'betöltés…',
        columns: {
          invoice: 'Számla',
          billingParty: 'Számlázott fél',
          issuer: 'Kibocsátó',
          amount: 'Összeg',
          dates: 'Dátumok',
          status: 'Státusz',
          actions: 'Műveletek',
        },
        // A lista ÖSSZEG-cellájának három címkéje (az eszközök ár-cellájának mintája: címke ─ érték).
        net: 'Nettó',
        vat: 'ÁFA',
        gross: 'Bruttó',
        issueDateLabel: 'Kiállítva',
        // RÖVID címke a LISTÁRA (a detail-oldal a saját `detail.fields.dueDate` = „Fizetési határidő"-t
        // használja): a hosszú felirat a dátummal együtt csonkolt a hasábban (mérve 1600px-en).
        dueDateLabel: 'Határidő',
        paymentDateLabel: 'Fizetve',
        statusLabel: {
          draft: 'Piszkozat',
          // A legacy FÜL-csoportok a szűrőben (a BFF oldja fel őket státusz-halmazra).
          'group:issued': 'kiállított (lejárttal)',
          'group:void': 'érvénytelenített (sztornó is)',
          issued: 'Kiállítva',
          paid: 'Fizetve',
          overdue: 'Lejárt',
          cancelled: 'Visszavonva',
          storno: 'Sztornózva',
        },
        typeLabel: {
          invoice: 'Számla',
          proforma: 'Díjbekérő',
        },
        // a bizonylat FAJTÁJA (típus + kézbesítés egy jelvényben) — a lista jelvénye és típus-szűrője
        docKind: {
          e_invoice: 'E-számla',
          paper_invoice: 'Számla',
          proforma: 'Díjbekérő',
          storno: 'Sztornó',
        },
        // a `none` NEM hiány: a Számlázz.hu előtag (számlaszám-prefix) nélküli számlatömbje
        issuerLabel: {
          none: '—',
          MOR: 'MOR',
          MDIG: 'MDIG',
        },
        more: '+{{count}}',
        moreItems: 'További elemek megjelenítése',
        countOf: '{{shown}} / {{total}} számla',
        empty: 'Nincs számla',
        emptyDesc: 'Még nincs betöltve egyetlen számla sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő számla.',
        // Draft-CRUD (írás-vertikum)
        new: 'Új számla',
        edit: 'Szerkesztés',
        view: 'Megtekintés',
        delete: 'Törlés',
        deleting: 'Törlés…',
        cancel: 'Mégse',
        draftNumberPlaceholder: 'Piszkozat',
        createTitle: 'Új számla',
        createSubtitle:
          'Számla-piszkozat létrehozása — mentés után a számla oldaláról állíthatod ki.',
        editTitle: 'Számla szerkesztése',
        editSubtitle: 'A piszkozat fej-adatai és tételei — mentés után a lista frissül.',
        notEditableTitle: 'A számla nem szerkeszthető',
        notEditableDesc: 'Csak piszkozat állapotú számla szerkeszthető.',
        notFound: 'A számla nem található',
        notFoundDesc: 'Lehet, hogy törölték, vagy nincs hozzá jogosultságod.',
        forbidden: 'Nincs jogosultság',
        forbiddenDesc: 'Nincs jogosultságod számla létrehozásához vagy szerkesztéséhez.',
        deleteTitle: 'Piszkozat törlése',
        deleteDesc: 'Biztosan törlöd ezt a számla-piszkozatot? A művelet nem vonható vissza.',
        scope: { account: 'Egy fiók', client: 'Ügyfél (összevont)' },
        form: {
          sectionScope: 'Címzett',
          sectionMeta: 'Bizonylat',
          sectionDates: 'Dátumok',
          sectionBilling: 'Vevő-adatok',
          sectionItems: 'Tételek',
          client: 'Ügyfél',
          clientPlaceholder: 'Válassz ügyfelet…',
          scope: 'Számlázás módja',
          account: 'Fiók',
          accountPlaceholder: 'Válassz fiókot…',
          accounts: 'Fiókok',
          accountsPlaceholder: 'Válassz fiókokat…',
          accountsCount: '{{count}} kijelölve',
          issuer: 'Kibocsátó',
          type: 'Típus',
          deliveryType: 'Számlatípus',
          proformaPaperHint: 'A díjbekérő mindig papír alapú.',
          paymentMethod: 'Fizetési mód',
          currency: 'Pénznem',
          fulfillmentDate: 'Teljesítés',
          issueDate: 'Kelt',
          dueDate: 'Fizetési határidő',
          billingProfile: 'Számlázási profil (előkitöltés)',
          billingName: 'Számlázási név',
          billingTaxNumber: 'Adószám',
          billingEuTaxNumber: 'EU-adószám',
          billingCountryCode: 'Ország (kód)',
          billingZipCode: 'Irányítószám',
          billingCity: 'Település',
          billingAddress: 'Cím',
          billingEmails: 'E-mail címek',
          billingEmailsHint: 'Több cím vesszővel elválasztva.',
          sectionNote: 'Megjegyzés',
          note: 'Számla megjegyzése',
          noteHint: 'A bizonylat megjegyzés-blokkjába nyomtatódik (pl. áfa-mentességi klauzula).',
          addItem: 'Tétel hozzáadása',
          removeItem: 'Tétel törlése',
          back: 'Vissza',
          save: 'Piszkozat mentése',
          saving: 'Mentés…',
        },
        item: {
          servicePlan: 'Szolgáltatás',
          servicePlanPlaceholder: 'Válassz a katalógusból…',
          servicePlanLockedHint:
            'Generált tétel: a szolgáltatása a hozzárendelésé, nem cserélhető. A név és az ár átírható.',
          servicePlanUnavailable: 'A katalógusban már nem elérhető szolgáltatás',
          name: 'Megnevezés',
          quantity: 'Mennyiség',
          quantityUnit: 'Egység',
          unitPrice: 'Egységár',
          vatRate: 'ÁFA-kulcs',
          note: 'Megjegyzés',
          net: 'Nettó',
          vatAmount: 'ÁFA',
          gross: 'Bruttó',
        },
        totals: { net: 'Nettó összesen', vat: 'ÁFA összesen', gross: 'Végösszeg' },
        quantityUnitLabel: { piece: 'darab', hour: 'óra', day: 'nap', other: 'egyéb' },
        vatRateLabel: {
          VAT_27: '27%',
          VAT_18: '18%',
          VAT_5: '5%',
          VAT_0: '0%',
          AAM: 'AAM (alanyi adómentes)',
          TAM: 'TAM (tárgyi adómentes)',
          FAA: 'FAA (fordított adózás)',
          EUFAD37: 'EUFAD37 (EU fordított, 37. §)',
        },
        deliveryTypeLabel: { electronic: 'E-számla', paper: 'Papír alapú' },
        paymentMethodLabel: {
          bank_transfer: 'Átutalás',
          cash: 'Készpénz',
          card: 'Bankkártya',
          other: 'Egyéb',
        },
        footerLegal: {
          FAA: 'Fordított adózás (Áfa tv. 142. §).',
          EUFAD37:
            'Az adó megfizetésére a szolgáltatást igénybe vevő adóalany kötelezett (fordított adózás, Áfa tv. 37. §).',
        },
        // Read-only számla-detail (/invoices/$id)
        detail: {
          back: 'Vissza a listára',
          sections: {
            document: 'Bizonylat-adatok',
            issuer: 'Kibocsátó',
            billing: 'Vevő',
            items: 'Tételek',
            summary: 'Összesítő',
            note: 'Megjegyzés',
            accounts: 'Érintett fiókok',
          },
          fields: {
            currency: 'Pénznem',
            deliveryType: 'Számlatípus',
            paymentMethod: 'Fizetési mód',
            fulfillmentDate: 'Teljesítés',
            issueDate: 'Kelt',
            dueDate: 'Fizetési határidő',
            paymentDate: 'Fizetés dátuma',
            stornoInvoiceNumber: 'Sztornó számlaszám',
            relatedInvoiceNumber: 'Kapcsolódó számla',
            stornoComment: 'Sztornó megjegyzés',
            name: 'Név',
            taxNumber: 'Adószám',
            euTaxNumber: 'EU-adószám',
            address: 'Cím',
            bank: 'Bank',
            emails: 'E-mail címek',
            note: 'Megjegyzés',
          },
          accountsCount: '{{count}} fiók',
        },
        // Sor-kijelölés (a tömeges kiállításhoz) — a jelölők aria-labelje.
        selectRow: 'Sor kijelölése',
        selectAll: 'Az összes betöltött piszkozat kijelölése',
        // Számla-kiállítási életciklus (Számlázz.hu) akciók + megerősítő-dialógusok.
        actions: {
          issue: 'Kiállítás',
          // TÖMEGES kiállítás (a legacy „bulk issue") — a gomb a kijelölt DRAFT sorok számát mutatja.
          bulkIssue: 'Kiállítás ({{count}})',
          bulkIssueTitle: 'Tömeges kiállítás',
          bulkIssueDesc:
            '{{count}} piszkozat kiállítása a Számlázz.hu-n. A kiállítás VISSZAFORDÍTHATATLAN: a szolgáltató sorszámot ad ki, a bizonylat utána már csak sztornózható.',
          bulkIssueConfirm: 'Kiállítás',
          issueTitle: 'Számla kiállítása',
          issueDesc:
            'Biztosan kiállítod ezt a bizonylatot a Számlázz.hu-n? A kiállítás után a számla már nem szerkeszthető.',
          issuing: 'Kiállítás…',
          storno: 'Sztornó',
          stornoTitle: 'Számla sztornózása',
          stornoDesc:
            'Biztosan sztornózod ezt a számlát? A Számlázz.hu létrehozza a sztornó (fordított) számlát.',
          stornoComment: 'Sztornó megjegyzés',
          stornoCommentPlaceholder: 'Megjegyzés a sztornóhoz (opcionális)…',
          stornoing: 'Sztornózás…',
          cancel: 'Érvénytelenítés',
          cancelTitle: 'Díjbekérő érvénytelenítése',
          cancelDesc:
            'Biztosan érvényteleníted ezt a díjbekérőt? A Számlázz.hu törli — a PDF többé nem lesz elérhető.',
          cancelling: 'Érvénytelenítés…',
          pdf: 'PDF letöltése',
          pdfStorno: 'Sztornó PDF',
          pdfOriginal: 'Eredeti PDF',
          sync: 'Szinkronizálás',
        },
      },
      billingProfiles: {
        filterClient: 'Ügyfél',
        filterPrimary: 'Elsődleges',
        onlyPrimary: 'Csak elsődleges',
        loading: 'betöltés…',
        columns: {
          billingName: 'Számlázási név',
          client: 'Ügyfél',
          taxNumber: 'Adószám',
          address: 'Cím',
          paymentDeadline: 'Fiz. határidő',
          primary: 'Elsődleges',
          actions: 'Műveletek',
        },
        primaryBadge: 'elsődleges',
        notPrimaryTooltip: 'Nem elsődleges számlázási profil',
        directors: {
          label: 'Ügyvezetők',
          sectionHint:
            'Több ügyvezető is felvehető, tól–ig időszakkal. Az időszak megadása nem kötelező — a végdátum nélküli bejegyzés a jelenlegi ügyvezetőt jelöli.',
          name: 'Név',
          startsOn: 'Ettől',
          endsOn: 'Eddig',
          add: 'Ügyvezető hozzáadása',
          remove: 'Ügyvezető törlése',
          empty: 'Nincs rögzített ügyvezető.',
          periodRange: '{{from}} – {{to}}',
          periodFrom: '{{from}} óta',
          periodUntil: '{{to}}-ig',
          periodUnknown: 'ismeretlen időszak',
          current: 'jelenlegi',
          overlapWarning: 'Átfedő időszak — ebben az időszakban másik ügyvezető is szerepel.',
          autoClosedHint:
            'Az előző, még nyitott ügyvezető végdátuma automatikusan kitöltődött. Módosíthatod.',
        },
        days: '{{count}} nap',
        countOf: '{{shown}} / {{total}} profil',
        empty: 'Nincs számlázási profil',
        emptyDesc: 'Még nincs betöltve egyetlen számlázási profil sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő számlázási profil.',
        new: 'Új profil',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        deleting: 'Törlés…',
        setPrimary: 'Elsődlegessé tétel',
        copyDetails: {
          action: 'Számlázási adatok másolása',
          actionFor: 'Számlázási adatok másolása: {{name}}',
          editFor: 'Szerkesztés: {{name}}',
          name: 'Cégnév',
          seat: 'Székhely',
          taxNumber: 'Adószám',
          euTaxNumber: 'EU adószám',
          director: 'Képviselő',
          emails: 'E-számla cím',
        },
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        createTitle: 'Új számlázási profil',
        createDesc: 'Egy ügyfél számlázási identitása — név, adószám, cím, alap-beállítások.',
        editTitle: 'Számlázási profil szerkesztése',
        deleteTitle: 'Számlázási profil törlése',
        deleteDesc:
          'Biztosan törlöd a(z) „{{name}}" számlázási profilt? A művelet nem vonható vissza.',
        form: {
          client: 'Ügyfél',
          clientPlaceholder: 'Válassz ügyfelet…',
          billingName: 'Számlázási név',
          taxNumber: 'Adószám',
          euTaxNumber: 'EU-adószám',
          countryCode: 'Ország',
          countryPlaceholder: 'Válassz országot…',
          zipCode: 'Irányítószám',
          city: 'Település',
          address: 'Cím',
          billingEmails: 'Számlázási e-mail címek',
          billingEmailsHint: 'Több cím vesszővel elválasztva.',
          paymentDeadline: 'Fizetési határidő (nap)',
          paymentDeadlineHint: '1 és 31 közötti szám (opcionális).',
          note: 'Alapértelmezett számla-megjegyzés',
          noteHint: 'A számla megjegyzés-blokkjába töltődik elő (pl. közösségi adószám).',
          isPrimary: 'Elsődleges profil',
          isPrimaryHint:
            'Az ügyfél alapértelmezett számlázási profilja — erről áll ki a számla. Az első profil automatikusan elsődleges lesz; bekapcsolva a korábbi elsődleges lefokozódik.',
          // A ZÁROLT állapot indoklása: nem elvesszük a lehetőséget, hanem megmondjuk, MERRE van.
          isPrimaryLockedHint:
            'Ez a jelenlegi elsődleges profil. Kikapcsolni nem lehet (az ügyfél elsődleges profil nélkül maradna) — tegyél elsődlegessé egy másik profilt, az automatikusan lefokozza ezt.',
        },
      },
      paymentCards: {
        filterStatus: 'Státusz',
        loading: 'betöltés…',
        columns: {
          assignments: 'Hozzárendelések',
          card: 'Kártya',
          bank: 'Bank',
          expiry: 'Lejárat',
          status: 'Státusz',
          client: 'Ügyfél',
          actions: 'Műveletek',
        },
        // A legacy 5. oszlopának feliratai: üres állapot, scope-címkék, „+N" legördülő.
        noAssignments: 'nincs hozzárendelés',
        scopeLabel: { client: 'ügyfél szintű', account: 'fiók szintű' },
        moreAssignments: '+{{count}} további',
        moreAssignmentsAria: 'További hozzárendelések',
        status: {
          valid: 'Érvényes',
          expiring: 'Hamarosan lejár',
          expired: 'Lejárt',
        },
        brands: {
          visa: 'VISA',
          mastercard: 'Mastercard',
          amex: 'American Express',
          maestro: 'Maestro',
          other: 'Egyéb',
        },
        countOf: '{{shown}} / {{total}} kártya',
        empty: 'Nincs bankkártya',
        emptyDesc: 'Még nincs betöltve egyetlen bankkártya sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő bankkártya.',
        new: 'Új kártya',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        deleting: 'Törlés…',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        createTitle: 'Új bankkártya',
        createDesc: 'Egy ügyfél bankkártyája — kártyatípus, utolsó 4 jegy, lejárat, bank.',
        editTitle: 'Bankkártya szerkesztése',
        deleteTitle: 'Bankkártya törlése',
        deleteDesc: 'Biztosan törlöd a(z) „{{card}}" bankkártyát? A művelet nem vonható vissza.',
        form: {
          client: 'Ügyfél',
          clientPlaceholder: 'Válassz ügyfelet…',
          holderName: 'Kártyabirtokos neve',
          brand: 'Kártyatípus',
          last4: 'Utolsó 4 számjegy',
          last4Hint: 'A kártyaszám utolsó 4 számjegye.',
          expiryMonth: 'Lejárat hónapja',
          expiryMonthHint: '1 és 12 közötti szám.',
          expiryYear: 'Lejárat éve',
          expiryYearHint: 'Négyjegyű év (pl. 2030).',
          bankName: 'Bank neve',
          note: 'Megjegyzés',
        },
      },
      // ── PÉNZÜGY / KIADÁSOK ─────────────────────────────────────────────────────────────────────────
      // A lap HÁROM füle (összegzés · bevétel-előrejelzés · kiadások) — a fülek szövege a `finance`, a
      // kiadás-oldal sajátjai az `expenses` alatt.
      finance: {
        // A LAP jog-kapuja `finance.revenue.view` — a korábbi szöveg a bevétel-előrejelzés SZOLGÁLTATÁS-HOZZÁRENDELÉS
        // jogát nevezte meg, ami már akkor sem stimmelt, a három füles lapon pedig kifejezetten félrevisz.
        forbiddenTitle: 'Nincs jogosultságod',
        forbiddenDesc:
          'A bevétel/kiadás lap megtekintéséhez pénzügyi jogosultság szükséges. Ha szükséged van rá, szólj a rendszergazdának.',
        tabs: {
          summary: 'Összegzés',
          // A FÜL NEVE „Bevételek" (user-döntés): a lapon a TERV és a TÉNY egymás mellett áll, tehát nem
          // csak előrejelzés — a hosszabb, régi név mást ígért, mint amit a fül ma mutat. A KULCS is
          // `revenue`, nem `forecast`: a tab-azonosítóval egyezik (`FinanceTab`), így nem lehet a kulcs
          // alapján rossz fülre gondolni.
          revenue: 'Bevételek',
          expenses: 'Kiadások',
        },
        // Az ÖSSZEGZÉS al-nézetei: minden kimutatás az Összegzésen él, három nézetre bontva
        summaryViews: {
          label: 'Összegzés nézet',
          overview: 'Áttekintés',
          revenue: 'Bevétel-elemzés',
          expenses: 'Kiadás-elemzés',
        },
        overview: {
          cashflowTitle: 'Bevétel és kiadás — 12 hónap',
          revenueActual: 'bevétel (számlázott)',
          revenuePlan: 'bevétel (terv)',
          expense: 'kiadás (könyvelt)',
          balance: 'egyenleg',
          margin: 'haszonkulcs',
          cashflowNote:
            'A bevétel a lezárt hónapokon a kiszámlázott tény, a nyitott hónapokon és {{month}} előtt a terv (halványabb oszlop). A kiadás a könyvelt nettó, forintban.',
          cashflowNoteNoHorizon:
            'A bevétel a lezárt hónapokon a kiszámlázott tény, a nyitott hónapokon a terv (halványabb oszlop). A kiadás a könyvelt nettó, forintban.',
          foreignNote: 'A devizás kiadás nincs benne — a Kiadás-elemzés devizánként mutatja.',
          topRevenue: 'Legnagyobb bevételi források (a hónap terve)',
          topRevenueActual: 'Legnagyobb bevételi források (a hónap ténye)',
          topExpense: 'Legnagyobb kiadási kategóriák (a hónap)',
        },
        kpi: {
          none: 'Nincs adat',
          margin: 'Haszonkulcs',
          marginDesc: 'Az egyenleg a bevétel arányában (a fenti kártyákból)',
          receivables: 'Kintlévőség',
          receivablesDesc: 'Kiállított, ki nem fizetett számlák bruttója, mára',
          receivablesForeign: 'Devizában is: {{list}}',
          overdue_one: 'ebből lejárt: {{amount}} ({{count}} számla)',
          overdue_other: 'ebből lejárt: {{amount}} ({{count}} számla)',
          concentration: 'Ügyfél-koncentráció',
          concentrationDesc: 'A {{count}} legnagyobb ügyfél aránya a hónap terv-bevételéből',
          concentrationDescActual:
            'A {{count}} legnagyobb ügyfél aránya a hónap kiszámlázott bevételéből',
          nextMonth: 'Következő hónap (terv)',
          nextMonthDesc: '{{month}} várható bevétele',
        },
        // a Kiadás-elemzés szűrő-sávja: típus = a bér-jelölés (mind / bér / egyéb)
        expenseFilters: {
          type: 'Típus',
          salary: 'bér',
          other: 'egyéb',
        },
        expenseAnalytics: {
          byCategory: 'Kiadások kategóriánként (a hónap)',
          salarySplit: 'Bér és egyéb kiadás (a hónap)',
          salary: 'bér',
          nonSalary: 'egyéb kiadás',
          uncategorized: 'besorolatlan',
          trend: 'Kiadások kategóriánként — 12 hónap',
          topSuppliers: 'Legnagyobb szállítók (a hónap)',
          other: 'egyéb',
        },
        summary: {
          note: 'A bevétel előrejelzés, a kiadás könyvelt tény — a kettő különbsége tájékoztató egyenleg.',
          // LEZÁRT hónapon a bevétel is tény (user-döntés) — a mondat ezt mondja ki
          noteActual:
            'Lezárt hónap: a bevétel a kiszámlázott tény, a kiadás a könyvelt tény — a kettő különbsége az egyenleg.',
          // CSAK aktív szűkítésnél. Enélkül a három szám „beragadtnak" látszik: a lap alja átíródik, ez áll.
          unfilteredNote:
            'A fenti három szám mindig a hónap TELJES halmazát mutatja — a szűrés az alatta lévő kimutatásra és a Bevételek fülre hat.',
          revenue: 'Bevétel',
          revenueDesc: 'A hónapra jutó eredmény, aktív hozzárendelésekből',
          revenueDescActual: 'A hónapra kiszámlázott tény, a bizonylatokból',
          expense: 'Kiadás',
          expenseDesc: 'A hónaphoz sorolt kiadások nettó összege',
          balance: 'Egyenleg',
          balanceDesc: 'Bevétel mínusz kiadás (csak a HUF-oldal)',
          foreignNote: 'Deviza-kiadás az egyenlegen kívül:',
          uncategorized: '{{count}} kiadás vár még besorolásra — a Kiadások fülön sorolhatók be.',
          unknownAmount:
            '{{count}} bizonylat összegét a NAV nem közölte (egyszerűsített számla) — a kiadás-végösszeg ennyivel alulbecsül.',
          byCategory: 'Kiadások kategóriánként',
          byCategoryEmpty: 'Ebben a hónapban még nincs rögzített kiadás.',
          itemCount: '{{count}} tétel',
        },
      },
      expenses: {
        loading: 'Betöltés…',
        countOf: '{{shown}} / {{total}} kiadás',
        new: 'Új kiadás',
        newForbidden: 'Nincs jogosultságod kiadást rögzíteni.',
        empty: 'Nincs kiadás ebben a hónapban',
        emptyDesc:
          'A NAV-ból importált számlák automatikusan idekerülnek; a külföldi és a nyugtás tételeket kézzel lehet rögzíteni.',
        noMatches: 'Nincs a szűrésnek megfelelő kiadás',
        noMatchesDesc: 'Próbáld tágítani a szűrést vagy törölni a keresőszót.',
        // KISBETŰS (user-döntés): a legördülőkben és a bontás-listákban a KATEGÓRIA-NEVEK mellett áll,
        // és ez nem név, hanem a név HIÁNYA — a kisbetű ezt a különbséget is kimondja (a repo szűrő-opció
        // konvenciója is kisbetűs).
        uncategorized: 'Besorolatlan',
        employee: 'Munkatárs',
        employeeHint: 'Kihez tartozik a költség (bér, juttatás) — üresen hagyható.',
        employeeNone: 'Nincs megadva',
        employeeAria: '{{supplier}} — munkatárs hozzárendelése',
        // A VÁLASZTÓ CSAK BÉR-ÁGBAN áll: máshol a mező nem üres, hanem NEM ÉRTELMEZETT — a két állapot
        // különbözik, és a kisbetűs jelzés ezt mondja ki (a „Nincs megadva" hiányt jelentene).
        employeeNotApplicable: 'nem bér tétel',
        // TÖMEGES BESOROLÁS — a besorolás köteges munka (a NAV egyszerre hoz le egy hónapnyi bizonylatot).
        bulk: {
          selectRow: 'Sor kijelölése',
          selectAll: 'Minden betöltött sor kijelölése',
          button: 'Besorolás ({{count}})',
          title: '{{count}} kiadás besorolása',
          description:
            'A „ne változzon" mezőhöz nem nyúlunk — így a hónap átsorolása nem törli a kategóriát, és fordítva.',
          keep: 'ne változzon',
          monthDerived: 'Bizonylat szerinti',
          apply: 'Besorolás ({{count}})',
        },
        // VONATKOZÁSI HÓNAP: melyik hónap EREDMÉNYÉT terheli a tétel (eredményszemlélet). A bevétel-oldalon
        // ez levezethető a szolgáltatási periódusból, itt NEM — ezért kézzel állítható.
        month: {
          label: 'Vonatkozási hónap',
          unknown: 'nincs dátum',
          // A LISTA jelölése a bizonylat szerinti hónap mellett, ÉS a jog nélküli nézet alsó sora.
          derived: 'bizonylat szerinti',
          // A FELVITELI ŰRLAP select-tétele — ott nem jelölés, hanem választható érték, ezért nagybetűs.
          derivedOption: 'Bizonylat szerint',
          aria: '{{supplier}} kiadásának vonatkozási hónapja',
          filterAll: 'Minden hónap',
        },
        // Az egyszerűsített NAV-számlához a digest nem közöl összeget — a 0,00 itt a HIÁNY, nem az ár.
        amountUnknown: 'nincs összeg',
        // MENTÉS a soron: a kategória- és hónap-választó NEM ment kattintásra (user-döntés) — a függő
        // módosítást ez a gomb küldi ki, egyetlen kérésben. Csak akkor látszik, ha van mit menteni.
        saveRow: 'Mentés',
        // ELVETÉS: nem törlés — a bizonylat megmarad, csak kiesik a listából és a havi összegből.
        dismiss: 'Elvetés',
        undismiss: 'Visszavétel',
        amountUnknownHint:
          'A NAV nem közölt összeget ehhez a bizonylathoz (egyszerűsített számla) — a tárolt 0 nem az ár, hanem a hiány.',
        // A PÓTOLT összeg a MI kiegészítésünk, nem az adóhatóság adata — az auditálhatóság kedvéért jelöljük.
        amountManualHint:
          'Az összeget kézzel pótoltuk — a NAV ehhez a bizonylathoz nem közölt összeget. Módosítható.',
        amountFill: 'Összeg pótlása',
        copy: 'Másolás',
        amountEdit: 'Összeg módosítása',
        amountFillTitle: 'Hiányzó összeg pótlása',
        amountEditTitle: 'Pótolt összeg módosítása',
        amountFillDesc:
          'A NAV ehhez az egyszerűsített számlához nem közölt összeget. Írd be a bizonylatról a nettót és az áfát — a bruttót a rendszer számolja.',
        amountNet: 'Nettó',
        amountVat: 'Áfa',
        amountGross: 'Bruttó (számolt)',
        // A LISTA cella-címkéi (a modál „Bruttó (számolt)"-ja ott a mező magyarázata, itt zavarna).
        amountGrossShort: 'Bruttó',
        issueDateLabel: 'Kiállítva',
        dueDateLabel: 'Esedékesség',
        classifyAria: '{{supplier}} kiadásának besorolása',
        deleteTitle: 'Kiadás törlése',
        deleteDesc: 'Biztosan törlöd a(z) {{supplier}} tételét? A művelet nem vonható vissza.',
        columns: {
          // A LISTA oszlopfejléce: EGY oszlop viszi a PARTNERT (szállítót) és a bizonylat azonosítóit. A
          // fejléc a SORT nevezi meg, nem a benne lévő dokumentumot — a „Számla" félrevezető volt, mert az
          // oszlop olvasott azonosítója a partner neve (a rendezés is arra megy).
          invoice: 'Partner',
          invoiceNumber: 'Számlaszám',
          taxNumber: 'Adószám',
          originalInvoiceNumber: 'Eredeti számlaszám',
          stornoedBy: 'Sztornózó bizonylat',
          category: 'Kategória',
          // ÖSSZEG + DÁTUMOK — a számla-lista oszlopneveivel azonosan (ott is a hármas/a pár áll bennük).
          amount: 'Összeg',
          dates: 'Dátumok',
          note: 'Megjegyzés',
        },
        filters: {
          month: 'Hónap',
          category: 'Kategória',
          // KIBOCSÁTÓ: melyik CÉGÜNK kapta a számlát. Nem keverendő a FORRÁSSAL (NAV / kézi) — az azt
          // mondja meg, honnan jött a sor, ez pedig azt, kinek a könyvelésébe tartozik.
          issuer: 'Kibocsátó',
          source: 'Forrás',
          // STÁTUSZ, nem „Elvetettek": a szűrő a sorok ÁLLAPOTÁT kérdezi (aktív / elvetett / mind), nem
          // egy halmazt nevez meg. Az értékek a szerződés `hidden`/`only`/`all`-ja maradtak.
          status: 'Státusz',
        },
        dismissedFilter: {
          // A SEMLEGES (alapértelmezett) állapot: csak az ÉLŐ sorok. Korábban „Rejtve" volt — az a szűrő
          // saját működését írta le, nem azt, hogy milyen sorokat látok.
          hidden: 'Aktív',
          only: 'Elvetett',
          // A „mind" NINCS itt: azt a közös `common.all` adja (a `filter-all-label.wall.spec.ts` fala).
        },
        source: {
          nav: 'NAV',
          manual: 'kézi',
        },
        navOperation: {
          MODIFY: 'módosító',
          STORNO: 'sztornó',
        },
        // A SZTORNÓ CÉLPONTJA — a sor maga nem sztornó bizonylat, hanem az, amit semmissé tettek.
        stornoed: 'sztornózva',
        summary: {
          none: 'Ebben a hónapban még nincs rögzített kiadás.',
          // A KÉT ÖSSZEGZŐ-KÁRTYA fejléce: balra a pénz, jobbra az állapot.
          title: 'A hónap kiadásai',
          statusTitle: 'Állapot',
          netIn: 'Nettó ({{currency}})',
          grossAndCount: 'bruttó {{gross}} · {{count}} tétel',
          // Az ÁLLAPOT-kártya sorai CÍMKE–ÉRTÉK párok (a szám külön, jobbra igazítva), ezért a címke már nem
          // hordozza a darabszámot — a régi, mondat-alakú kulcsok ezzel kivezetve.
          uncategorizedLabel: 'Besorolásra vár',
          salaryWithoutEmployeeLabel: 'Munkatárs nélküli bér-tétel',
          unknownAmountLabel: 'Összeg nélküli sor',
          navImportLabel: 'Utolsó NAV-import',
          dismissedLabel: 'Elvetett',
        },
        form: {
          createTitle: 'Új kiadás',
          createDesc:
            'Kézi tétel: külföldi szállító, nyugta vagy adószám nélküli számla — ami a NAV-adatszolgáltatásban nem jelenik meg.',
          editTitle: 'Kiadás szerkesztése',
          copyTitle: 'Kiadás másolása',
          copyDesc:
            'Új kiadás a(z) {{supplier}} tétel adataival. A számlaszám üres — egy új bizonylatnak saját száma van.',
        },
        fields: {
          supplierName: 'Szállító',
          supplierTaxNumber: 'Adószám',
          supplierTaxNumberHint: 'Külföldi szállítónál üresen hagyható.',
          invoiceNumber: 'Számlaszám',
          noBill: 'Nincs számla',
          noBillHint:
            'Nyugta vagy bizonylat nélküli költség — ilyenkor a számlaszám elhagyható. A dátum továbbra is kell: a vonatkozási hónap abból származik.',
          noBillValue: 'Nincs számla',
          issueDate: 'Kiállítás dátuma',
          costDate: 'A költség dátuma',
          fulfillmentDate: 'Teljesítés dátuma',
          dueDate: 'Fizetési határidő',
          netAmount: 'Nettó',
          vatAmount: 'Áfa',
          grossAmount: 'Bruttó (számított)',
          currency: 'Deviza',
          // A KIBOCSÁTÓ a két cégünk közül — a NAV-soron az import tölti ki, a kézin a kezelő választja.
          issuer: 'Kibocsátó',
          issuerHint:
            'Melyik cégünk költsége — a NAV-ból importált soron ezt a lekérés adószáma dönti el.',
          category: 'Kategória',
          // Már a FELVITELKOR állítható (user-kérés): ha a kezelő tudja, hogy a tétel korábbi hónapot
          // terhel, ne kelljen utólag megkeresnie a listában.
          accountingMonth: 'Vonatkozási hónap',
          accountingMonthHint:
            'Melyik hónap eredményét terheli a tétel. Bizonylat szerint = a teljesítés, annak híján a kiállítás dátumából.',
          note: 'Megjegyzés',
        },
        nav: {
          action: 'Lekérés a NAV-ból',
          running: 'Lekérés…',
          done: '{{count}} új bizonylat érkezett',
          nothingNew: 'Nem érkezett új bizonylat',
          reportLine: 'Új: {{created}} · már megvolt: {{skipped}}',
          // A BUKOTT SOROK KÜLÖN MONDATBAN: ezek ÚJRAPRÓBÁLHATÓK, és a következő futás pótolja őket —
          // a `{{created}} · {{skipped}}` páros mögött ez a szám elveszne, pedig épp ez igényel figyelmet.
          reportFailed: '{{count}} bizonylat írása nem sikerült — a következő lekérés újrapróbálja',
          // AUTOMATIKUS ELVETÉS: ki kell mondani, mert SOROK TŰNTEK EL a listáról.
          reportDismissed: '{{count}} sor elvetve (teljes sztornó-párok)',
          failed: 'A NAV-lekérés nem sikerült ({{code}})',
          disabled: 'A NAV-import nincs bekötve ezen a környezeten.',
          lastImport: 'utolsó bizonylat: {{date}}',
          keyExpiringShort: 'aláírókulcs',
          keyExpiring:
            'A NAV aláírókulcs {{days}} nap múlva lejár ({{date}}) — cserélni kell, különben az import megáll.',
          keyExpired: 'A NAV aláírókulcs lejárt ({{date}}) — az import aláírása el fog hasalni.',
        },
        // TÖMEGES IMPORT — sablon → fájl → előnézet → véglegesítés → jelentés → visszavonás.
        import: {
          button: 'Kiadás-import',
          rowBadge: 'importált',
          title: 'Tömeges kiadás-import',
          description:
            'Ugyanazt viszi be, amit kézzel is felvennél — csak sok sort egyszerre. Az előnézet előtt semmi nem íródik.',
          pickHelp:
            'Töltsd le a sablont, írd bele a sorokat, és töltsd vissza. A második munkalapon ott vannak az érvényes kategória-utak és munkatárs-nevek — onnan másolható.',
          downloadTemplate: 'Sablon letöltése',
          chooseFile: 'Fájl kiválasztása',
          sheet: {
            rows: 'Kiadások',
            values: 'Választható értékek',
            categories: 'Kategória (teljes út)',
            salary: 'Ebből bér típusú',
            employees: 'Munkatársak',
            issuers: 'Kibocsátó',
            currencies: 'Deviza',
            noBill: 'Nincs számla',
            months: 'Vonatkozási hónap',
          },
          columns: {
            issuerKey: 'Kibocsátó',
            supplierName: 'Szállító',
            supplierTaxNumber: 'Adószám',
            invoiceNumber: 'Számlaszám',
            noBill: 'Nincs számla',
            issueDate: 'Dátum',
            fulfillmentDate: 'Teljesítés',
            dueDate: 'Fizetési határidő',
            netAmount: 'Nettó',
            vatAmount: 'Áfa',
            currency: 'Deviza',
            categoryPath: 'Kategória (teljes út)',
            employeeName: 'Munkatárs',
            accountingMonth: 'Vonatkozási hónap',
            note: 'Megjegyzés',
          },
          guides: {
            issuerKey: 'Melyik cégünk költsége. Válassz a legördülőből: MOR vagy MDIG.',
            supplierName:
              'Kitől jött a költség. Bér-sornál hagyd ÜRESEN — a munkatárs neve kerül ide magától.',
            supplierTaxNumber:
              'Csak ha van. Alak: 12345678-2-41. Külföldi szállítónál hagyd üresen.',
            invoiceNumber:
              'A bizonylat sorszáma. KÖTELEZŐ — kivéve, ha az előtte lévő oszlopban „nincs"-et választasz.',
            noBill:
              'Van-e bizonylat? „nincs" → nyugta vagy számla nélküli költség: a számlaszám elhagyható. A DÁTUM ettől még kell (abból lesz a vonatkozási hónap).',
            issueDate:
              'KÖTELEZŐ. Alak: 2026-09-05 (év-hónap-nap). A 2026. 09. 05. és a 2026/09/05 is jó.',
            fulfillmentDate:
              'Ha van. Alak: 2026-09-05. Ez dönti el a vonatkozási hónapot a kiállítás előtt.',
            dueDate: 'Ha van. Alak: 2026-09-30.',
            netAmount:
              'KÖTELEZŐ, nettó összeg. Tizedes: VESSZŐ (1234,56). Ezres-csoport lehet szóköz vagy pont.',
            vatAmount:
              'Áfa összege. Bérnél és hitelnél 0. A bruttót MI számoljuk — nincs rá oszlop.',
            currency: 'Válassz a legördülőből. Üresen hagyva HUF.',
            categoryPath:
              'Válassz a legördülőből — a TELJES út kell (pl. Advisory bér / Bónusz). Gyűjtő-kategória nem választható.',
            employeeName:
              'Csak BÉR típusú tételnél. Válassz a legördülőből; a sorrend mindegy (Kiss Anna vagy Anna Kiss).',
            accountingMonth:
              'Melyik hónap eredményét terheli. Alak: 2026-09. Üresen hagyva a dátumból számoljuk.',
            note: 'Szabad szöveg, elhagyható.',
          },
          // A „nincs számla" legördülő két szava. Az oszlop azt kérdezi, VAN-E bizonylat — `nincs` → a
          // számlaszám elhagyható.
          noBill: { missing: 'nincs', present: 'van' },
          rowCount: '{{count}} sor',
          errorCount: '{{count}} hibás',
          warningCount: '{{count}} figyelmeztetés',
          blocked:
            'Amíg van hibás sor, a véglegesítés nem indítható — javítsd őket a sor végi gombbal.',
          fix: 'Javítás',
          fixTitle: 'Sor javítása',
          fixDesc: '{{row}}. sor — ugyanaz az űrlap, mint a kézi felvitelnél.',
          commit: 'Véglegesítés ({{count}})',
          another: 'Másik fájl',
          doneTitle: '{{count}} kiadás rögzítve.',
          doneFailed: '{{count}} sor nem ment be — a köteg visszavonható.',
          doneHelp:
            'Ha mégsem jó, a köteg egy kattintással visszavonható. A sorok a listán „importált" jelölést kapnak.',
          undo: 'Visszavonom',
          showBatch: 'Megnézem a listán',
        },
        categories: {
          button: 'Kategóriák',
          title: 'Kiadás-kategóriák',
          desc: 'A fa legfeljebb három szintű: főkategória → alkategória → tétel. Egyik szint sem kötelező.',
          empty: 'Még nincs kategória — vegyél fel egyet lent.',
          newTitle: 'Új kategória',
          editTitle: 'Kategória szerkesztése',
          name: 'Név',
          parent: 'Szülő kategória',
          parentHint:
            'Üresen hagyva maga is főkategória lesz. Tétel alá nem vehető fel újabb szint.',
          rootOption: 'Nincs (főkategória)',
          requiresEmployee: 'Bér típusú tétel',
          requiresEmployeeHint: 'Az ide sorolt költségnél megadható, melyik munkatárshoz tartozik.',
          employeeBadge: 'Bér típusú tétel',
          add: 'Hozzáadás',
          saveEdit: 'Mentés',
          editAria: '{{name}} szerkesztése',
          deleteAria: '{{name}} törlése',
          deleteTitle: 'Kategória törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" kategóriát?',
        },
      },
      revenueForecast: {
        loading: 'Számítás…',
        // ── A BEVÉTELEK LISTA (a Pénzügy lap második füle) ──────────────────────────────────────────────
        columns: {
          client: 'Ügyfél',
          service: 'Szolgáltatás',
          plan: 'Csomag',
          category: 'Kategória',
          amount: 'Összeg',
          source: 'Forrás',
          invoice: 'Bizonylat',
          // A lista OSZLOPNEVEI a Kiadások fül nyelvét követik: a fejléc a SORT nevezi meg, nem a mögötte
          // álló dokumentumot. A gyakoriság és a státusz a „Részletek" kibontó alá költözött.
          partner: 'Partner',
          // A SZÁMLÁZÁSI NÉV a kibontóban áll (a FIÓK helyén, ami a sor fejében már ott van): ez mondja meg,
          // kinek a nevére megy a bizonylat — a sorból nem olvasható ki.
          billingName: 'Számlázási név',
          frequency: 'Gyakoriság',
          status: 'Státusz',
        },
        /** A sor forrása: a szerződésből SZÁMOLT terv, vagy a kiállított bizonylatból olvasott tény. */
        source: {
          plan: 'terv',
          actual: 'tény',
        },
        /** Kézi számla / már törölt hozzárendelés: van bevétel, de nincs mögötte szolgáltatás-sor. */
        // Az ÖSSZEG-blokk címkéi — a számla- és a kiadás-lista alakjával azonosan.
        amountNet: 'Nettó',
        amountVat: 'ÁFA',
        amountGross: 'Bruttó',
        unclassified: 'Besorolatlan',
        unclassifiedItem: 'besorolatlan tétel',
        list: {
          loading: 'Betöltés…',
          countOf: '{{shown}} / {{total}} tétel',
          monthTotal: 'A hónap bevétele',
          // A KÉT ÖSSZEGZŐ-KÁRTYA (a Kiadások fül alakjával azonosan): balra a pénz, jobbra az állapot.
          statusTitle: 'Állapot',
          netTotal: 'Nettó (HUF)',
          itemCount_one: '{{count}} tétel',
          itemCount_other: '{{count}} tétel',
          sourceLabel: 'Forrás',
          rateLabel: 'Árfolyam',
          rateFallback: 'hiányzik',
          listLabel: 'Lista',
          listTruncated: 'csonka',
          // A forrás HAVONTA dől el, nem soronként — ezért EGY jelvény a státusz-kártyán, rövid alakban.
          sourceShort: { plan: 'terv', actual: 'tény' },
          // a horizont ELŐTTI hónap: nem „nem futott le a kör", hanem nincs Atlas-számla
          beforeHorizon:
            'Erre a hónapra nincs tény-adat: az Atlasban {{from}} óta vannak számlák. A lista ezért a hozzárendelésekből számolt TERV-et mutatja.',
          // lezárt hónapon a sorok a kiszámlázott tételek — a státusz-szűrő rájuk nem hat (a kártyával azonos szabály)
          statusNoEffectOnActual:
            'Lezárt hónap: a sorok a kiszámlázott tételek — a státusz-szűrő rájuk nem hat (a kiszámlázott bevétel a hozzárendelés mai státuszától független).',
          notClosedYet:
            'A hónap még tart, vagy még nem futott le rá a számlázási kör — ezért a lista a hozzárendelésekből számolt TERV-adatot mutatja.',
        },
        // a KÉT SZABÁLY (user-döntés): a havi tétel eredményszemléletben, a periodikus egyben, a számla hónapjában
        periodRule:
          'Havi díj: a szolgáltatás hónapjában számít. Negyedéves / féléves / éves díj: a teljes összeg egyben, abban a hónapban, amikor a számla kiáll — nem oszlik el a hónapokra.',
        // a TERV és a TÉNY MÁS árfolyamon áll (review-lelet: a korábbi mondat a tényre nem volt igaz) —
        // terv: egy MNB előző-hó-végi kurzus; tény: a hónapra rögzített kézi árfolyam, ennek híján a hónap MNB-átlaga
        currencyNote:
          'Minden összeg HUF-ban. Terv: deviza → MNB előző-hó-végi árfolyam. Tény (lezárt hónap): a hónapra rögzített árfolyam, ennek híján a hónap MNB-átlaga.',
        // nem-alap státusznál: mire hat a szűrő és mire nem (a szabály: `docs/modules/admin/finance.md`)
        statusScopeNote:
          'A státusz-szűrő a terv-oldali mutatókra és bontásokra hat (MRR, PRR, NRR, ARR, AR, a szolgáltatás-, ügyfél- és kategória-bontás, a részletező). Nem követi: az „Adott havi” és a „Várható következő havi” kártya és a terv–tény trend tervvonala — ezek mindig az aktív állományt mutatják —, valamint a tény-számok: a kiszámlázott bevétel a hozzárendelés mai státuszától független.',
        fallbackRateNote:
          'Az árfolyam-adat nem elérhető; a deviza-tételek becsült/1:1 árfolyammal szerepelnek.',
        usageMissingNote:
          '{{count}} sávos/jutalékos tétel ehhez a lezárt hónaphoz nem kapott használati adatot — ezek az alapdíjjal szerepelnek, a tényleges összeg ennél magasabb lehet.',
        // a FUTÓ / jövőbeli hónap: az adat a hónap lezárása után érkezik — ez nem hiány, csak az olvasat pontosítása
        usageOpenMonthNote:
          'A hónap még nem zárult le: a használati adat a lezárás után érkezik, addig {{count}} sávos/jutalékos tétel az alapdíjjal szerepel.',
        usageMissingShow: 'Melyek ezek?',
        // AZ ADAT-HORIZONT — a vezetőségnek: visszamenőleg a kalkuláció adathiány miatt nem lehet pontos
        dataHorizon: {
          title: 'Visszamenőleg a kalkuláció nem lehet pontos — adathiány',
          intro:
            'Ami nincs rögzítve, azt nem tudjuk becsülni. Az Atlasban az adatok ezektől a hónapoktól teljesek:',
          actuals:
            'Számlák (tény): {{month}} óta — előtte nincs tény-szám, a terv–tény összevetés csak ettől értelmezhető.',
          usage:
            'Használati adatok: {{month}} óta — előtte a sávos/jutalékos tételek csak alapdíjjal becsülhetők.',
          oneTime:
            'Eseti díjak: nagyjából {{month}} óta — előtte hiányosan vannak rögzítve, a hiányukat nem lehet pótolni.',
          beforeActuals:
            'A kiválasztott hónapra nincs tény-szám: az Atlasban {{month}} óta vannak számlák. A terv-szám a hozzárendelésekből számol.',
        },
        usageMissingMore: '…és még {{count}} tétel',
        usagePricedNote: '{{count}} sávos/jutalékos tétel a hónap mért használati adatából számol.',
        prevMonth: 'Előző hónap',
        nextMonth: {
          title: 'Várható következő havi bevétel',
          desc: 'A valódi következő naptári hónap bevétele: a havi díjak, és a hónapban kiszámlázandó negyedéves / féléves / éves díjak teljes összeggel (mindig aktív státusz).',
        },
        selectedMonth: {
          title: 'Adott havi bevétel',
          desc: 'A kiválasztott hónap bevétele: a havi díjak, és a hónapban kiszámlázott negyedéves / féléves / éves díjak teljes összeggel (mindig aktív státusz, arányosítással).',
        },
        counts: {
          // a STÁTUSZ-SÁV (a szűrő-sáv jobb oldala): kisbetűs jelölések, a darabszám külön; kattintásra szűr
          label: 'Tételek státusz szerint',
          filterHint: 'Szűrés erre a státuszra (ismételt kattintás: vissza)',
          active: 'aktív',
          pending: 'függőben',
          inactive: 'inaktív, lezáratlan',
          inactiveHint:
            'Inaktív, de nyitott vagy jövőbeli záró dátumú tételek (maga a sor vagy az ügyfele / fiókja inaktív) — nem számlázódnak. Kattints a listáért.',
        },
        nextMonthAria: 'Következő hónap',
        emptyTitle: 'Nincs találat a szűrésre',
        emptyDesc:
          'A hónapban egyetlen hozzárendelés sem felel meg a keresésnek és a szűrőknek — a mutatók ezért nullák. Próbáld tágítani a szűrést, vagy lépj másik hónapra.',
        emptyMonthTitle: 'Ebben a hónapban nincs bevétel',
        emptyMonthDesc:
          'A hónapra egyetlen érvényes szolgáltatás-hozzárendelés sincs. Lépj másik hónapra, vagy vedd fel a hiányzó hozzárendeléseket.',
        filterStatus: 'Státusz',
        // DÍJTÍPUS (nem „Gyakoriság": az a havi / negyedéves / féléves / éves értéket jelenti a rendszerben)
        filterBillingType: 'Díjtípus',
        billingType: { recurring: 'ismétlődő', one_time: 'eseti' },
        // „eseti" díjtípusra szűrve az ismétlődő mutatók 0-k — a megjegyzés, hogy ne hibának látsszon
        oneTimeOnlyNote:
          'Eseti díjtípusra szűrve nincs ismétlődő bevétel: az MRR, a PRR és az ARR ezért 0, az MRR-trend és a státusz-darabszámok rejtve.',
        filterMainCategory: 'Főkategória',
        filterSubCategory: 'Alkategória',
        filterService: 'Szolgáltatás',
        filterPlan: 'Csomag',
        filterClient: 'Ügyfél',
        filterAccount: 'Fiók',
        status: { active: 'Aktív', pending: 'Függőben', inactive: 'Inaktív' },
        // A KATEGÓRIA-NEVEK A CÉG SZAKNYELVÉN állnak magyarul is (user-döntés): `product`/`advisory`, illetve
        // `web development`/`digital marketing` — ezeket senki nem fordítja a napi munkában, a „Termék"/
        // „Tanácsadás"/„Webfejlesztés" alak idegen volt. A `RoomSome` márkanév, a `revenue management`
        // szakkifejezés; csak az „Egyéb" magyar.
        mainCategory: { product: 'Product', advisory: 'Advisory', other: 'Egyéb' },
        subCategory: {
          roomsome: 'RoomSome',
          innovation: 'Innováció',
          digital_marketing: 'Digital marketing',
          revenue_management: 'Revenue management',
          web_development: 'Web development',
          other: 'Egyéb',
        },
        freqShort: { quarterly: 'N', semi_annual: 'F', annual: 'É' },
        mrr: {
          title: 'MRR',
          desc: 'Havi ismétlődő bevétel',
          info: 'A hónapra jutó, CSAK havi frekvenciájú ismétlődő bevétel — eredményszemléletben: ahhoz a hónaphoz számít, amikor a szolgáltatás fut, nem amikor a számla kiáll. A negyedéves/féléves/éves tételek a PRR-be tartoznak. A badge az előző hónaphoz viszonyított változás.',
          subtitle: {
            current: 'Havi ismétlődő bevétel',
            historical: 'Korábbi havi ismétlődő bevétel',
            projected: 'Előrejelzett havi ismétlődő bevétel',
          },
        },
        prr: {
          title: 'PRR',
          desc: 'Időszakos ismétlődő bevétel',
          info: 'A NEM havi (negyedéves/féléves/éves) ismétlődő tételek TELJES díja, abban a hónapban, amikor a számla kiáll — egy éves díj tehát egyetlen hónapban, egyben jelenik meg, a többi hónapban nem. A badge-ek a frekvencia szerinti bontást mutatják.',
        },
        nrr: {
          title: 'NRR',
          desc: 'Eseti bevétel',
          info: 'A kiválasztott hónapban INDULÓ eseti (egyszeri) díjak TELJES ára — az eseti díj nem terül szét hónapokra. A nyilvántartott sorok itt sem számolnak bele.',
        },
        arr: {
          title: 'ARR',
          desc: 'Éves ismétlődő bevétel',
          info: 'A kiválasztott hónappal bezárólag GÖRDÜLŐ 12 hónap ismétlődő bevétele: a havi díjak + az ablakban kiszámlázott negyedéves / féléves / éves díjak (egy éves díj egyszer, teljes összeggel). NEM MRR × 12.',
        },
        ar: {
          title: 'AR',
          desc: 'Teljes éves bevétel',
          info: 'Teljes éves bevétel: ARR + az UGYANEZEN a gördülő 12 hónapon induló ÖSSZES eseti tétel teljes ára. NEM NRR × 12 — az NRR egy hónapra szól.',
        },
        charts: {
          mrrTrend: 'MRR-trend (12 hónap)',
          mrrTrendAria: 'MRR-trend az elmúlt 12 hónapra. Legutóbbi érték: {{value}}.',
          byService: 'Bevétel szolgáltatásonként',
          // a szolgáltatás-sáv két része (user-döntés): fix díjú és használat-alapú (sávos / jutalékos).
          // A JELMAGYARÁZAT-feliratok KISBETŰSEK (user-döntés, a badge-ek mintájára) — kivéve a tulajdonnevet / márkát.
          fixedFee: 'fix díj',
          usageBased: 'használat-alapú',
          // a szűretlen nézetben egy sorra kattintva rászűr a szolgáltatásra
          byServiceHint: 'Kattints egy szolgáltatásra a csomag-szintű bontásért.',
          // csomag-szinten: a sor a lenti részletező csoportjához ugrik
          byPlanHint: 'Kattints egy csomagra a lenti részletekért.',
          byClient: 'Top 10 ügyfél',
          categoryDistribution: 'Bevétel-megoszlás főkategóriánként',
          categoryAria: 'Bevétel-megoszlás főkategóriánként (termék, tanácsadás, egyéb).',
          recurring: 'ismétlődő',
          oneTime: 'eseti',
          // a főkategória-fánk jelmagyarázata: az üzletág-név (Product / Advisory) tulajdonnév, az „egyéb" jelölés
          legendCategory: {
            product: 'Product',
            advisory: 'Advisory',
            other: 'egyéb',
            unclassified: 'besorolatlan',
          },
          empty: 'Nincs megjeleníthető adat erre a hónapra.',
          planVsActual: 'Terv és tény (12 hónap)',
          planVsActualAria:
            'Terv és tény bevétel az elmúlt 12 hónapra. A tény csak a lezárt hónapokra látszik.',
          planSeries: 'terv',
          actualSeries: 'tény',
          actualByService: 'Tény — szolgáltatásonként',
          actualByClient: 'Tény — ügyfelenként',
          actualCategory: 'Tény — főkategóriánként',
        },
        actual: {
          planLabel: 'Terv',
          actualLabel: 'Tény',
          /** A hónapra még nem futott le a generálás — tény-számot ezért nem mutatunk (a 0 félrevezetne). */
          open: 'A hónap még nem zárult le',
          // a horizont előtti hónap: nem „még nem zárult le", hanem nincs adat
          noData: 'Nincs adat',
          openHint:
            'A hónap még tart, vagy még nem futott le rá a számlázási kör — ezért tény-adat nincs. A terv-szám változatlanul látszik.',
          unclassified: 'Besorolatlan',
          unknownClient: 'Ismeretlen ügyfél',
          skipped_one: '{{count}} tétel kimaradt a számlázásból — nézd meg a kihagyás-naplót',
          skipped_other: '{{count}} tétel kimaradt a számlázásból — nézd meg a kihagyás-naplót',
          rateEstimated:
            'A hónapra nincs rögzített árfolyam, ezért a deviza-tételek az MNB havi átlagával szerepelnek (becsült).',
          rateFallback:
            'A hónapra nem volt feloldható árfolyam, ezért a deviza-tételek 1:1-gyel szerepelnek — a tény-összeg ALULBECSÜLT.',
          truncated:
            'A bizonylat-lista elérte a lapozó-plafont, ezért a tény-összeg csonka lehet. Szűkíts a szűrőkkel.',
        },
        scope: { client: 'Ügyfél', account: 'Fiók' },
        frequencyLabel: {
          monthly: 'havi',
          quarterly: 'negyedéves',
          semi_annual: 'féléves',
          annual: 'éves',
          one_time: 'eseti',
        },
        details: {
          empty: 'Nincs megjeleníthető tétel.',
          total: 'Összesen',
          oneTime: 'eseti',
          notDue: 'ebben a hónapban nincs számla',
          dueRevenue: 'a hónap bevétele',
          // PERIODIKUS tétel a kártyán: a szám mindig a TELJES díj, és kimondjuk, mire vonatkozik
          periodFee: {
            quarterly: 'negyedéves díj',
            semi_annual: 'féléves díj',
            annual: 'éves díj',
          },
          billedNow: 'egyben, ebben a hónapban számlázva',
          nextBilling: 'következő számla: {{month}}',
          // a szűrt tételes kártyák exportjának oszlopai (`service-details-export.tsx`)
          export: {
            service: 'Szolgáltatás',
            client: 'Ügyfél',
            account: 'Fiók',
            scope: 'Hatókör',
            frequency: 'Gyakoriság',
            status: 'Státusz',
            amount: 'A hónap összege (HUF)',
            periodFee: 'Teljes díj (HUF)',
            billedThisMonth: 'Számlázódik ebben a hónapban',
            nextInvoice: 'Következő számla',
            components: 'Összetevők',
            yes: 'igen',
            no: 'nem',
          },
          nrrTitle: 'NRR — eseti tételek',
          prrTitle: 'PRR — időszakos tételek',
          pendingTitle: 'Függőben lévő tételek',
          inactiveTitle: 'Inaktív, lezáratlan tételek',
          inactiveDesc:
            'Ezek a tételek nem számlázódnak: maga a sor vagy az ügyfele / fiókja inaktív, de a záró dátum nyitott vagy a jövőben van. A rendbetételük: a záró dátum beállítása (vagy a sor újraaktiválása).',
        },
      },
      services: {
        // A SZOLGÁLTATÁSOK lap (két fül: hozzárendelések + katalógus) HÉJÁNAK szövegei.
        tabs: { assignments: 'Szolgáltatások', catalog: 'Katalógus' },
        forbiddenTitle: 'Nincs jogosultságod ehhez a laphoz',
        forbiddenDesc:
          'A szolgáltatás-hozzárendelések és a katalógus megtekintéséhez külön jogosultság kell. Kérd a rendszergazdától.',
      },
      serviceAssignments: {
        // Az ügyfél-/fiók-szűrő a LISTA felületéről kivezetve: a konkrét ügyfélre/fiókra a kereső
        // szűr. A kulcsok maradnak: az ügyfél-detail „Szolgáltatások" szelete és a deep-link használja őket.
        filterClient: 'Ügyfél',
        filterAccount: 'Fiók',
        filterScope: 'Hatókör',
        filterType: 'Típus',
        filterStatus: 'Státusz',
        filterFrequency: 'Gyakoriság',
        filterAgreement: 'Fizetés',
        filterPricing: 'Árazás',
        loading: 'betöltés…',
        columns: {
          service: 'Szolgáltatás',
          owner: 'Ügyfél / fiók',
          type: 'Típus',
          status: 'Státusz',
          quantity: 'Mennyiség',
          frequency: 'Gyakoriság',
          // A NETTÓ oszlop „Árazás"-ra váltott: az összeg alatt az árazási MODELL badge-e áll
          // (fix díjas / sávos / …), ahogy a legacy oszlopa is tette — a fejléc ezért nem „Nettó" többé.
          pricing: 'Árazás',
          netAmount: 'Nettó',
          actions: 'Műveletek',
        },
        // KOMPLEX (komponenses) szolgáltatás jelzése a lista szolgáltatás-cellájában — a legacy „komplex
        // szolgáltatás" felirata, a komponens-számmal kiegészítve.
        complexService: 'komplex szolgáltatás · {{count}} komponens',
        // Scope-jelzés (a legacy „ügyfél szintű" / „fiók szintű" feliratai).
        scopeLabel: { client: 'ügyfél szintű', account: 'fiók szintű' },
        // Ügyfél-szintű hozzárendelés kijelölt cél-fiókjai (a legacy legördülője).
        moreTargets: '+{{count}} fiók',
        moreTargetsAria: 'További cél-fiókok',
        componentsCount_one: '{{count}} komponens',
        componentsCount_other: '{{count}} komponens',
        componentsAria: 'Komponensek és áraik',
        countOf: '{{shown}} / {{total}} hozzárendelés',
        empty: 'Nincs szolgáltatás-hozzárendelés',
        emptyDesc: 'Még nincs betöltve egyetlen hozzárendelés sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő hozzárendelés.',
        new: 'Új hozzárendelés',
        edit: 'Szerkesztés',
        close: 'Lezárás',
        // LEZÁRT jelölő a lista első oszlopában + a feloldás művelete (a záró dátum törlése).
        closedBadge: 'Lezárt',
        reopen: {
          action: 'Lezárás feloldása',
          success: 'A lezárás feloldva — a záró dátum törölve.',
        },
        delete: 'Törlés',
        deleting: 'Törlés…',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        createTitle: 'Új szolgáltatás-hozzárendelés',
        createDesc:
          'Egy ügyfélhez/fiókhoz rendelt szolgáltatás-konstrukció — a snapshotot a rendszer a katalógusból építi.',
        editTitle: 'Hozzárendelés szerkesztése',
        deleteTitle: 'Hozzárendelés törlése',
        deleteDesc: 'Biztosan törlöd a(z) „{{name}}" hozzárendelést? A művelet nem vonható vissza.',
        // MÁSOLÁS (klónozás): a sor-akció + a cél-választó modál. A modál CSAK a célt kérdezi — az árazás,
        // az ár-sorok, a komponens-fa és a számlatétel-mezők kérdés nélkül jönnek át.
        clone: {
          action: 'Másolás',
          title: 'Hozzárendelés másolása',
          desc: 'A(z) „{{name}}" hozzárendelés másolata — válaszd ki, hová kerüljön.',
          summaryPlan: 'Konstrukció',
          summaryPricing: 'Árazás',
          summaryPrices: 'Ár-sorok',
          summaryComponents: 'Komponensek',
          summaryPeople: 'Felelősök',
          priceCount_one: '{{count}} ár-sor átmásolva',
          priceCount_other: '{{count}} ár-sor átmásolva',
          componentCount_one: '{{count}} komponens átmásolva',
          componentCount_other: '{{count}} komponens átmásolva',
          peopleAll: 'kapcsolattartó és szakértő átmásolva',
          peopleExpertOnly: 'csak a szakértő másolódik át',
          otherClientWarning:
            'Másik ügyfélhez másolod: a kapcsolattartó, a bankkártya és a kijelölt cél-fiókok nem jönnek át — a másolás után állítsd be őket.',
          startDate: 'Indulás dátuma',
          startDateHint:
            'A másolat ezzel a dátummal indul (a forrás indulás- és lezárás-dátuma nem öröklődik).',
          confirm: 'Másolás',
          saving: 'Másolás…',
        },
        closeTitle: 'Hozzárendelés lezárása',
        closeDesc: 'A(z) „{{name}}" számlázása a megadott dátummal zárul le.',
        closeEndDate: 'Számlázás vége',
        closeConfirm: 'Lezárás',
        closing: 'Lezárás…',
        lockedRecurring:
          'Számlázott, ismétlődő hozzárendelés — csak a státusz, mennyiség, kibocsátó, lezárás- és szerződés-dátumok, valamint a jegyzet szerkeszthető.',
        lockedOneTime: 'Számlázott, egyszeri hozzárendelés — csak a jegyzet szerkeszthető.',
        scope: { client: 'Ügyfél', account: 'Fiók' },
        typeLabel: { tracking: 'nyilvántartás', billing: 'számlázás' },
        statusLabel: { active: 'aktív', pending: 'függőben', inactive: 'inaktív' },
        billingTypeLabel: { one_time: 'egyszeri', recurring: 'ismétlődő' },
        quantityUnitLabel: { piece: 'darab', hour: 'óra', day: 'nap', other: 'egyéb' },
        issuerLabel: { none: '—', MOR: 'MOR', MDIG: 'MDIG' },
        invoiceTypeLabel: { invoice: 'számla', proforma: 'díjbekérő' },
        deliveryTypeLabel: { electronic: 'e-számla', paper: 'papír alapú' },
        frequencyLabel: {
          monthly: 'havi',
          quarterly: 'negyedéves',
          semi_annual: 'féléves',
          annual: 'éves',
          // Az ESETI sornak nincs számlázási ciklusa — a „Gyakoriság" cella EZT írja ki helyette (a
          // korábbi em-dash helyett), mert az „eseti" maga a válasz, nem hiányzó adat.
          one_time: 'eseti',
        },
        agreementLabel: { prepaid: 'előre fizetett', postpaid: 'utólag fizetett' },
        // az ügyfél / fiók adatlap „Számlánként" nézete — a hozzárendelések a leendő számlák szerint
        invoiceView: {
          label: 'Nézet',
          list: 'Lista',
          invoices: 'Számlánként',
          note: 'Melyik szolgáltatás melyik számlára kerül a generáláskor (ugyanazzal a szabállyal, mint a számlázás). Összeget és esedékességet ez a nézet nem mutat.',
          recipientClient: 'Közös ügyfél-számla · {{name}}',
          recipientAccount: 'Fiókra eltérítve · {{name}}',
          vatCurrency: 'ÁFA {{vat}} · {{currency}}',
          issuerMissing:
            'Nincs érvényes kibocsátó — erre a számlára a generálás nem állít ki semmit, amíg a hozzárendeléseken nincs kibocsátó.',
          autoBillingOff: 'Automata számlázás kikapcsolva — a generálás kihagyja',
          clientScope: 'ügyfél-szintű hozzárendelés',
          otherCount_one: 'Ezen a számlán még {{count}} tétel szerepel más fiókoktól.',
          otherCount_other: 'Ezen a számlán még {{count}} tétel szerepel más fiókoktól.',
          excludedTitle: 'Nem kerül számlára',
          reason: {
            tracking: 'Nyilvántartásos hozzárendelés — nem számlázódik',
            one_time: 'Eseti díj — kézzel kerül számlára',
            ended: 'Lezárult — már nem számlázódik',
            inactive: 'Inaktív — nem számlázódik',
            incomplete:
              'Hiányos beállítás (árazás, gyakoriság vagy kezdő dátum) — a generálás kihagyja',
          },
          empty: 'Nincs olyan szolgáltatás, ami számlára kerülne.',
          error: 'A számla-nézet nem tölthető be.',
          forbidden: 'Nincs jogosultságod a számla-nézethez.',
          retry: 'Újra',
        },
        // FIGYELEM-PANEL: hamarosan induló, még FÜGGŐ hozzárendelések (a napi cron ugyanezt a szabályt nézi,
        // `service_assignment.pending_start_soon` értesítés). A `noticeDays` a BFF konfigurációja.
        attention: {
          bannerTitle_one:
            '{{count}} függő hozzárendelés a következő {{noticeDays}} napon belül indul — ellenőrizendő',
          bannerTitle_other:
            '{{count}} függő hozzárendelés a következő {{noticeDays}} napon belül indul — ellenőrizendő',
          reviewButton: 'Megnézem',
          dialogTitle: 'Hamarosan induló, függő hozzárendelések',
          dialogDescription:
            'Ha a hozzárendelés az induló dátumig nem kerül „aktív" állapotba, a havi számla-generátor kihagyja.',
          columns: { client: 'Ügyfél / fiók', service: 'Szolgáltatás', startDate: 'Indulás' },
          daysToday: 'ma',
          daysIn: '{{count}} nap',
          daysOverdue: '{{count}} napja esedékes',
          footerLink: 'Csak a függők listázása',
          startBadge: {
            later: 'indul: {{date}}',
            soon: 'hamarosan indul: {{date}}',
            overdue: 'indulás elmaradt: {{date}}',
            missing: 'nincs kezdő dátum',
          },
          startTooltip: {
            later:
              'A számlázás kezdete. Ha addig nem kerül aktív állapotba, a havi számla-generátor kihagyja.',
            soon: 'Hamarosan indul, de még függőben van: ha addig nem kerül aktív állapotba, a havi számla-generátor kihagyja.',
            overdue:
              'A számlázás kezdete elmúlt, a hozzárendelés mégis függőben van: a havi számla-generátor kihagyja.',
            missing:
              'Nincs megadva a számlázás kezdete: az indulását semmi nem jelzi, és a havi számla-generátor sem számlázza.',
          },
        },
        form: {
          scope: 'Hatókör',
          client: 'Ügyfél',
          clientPlaceholder: 'Válassz ügyfelet…',
          account: 'Fiók',
          accountPlaceholder: 'Válassz fiókot…',
          accountPickClient: 'Előbb válassz ügyfelet…',
          service: 'Szolgáltatás',
          servicePlaceholder: 'Válassz szolgáltatást…',
          plan: 'Konstrukció',
          planPlaceholder: 'Válassz konstrukciót…',
          planPickService: 'Előbb válassz szolgáltatást…',
          planBillingType: 'Számlázás módja',
          planPricingType: 'Árazás',
          planVatRate: 'ÁFA',
          type: 'Típus',
          status: 'Státusz',
          quantity: 'Mennyiség',
          quantityUnit: 'Egység',
          sectionBilling: 'Számlázási beállítások',
          issuer: 'Kibocsátó',
          invoiceType: 'Bizonylat típusa',
          invoiceDeliveryType: 'Számlatípus',
          billingFrequency: 'Számlázási gyakoriság',
          frequencyNone: '— nincs —',
          billingAgreement: 'Számlázási megállapodás',
          agreementNone: '— nincs —',
          billingStartDate: 'Számlázás kezdete',
          billingEndDate: 'Számlázás vége',
          // ESETI DÍJ egyetlen dátuma (a számlázási kezdet ÉS vég) — a kimutatás ennek a hónapjába sorolja
          billingDate: 'Számlázás dátuma',
          billingDateHint: 'Az eseti díj ebben a hónapban jelenik meg a bevétel-kimutatásban.',
          contractStartDate: 'Szerződés kezdete',
          contractEndDate: 'Szerződés vége',
          firstInvoiceProrated: 'Első számla arányosítása',
          firstInvoiceProratedHint: 'Az első időszak töredékére arányosított díj.',
          sectionPricing: 'Árazás (felülírás)',
          pricingType: 'Árazás típusa',
          pricingTypeHint:
            'A katalógus szerinti típus vagy fix díj (lefelé váltható, felfelé nem).',
          vatRate: 'ÁFA-kulcs',
          paymentCard: 'Bankkártya',
          paymentCardNone: '— nincs —',
          contact: 'Kapcsolattartó',
          // A KÉT választó KÜLÖN halmazból választ: a kapcsolattartó ügyfél-oldali KÜLSŐS, a szakértő
          // a cég BELSŐ munkatársa — a placeholder ezt ki is mondja, hogy ne kelljen kitalálni.
          contactPlaceholder: 'Keresés a külsős kapcsolattartók közt…',
          expert: 'Szakértő',
          expertPlaceholder: 'Keresés a belső munkatársak közt…',
          identityNone: '— nincs —',
          accountIds: 'Érintett fiókok',
          accountIdsPlaceholder: 'Válassz fiókokat…',
          accountIdsCount: '{{count}} kijelölve',
          accountIdsHint: 'A hozzárendelés több fiókot is érinthet (ügyfél-szintű).',
          invoiceItemName: 'Egyedi számlatétel-név',
          invoiceItemNameHint: 'Ha üres, a katalógusban megadott számlatétel-név kerül a számlára.',
          invoiceItemNote: 'Egyedi számlatétel-megjegyzés',
          // A KATALÓGUS áfakulcsa melletti jelzés, ha a soron MÁS van érvényben (a számlára ez utóbbi megy).
          vatRateOverridden: 'ezen a soron: {{vatRate}}',
          showComponentsOnInvoice: 'Komponensek a számlatételen',
          showComponentsOnInvoiceHint: 'A komponens-nevek a számlatétel megjegyzésébe kerülnek.',
          usageSourceType: 'Használat forrása',
          usageSourceNone: '— nincs —',
          usageSourceHint: 'Sávos / alapdíj+sávos / jutalékos árazásnál kötelező.',
          oneTimeTypeHint:
            'Eseti díj mindig nyilvántartás: kézi számlára megy, a bevétel-kimutatásban a számlázás dátumának hónapjában számít.',
          usageRefs: 'Forrás azonosító',
          usageRefsPlaceholder: 'Melyik azonosító mérése számít bele?',
          splitSharedUsage: 'Regisztráltak utáni díj megosztása',
          splitSharedUsageHint:
            'Ha több hotel ugyanarra a Loyalty-azonosítóra köt, a regisztráltak utáni díj egyenlően oszlik a bekapcsolt hotelek között. Az alapdíj nem oszlik, a megjegyzésben a teljes regisztrált-szám marad.',
          usageRefsCount: '{{count}} azonosító',
          usageRefsHint:
            'A tétel összege a kiválasztott azonosítók mérésének összege. Az azonosítókat a fiók adatlapján, az Integrációk szekcióban lehet felvenni.',
          usageRefsEmpty:
            'A kiválasztott fiók(ok)on nincs ehhez a forráshoz illő azonosító (hírlevél → MailWizz, minden más → HotelCMS / ZadirCMS). Vidd fel a fiók adatlapján, az Integrációk szekcióban.',
          note: 'Jegyzet',
          sectionComponents: 'Komponensek (csomag)',
          componentsHint:
            'Komplex (csomag) hozzárendelés: a szülő ára a komponensek összege, egy számlatételben. A fő csomag is komponens (az első hozzáadáskor automatikusan bekerül a beírt alapdíjjal), így az alapdíj nem vész el.',
          pricingFromComponents:
            'Az ár a komponensek összege: {{amount}} {{currency}} — a fő csomag alapdíja a komponensek között, első sorként módosítható.',
          addComponent: 'Komponens hozzáadása',
          removeComponent: 'Eltávolítás',
          componentService: 'Szolgáltatás',
          componentPlan: 'Konstrukció',
          componentContact: 'Komponens kapcsolattartója',
          componentExpert: 'Komponens szakértője',
          componentQuantity: 'Menny.',
          componentUnitPrice: 'Egységár',
          componentCurrency: 'Deviza',
          componentsTotal: 'Komponensek összege',
          componentsCurrencyMismatch: 'A komponensek devizája nem egységes.',
          componentsLocked: 'Számlázott hozzárendelésnél a komponensek nem módosíthatók.',
          noComponents: 'Nincs komponens — a hozzárendelés egyszerű.',
        },
        activeWarning: {
          title: 'Már van aktív hozzárendelés',
          desc: 'Ehhez a tulajdonoshoz már tartozik aktív hozzárendelés ugyanerre a szolgáltatásra („{{service}}" — {{owner}}). Biztosan létrehozol egy másikat? A mentés folytatható.',
          descInComponent:
            'Ez a szolgáltatás („{{service}}") már egy MÁSIK hozzárendelés komponenseként aktív ennél a tulajdonosnál ({{owner}}). Biztosan létrehozol egy önálló hozzárendelést is? A mentés folytatható.',
          descFromComponent:
            'A felvett komponens szolgáltatására („{{service}}") már van aktív hozzárendelés ennél a tulajdonosnál ({{owner}}). Biztosan így mented? A mentés folytatható.',
          proceed: 'Mentés folytatása',
          viewExisting: 'Meglévő megtekintése',
          cancel: 'Mégsem',
        },
        pricingTypeLabel: {
          flat: 'Fix díjas',
          tiered: 'Sávos',
          base_tiered: 'Alapdíj + sávos',
          commission: 'Jutalékos',
        },
        vatRateLabel: {
          VAT_27: '27% ÁFA',
          VAT_18: '18% ÁFA',
          VAT_5: '5% ÁFA',
          VAT_0: '0% ÁFA',
          AAM: 'AAM (alanyi adómentes)',
          TAM: 'TAM (tárgyi adómentes)',
          FAA: 'FAA (fordított adózás)',
          EUFAD37: 'EUFAD37 (EU-fordított)',
        },
        pricing: {
          section: 'Árazás',
          currency: 'Deviza',
          primaryLabel: {
            flat: 'Egységár (nettó)',
            tiered: 'Passzív díj (nettó)',
            base_tiered: 'Alapdíj (nettó)',
            commission: 'Minimum díj (nettó)',
          },
          tiers: 'Sávok',
          addTier: 'Sáv hozzáadása',
          removeTier: 'Sáv törlése',
          noTiers: 'Még nincs sáv. Adj hozzá legalább egyet.',
          tierMin: 'Sáv-minimum',
          tierMax: 'Sáv-maximum',
          tierMaxUnlimited: 'korlátlan',
          netPrice: 'Nettó ár',
          overflowEnabled: 'Sáv-feletti (overflow) díj',
          blockSize: 'Blokkméret',
        },
      },
      serviceCatalog: {
        filterStructure: 'Szerkezet',
        filterCategory: 'Kategória',
        filterSubCategory: 'Alkategória',
        filterBilling: 'Számlázás',
        filterPricing: 'Árazás',
        filterStatus: 'Státusz',
        filterAll: '{{label}}: mind',
        loading: 'betöltés…',
        columns: {
          service: 'Szolgáltatás',
          pricing: 'Árazás',
          supportTeam: 'Felelős ügyfélszolgálat',
          status: 'Státusz',
          actions: 'Műveletek',
        },
        morePlans: '+{{count}}',
        morePlansAria: 'További csomagok',
        // Az árazás-cellában csonkolt ársorok jelzése (a teljes bontás a szerkesztőben).
        morePrices: '+{{count}} további ársáv',
        noPrices: 'Nincs ár',
        blockUnit: 'db',
        priceType: {
          passive: 'passzív díj',
          minimum: 'minimum díj',
          base: 'alapdíj',
          unit: 'egységár',
        },
        new: 'Új szolgáltatás',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        deleting: 'Törlés…',
        createTitle: 'Új szolgáltatás',
        createDesc: 'A szolgáltatás alapadatai, csomagjai és árazása.',
        editTitle: 'Szolgáltatás szerkesztése',
        deleteTitle: 'Szolgáltatás törlése',
        deleteDesc: 'Biztosan törlöd a(z) „{{name}}" szolgáltatást? A művelet nem vonható vissza.',
        tabs: {
          basic: 'Alapadatok',
          plans: 'Csomagok',
        },
        fields: {
          name: 'Név',
          description: 'Leírás',
          structureType: 'Szerkezet',
          structureTypeLockedHint: 'A szerkezet-típus meglévő szolgáltatásnál nem módosítható.',
          status: 'Státusz',
          showInTicketDropdown: 'Megjelenés a jegy-legördülőben',
          showInTicketDropdownHint: 'Kiválasztható-e a szolgáltatás új ticket nyitásakor.',
          groupedInCharts: 'Összevonás a kimutatásokon',
          groupedInChartsHint:
            'A szolgáltatás a kimutatásokon a testvéreivel EGY tételként jelenik meg (a legacy „grouped in charts").',
          supportTeam: 'Felelős ügyfélszolgálat',
          supportTeamHint: 'Melyik support-csapathoz tartozik a szolgáltatás (opcionális).',
          supportTeamNone: 'Nincs',
          supportTeamUnknown: 'Jelenlegi (ismeretlen) csapat',
        },
        // UGYANAZ A SZABÁLY, mint a bevétel-előrejelzésen: a kategória-nevek a cég szaknyelvén állnak
        // magyarul is (`product`/`advisory`, `web development`/`digital marketing`) — csak az „Egyéb" magyar.
        categoryLabel: {
          product: 'Product',
          advisory: 'Advisory',
          other: 'Egyéb',
        },
        subCategoryLabel: {
          roomsome: 'RoomSome',
          innovation: 'Innováció',
          digital_marketing: 'Digital marketing',
          revenue_management: 'Revenue management',
          web_development: 'Web development',
          other: 'Egyéb',
        },
        billingTypeLabel: {
          recurring: 'Ismétlődő',
          one_time: 'Eseti',
        },
        pricingTypeLabel: {
          flat: 'Fix díjas',
          tiered: 'Sávos',
          base_tiered: 'Alapdíj + sávos',
          commission: 'Jutalékos',
        },
        vatRateLabel: {
          VAT_27: '27% ÁFA',
          VAT_18: '18% ÁFA',
          VAT_5: '5% ÁFA',
          VAT_0: '0% ÁFA',
          AAM: 'AAM (alanyi adómentes)',
          TAM: 'TAM (tárgyi adómentes)',
          FAA: 'FAA (fordított adózás)',
          EUFAD37: 'EUFAD37 (EU-fordított)',
        },
        structureLabel: {
          simple: 'Egyszerű',
          package: 'Csomag alapú',
        },
        statusLabel: {
          active: 'Aktív',
          inactive: 'Inaktív',
        },
        plan: {
          name: 'Csomag neve',
          mainCategory: 'Főkategória',
          subCategory: 'Alkategória',
          billingType: 'Számlázás típusa',
          pricingType: 'Árazás típusa',
          vatRate: 'ÁFA-kulcs',
          usageSourceType: 'Használat forrása',
          invoiceItemName: 'Számlatétel neve',
          // KÖTELEZŐ (user-döntés): üresen a számlára egy azóta átnevezett csomagnév került
          invoiceItemNameHint:
            'Ez a név kerül a számlára (a hozzárendelésen egyedileg felülírható).',
          invoiceItemNote: 'Egyedi számlatétel-megjegyzés',
          oneTimeFlatHint: 'Eseti díjnál az árazás mindig fix díjas.',
          add: 'Csomag hozzáadása',
          remove: 'Csomag törlése',
          untitled: '{{index}}. csomag',
        },
        pricing: {
          section: 'Árazás',
          currency: 'Deviza',
          usageSourceNone: 'Nincs megadva',
          primaryLabel: {
            flat: 'Egységár (nettó)',
            tiered: 'Passzív díj (nettó)',
            base_tiered: 'Alapdíj (nettó)',
            commission: 'Minimum díj (nettó)',
          },
          tiers: 'Sávok',
          addTier: 'Sáv hozzáadása',
          removeTier: 'Sáv törlése',
          noTiers: 'Még nincs sáv. Adj hozzá legalább egyet.',
          tierMin: 'Sáv-minimum',
          tierMax: 'Sáv-maximum',
          tierMaxUnlimited: 'korlátlan',
          netPrice: 'Nettó ár',
          overflowEnabled: 'Sáv-feletti (overflow) díj',
          blockSize: 'Blokkméret',
        },
        countOf: '{{shown}} / {{total}} szolgáltatás',
        empty: 'Nincs szolgáltatás',
        emptyDesc: 'Még nincs betöltve egyetlen szolgáltatás sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek vagy szűrőnek megfelelő szolgáltatás.',
      },
      orgStructure: {
        loading: 'betöltés…',
        edit: 'Szerkesztés',
        delete: 'Törlés',
        cancel: 'Mégse',
        save: 'Mentés',
        saving: 'Mentés…',
        deleting: 'Törlés…',
        // A „Munkatársak" oszlop KÖZÖS mind a három fülön, a support-teams „Tagok"-designjával.
        columns: { actions: 'Műveletek', employees: 'Munkatársak' },
        moreMembers: '+{{count}}',
        moreMembersAria: 'További munkatársak',
        morePositions: '+{{count}}',
        morePositionsAria: 'További beosztások',
        tabs: {
          departments: 'Részlegek',
          teams: 'Csapatok',
          positions: 'Beosztások',
        },
        fields: {
          name: 'Név',
          color: 'Szín',
          department: 'Részleg',
          departmentPlaceholder: 'Válassz részleget…',
          team: 'Csapat',
          teamPlaceholder: 'Válassz csapatot…',
          lead: 'Csapatvezető',
          description: 'Leírás',
        },
        colorLabel: {
          burgundy: 'Bordó',
          coral: 'Korall',
          honey: 'Méz',
          kiwi: 'Kivi',
          grove: 'Liget',
          mint: 'Menta',
          forest: 'Erdő',
          lagoon: 'Lagúna',
          iris: 'Írisz',
          midnight: 'Éjkék',
          steel: 'Acél',
          gray: 'Szürke',
        },
        departments: {
          new: 'Új részleg',
          countOf: '{{shown}} / {{total}} részleg',
          empty: 'Nincs részleg',
          emptyDesc: 'Még nincs egyetlen részleg sem. Hozz létre egyet a „Új részleg" gombbal.',
          noMatches: 'Nincs találat',
          noMatchesDesc: 'Nincs a keresésnek megfelelő részleg.',
          createTitle: 'Új részleg',
          createDesc:
            'A részleg kétnyelvű neve és leírása. Az azonosítót a rendszer generálja. A színt a csapatoknál állítod be.',
          editTitle: 'Részleg szerkesztése',
          deleteTitle: 'Részleg törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" részleget? A művelet nem vonható vissza.',
          columns: {
            name: 'Részleg',
            teams: 'Csapatok',
          },
          // A „Csapatok" cella legördülője (a bevett `ExpandableCell` feliratai).
          moreTeams: '+{{count}}',
          moreTeamsAria: 'További csapatok',
        },
        teams: {
          new: 'Új csapat',
          filterDepartment: 'Részleg',
          countOf: '{{shown}} / {{total}} csapat',
          empty: 'Nincs csapat',
          emptyDesc: 'Még nincs egyetlen csapat sem. Hozz létre egyet a „Új csapat" gombbal.',
          noMatches: 'Nincs találat',
          noMatchesDesc: 'Nincs a keresésnek megfelelő csapat.',
          createTitle: 'Új csapat',
          createDesc:
            'A csapat kétnyelvű neve, színe és a részlege. Az azonosítót a rendszer generálja.',
          editTitle: 'Csapat szerkesztése',
          deleteTitle: 'Csapat törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" csapatot? A művelet nem vonható vissza.',
          selectDepartment: 'Válassz részleget…',
          noDepartment: 'Nincs részleg',
          leadPlaceholder: 'Válassz munkatársat…',
          leadHint:
            'Az eszköz-igények jóváhagyásáról tájékoztatót kap. Nem kötelező, de nélküle a csapat igényeiről senki nem kap vezetői értesítést.',
          noLead: 'Nincs vezető',
          noLeadHint:
            'Nincs kijelölt csapatvezető — a csapat eszköz-igényeiről nem megy vezetői értesítés.',
          columns: {
            name: 'Csapat',
            lead: 'Vezető',
            positions: 'Beosztások',
          },
        },
        positions: {
          new: 'Új beosztás',
          filterDepartment: 'Részleg',
          filterTeam: 'Csapat',
          countOf: '{{shown}} / {{total}} beosztás',
          empty: 'Nincs beosztás',
          emptyDesc: 'Még nincs egyetlen beosztás sem. Hozz létre egyet a „Új beosztás" gombbal.',
          noMatches: 'Nincs találat',
          noMatchesDesc: 'Nincs a keresésnek megfelelő beosztás.',
          createTitle: 'Új beosztás',
          createDesc:
            'A beosztás kétnyelvű neve és a csapata. A részleg a csapatból következik; az azonosítót a rendszer generálja.',
          editTitle: 'Beosztás szerkesztése',
          deleteTitle: 'Beosztás törlése',
          deleteDesc: 'Biztosan törlöd a(z) „{{name}}" beosztást? A művelet nem vonható vissza.',
          columns: {
            name: 'Beosztás',
          },
        },
      },
      portalAccess: {
        sectionHint:
          'Ki férhet hozzá a partner-portálhoz EZEN a fiókon, és milyen szerepben. A szerep FIÓKONKÉNT eltérhet: ugyanaz a kapcsolattartó az egyik fiókon portál-admin, a másikon csak megtekintő.',
        empty: 'Ehhez a fiókhoz még nincs kapcsolattartó rendelve.',
        roles: {
          none: 'nincs hozzáférése',
          'portal-admin': 'Portál-adminisztrátor',
          'portal-member': 'Portál-felhasználó',
          'portal-viewer': 'Portál-megtekintő',
        },
        roleAria: '{{name}} portál-szerepe',
        roleLabel: 'Partner-portál szerep',
        roleHint:
          'Mit tehet ez a kapcsolattartó a partner-portálon EZEN a fiókon. Fiókonként eltérhet — másik fiókon másik szerepet kaphat.',
        revokeAria: '{{name}} portál-hozzáférésének visszavonása',
        inviteAria: 'Portál-meghívó küldése {{name}} részére',
        revokeTitle: 'Portál-hozzáférés visszavonása',
        revokeBody:
          '{{name}} ezután nem éri el ezt a fiókot a partner-portálon. A kapcsolattartói kötés és a személy adatai VÁLTOZATLANOK maradnak — csak a portál-hozzáférés szűnik meg, azonnal.',
        revokeConfirm: 'Visszavonás',
        missingPlatformScope:
          'A hozzáférések módosításához platform-oldali jog is kell (iam:portal-access) — kérd a jogosultság-kezelőtől.',
      },
      accounts: {
        filterType: 'Típus',
        filterCategory: 'Kategória',
        filterService: 'Szolgáltatás',
        filterStatus: 'Státusz',
        filterAll: '{{label}}: mind',
        loading: 'betöltés…',
        columns: {
          account: 'Fiók',
          address: 'Cím',
          clientAdmin: 'Ügyféladminisztrátor',
          contact: 'Kapcsolattartó',
          services: 'Szolgáltatások',
          domains: 'Domainek',
          note: 'Jegyzet',
          status: 'Státusz',
          actions: 'Műveletek',
        },
        more: '+{{count}}',
        moreItems: 'További elemek megjelenítése',
        typeLabel: {
          hospitality: 'Vendéglátás',
          accommodation: 'Szálláshely',
          spa: 'Fürdő',
          other_tourism: 'Egyéb turisztikai',
          other_industry: 'Egyéb iparág',
        },
        statusLabel: { active: 'Aktív', pending: 'Függőben', inactive: 'Inaktív' },
        countOf: '{{shown}} / {{total}} fiók',
        empty: 'Nincs fiók',
        emptyDesc: 'Még nincs betöltve egyetlen fiók sem.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő fiók.',
        // Sor-akció / detail (read-only)
        view: 'Megtekintés',
        edit: 'Szerkesztés',
        back: 'Vissza a fiókokhoz',
        notFound: 'A fiók nem található',
        notFoundDesc: 'Lehet, hogy törölték, vagy nincs jogosultságod megtekinteni.',
        forbidden: 'Nincs hozzáférésed',
        forbiddenDesc: 'Nincs jogosultságod a fiókok megtekintéséhez.',
        // Törlés (ConfirmDialog)
        delete: 'Törlés',
        cancel: 'Mégse',
        deleting: 'Törlés…',
        deleteTitle: 'Fiók törlése',
        deleteDesc: 'Biztosan törlöd a(z) „{{name}}" fiókot? A művelet nem vonható vissza.',
        // A LAP ALJI VESZÉLYZÓNA (a fejlécből ide költözött — a visszafordíthatatlan műveletnek saját,
        // magyarázott helye van, nem a szerkesztés-gomb mellett lapul).
        dangerTitle: 'Fiók törlése',
        dangerDesc:
          'A fiók kötései (kapcsolattartók, ügyféladminisztrátorok) és szolgáltatás-hozzárendelései is elvesznek a listákból. A művelet nem vonható vissza.',
        // A DETAIL-OLDAL SZEKCIÓI — a régi CRM tab-sorrendjét követve, egy görgethető lapon. A `SectionNav`
        // ezeket a címeket sorolja; a horgonyok a `features/accounts/sections.ts`-ben élnek.
        sections: {
          basics: 'Alapadatok',
          other: 'Egyéb adatok',
          otherDesc: 'A fiókot kiszolgáló rendszerek, a domainek és a szabad jegyzet.',
          services: 'Szolgáltatások',
          billing: 'Számlázás',
          billingDesc:
            'A fiók számlázása: kap-e külön számlát, és melyik profilra álljon ki. Saját számlázás nélkül az ügyfél elsődleges profilja számláz, és a fiók tételei az ügyfél számlájába olvadnak be.',
          integrations: 'Integrációk',
          integrationsDesc: 'Külső rendszer-azonosítók — belső használatra.',
          contacts: 'Kapcsolattartók',
          portalAccess: 'Partner-portál hozzáférés',
          admins: 'Ügyféladminisztrátorok',
        },
        sectionNavLabel: 'Szekciók ezen az oldalon',
        // A kapcsolódó (RelatedList-es) szekciók KÖZÖS szövegei
        relatedError: 'Nem sikerült betölteni.',
        relatedForbidden: 'Nincs jogosultságod megtekinteni.',
        relatedRetry: 'Újra',
        relatedTotal: 'Összesen {{count}} — az első 8 látszik',
        contactsEmpty: 'Ehhez a fiókhoz még nincs kapcsolattartó.',
        adminsEmpty: 'Ehhez a fiókhoz még nincs ügyféladminisztrátor.',
        addContact: 'Kapcsolattartó hozzáadása',
        addAdmin: 'Ügyféladminisztrátor hozzáadása',
        // Kötés-badge-ek — KISBETŰVEL (állapot-jelzés, nem cím)
        primaryBadge: 'elsődleges',
        portalBadge: 'portál-hozzáférés',
        // A LÉTREHOZÁS-oldal zárolt szekciói (a fiók megléte a feltétel)
        pendingHint: 'Elérhető lesz, amint létrehoztad a fiókot.',
        pendingServicesDesc: 'A fiókra kötött szolgáltatás-hozzárendelések.',
        pendingContactsDesc: 'A partner oldali kapcsolattartók.',
        pendingAdminsDesc: 'A fiókért felelős belső kollégák.',
        fields: {
          pms: 'PMS',
          bookingEngine: 'Foglalási motor',
          channelManager: 'Channel Manager',
          emailWebHosting: 'E-mail / web hosting',
          asanaName: 'Asana név',
        },
        // A külső rendszerek megjelenített neve (a fiók-lap integráció-listája és a hozzárendelés-kötés közös)
        integrationSystem: { hotelcms: 'HotelCMS', zadircms: 'ZadirCMS', mailwizz: 'MailWizz' },
        // A fiók külső rendszer-azonosítóinak listája (rendszerenként több is, megjegyzéssel)
        integrations: {
          label: 'Rendszer-azonosítók',
          hint: 'Rendszerenként több azonosító is felvehető (a HotelCMS-ben pl. külön loyalty-, booking- és ticket-domain). A megjegyzésbe írd, melyik mire való — a szolgáltatás-hozzárendelésen ez alapján választod ki, melyikből számlázunk. Egy sor törlése a rá kötött hozzárendelés kötését is megszünteti.',
          empty: 'Még nincs azonosító.',
          system: 'Rendszer',
          externalId: 'Azonosító',
          note: 'Megjegyzés',
          notePlaceholder: 'pl. loyalty.hotel.hu',
          add: 'Azonosító hozzáadása',
          remove: 'Azonosító törlése',
        },
        // Fiók „Domainek" szekciója (a régi „Egyéb domainek" szöveg utódja) — a fő domain a Weboldal
        domains: {
          title: 'Domainek',
          hint: 'A fiók további domainjei (a fő domain a Weboldal). Elég a domaint beírni — a https:// és az útvonal magától lemarad.',
          add: 'Domain felvétele',
          empty: 'Nincs felvett domain',
          domain: 'Domain',
          domainPlaceholder: 'pl. book.hotel.hu',
          statusLabel: 'Állapot',
          status: { active: 'aktív', setup: 'setup alatt', inactive: 'inaktív' },
          remove: 'Domain eltávolítása',
        },
        // Fiók-kötés szerkesztő (ügyféladminisztrátor / kapcsolattartó)
        affiliation: {
          add: 'Kapcsolat hozzáadása',
          addTitle: 'Kapcsolat hozzáadása',
          addDesc:
            'Meglévő személy kötése a fiókhoz — ügyféladminisztrátorként vagy kapcsolattartóként.',
          editTitle: 'Kapcsolat szerkesztése',
          edit: 'Kapcsolat szerkesztése',
          // Sor-akciók akadálymentes neve: a puszta „szerkesztés" több sor mellett nem mondja meg, KIÉ.
          editAria: '{{name}} kötésének szerkesztése',
          removeAria: '{{name}} eltávolítása a fiókról',
          remove: 'Eltávolítás',
          removeTitle: 'Kapcsolat eltávolítása',
          removeDesc:
            '„{{name}}" leválasztása erről a fiókról? A személy globális identitása ÉRINTETLEN marad — csak a fiók-kötés szűnik meg.',
          kind: 'Minőség',
          kindAdmin: 'Ügyféladminisztrátor',
          kindContact: 'Kapcsolattartó',
          identity: 'Személy',
          identityPlaceholder: 'Válassz személyt…',
          identityHintAdmin: 'Belső kolléga (ügyféladminisztrátor).',
          identityHintContact: 'Partner (kapcsolattartó).',
          managerType: 'Menedzser-típus',
          managerNone: '— nincs —',
          managerDigitalMarketing: 'Digital marketing',
          managerRevenue: 'Revenue manager',
          // A kötés-sor menedzser-típus felirata (a select-opciókkal azonos szöveg, kód szerint indexelve).
          managerLabel: {
            digital_marketing: 'Digital marketing',
            revenue_management: 'Revenue manager',
          },
          isPrimary: 'Elsődleges',
          isPrimaryHint:
            'Elsődleges a fiókon (minőségenként egy) — ÜZLETI jelölés; a portál-jogkört a szerep adja.',
        },
        // Létrehozó/szerkesztő-űrlap (new / edit route)
        create: 'Új fiók',
        new: 'Új fiók',
        form: {
          sectionParent: 'Szülő-ügyfél',
          servicesEmpty: 'Ehhez a fiókhoz még nincs szolgáltatás rendelve.',
          name: 'Név',
          website: 'Weboldal',
          type: 'Típus',
          types: 'Típus / kategória',
          category: 'Besorolás',
          region: 'Régió',
          roomCount: 'Szobaszám',
          billingProfile: 'Számlázási profil',
          billingProfileNone: '— nincs (ügyfél elsődleges) —',
          billingProfilePrimary: '(elsődleges)',
          billingProfileCreateHint: 'Előbb válassz ügyfelet a profilok betöltéséhez.',
          billingProfileDisabledHint:
            'Saját számlázás nélkül az ügyfél elsődleges profilja számláz.',
          overrideBilling: 'Saját számlázás (a fiók külön számlát kap)',
          overrideBillingHint:
            'Bekapcsolva a fiók a lent választott profillal, ÖNÁLLÓ számlát kap. Kikapcsolva az ügyfél elsődleges profilja számláz, és a fiók szolgáltatásai az ÜGYFÉL számlájába olvadnak be.',
          parentClient: 'Ügyfél',
          parentClientPlaceholder: 'Válassz ügyfelet…',
          parentClientHint: 'A fiók ehhez az ügyfélhez tartozik — létrehozás után nem módosítható.',
          status: 'Státusz',
          zip: 'Irányítószám',
          city: 'Település',
          address: 'Cím',
          countryCode: 'Országkód',
          countryName: 'Ország',
          // ── LEGACY-PARITÁS mezők (v4) ──
          latitude: 'Szélesség',
          longitude: 'Hosszúság',
          coordinateHint:
            'Tizedes fok, max 6 tizedesjegy (pl. 47.497912). A kettő együtt kötelező.',
          cloudflare: 'Cloudflare-en van',
          autoBilling: 'Automatikus (havi) számlázás',
          autoBillingHint:
            'A havi számlázó a fiók-szintű hozzárendeléseket csak akkor számlázza automatikusan, ha ez be van kapcsolva ÉS a fiók státusza aktív.',
          // a ZÁROLT jelölő indoklása (az ügyfél-űrlap mintája) — sosem néma szürkeség
          autoBillingBlockedNoPrimary:
            'Az ügyfélnek nincs elsődleges számlázási profilja — előbb vegyél fel egyet az ügyfél adatlapján, vagy kapcsold be a fiók saját számlázását egy profillal.',
          smtps: 'SMTP-szolgáltatók',
          mailings: 'Levelezés / DNS',
          exportCampaigns: 'Export-kampányok',
          coordinatePairRequired: 'A szélesség és a hosszúság csak együtt adható meg.',
          note: 'Jegyzet',
          none: '— nincs —',
          create: 'Létrehozás',
          save: 'Mentés',
          saving: 'Mentés…',
        },
      },
      accessRights: {
        rolesTab: 'Szerepkörök',
        // FELÜLET-VÁLTÓ (audience): a Felhasználók fül + a felület-választó — belső rendszer vs partner-felület.
        usersTab: 'Felhasználók',
        audienceLabel: 'Felhasználó-típus',
        audienceInternal: 'Munkatársak',
        audienceExternal: 'Külsősök',
        countExternal: '{{shown}} / {{total}} külsős',
        externalEmpty: 'Még senkinek nincs portál-hozzáférése',
        externalEmptyDesc:
          'Ez a lista NEM a kapcsolattartók névsora — itt azok a külsősök jelennek meg, akik MÁR kaptak partner-portál hozzáférést. A hozzáférést a FIÓK oldalán add meg (a „Kapcsolattartók" szekcióban): ott a fiók kapcsolattartói közül választasz, és ott dől el a szerepkörük is (fiókonként külön).',
        portalNoAccess: 'nincs portál-hozzáférése',
        portalGrantAria: '{{account}} — {{role}} (ugrás a fiókra)',
        partnerRoleNote:
          'A partner-felület szerepkörei és jogaik — itt azt állítod be, MIT JELENT egy szerep. Hogy KI melyik fiókon viseli, az a fiók-oldalon dől el (a „Kapcsolattartók" szekcióban), mert a partner jogköre fiókonként eltérhet.',
        partnerMembersNote:
          'A külsős felhasználók listája — a szerep FIÓKONKÉNTI, ezért itt csak látszik. A beállítás helye a fiók-oldali „Kapcsolattartók" szekció (kattints a szerep-jelvényre).',
        tier: 'Szerepkör',
        tierGrants: 'Jogosultságok',
        tierNote:
          'A szerepkör a hozzárendelt munkatársak alap-jogait adja (kumulatív: Tag ⊂ Adminisztrátor ⊂ Szuperadmin). A Tag és az Adminisztrátor szerepkör alap-jogait szerkesztheted; egyéni kivételt (többlet/tiltás) a Munkatársak fülön adhatsz. A Szuperadmin szerepkör rögzített (mindig teljes jog).',
        roleLocked: 'Rögzített',
        deleteRole: 'Szerepkör törlése',
        deleteRoleTitle: 'Szerepkör törlése',
        deleteRoleBody:
          'A(z) „{{role}}" szerepkör törlődik a katalógusból. Csak akkor lehetséges, ha SENKI nem viseli (sem közvetlenül, sem fiók-hozzáférésen keresztül). A kódból kezelt szerepköröket nem lehet innen törölni.',
        deleteRoleError: 'A szerepkör törlése nem sikerült',
        roleDeleted: 'Szerepkör törölve',
        roleSourceManifest: 'kódból kezelt',
        roleSourceSeed: 'alap, szerkeszthető',
        roleSourceConsole: 'egyedileg rögzített',
        rolePermsSuffix: 'szerepkör jogai',
        roleSaved: 'Szerepkör jogai mentve',
        name: 'Név',
        overrides: 'Egyéni felülírások',
        tierDefaultOnly: 'A szerepkör alapértékei',
        grantShort: 'többlet',
        denyShort: 'tiltva',
        noRole: '— nincs szerepkör',
        countMembers: '{{shown}} / {{total}} munkatárs',
        filterRole: 'Szerepkör',
        filterPermission: 'Jogosultság',
        countRoles: '{{shown}} / {{total}} szerepkör',
        more: '+{{count}}',
        moreItems: 'További jogok',
        rolesEmpty: 'Nincs szerepkör',
        rolesEmptyDesc: 'Ehhez az alkalmazáshoz még nincs szerepkör definiálva.',
        membersEmpty: 'Nincs munkatárs',
        membersEmptyDesc: 'Ehhez az alkalmazáshoz még nincs munkatárs rendelve.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a keresésnek megfelelő felhasználó.',
        roleUpdated: 'Szerepkör módosítva',
        overridesSaved: 'Felülírások mentve',
        overridesSuffix: 'egyéni jog-felülírások',
        overrideHint:
          'Jelöld be, amit a személy kapjon. A(z) {{role}} szerepkör alapból adott jogai öröklöttek; a pipa bevétele/kivétele ehhez képest ad többletet vagy von meg jogot.',
        grantTag: 'többlet',
        denyTag: 'tiltva',
        inheritedTag: 'öröklött',
        prereqAdded: 'A megtekintés is bekerült: {{list}}',
        prereqRemoved: 'Vele együtt kikerült: {{list}}',
        prereqMissing: 'Megtekintés nélkül nem használható: {{list}}',
        overrideSummary: '{{grants}} többlet · {{denies}} tiltva',
        // VÉDETT jogok: a szerver a fal (Odin), ez a szöveg csak megmondja, MIÉRT nem kattintható.
        protectedSelfLock: 'Védett jogot magadnak nem állíthatsz — kérj meg egy másik birtokost',
        protectedNotHeldLock: 'Védett jog: csak az állíthatja, aki maga is birtokolja',
        // A SZEREP-szerkesztő zárolása: a védett jog szerephez SOHA nem köthető (az Odin 422-vel zárja),
        // ezért ott a break-glass platform-admin sem tudja bepipálni.
        protectedRoleLock: 'Védett jog: szerephez nem köthető — csak személyenként adható',
        // Tag hozzáadása apphoz — KÉT FELÜLET (munkatárs / külsős), CSAK MEGLÉVŐ személy + kezdő szerep.
        // Az „új személy" mód kivezetve: a felvétel a Munkatársak / Kapcsolattartók oldal dolga (más adat
        // tartozik hozzá), itt csak HOZZÁFÉRÉST adunk.
        addMember: 'Tag hozzáadása',
        addMemberTitle: 'Tag hozzáadása az apphoz',
        addMemberDesc:
          'Válaszd ki, kinek adsz hozzáférést: munkatárs vagy külsős felhasználó. Új személyt a Munkatársak, illetve a Kapcsolattartók oldalon lehet felvenni.',
        addAudienceInternal: 'Munkatárs',
        addAudienceExternal: 'Külsős',
        personLabel: 'Személy',
        selectPerson: 'Válassz személyt…',
        noInternalCandidates:
          'Nincs hozzáadható munkatárs — vagy már mindenki tag, vagy előbb fel kell venni a Munkatársak oldalon.',
        noPartnerCandidates:
          'Nincs hozzáadható külsős felhasználó — vagy már mindenki tag, vagy előbb fel kell venni a Kapcsolattartók oldalon.',
        initialRole: 'Kezdő szerepkör',
        noAppRoles: 'Ehhez az apphoz még nincs szerepkör — előbb hozz létre egyet.',
        addMemberSubmit: 'Hozzáadás',
        memberAdded: 'Tag hozzáadva az apphoz',
        addMemberError: 'A tag hozzáadása nem sikerült',
        // Tag eltávolítása az appból (app-offboarding)
        removeMember: 'Eltávolítás az appból',
        removeMemberTitle: 'Tag eltávolítása az appból',
        removeMemberDesc:
          '„{{name}}" eltávolítása ebből az appból: elveszíti az app-hozzáférését és -szerepét. A személy globális identitása és más appok hozzáférése ÉRINTETLEN marad.',
        removeMemberPending: 'Eltávolítás…',
        memberRemoved: 'Tag eltávolítva az appból',
        removeMemberError: 'Az eltávolítás nem sikerült',
        selfLockout: 'A saját hozzáférésedet nem vonhatod meg — kérj meg egy másik jogkezelőt',
        // Új szerep létrehozása
        newRole: 'Új szerepkör',
        newRoleTitle: 'Új szerepkör létrehozása',
        newRoleDesc:
          'Adj meg egy kulcsot (azonosító) és egy megjelenített nevet. A jogokat a létrehozás után állíthatod be.',
        roleNameLabel: 'Megjelenített név',
        roleNamePlaceholder: 'pl. Support-ügynök',
        roleKeyLabel: 'Kulcs (azonosító)',
        roleKeyPlaceholder: 'pl. support-agent',
        roleKeyHint: 'Csak kisbetű, szám, kötőjel és aláhúzás.',
        roleKeyDup: 'Ez a kulcs már foglalt ebben az appban.',
        createRoleSubmit: 'Létrehozás',
        roleCreated: 'Szerepkör létrehozva',
        createRoleError: 'A szerepkör létrehozása nem sikerült',
      },
      activityLog: {
        actorPlaceholder: 'Szereplő (név / e-mail)',
        from: 'Ettől',
        to: 'Eddig',
        loading: 'betöltés…',
        count: '{{count}} esemény',
        system: 'Rendszer',
        // Megszemélyesítés-jelzés a szereplő mellett (a szereplő MINDIG a valódi aktor).
        impersonatedBadge: '{{name}} nevében',
        impersonatedUnknown: 'más felhasználó',
        // KÖZPONTI konzol app-szűrője (több appnál): default Atlas, „Minden app" a kereszt-app feedhez.
        appLabel: 'Alkalmazás',
        // NÉZET-preset (kategória-szűrő): a feed egy szűrt nézet a teljes audit-trailre.
        viewLabel: 'Nézet',
        view: {
          default: 'Alapértelmezett',
          data: 'Adatváltozások',
          admin: 'Jogosultságok',
          auth: 'Bejelentkezések',
          security: 'Biztonsági olvasás',
          system: 'Rendszer',
        },
        columns: {
          actor: 'Végrehajtó',
          action: 'Művelet',
          target: 'Érintett entitás',
          time: 'Időpont',
        },
        // Detail-panel (kinyíló): mezőszintű változás + forenzikus request-metaadat.
        ip: 'IP-cím',
        device: 'Eszköz',
        trace: 'Trace-azonosító',
        // Az „Érintett entitás" oszlop szerepkör-címkéje (admin-esemény): pl. „Tag szerepkör".
        roleTarget: '{{role}} szerepkör',
        statusLabel: { success: 'Sikeres', failed: 'Sikertelen' },
        empty: 'Nincs esemény',
        emptyDesc: 'A napló még üres.',
        noMatches: 'Nincs találat',
        noMatchesDesc: 'Nincs a szűrőknek megfelelő esemény.',
        // Mezőszintű változás-panel (a magyar szám után egyes szám: „3 változás").
        changesCount_one: '{{count}} változás',
        changesCount_other: '{{count}} változás',
        emptyValue: '∅',
        // Natív {type}.{verb} taxonómia → kompozíciós címke: entitás-típus + ige.
        actionPattern: '{{entity}} {{verb}}',
        verb: {
          create: 'létrehozása',
          update: 'módosítása',
          delete: 'törlése',
          archive: 'archiválása',
          recalculate: 'újraszámítása',
        },
        // Entitás-típusok (a resource-ref 2. szegmenséből + a natív akció-típusból). Ismeretlen → humanizált.
        entity: {
          account: 'Fiók',
          accounts: 'Fiók',
          client: 'Ügyfél',
          clients: 'Ügyfél',
          contact: 'Kapcsolattartó',
          contacts: 'Kapcsolattartó',
          ticket: 'Jegy',
          tickets: 'Jegy',
          invoice: 'Számla',
          invoices: 'Számla',
          expense: 'Kiadás',
          expenses: 'Kiadás',
          'expense-category': 'Kiadás-kategória',
          'expense-categories': 'Kiadás-kategória',
          message: 'Üzenet',
          messages: 'Üzenet',
          meeting: 'Meeting',
          meetings: 'Meeting',
          asset: 'Eszköz',
          assets: 'Eszköz',
          department: 'Részleg',
          departments: 'Részleg',
          team: 'Csapat',
          teams: 'Csapat',
          employee: 'Munkatárs',
          employees: 'Munkatárs',
          'account-domains': 'Fiók-domain',
          'support-teams': 'Ügyfélszolgálati csapat',
          'service-plans': 'Csomag',
          'service-plan-prices': 'Csomagár',
          services: 'Szolgáltatás',
          'billing-profiles': 'Számlázási profil',
          'ticket-comments': 'Jegy-hozzászólás',
          'ticket-contact-access': 'Jegy-hozzáférés (kapcsolattartó)',
          'ticket-attachments': 'Jegy-csatolmány',
          'ticket-comment-templates': 'Válasz-sablon',
          'account-affiliations': 'Fiók-kapcsolat',
          'meeting-participants': 'Meeting-résztvevő',
          'meeting-series': 'Ismétlődő megbeszélés-sorozat',
          widgets: 'Widget',
          // A BFF által írt, de korábban HIÁNYZÓ típusok — nélkülük az eseménynapló a NYERS SLUG-ot írta ki
          // (a user által jelentett „message-reads létrehozása"). A fal: `activity-log-entity.wall.spec.ts`.
          'payment-cards': 'Bankkártya',
          positions: 'Beosztás',
          'service-plan-assignments': 'Szolgáltatás-hozzárendelés',
          'asset-categories': 'Eszköz-kategória',
          'asset-types': 'Eszköz-típus',
          'service-plan-assignment-prices': 'Hozzárendelés-ár',
          'service-plan-assignment-users': 'Hozzárendelés-résztvevő',
          'assignment-accounts': 'Hozzárendelés-fiók',
          'support-team-members': 'Csapat-tag',
          'employee-details': 'Munkatárs-adatlap',
          'invoice-items': 'Számlatétel',
          // A havi usage-lekérés (cron + kézi indítás) ÍRJA ezt a típust — enélkül az eseménynapló nyers
          // slugot mutatna („service-usage").
          'service-usage': 'Használati adat',
          'invoice-accounts': 'Számla-fiók',
          // A számla-generátor kihagyás-naplója (`billing/invoice-generation-skips`) — enélkül az
          // eseménynapló nyers slugot mutatna, ha valaha INTERNAL Odin-erőforrásként megjelenne itt is.
          'invoice-generation-skips': 'Generálási kihagyás',
          'message-reads': 'Olvasási visszajelzés',
          // A partner-portál üzenet-lapja OLVASSA a címzett-pivotokat (a jogosultság-döntés a BFF-en van) —
          // írni nem írja őket, de a fal minden BFF-ben szereplő resource-típusra kér címkét.
          'message-recipient-contacts': 'Üzenet címzettje (kapcsolattartó)',
          'message-recipient-accounts': 'Üzenet címzettje (fiók)',
          'message-attachments': 'Üzenet-melléklet',
          // ÚJABB HIÁNYZÓ KÖR (user-lelet: „notification létrehozása", „product-updates
          // módosítása" — nyers slug a naplóban). A fal SZŰKEN mérte a BFF-et: csak a `/v1/resources/ns/type`
          // URL-mintát kereste, a modulok viszont KONSTANSBÓL (`TYPE = 'product-updates'`) és
          // `'ns/type'` alakú listákból is hivatkoznak típusra — a fal ezért most mindhárom alakot nézi.
          'ticket-comment-reactions': 'Komment-reakció',
          'product-updates': 'Fejlesztés',
          campaigns: 'Kampány',
          // A típus KIVEZETVE (Odin `20260907160000_product_updates_editor_images_only`), a címke mégis
          // marad: a RÉGI napló-sorok hivatkozzák, és enélkül nyers slug látszana a visszamenőleges nézetben.
          'product-update-attachments': 'Fejlesztés-melléklet',
          'message-revisions': 'Üzenet-változat',
          'message-recipient-employees': 'Üzenet címzettje (munkatárs)',
          'message-recipient-departments': 'Üzenet címzettje (részleg)',
          'message-recipient-teams': 'Üzenet címzettje (csapat)',
          'message-recipient-services': 'Üzenet címzettje (szolgáltatás)',
          'asset-events': 'Eszköz-esemény',
          'identity-profiles': 'Fiók-beállítás',
          issuers: 'Számlakibocsátó',
          comments: 'Hozzászólás',
          // Tudásbázis (Mo) — az Atlas naplója kereszt-app feedet is mutat, tehát ezek a típusok is
          // megjelenhetnek a listán.
          spaces: 'Tudástér',
          categories: 'Kategória',
          articles: 'Szócikk',
          'article-attachments': 'Szócikk-melléklet',
          'article-revisions': 'Szócikk-változat',
          drafts: 'Piszkozat',
          'knowledge-gaps': 'Tudás-hiány',
          // Platform-entitások (nem Atlas-resource-ok, de a napló kereszt-app feedjében megjelennek):
          // a `{type}.{verb}` kompozíciós ág ezeket a kulcsokat kéri (`external_app.create`, `file.delete`).
          external_app: 'Alkalmazás',
          file: 'Fájl',
          // Az ÉRTESÍTÉS-sorokat az Odin írja (a BFF a `notify` végpontot hívja, nem resource-URL-t), ezért
          // a fal resource-listája nem tartalmazza — a naplóban viszont `notification.create`-ként jelenik meg.
          notification: 'Értesítés',
          notifications: 'Értesítés',
        },
        // Kurált címkék a NEM-kompozíciós (legacy / auth / admin) kulcsokhoz (pont → aláhúzás a kulcsban).
        // Kurált címkék a NEM-kompozíciós (legacy / auth / admin) action-kulcsokhoz. A kulcs = a tárolt action
        // (pont → aláhúzás) — a tárolt adat VÁLTOZATLAN; ez csak megjelenítés. Ismeretlen → humanizált fallback.
        actionLabel: {
          // — Auth / munkamenet / megszemélyesítés —
          login: 'Bejelentkezés',
          logout: 'Kijelentkezés',
          login_failed: 'Sikertelen bejelentkezés',
          auth_token_reuse: 'Token-újrafelhasználás (visszavonva)',
          switch_lang: 'Nyelvváltás',
          impersonate_start: 'Megszemélyesítés indítása',
          impersonate_leave: 'Megszemélyesítés befejezése',
          iam_impersonate_start: 'Megszemélyesítés indítása',
          iam_impersonate_stop: 'Megszemélyesítés befejezése',
          login_blocked: 'Blokkolt bejelentkezés',
          login_mfa_failed: 'Sikertelen kétfaktoros ellenőrzés',
          // — Saját identitás (self-service) + 2FA + jelszó-beállítás —
          identity_2fa_setup: 'Kétfaktoros hitelesítés beállítása',
          identity_2fa_enabled: 'Kétfaktoros hitelesítés bekapcsolása',
          identity_2fa_disabled: 'Kétfaktoros hitelesítés kikapcsolása',
          identity_2fa_recovery_regenerated: 'Helyreállító kódok újragenerálása',
          identity_2fa_recovery_used: 'Helyreállító kód felhasználása',
          identity_self_update_profile: 'Saját profil módosítása',
          identity_self_change_password: 'Saját jelszó módosítása',
          identity_self_change_password_failed: 'Sikertelen jelszó-módosítás',
          identity_self_asana_pat_set: 'Saját Asana-token beállítása',
          identity_self_asana_pat_clear: 'Saját Asana-token törlése',
          identity_self_avatar_set: 'Profilkép beállítása',
          identity_self_avatar_clear: 'Profilkép törlése',
          identity_service_elevated_scopes_set: 'Szolgáltatás-identitás emelt scope-jai',
          workforce_employee_details_self_update: 'Saját személyes adatok módosítása',
          workforce_employee_details_personal_purge:
            'Személyes adatok törlése (adatvédelmi határidő)',
          platform_audit_log_gdpr_redaction: 'Napló kitakarása (adatvédelem)',
          identity_password_reset_issued: 'Jelszó-beállító link kiküldése',
          identity_password_reset_issue_skipped: 'Jelszó-beállító link kihagyva',
          identity_password_reset_redeemed: 'Jelszó-beállító link felhasználása',
          identity_password_reset_rejected: 'Jelszó-beállító link elutasítva',
          // — Admin / jogosultság —
          admin_role_assign: 'Szerepkör hozzárendelése',
          admin_role_revoke: 'Szerepkör visszavonása',
          admin_role_upsert: 'Szerepkör módosítása',
          admin_permission_grant: 'Jogosultság megadása',
          admin_permission_sync: 'Jogosultságok szinkronizálása',
          admin_member_add: 'Munkatárs hozzáadása',
          admin_member_remove: 'Munkatárs eltávolítása',
          admin_permission_revoke: 'Jogosultság visszavonása',
          admin_rbac_sync: 'RBAC-manifest szinkronizálása',
          admin_grant_create: 'Hozzáférés megadása',
          admin_portal_access_grant: 'Partner-portál hozzáférés megadása',
          admin_portal_access_revoke: 'Partner-portál hozzáférés visszavonása',
          admin_grant_revoke: 'Hozzáférés visszavonása',
          admin_identity_provision: 'Identitás létrehozása',
          admin_identity_core_update: 'Identitás alapadatainak módosítása',
          admin_identity_offboard: 'Identitás kiléptetése',
          admin_identity_asana_pat_set: 'Asana-token beállítása (adminként)',
          admin_identity_asana_pat_clear: 'Asana-token törlése (adminként)',
          admin_app_allowed_senders_set: 'Engedélyezett feladók beállítása',
          admin_email_suppression_create: 'E-mail-tiltólista: felvétel',
          admin_email_suppression_delete: 'E-mail-tiltólista: törlés',
          admin_app_upsert: 'Alkalmazás módosítása',
          access_rights_update_action_permissions: 'Műveleti jogosultságok módosítása',
          access_rights_update_view_permissions: 'Láthatósági jogosultságok módosítása',
          // — Rendszer / migráció / séma —
          schema_register: 'Séma regisztrálása',
          reference_upsert: 'Referencia-adat frissítése',
          read_see_all: 'Kereszt-tenant olvasás',
          admin_import_grant_create: 'Migráció: hozzáférés-grant',
          admin_import_grant_update: 'Migráció: hozzáférés-grant frissítés',
          admin_import_typed: 'Migráció: tipizált adat',
          admin_import_audit_log: 'Migráció: eseménynapló',
          admin_import_notifications: 'Migráció: értesítések',
          admin_import_access_grants: 'Migráció: hozzáférések',
          admin_import_resource: 'Migráció: erőforrás',
          admin_import_identities: 'Migráció: identitások',
          admin_import_clients: 'Migráció: ügyfelek',
          admin_import_accounts: 'Migráció: fiókok',
          // — Ügyfél —
          clients_create_client: 'Ügyfél létrehozása',
          clients_update_client: 'Ügyfél módosítása',
          clients_delete_client: 'Ügyfél törlése',
          clients_export_clients: 'Ügyfelek exportálása',
          // — Fiók —
          accounts_create_account: 'Fiók létrehozása',
          accounts_update_account: 'Fiók módosítása',
          accounts_delete_account: 'Fiók törlése',
          accounts_assign_contact: 'Kapcsolattartó hozzárendelése',
          accounts_remove_contact: 'Kapcsolattartó eltávolítása',
          accounts_set_as_primary_contact: 'Elsődleges kapcsolattartó beállítása',
          accounts_assign_client_admin: 'Ügyféladminisztrátor hozzárendelése',
          accounts_remove_client_admin: 'Ügyféladminisztrátor eltávolítása',
          accounts_export_accounts: 'Fiókok exportálása',
          // — Kapcsolattartó —
          contacts_create_contact: 'Kapcsolattartó létrehozása',
          contacts_update_contact: 'Kapcsolattartó módosítása',
          contacts_delete_contact: 'Kapcsolattartó törlése',
          contacts_welcome_contact: 'Kapcsolattartó üdvözlése',
          contacts_export_contacts: 'Kapcsolattartók exportálása',
          // — Jegy —
          // Lista-export (a felület bejelentése) — `security` kategória: adat-KIVITEL.
          export_list: 'Lista exportálása',
          tickets_open_ticket: 'Jegy megnyitása',
          tickets_update_ticket: 'Jegy módosítása',
          // Domén-művelet: az Odin audit-sora `tickets.status_changed` action-t kap, ha a mentés
          // státuszt VÁLTOZTAT (a generikus „Jegy frissítése + status-diff" helyett).
          tickets_status_changed: 'Jegy státuszának módosítása',
          tickets_send_comment: 'Hozzászólás a jegyhez',
          tickets_create_asana_task: 'Asana-feladat létrehozása',
          tickets_unlink_asana_task: 'Asana-hivatkozás leválasztása',
          tickets_contact_access: 'Kapcsolattartói hozzáférés a jegyhez',
          tickets_save_comment_as_template: 'Hozzászólás mentése sablonként',
          // — Meeting —
          meetings_create_meeting: 'Meeting létrehozása',
          meetings_update_meeting: 'Meeting módosítása',
          meetings_delete_meeting: 'Meeting törlése',
          meetings_change_participation: 'Részvétel módosítása',
          meetings_reschedule_meeting: 'Meeting átütemezése',
          // — Üzenet —
          messages_view_message: 'Üzenet megtekintése',
          messages_send_message: 'Üzenet küldése',
          // — Számlázás —
          invoices_create_invoice: 'Számla létrehozása',
          invoices_create_proforma: 'Díjbekérő létrehozása',
          billing_profiles_create_billing_profile: 'Számlázási profil létrehozása',
          billing_profiles_update_billing_profile: 'Számlázási profil módosítása',
          billing_profiles_delete_billing_profile: 'Számlázási profil törlése',
          billing_profiles_set_default_profile: 'Alapértelmezett számlázási profil beállítása',
          // — Szolgáltatás / csomag / díjszabás —
          services_create_service: 'Szolgáltatás létrehozása',
          services_update_service: 'Szolgáltatás módosítása',
          services_delete_service: 'Szolgáltatás törlése',
          services_create_plan: 'Csomag létrehozása',
          services_update_plan: 'Csomag módosítása',
          services_delete_plan: 'Csomag törlése',
          services_create_price: 'Díjszabás létrehozása',
          services_create_assignment: 'Szolgáltatás-hozzárendelés létrehozása',
          services_update_assignment: 'Szolgáltatás-hozzárendelés módosítása',
          services_delete_assignment: 'Szolgáltatás-hozzárendelés törlése',
          // — Munkatárs / pozíció / részleg —
          employees_create_employee: 'Munkatárs létrehozása',
          employees_update_employee: 'Munkatárs módosítása',
          employees_delete_employee: 'Munkatárs törlése',
          employees_welcome_employee: 'Munkatárs üdvözlése',
          positions_create_position: 'Pozíció létrehozása',
          positions_update_position: 'Pozíció módosítása',
          positions_delete_position: 'Pozíció törlése',
          departments_create_department: 'Részleg létrehozása',
          departments_update_department: 'Részleg módosítása',
          departments_delete_department: 'Részleg törlése',
          teams_create_team: 'Csapat létrehozása',
          teams_update_team: 'Csapat módosítása',
          teams_delete_team: 'Csapat törlése',
          // — Eszköz —
          assets_create_asset: 'Eszköz létrehozása',
          assets_update_asset: 'Eszköz módosítása',
          assets_submit_asset_request: 'Eszköz-igény beküldése',
          assets_approve_asset_request: 'Eszköz-igény jóváhagyása',
          assets_reject_asset_request: 'Eszköz-igény elutasítása',
          assets_purchase_asset: 'Eszköz beszerzése',
          assets_being_procured_asset: 'Eszköz beszerzés alatt',
          assets_received_asset: 'Eszköz beérkezett',
          assets_export_assets: 'Eszközök exportálása',
          // — Ügyfélszolgálat / kvóta / szabadság / egyéb —
          client_supports_create_client_support: 'Ügyfélszolgálat létrehozása',
          client_supports_update_client_support: 'Ügyfélszolgálat módosítása',
          account_quotas_create_quota_data: 'Kvóta-adat létrehozása',
          account_quotas_export_accounts: 'Fiók-kvóták exportálása',
          account_quotas_create_currency_exchange_rate: 'Árfolyam létrehozása',
          account_quotas_update_currency_exchange_rate: 'Árfolyam módosítása',
          vacations_create_vacation: 'Szabadság létrehozása',
          external_app_create: 'Alkalmazás létrehozása',
          external_app_update: 'Alkalmazás módosítása',
          external_app_delete: 'Alkalmazás törlése',
          feedprocessor_feed_update_field: 'Feed-mező módosítása',
          // — Dokumentumtár (⛔ ELVETETT Atlas-modul, de a LEGACY napló-sorok olvashatók maradnak) —
          documents_upload_document: 'Dokumentum feltöltése',
          documents_upload_existing_document: 'Meglévő dokumentum feltöltése',
          documents_pre_register_document: 'Dokumentum előregisztrálása',
          documents_update_document: 'Dokumentum módosítása',
          documents_archive_document: 'Dokumentum archiválása',
          documents_delete_document: 'Dokumentum törlése',
          document_alerts_create_alert: 'Dokumentum-emlékeztető létrehozása',
          // — Foundation (Odin ügyfél/fiók életciklus — a natív `{type}.{verb}`-től eltérő, PAST-TENSE alak) —
          client_created: 'Ügyfél létrehozva',
          client_updated: 'Ügyfél módosítva',
          client_deleted: 'Ügyfél törölve',
          account_created: 'Fiók létrehozva',
          account_updated: 'Fiók módosítva',
          account_deleted: 'Fiók törölve',
          employees_export_employees: 'Munkatársak exportálása',
          profile_update_profile_info: 'Profil módosítása',
          profile_update_password: 'Jelszó módosítása',
          // — Fájl (natív file-flow) —
          file_upload: 'Fájl feltöltése',
          file_download: 'Fájl letöltése',
          file_delete: 'Fájl törlése',
          file_commit: 'Fájl véglegesítése',
        },
        // Mezőnevek a diff-panelhez (natív camelCase ÉS legacy snake_case gyakori mezők). Ismeretlen → humanizált.
        field: {
          // Lista-export audit-nyom (`export.list`) detail-mezői — a diff-panel ezeket mutatja.
          module: 'Lista',
          format: 'Formátum',
          rowCount: 'Sorok száma',
          truncated: 'Csonkolva',
          filters: 'Szűrők',
          // A talalkozo-idopontok a diffben ISO-instantkent allnak; a mezoNEV eddig humanizalt angol
          // fallbackre esett („Starts at” / „Ends at”) - merve ez volt a leggyakoribb angol mezonev.
          startsAt: 'Kezdés',
          endsAt: 'Befejezés',
          notifyEmail: 'E-mail értesítés',
          locationKey: 'Helyszín',
          zoomAccountKey: 'Zoom-fiók',
          filename: 'Fájlnév',
          contentType: 'Fájltípus',
          sizeBytes: 'Fájlméret',
          fileId: 'Fájl',
          messageId: 'Üzenet',
          euTaxNumber: 'EU-adószám',
          // KIADÁSOK — a NAV-import és a kézi rögzítés diff-mezői. A `invoiceNumber` és a `currency` NEM
          // ismétlődik: azok már a számlázás oldaláról ott állnak a szótárban, ugyanazzal a jelentéssel. A `humanizeField()` fallback ezekre
          // angolos szöveget adott magyar felületen („Supplier name", „Dismissed at"), ami pont a
          // pénzügyi naplóban a legrosszabb: ott a pontos szó számít.
          supplierName: 'Szállító',
          supplierTaxNumber: 'Szállító adószáma',
          issueDate: 'Kiállítás dátuma',
          fulfillmentDate: 'Teljesítés dátuma',
          dueDate: 'Fizetési határidő',
          netAmount: 'Nettó',
          vatAmount: 'Áfa',
          grossAmount: 'Bruttó',
          categoryId: 'Kategória',
          // A `dismissedAt` NEM könyvelési időbélyeg (mint a `created_at`), hanem üzleti DÖNTÉS nyoma —
          // ezért marad benne a diffben, és ezért kap saját, múlt idejű címkét (a `readAt` mintájára).
          dismissedAt: 'Elvetve',
          navOperation: 'NAV-művelet',
          originalInvoiceNumber: 'Eredeti számlaszám',
          navInsDate: 'NAV-beérkezés',
          readAt: 'Olvasva',

          name: 'Név',
          email: 'E-mail',
          phone: 'Telefon',
          status: 'Státusz',
          website: 'Weboldal',
          note: 'Megjegyzés',
          notes: 'Megjegyzés',
          title: 'Cím',
          subject: 'Tárgy',
          description: 'Leírás',
          autoBilling: 'Automatikus számlázás',
          auto_billing: 'Automatikus számlázás',
          channelManager: 'Channel Manager',
          channel_manager: 'Channel Manager',
          bookingEngine: 'Foglalómotor',
          booking_engine: 'Foglalómotor',
          countryCode: 'Országkód',
          country_code: 'Országkód',
          jobTitle: 'Beosztás',
          job_title: 'Beosztás',
          // A LEGACY napló diff-mezői (mérve: 114 distinct mező, ebből 102 címke nélkül futott, tehát a
          // panel angolos nyers slugot írt ki — „Close reason", „Assigned to"). Minden mező a snake_case
          // (legacy MySQL) ÉS a camelCase (natív Odin-payload) alakjában is szerepel: ugyanaz a változás
          // a régi és az új íráson át MÁS kulcs-alakkal érkezik. A fal: `activity-log-action.wall.spec.ts`.
          slug: 'Azonosító (slug)',
          first_name: 'Utónév',
          firstName: 'Utónév',
          last_name: 'Családi név',
          lastName: 'Családi név',
          nickname: 'Becenév',
          profile_picture: 'Profilkép',
          profilePicture: 'Profilkép',
          name_hu: 'Név (magyar)',
          nameHu: 'Név (magyar)',
          name_en: 'Név (angol)',
          nameEn: 'Név (angol)',
          description_hu: 'Leírás (magyar)',
          descriptionHu: 'Leírás (magyar)',
          description_en: 'Leírás (angol)',
          descriptionEn: 'Leírás (angol)',
          type: 'Típus',
          category: 'Kategória',
          main_category: 'Fő kategória',
          mainCategory: 'Fő kategória',
          sub_category: 'Alkategória',
          subCategory: 'Alkategória',
          client_category: 'Ügyfél-kategória',
          clientCategory: 'Ügyfél-kategória',
          structure_type: 'Szervezeti forma',
          structureType: 'Szervezeti forma',
          is_primary: 'Elsődleges',
          isPrimary: 'Elsődleges',
          checked: 'Ellenőrizve',
          roles: 'Szerepkörök',
          position: 'Beosztás',
          department: 'Részleg',
          department_id: 'Részleg',
          departmentId: 'Részleg',
          managing_director: 'Ügyvezető',
          managingDirector: 'Ügyvezető',
          managing_directors: 'Ügyvezetők',
          managingDirectors: 'Ügyvezetők',
          address: 'Cím',
          city: 'Város',
          zip: 'Postai irányítószám',
          zip_code: 'Postai irányítószám',
          zipCode: 'Postai irányítószám',
          latitude: 'Szélesség',
          longitude: 'Hosszúság',
          other_domains: 'További domainek',
          otherDomains: 'További domainek',
          domain: 'Domain',
          email_web_hosting: 'E-mail / web-hosting',
          emailWebHosting: 'E-mail / web-hosting',
          cloudflare: 'Cloudflare',
          pms: 'PMS',
          smtps: 'SMTP-beállítások',
          mailings: 'Levelezőlisták',
          notifications: 'Értesítések',
          email_notifications: 'E-mail-értesítések',
          emailNotifications: 'E-mail-értesítések',
          export_campaigns: 'Kampány-exportok',
          exportCampaigns: 'Kampány-exportok',
          mailwizz_id: 'MailWizz-azonosító',
          mailwizzId: 'MailWizz-azonosító',
          hotelcms_id: 'HotelCMS-azonosító',
          hotelcmsId: 'HotelCMS-azonosító',
          zadircmsId: 'ZadirCMS-azonosító',
          integrations: 'Rendszer-azonosítók',
          usageRefs: 'Forrás azonosító',
          asana_client_name: 'Asana-ügyfélnév',
          asanaClientName: 'Asana-ügyfélnév',
          start: 'Kezdés',
          end: 'Vége',
          purchase_date: 'Beszerzés dátuma',
          purchaseDate: 'Beszerzés dátuma',
          retirement_date: 'Selejtezés dátuma',
          retirementDate: 'Selejtezés dátuma',
          billing_start_date: 'Számlázás kezdete',
          billingStartDate: 'Számlázás kezdete',
          assigned_to: 'Felelős',
          assignedTo: 'Felelős',
          close_reason: 'Zárás indoka',
          closeReason: 'Zárás indoka',
          reject_reason: 'Elutasítás indoka',
          rejectReason: 'Elutasítás indoka',
          client_support_id: 'Ügyfélszolgálati csapat',
          clientSupportId: 'Ügyfélszolgálati csapat',
          show_in_ticket_dropdown: 'Megjelenik a jegy-választóban',
          showInTicketDropdown: 'Megjelenik a jegy-választóban',
          non_primary_contact_accesses: 'Nem-elsődleges kapcsolattartói hozzáférés',
          nonPrimaryContactAccesses: 'Nem-elsődleges kapcsolattartói hozzáférés',
          primary_contact: 'Elsődleges kapcsolattartó',
          primaryContact: 'Elsődleges kapcsolattartó',
          location: 'Helyszín',
          zoom_account: 'Zoom-fiók',
          zoomAccount: 'Zoom-fiók',
          zoom_link: 'Zoom-link',
          zoomLink: 'Zoom-link',
          google_meet_link: 'Google Meet-link',
          googleMeetLink: 'Google Meet-link',
          microsoft_teams_link: 'Microsoft Teams-link',
          microsoftTeamsLink: 'Microsoft Teams-link',
          employee_participants: 'Munkatárs-résztvevők',
          employeeParticipants: 'Munkatárs-résztvevők',
          contact_participants: 'Kapcsolattartó-résztvevők',
          contactParticipants: 'Kapcsolattartó-résztvevők',
          serial_number: 'Sorozatszám',
          serialNumber: 'Sorozatszám',
          license_key: 'Licenckulcs',
          licenseKey: 'Licenckulcs',
          usage_location: 'Felhasználás helye',
          usageLocation: 'Felhasználás helye',
          purchase_location: 'Beszerzés helye',
          purchaseLocation: 'Beszerzés helye',
          employee_id: 'Munkatárs',
          employeeId: 'Munkatárs',
          approver_id: 'Jóváhagyó',
          approverId: 'Jóváhagyó',
          rejecter_id: 'Elutasító',
          rejecterId: 'Elutasító',
          purchaser_id: 'Beszerző',
          purchaserId: 'Beszerző',
          receiver_id: 'Átvevő',
          receiverId: 'Átvevő',
          procurement_initiator_id: 'Beszerzést indító',
          procurementInitiatorId: 'Beszerzést indító',
          services: 'Szolgáltatások',
          clientSupports: 'Ügyfélszolgálati csapatok',
          net_price: 'Nettó ár',
          netPrice: 'Nettó ár',
          vat_rate: 'Áfa-kulcs',
          vatRate: 'Áfa-kulcs',
          currency: 'Pénznem',
          pricing_type: 'Árazás típusa',
          pricingType: 'Árazás típusa',
          commission_type: 'Jutalék típusa',
          commissionType: 'Jutalék típusa',
          payment_methods: 'Fizetési módok',
          paymentMethods: 'Fizetési módok',
          billing_type: 'Számlázás típusa',
          billingType: 'Számlázás típusa',
          billing_name: 'Számlázási név',
          billingName: 'Számlázási név',
          billing_email: 'Számlázási e-mail',
          billingEmail: 'Számlázási e-mail',
          billing_emails: 'Számlázási e-mailek',
          billingEmails: 'Számlázási e-mailek',
          billing_address: 'Számlázási cím',
          billingAddress: 'Számlázási cím',
          billing_city: 'Számlázási város',
          billingCity: 'Számlázási város',
          billing_zip: 'Számlázási irányítószám',
          billingZip: 'Számlázási irányítószám',
          tax_number: 'Adószám',
          taxNumber: 'Adószám',
          vat_id_number: 'Közösségi adószám',
          vatIdNumber: 'Közösségi adószám',
          invoice_number: 'Számlaszám',
          invoiceNumber: 'Számlaszám',
          invoice_notes: 'Számla-megjegyzések',
          invoiceNotes: 'Számla-megjegyzések',
          invoice_item_name: 'Számlatétel neve',
          invoiceItemName: 'Számlatétel neve',
          invoice_item_note: 'Számlatétel megjegyzése',
          invoiceItemNote: 'Számlatétel megjegyzése',
          override_billing: 'Számlázás felülírása',
          overrideBilling: 'Számlázás felülírása',
          override_billing_profile_id: 'Felülíró számlázási profil',
          overrideBillingProfileId: 'Felülíró számlázási profil',
          default_invoice_note: 'Alap számla-megjegyzés',
          defaultInvoiceNote: 'Alap számla-megjegyzés',
          // TÖRTÉNETI slugok: a összevonás ELŐTTI napló-sorok mezőnevei. Az `audit_log`
          // változatlan, tehát a címke MARAD — nélküle a napló nyers angol slugot írna ki.
          default_invoice_header_note: 'Alap számla-megjegyzés (fejléc, korábbi)',
          defaultInvoiceHeaderNote: 'Alap számla-megjegyzés (fejléc, korábbi)',
          default_invoice_footer_note: 'Alap számla-megjegyzés (lábléc, korábbi)',
          defaultInvoiceFooterNote: 'Alap számla-megjegyzés (lábléc, korábbi)',
          default_invoice_payment_deadline_days: 'Alap fizetési határidő (nap)',
          defaultInvoicePaymentDeadlineDays: 'Alap fizetési határidő (nap)',
          parent_document_id: 'Szülő dokumentum',
          parentDocumentId: 'Szülő dokumentum',
          old: 'Korábbi érték',
          new: 'Új érték',
        },
      },
      // Szerep-tier címkéi (roleLabel — UNIVERZÁLIS). A jog-CÍMKÉK/CSOPORTOK mostantól az Odinból jönnek (minden
      // app a saját gazdag metaadatát adja — lásd access-rights.tsx), ezért a perm.resource/action/group/pattern KIKERÜLT.
      perm: {
        role: {
          superadmin: 'Szuperadmin',
          admin: 'Adminisztrátor',
          member: 'Tag',
          support: 'Ügyfélszolgálat',
          contact: 'Kapcsolattartó',
        },
      },
      profile: {
        menu: 'Profilom',
        title: 'Profilom',
        subtitle: 'A fiókod adatai és biztonsági beállításai',
        save: 'Mentés',
        saving: 'Mentés…',
        loadErrorTitle: 'A profil nem tölthető be',
        loadErrorDesc: 'Váratlan hiba történt. Töltsd újra az oldalt.',
        avatar: {
          edit: 'Profilkép módosítása',
          remove: 'Kép eltávolítása',
        },
        basics: {
          title: 'Alapadatok',
          givenName: 'Keresztnév',
          familyName: 'Vezetéknév',
          nickname: 'Becenév',
          nicknameHint: 'Ahogy a rendszer megszólít. Üresen hagyva a keresztnevedet használja.',
          probationEnds: 'Próbaidő lejár: {{date}}',
        },
        contact: {
          title: 'Kapcsolat',
          email: 'E-mail cím',
          phone: 'Telefonszám',
          emailPartnerHint:
            'A bejelentkezéshez is ezt használja — módosításhoz nyisson jegyet, és kollégáink elvégzik.',
          stepUpLabel: 'Jelenlegi jelszó',
          stepUpHint: 'Az e-mail cím módosításához biztonsági okból add meg a jelenlegi jelszavad.',
        },
        // „Egyéb adatok" — CSAK a belső Profilomon (a partner-portálon nincs)
        personal: {
          title: 'Egyéb adatok',
          birthDate: 'Születésnap',
          nameDay: 'Névnap',
          celebrationsOptOut: 'Ne jelenjek meg az Ünnepeltek között',
          celebrationsOptOutHint:
            'Ha bekapcsolod, a kollégák kezdőlapján az Ünnepeltek csempén sem a születésnapod, sem a névnapod, sem az évfordulód, sem a belépésed nem jelenik meg.',
        },
        integrations: {
          title: 'Integrációk',
          discordId: 'Discord-azonosító',
          discordIdHint: 'A Discord felhasználó numerikus azonosítója (nem a felhasználónév).',
          discordIdPlaceholder: 'pl. 123456789012345678',
          asanaLabel: 'Asana személyes hozzáférési token (PAT)',
          asanaSave: 'Token mentése',
          asanaDelete: 'Törlés',
          asanaCancel: 'Mégse',
          asanaHintSet: 'Beállítva. Új token felülírja; a mező üresen hagyása nem változtat rajta.',
          asanaHintUnset: 'Nincs beállítva. Token megadásával összekötheted az Asana-fiókod.',
          asanaHintClearing: 'Törlésre jelölve — mentéskor törlődik.',
        },
        notifications: {
          title: 'Értesítések',
          email: 'E-mail értesítések',
          emailHint: 'Fontos eseményekről e-mailben is értesítünk.',
          inApp: 'Alkalmazáson belüli értesítések',
          inAppHint: 'Értesítések a rendszeren belül (harang).',
        },
        password: {
          title: 'Jelszó módosítása',
          policyHint:
            'Kötelező: legalább {{count}} karakter, kis- és nagybetű, valamint szám. Szimbólum ajánlott.',
          current: 'Jelenlegi jelszó',
          new: 'Új jelszó',
          confirm: 'Új jelszó megerősítése',
          submit: 'Jelszó módosítása',
          saving: 'Mentés…',
          // Élő követelmény-lista a jelszó-mező alatt. KÖVETELMÉNY-megfogalmazás (mit várunk), NEM hiba
          // („Tartalmazzon nagybetűt is") — a felhasználó még be sem fejezte a gépelést.
          rule: {
            length: 'legalább {{count}} karakter',
            // A kis- és nagybetű EGY követelmény: a policy valójában „vegyes kis-/nagybetű". Így 4 szabály
            // marad → kiegyensúlyozott 2×2 rács, nincs árván lógó ötödik elem.
            case: 'kis- és nagybetű',
            digit: 'szám',
            different: 'eltér a jelenlegitől',
          },
          // Csak képernyőolvasónak: az ikon önmagában néma.
          ruleMet: 'teljesítve',
          ruleMissing: 'még hiányzik',
        },
        twoFactor: {
          title: 'Kétlépcsős hitelesítés (2FA)',
          desc: 'Egy időalapú (TOTP) kód a hitelesítő appodból — extra védelem a fiókodnak.',
          statusOn: 'bekapcsolva',
          statusOff: 'kikapcsolva',
          statusPending: 'beállítás folyamatban',
          setup: '2FA beállítása',
          scanHint:
            'Olvasd be a QR-kódot a hitelesítő appoddal (pl. Google Authenticator, 1Password), majd add meg a megjelenő kódot.',
          manualKey: 'Kézi beállítókulcs',
          manualHint: 'Ha nem tudod beolvasni a QR-t, ezt a kulcsot add meg kézzel.',
          copyKey: 'Kulcs másolása',
          codeLabel: 'Hitelesítő kód',
          activate: 'Aktiválás',
          regenerate: 'Recovery-kódok újragenerálása',
          disable: 'Kikapcsolás',
          recovery: {
            title: 'Recovery-kódok',
            desc: 'Mentsd el ezeket biztonságos helyre. MOST látod utoljára — mindegyik EGYSZER használható, ha nincs nálad a hitelesítő appod.',
            copy: 'Másolás',
            download: 'Letöltés',
            saved: 'Elmentettem',
          },
          stepUp: {
            code: 'Hitelesítő vagy recovery-kód',
            or: 'vagy',
            password: 'Jelenlegi jelszó',
            cancel: 'Mégse',
          },
          disableDialog: {
            title: 'Kétlépcsős hitelesítés kikapcsolása',
            desc: 'A megerősítéshez add meg egy aktuális hitelesítő kódot VAGY a jelenlegi jelszavad.',
          },
          regenDialog: {
            title: 'Recovery-kódok újragenerálása',
            desc: 'Új kódok készülnek, a régiek érvénytelenednek. A megerősítéshez add meg egy hitelesítő kódot VAGY a jelszavad.',
          },
        },
      },
    },
    // Mező-hibák rétege (zod issue path → mező-üzenet). Hívás: t('validation:clients.email.invalid').
    validation: {
      // A PUBLIKUS (kijelentkezett) űrlapok mező-hibái. MIÉRT KELL: a login/„elfelejtett jelszó" a KONTRAKT
      // Zod-sémáját használta resolverként, aminek a hibaszövegei a Zod ANGOL defaultjai („Invalid email",
      // „String must contain at least 1 character(s)") — magyar felületen is angolul jelentek meg.
      auth: {
        emailRequired: 'Az e-mail cím megadása kötelező',
        emailInvalid: 'Érvénytelen e-mail cím',
        passwordRequired: 'A jelszó megadása kötelező',
        mfaInvalid: 'A kód 6 jegyű szám vagy egy recovery-kód',
      },
      expenses: {
        supplierName: 'A szállító neve kötelező',
        invoiceNumber: 'A számlaszám kötelező',
        issueDate: 'A kiállítás dátuma kötelező (ÉÉÉÉ-HH-NN)',
        amount: 'Érvénytelen összeg (legfeljebb 2 tizedes)',
        currency: 'Válassz devizát',
        accountingMonth: 'A vonatkozási hónap alakja ÉÉÉÉ-HH',
        issuerKey: 'Válassz kibocsátót',
        categoryName: 'A kategória magyar neve kötelező',
      },
      tickets: {
        description: { min: 'A leírás legalább 25 karakter legyen.' },
        account: 'Válassz fiókot',
        service: 'Válassz szolgáltatást',
        requester: 'Válassz igénylőt',
        subject: { required: 'A tárgy megadása kötelező', max: 'A tárgy legfeljebb 255 karakter' },
        link: { max: 'A link legfeljebb 2083 karakter' },
      },
      // GLOBÁLIS FALLBACK (`lib/zod-error-map.ts`): ha egy űrlapnak nincs saját üzenete egy hibára, ezek adják —
      // így a Zod angol alapszövege („String must contain at least 1 character(s)") sehol nem jelenhet meg.
      fallback: {
        required: 'Kötelező mező',
        tooShort: 'Legalább {{count}} karakter',
        tooLong: 'Legfeljebb {{count}} karakter',
        tooSmall: 'Legalább {{count}}',
        tooBig: 'Legfeljebb {{count}}',
        tooFewItems: 'Válassz legalább {{count}} elemet',
        tooManyItems: 'Legfeljebb {{count}} elem választható',
        email: 'Érvénytelen e-mail cím',
        url: 'Érvénytelen link',
        date: 'Érvénytelen dátum',
        option: 'Válassz a listából',
        invalid: 'Érvénytelen érték',
      },
      clients: {
        name: { required: 'A név megadása kötelező', max: 'Legfeljebb 255 karakter' },
        email: { invalid: 'Érvénytelen e-mail cím', max: 'Legfeljebb 255 karakter' },
        phone: { max: 'Legfeljebb 20 karakter' },
        generic: 'Érvénytelen érték',
      },
      accounts: {
        name: { required: 'A név megadása kötelező', max: 'Legfeljebb 255 karakter' },
        roomCount: { invalid: 'Add meg nem-negatív egész számként (pl. 120)' },
        integrationExternalId: { required: 'Add meg az azonosítót, vagy töröld a sort' },
        coordinate: { invalid: 'Tizedes fok, max 6 tizedesjegy (pl. 47.497912)' },
        tooLong: 'Túl hosszú érték',
        affiliation: { identity: 'A személy kiválasztása kötelező' },
        domain: {
          required: 'A domain megadása kötelező',
          invalid: 'Érvénytelen domain (pl. pelda.hu)',
          duplicate: 'Ez a domain kétszer szerepel',
        },
      },
      assets: {
        holder: { required: 'Válassz munkatársat, vagy kapcsold be az irodai használatot' },
        office: { noPerson: 'Irodai eszközhöz nem tartozhat személy' },
        usageLocation: {
          officeSocialOnly: 'Az irodai eszköz helye fixen: közösségi',
          personal: 'Válassz iroda, otthon vagy vegyes helyet',
        },
        catalog: {
          name: 'A magyar név kötelező (legfeljebb 120 karakter)',
          prefix: '2–8 nagybetű vagy szám (A–Z, 0–9)',
          category: 'Válassz kategóriát',
        },
        name: { required: 'A megnevezés kötelező', max: 'Legfeljebb 255 karakter' },
        type: { required: 'A típus kiválasztása kötelező' },
        retirementDate: { required: 'Kivezetett státusznál a selejtezés dátuma kötelező' },
        date: { invalid: 'Érvénytelen dátum (ÉÉÉÉ-HH-NN)' },
        netPrice: {
          required: 'A nettó ár megadása kötelező',
          format: 'Szám formában add meg (pl. 12000 vagy 12000.50)',
        },
        vatRate: {
          required: 'Az ÁFA-kulcs megadása kötelező',
          format: 'Szám formában add meg (pl. 27)',
        },
        website: { url: 'Érvénytelen URL (pl. https://példa.hu/termék)' },
        note: { max: 'Legfeljebb 2000 karakter.' },
        generic: 'Érvénytelen érték',
      },
      people: {
        children: {
          fullName: 'A gyerek teljes neve kötelező',
          birthDate: 'A gyerek születési dátuma kötelező',
        },
      },
      employees: {
        email: 'Érvényes e-mail cím kötelező',
        emailConflict: 'Ez az e-mail cím már foglalt.',
        // A jelszó TARTALMI szabályát a SZERVER validálja (gépi kódok → `common.passwordPolicy.*`); a
        // contract itt csak a durva hossz-korlátot adja, ez annak az üzenete.
        password: { tooLong: 'A jelszó túl hosszú (legfeljebb 200 karakter)' },
        positionId: { invalid: 'A beosztás megadása kötelező' },
        familyName: 'A vezetéknév megadása kötelező',
        givenName: 'A keresztnév megadása kötelező',
        probation_end_before_start: 'A próbaidő lejárata nem lehet a kezdete előtt',
        probation_off: 'Dátum csak bekapcsolt próbaidővel adható meg',
        generic: 'Érvénytelen érték',
      },
      contacts: {
        email: 'Érvényes e-mail cím kötelező',
        emailConflict: 'Ez az e-mail cím már foglalt.',
        familyName: 'A vezetéknév megadása kötelező',
        givenName: 'A keresztnév megadása kötelező',
        generic: 'Érvénytelen érték',
      },
      supportTeams: {
        name: 'A név (magyar és angol) megadása kötelező',
        email: 'Érvénytelen e-mail cím',
        generic: 'Érvénytelen érték',
      },
      org_structure: {
        name: { required: 'A név megadása kötelező', max: 'Legfeljebb 255 karakter' },
        department: { required: 'A részleg kiválasztása kötelező' },
        team: { required: 'A csapat kiválasztása kötelező' },
        generic: 'Érvénytelen érték',
      },
      messages: {
        pinOnlySentInternal: 'Csak kiküldött, belső üzenet tűzhető ki a kezdőlapra.',
        subject: {
          required: 'A tárgy megadása kötelező',
          max: 'Legfeljebb 255 karakter',
        },
        body: {
          required: 'Az üzenet szövege kötelező',
          min: 'Legalább {{count}} karakter szöveg kell (a formázás nem számít bele)',
          max: 'Az üzenet túl hosszú',
        },
        link: { url: 'Érvényes http(s) URL-t adj meg (pl. https://…)' },
        targets: { required: 'Válassz legalább egy címzettet' },
        audience: { required: 'Válaszd ki, kinek szól az üzenet' },
        scheduledAt: {
          required: 'Add meg a küldés időpontját',
          future: 'Az időpontnak a jövőben kell lennie',
        },
      },
      meetings: {
        // MEZŐ-hibák. Enélkül a zod NYERS, ANGOL alapüzenete jelent meg a magyar felületen
        // („String must contain at least 1 character(s)") — mérve az üres űrlap mentésekor.
        title: 'A megbeszélés címének megadása kötelező',
        location: 'A helyszín megadása kötelező',
        required: 'A mező kitöltése kötelező',
        // Cross-meeting ütközés (a BFF a mezőre — locationKey / zoomAccountKey — vezeti).
        locationConflict: 'Ezen a helyszínen már van egy időben átfedő megbeszélés.',
        zoomConflict: 'Ez a Zoom-fiók már foglalt ebben az idősávban.',
        diningRoomRestricted: 'Az étkezőben 11:00 és 13:00 között nem tartható megbeszélés.',
        endBeforeStart: 'A vége nem lehet a kezdetnél korábbi vagy azonos.',
        oneOnlineLink: 'Egyszerre csak egy online-link típus adható meg.',
        // ISMÉTLŐDŐ sorozat — a szabály-kibontás hibái (a BFF és az űrlap ugyanazt a kulcsot adja)
        recurrence: {
          endRequired: 'Add meg a sorozat végét: az alkalmak számát vagy az utolsó napot.',
          endAmbiguous: 'A sorozat végét egyféleképpen add meg: alkalomszámmal vagy dátummal.',
          weekdaysRequired: 'Válassz legalább egy napot.',
          startNotInWeekdays:
            'A kezdő nap legyen a kiválasztott napok egyike — munkanapos sorozatnál hétköznap (az első alkalom maga a kezdés).',
          untilBeforeStart: 'A sorozat vége nem lehet a kezdés előtt.',
          tooFew: 'Egy sorozat legalább 2 alkalomból áll.',
          tooMany: 'Egy sorozat legfeljebb 52 alkalomból állhat.',
          invalidStart: 'A kezdés és a vég nem értelmezhető idősáv.',
          tooLarge:
            'A sorozat túl nagy (alkalmak × résztvevők) — csökkentsd az alkalmak vagy a résztvevők számát.',
          dateChange:
            'Több alkalom szerkesztésénél a nap nem módosítható, csak az időpont — ehhez csak ezt az alkalmat szerkeszd.',
          notEditable: 'A sorozat szabálya utólag nem módosítható.',
          noFutureOccurrence: 'A sorozatnak nincs még el nem kezdődött alkalma ebben a körben.',
        },
        outsideBusinessHours: 'A megbeszélés {{from}} és {{to}} között tartható.',
      },
      invoices: {
        billingName: 'A számlázási név megadása kötelező',
        country: 'Kétbetűs országkód kötelező',
        zip: 'Az irányítószám megadása kötelező',
        city: 'A település megadása kötelező',
        address: 'A cím megadása kötelező',
        date: 'Érvénytelen dátum (ÉÉÉÉ-HH-NN)',
        email: 'Érvénytelen e-mail cím a listában',
        itemsRequired: 'Legalább egy tétel szükséges',
        accountRequired: 'Válassz fiókot',
        accountsRequired: 'Válassz legalább egy fiókot',
        vatMix: 'Egy számlán csak egy ÁFA-kulcs szerepelhet',
        euTaxRequired: 'EU fordított adózásnál (EUFAD37) az EU-adószám kötelező',
        tooLong: 'Túl hosszú érték',
        item: {
          servicePlan: 'Válassz szolgáltatást a katalógusból',
          name: 'A megnevezés megadása kötelező',
          quantity: 'A mennyiség legalább 0.01 legyen',
          format: 'Szám formában add meg (pl. 12000 vagy 12000.50)',
        },
      },
      billingProfiles: {
        billingName: 'A számlázási név megadása kötelező',
        country: 'Kétbetűs országkód kötelező',
        zip: 'Az irányítószám megadása kötelező',
        city: 'A település megadása kötelező',
        address: 'A cím megadása kötelező',
        email: 'Érvénytelen e-mail cím a listában',
        deadline: 'A fizetési határidő 1 és 31 közötti egész szám legyen',
        client: 'Válassz ügyfelet',
        directorPeriod: 'A záró dátum nem lehet korábbi a kezdőnél',
        tooLong: 'Túl hosszú érték',
      },
      paymentCards: {
        holderName: 'A kártyabirtokos nevének megadása kötelező',
        brand: 'Válassz kártyatípust',
        last4: 'Pontosan 4 számjegy kötelező',
        expiryMonth: 'A hónap 1 és 12 közötti szám legyen',
        expiryYear: 'Az év 2000 és 2100 közötti szám legyen',
        client: 'Válassz ügyfelet',
        tooLong: 'Túl hosszú érték',
      },
      serviceAssignments: {
        quantityFormat: 'Szám formában add meg (pl. 1 vagy 1.5)',
        quantityMin: 'A mennyiség legalább 0.01 legyen',
        planRequired: 'Válassz konstrukciót',
        accountRequired: 'Válassz fiókot',
        accountIdsRequired: 'Ügyfél-hatókörnél legalább egy érintett fiókot jelölj ki',
        billingStartRequired: 'Számlázásnál a kezdő dátum kötelező',
        billingDateRequired: 'Eseti díjnál a számlázás dátuma kötelező',
        billingEndBeforeStart: 'A számlázás vége nem lehet korábbi a számlázás kezdeténél',
        usageSourceRequired:
          'Sávos / alapdíj+sávos / jutalékos árazásnál a használat-forrás kötelező',
        usageRefsRequired: 'Válaszd ki, melyik azonosító mérése számít bele a tételbe',
        componentInvalid:
          'Ellenőrizd a komponenseket: minden komponens kapjon konstrukciót és érvényes árat, egységes devizában',
        priceFormat: 'Érvényes nettó árat adj meg (pl. 1000 vagy 1000.50)',
        tiersRequired: 'Legalább egy sáv szükséges',
        intRequired: 'Egész számot adj meg',
        tierRange: 'A sáv-minimum legyen kisebb a maximumnál',
        tierOverlap: 'A sávok nem fedhetnek át (növekvő, nem-átfedő sorrend)',
        tierPriceDecrease: 'A sáv ára nem lehet kisebb az előző sávénál',
        blockSize: 'A blokkméret pozitív egész szám legyen',
        tooLong: 'Túl hosszú érték',
      },
      serviceCatalog: {
        nameRequired: 'A név megadása kötelező',
        planNameRequired: 'A csomag nevének megadása kötelező',
        invoiceItemNameRequired: 'A számlatétel nevének megadása kötelező',
        oneTimeFlat: 'Eseti díjnál csak fix (flat) árazás választható',
        priceFormat: 'Érvényes nettó árat adj meg (pl. 1000 vagy 1000.50)',
        tiersRequired: 'Legalább egy sáv szükséges',
        intRequired: 'Egész számot adj meg',
        tierRange: 'A sáv-minimum legyen kisebb a maximumnál',
        tierOverlap: 'A sávok nem fedhetnek át (növekvő, nem-átfedő sorrend)',
        tierPriceDecrease: 'A sáv ára nem lehet kisebb az előző sávénál',
        blockSize: 'A blokkméret pozitív egész szám legyen',
        tooLong: 'Túl hosszú érték',
      },
      profile: {
        email: 'Érvénytelen e-mail cím',
        discordId: 'A Discord-azonosító csak számjegyekből állhat',
        stepUpRequired: 'Az e-mail módosításához add meg a jelenlegi jelszavad',
        password: {
          mismatch: 'A két jelszó nem egyezik',
          required: 'A jelszó megadása kötelező',
          too_short: 'Legalább {{count}} karakter szükséges',
          too_long: 'Legfeljebb {{count}} karakter lehet',
          missing_lowercase: 'Tartalmazzon kisbetűt is',
          missing_uppercase: 'Tartalmazzon nagybetűt is',
          missing_digit: 'Tartalmazzon számot is',
          same_as_current: 'Az új jelszó nem egyezhet a jelenlegivel',
          wrongCurrent: 'Hibás jelenlegi jelszó',
          generic: 'A jelszó nem felel meg a követelményeknek',
        },
      },
    },
    // Toast / visszajelzés rétege. Hívás: t('toast:clients.saved').
    toast: {
      // KEZDŐLAP-testreszabás
      dashboard: {
        saved: 'Az elrendezés elmentve',
        reset: 'Az alapértelmezett elrendezés visszaállt',
        conflict:
          'Az elrendezést közben máshol módosították — a friss elrendezést töltöttük be, a módosításaid elvesztek.',
        forbidden: 'Az elrendezés nem menthető (megszemélyesítés alatt tiltott).',
        validation: 'Az elrendezés érvénytelen — próbáld újra.',
        error: 'Az elrendezés mentése nem sikerült — a módosításaid megvannak, próbáld újra.',
        unavailable: 'Az elrendezés szolgáltatás most nem érhető el — próbáld újra egy perc múlva.',
      },
      // KIADÁSOK — a NAV-fal és a duplikátum SAJÁT üzenetet kap: a felhasználónak más a teendője.
      expenses: {
        import: {
          templateError: 'A sablon nem tölthető le — próbáld újra.',
          missingColumns: 'Hiányzó oszlop a fájlból: {{columns}}',
          empty: 'A fájlban nincs kitöltött sor.',
          unreadable: 'A fájl nem olvasható. Használd a letöltött sablont (.xlsx vagy .csv).',
          previewError: 'Az előnézet nem készült el — próbáld újra.',
          commitError: 'A beírás nem sikerült — a sorok nem kerültek be.',
          duplicateBatch: 'Ez a feltöltés már lefutott egyszer.',
          undoError: 'A visszavonás nem sikerült.',
          undone: '{{count}} sor visszavonva',
        },
        created: 'Kiadás rögzítve',
        updated: 'Kiadás módosítva',
        deleted: 'Kiadás törölve',
        classified: 'Besorolás mentve',
        monthChanged: 'Vonatkozási hónap mentve',
        amountSaved: 'Összeg mentve',
        bulkClassified: '{{count}} kiadás besorolva',
        bulkPartial: '{{success}} kiadás besorolva — {{failed}} sor nem sikerült',
        bulkFailed: 'Egyik sor sem sikerült ({{count}})',
        dismissed: 'Kész',
        duplicate: 'Ehhez a szállítóhoz már rögzítettük ezt a számlaszámot',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Ehhez nincs jogosultságod',
        validation: 'Ellenőrizd a kiemelt mezőket',
        navReadonly:
          'A NAV-ból importált sor adatai nem módosíthatók — csak a kategória és a megjegyzés',
        error: 'A művelet most nem sikerült',
        // ÁTMENETI zavar (fojtás, platform-kiesés) — a teendő MÁS, mint egy végleges hibánál: várni és újra.
        unavailable: 'Az adat most nem érhető el — próbáld újra néhány másodperc múlva',
        categoryCreated: 'Kategória létrehozva',
        categoryUpdated: 'Kategória módosítva',
        categoryDeleted: 'Kategória törölve',
        categoryInUse: 'A kategória használatban van — előbb sorold át a rá mutató tételeket',
        categoryDepth: 'A kategória-fa legfeljebb három szintű',
      },
      // MEGSZEMÉLYESÍTÉS — a támogatói „belépés más nevében" visszajelzései.
      impersonation: {
        started: 'Most {{name}} nevében dolgozol',
        stopped: 'Visszaléptél a saját fiókodra',
        sessionExpired: 'A munkamenet lejárt — jelentkezz be újra',
        stopFailed: 'A visszalépés most nem sikerült — próbáld újra',
        error: 'A megszemélyesítés nem sikerült',
      },
      // PARTNER-PORTÁL — a partnernek szóló, egyszerű megfogalmazás (nincs belső zsargon, nincs jog-kulcs).
      portal: {
        replySent: 'Válasz elküldve',
        attachmentDownloadFailed: 'A letöltési link nem kérhető le — próbáld újra',
        replyUpdated: 'A válasz módosítva',
        replyConflict: 'A választ közben módosították — töltsd újra a jegyet',
        pdfError: 'A számla PDF-je most nem letölthető',
        rsvpConfirmed: 'Rögzítettük: ott lesz',
        rsvpDeclined: 'Rögzítettük: nem tud részt venni',
        rsvpReset: 'A válaszát töröltük — később is jelezheti',
        messageArchived: 'Üzenet archiválva',
        messageUnarchived: 'Üzenet visszaállítva',
        messagesAllRead_zero: 'Nem volt olvasatlan üzenet',
        messagesAllRead_one: '{{count}} üzenet olvasottnak jelölve',
        messagesAllRead_other: '{{count}} üzenet olvasottnak jelölve',
        attachmentLimit: 'Legfeljebb {{count}} fájl csatolható',
        attachmentTooLarge: '{{name}} túl nagy (legfeljebb {{max}})',
        attachmentInvalidType: '{{name}} típusa nem engedett',
        attachmentUploaded: 'A mellékleteket feltöltöttük',
        attachmentAddInvalidType: 'Ez a fájltípus nem engedett',
        attachmentAddTooLarge: 'A fájl túl nagy (legfeljebb 50 MB)',
        attachmentAddFailed: 'A fájl feltöltése nem sikerült',
        attachmentPartial: 'A jegyet megnyitottuk, de ezt nem sikerült feltölteni: {{files}}',
        accessSaved: 'Hozzáférési lista frissítve',
        ticketCreated: 'A jegyet megnyitottuk — hamarosan jelentkezünk',
        forbidden: 'Ehhez a művelethez nincs jogosultságod',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      // Értesítés-harang: a sor-kattintásos olvasottnak-jelölés NÉMA (nincs siker-toast), csak a hiba-ág szól;
      // a „Mind olvasottnak" EXPLICIT akció → siker-toastot is kap.
      notifications: {
        markReadError: 'Az olvasottnak jelölés nem sikerült',
        markAllError: 'A művelet nem sikerült',
        allRead: 'Minden értesítés olvasottnak jelölve',
      },
      clients: {
        created: 'Ügyfél létrehozva',
        saved: 'Módosítások mentve',
        deleted: 'Ügyfél törölve',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
        // ÁTMENETI zavar — MÁS a teendő, mint a `validation`-nél: itt nincs mit javítani, újra kell próbálni.
        unavailable: 'A művelet most nem futtatható — próbáld újra néhány másodperc múlva',
      },
      portalAccess: {
        saved: 'Portál-szerep mentve',
        inviteSent: 'Portál-meghívó elindítva',
        inviteError: 'A portál-meghívó küldése nem sikerült',
        revoked: 'Portál-hozzáférés visszavonva',
        forbidden: 'Nincs jogosultság a portál-hozzáférés módosításához',
        validation: 'A portál-hozzáférés nem állítható be a megadott adatokkal',
        error: 'A portál-hozzáférés módosítása nem sikerült',
      },
      accounts: {
        created: 'Fiók létrehozva',
        saved: 'Módosítások mentve',
        deleted: 'Fiók törölve',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
        affiliationAdded: 'Kapcsolat hozzáadva',
        affiliationSaved: 'Kapcsolat mentve',
        affiliationRemoved: 'Kapcsolat eltávolítva',
      },
      meetings: {
        created: 'Megbeszélés létrehozva',
        updated: 'Megbeszélés frissítve',
        rescheduled: 'Megbeszélés átütemezve',
        notOwn: 'Csak a saját megbeszélésedet módosíthatod.',
        reschedulePast: 'Múltbeli időpontra nem lehet átütemezni',
        deleted: 'Megbeszélés törölve',
        rsvpSaved: 'Részvételi válasz mentve',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      serviceCatalog: {
        created: 'Szolgáltatás létrehozva',
        updated: 'Szolgáltatás frissítve',
        deleted: 'Szolgáltatás törölve',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      org_structure: {
        departments: {
          created: 'Részleg létrehozva',
          updated: 'Részleg frissítve',
          deleted: 'Részleg törölve',
          inUse:
            'A részleg használatban van (tartozik hozzá csapat) — előbb szüntesd meg a hivatkozásokat',
          conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
          forbidden: 'Nincs jogosultságod ehhez a művelethez',
          validation: 'Ellenőrizd a kiemelt mezőket',
          error: 'A művelet nem sikerült',
        },
        teams: {
          created: 'Csapat létrehozva',
          updated: 'Csapat frissítve',
          deleted: 'Csapat törölve',
          inUse:
            'A csapat használatban van (tartozik hozzá beosztás vagy eszköz) — előbb szüntesd meg a hivatkozásokat',
          conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
          forbidden: 'Nincs jogosultságod ehhez a művelethez',
          validation: 'Ellenőrizd a kiemelt mezőket',
          error: 'A művelet nem sikerült',
        },
        positions: {
          created: 'Beosztás létrehozva',
          updated: 'Beosztás frissítve',
          deleted: 'Beosztás törölve',
          inUse:
            'A beosztás használatban van (munkatárs hivatkozik rá) — előbb szüntesd meg a hivatkozásokat',
          conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
          forbidden: 'Nincs jogosultságod ehhez a művelethez',
          validation: 'Ellenőrizd a kiemelt mezőket',
          error: 'A művelet nem sikerült',
        },
      },
      tickets: {
        created: 'Jegy létrehozva',
        updated: 'Jegy frissítve',
        commentCreated: 'Komment hozzáadva',
        commentUpdated: 'Komment frissítve',
        attachmentUploaded: 'Csatolmány feltöltve',
        attachmentPartial: 'A jegy megnyílt, de ez nem sikerült feltölteni: {{files}}',
        attachmentDeleted: 'Csatolmány törölve',
        attachmentError: 'A csatolmány-művelet nem sikerült',
        attachmentDownloadFailed: 'A letöltési link nem kérhető le — próbáld újra',
        assigneeTeamAmbiguous: 'A kolléga több support-csapat tagja — a csapat nem változott',
        attachmentInvalidType: 'Nem engedélyezett fájltípus',
        attachmentTooLarge: 'A fájl túl nagy (max. 50 MiB)',
        attachmentLimit: 'Legfeljebb {{max}} csatolmány lehet egy jegyen',
        attachmentValidation: 'A fájl nem felel meg a csatolmány-feltételeknek',
        template: {
          saved: 'Komment-sablon mentve',
        },
        asanaCreated: 'Asana-feladat létrehozva',
        asanaAttachmentsIncomplete:
          'A feladat létrejött, de nem minden melléklet került át — a hiányzót csatold kézzel az Asanában',
        asanaUnlinked: 'Az Asana-hivatkozás leválasztva',
        asanaError: 'Az Asana-művelet nem sikerült',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      assets: {
        requestUpdated: 'Igény módosítva',
        catalogCreated: 'Felvéve a katalógusba',
        catalogUpdated: 'A katalógus-tétel mentve',
        catalogDeleted: 'Törölve a katalógusból',
        catalogInUse:
          'Nem törölhető: eszköz vagy igény még hivatkozik rá (vagy a kategóriának van típusa)',
        unavailable: 'A szolgáltatás átmenetileg nem érhető el — próbáld újra egy perc múlva',
        created: 'Eszköz létrehozva',
        updated: 'Eszköz frissítve',
        deleted: 'Eszköz törölve',
        requestCreated: 'Igénylés beadva',
        requestApproved: 'Igénylés jóváhagyva',
        requestRejected: 'Igénylés elutasítva',
        progressSaved: 'Beszerzési lépés rögzítve',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      employees: {
        updated: 'Munkatárs adatai mentve',
        created: 'Munkatárs létrehozva',
        createError: 'A munkatárs létrehozása nem sikerült',
        // MATCH-ág (létező e-mail): a megadott kezdő jelszó SZÁNDÉKOSAN nem íródott — a meglévő fiók jelszavát
        // a felvételi űrlap nem írhatja felül (fiók-átvétel elleni fal).
        passwordNotSet:
          'A munkatárs létrejött, de a kezdő jelszó nem lett beállítva: ehhez az e-mail-címhez már tartozik fiók — a meglévő jelszavával lép be.',
        alreadyEmployee: 'Ez az e-mail cím már munkatárs az Atlasban',
        deleted: 'Munkatárs inaktiválva',
        deleteError: 'A munkatárs inaktiválása nem sikerült',
        // Üdvözlő levél: a kézbesítés a válasz UTÁN fut (fire-and-forget) → „elindítottuk", nem „megkapta".
        welcomeSent: 'Üdvözlő levelet küldtünk a munkatársnak',
        // A cél nem kaphat meghívót (nincs aktív Atlas-hozzáférése / inaktív / platform-admin). NEM néma siker:
        // a leggyakoribb eset a provisioning-sorrend hibája — ha elhallgatnánk, az admin telefonon adná át a jelszót.
        welcomeNotEligible:
          'Ehhez a munkatárshoz most nem küldhető meghívó — ellenőrizd az Atlas-hozzáférését a Jogosultságok oldalon.',
        welcomeError: 'Az üdvözlő levél küldése nem sikerült',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      contacts: {
        created: 'Kapcsolattartó létrehozva',
        updated: 'Kapcsolattartó adatai mentve',
        createError: 'A kapcsolattartó létrehozása nem sikerült',
        deleted: 'Kapcsolattartó kivezetve',
        deleteError: 'A kapcsolattartó kivezetése nem sikerült',
        restored: 'Kapcsolattartó visszavéve — a belépéshez új jelszót kell beállítania',
        restoreError: 'A kapcsolattartó visszavétele nem sikerült',
        // NEM hiba: a sor időközben már élő lett (másik admin) — a teendő az újratöltés.
        restoreConflict: 'Ez a kapcsolattartó már nincs kivezetve — töltsd újra a listát',
        welcomeSent: 'Üdvözlő levelet küldtünk a kapcsolattartónak',
        // A futásidejű 409: a cél nem kaphat meghívót (nincs aktív app-tagsága — a portál-hozzáférés közben
        // megszűnt —, vagy kivezették). A gomb a portál-hozzáférés nélküli soron eleve tiltott, tehát ez ritka.
        welcomeNotEligible:
          'Ehhez a kapcsolattartóhoz most nem küldhető meghívó — ellenőrizd a portál-hozzáférését.',
        welcomeError: 'Az üdvözlő levél küldése nem sikerült',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      campaignForecast: {
        created: 'Kampány létrehozva',
        createdMany: '{{count}} kampány létrehozva',
        saved: 'Kampány mentve',
        deleted: 'Kampány törölve',
        deleteError: 'A kampány törlése nem sikerült',
        partial: 'Néhány fiókra nem sikerült — nézd meg a listát',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      productUpdates: {
        created: 'Fejlesztés létrehozva (piszkozat)',
        saved: 'Fejlesztés mentve',
        deleted: 'Fejlesztés törölve',
        deleteError: 'A fejlesztés törlése nem sikerült',
        published: 'A kihirdetés mentve',
        publishForbidden: 'A kihirdetéshez nincs jogosultságod',
        publishNeedsSummary: 'Partneres kihirdetéshez leírás kell',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
        attachmentTooLarge: '{{name}} túl nagy (legfeljebb {{max}})',
        attachmentInvalidType: '{{name}} típusa nem engedett',
        attachmentValidation: 'A fájl nem felel meg a feltételeknek',
      },
      supportTeams: {
        updated: 'Csapat mentve',
        created: 'Support-csapat létrehozva',
        createError: 'A support-csapat létrehozása nem sikerült',
        deleted: 'Support-csapat törölve',
        deleteError: 'A support-csapat törlése nem sikerült',
        inUse: 'A csapat nem törölhető: aktív jegy(ek) hivatkoznak rá.',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      messages: {
        sent: 'Üzenet elküldve',
        scheduled: 'Üzenet időzítve',
        unscheduled: 'Időzítés visszavonva',
        updated: 'Üzenet mentve',
        pinUpdated: 'Kitűzés frissítve',
        pinValidation: 'Csak kiküldött, belső üzenet tűzhető ki.',
        archiveUpdated: 'Archívum frissítve',
        allRead: 'Minden üzenet olvasottnak jelölve',
        attachmentUploaded: 'Melléklet feltöltve',
        attachmentDeleted: 'Melléklet törölve',
        attachmentPartial: 'Az üzenet elkészült, de ez nem sikerült feltölteni: {{files}}',
        attachmentError: 'A melléklet-művelet nem sikerült',
        attachmentValidation: 'A fájl nem felel meg a feltételeknek',
        attachmentInvalidType: 'Nem engedélyezett fájltípus',
        attachmentTooLarge: 'A fájl túl nagy (max. 50 MiB)',
        attachmentLimit: 'Legfeljebb {{max}} melléklet lehet egy üzeneten',
        // A melléklet-kezelés szerver-oldali konfigurációja hiányzik (belső ügyfél-rekord) → fail-closed 503.
        attachmentUnavailable: 'A melléklet-kezelés nincs beállítva — szólj az üzemeltetésnek',
        conflict: 'Az üzenet időközben módosult vagy már elment — a lista frissül',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      invoices: {
        created: 'Számla-piszkozat létrehozva',
        saved: 'Módosítások mentve',
        deleted: 'Piszkozat törölve',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
        // Számla-kiállítási életciklus (Számlázz.hu)
        issued: 'Számla kiállítva',
        stornoed: 'Számla sztornózva',
        cancelled: 'Díjbekérő érvénytelenítve',
        // TÖMEGES kiállítás (a legacy toast-bontása: típusonként siker/hiba-darabszám).
        bulkIssued: '{{invoice}} számla és {{proforma}} díjbekérő kiállítva',
        bulkIssuePartial: '{{invoice}} számla és {{proforma}} díjbekérő NEM sikerült',
        bulkIssueFailed:
          'Egyetlen kiállítás sem sikerült ({{invoice}} számla, {{proforma}} díjbekérő)',
        synced: 'Szinkronizálva — {{paid}} fizetett, {{overdue}} lejárt, {{related}} kapcsolt',
        syncLocked: 'A szinkronizálás már folyamatban van',
        notConfigured: 'A Számlázz.hu-integráció nincs beállítva',
        szamlazzError: 'A Számlázz.hu-művelet nem sikerült — a számla változatlan',
        generated: 'Számlagenerálás kész — {{invoices}} számla, {{items}} tétel',
        generationStarted:
          'A(z) {{month}} havi generálás elindult — szólunk, ha kész (a háttérben fut)',
        generationBusy: 'Már fut egy számla-generálás — várd meg, amíg befejeződik',
        generationUnavailable: 'A háttér-generálás most nem elérhető (hiányzó gépi kredenciál)',
      },
      usageData: {
        // A havi usage-lekérés hibái (a siker/„nincs adat" toast a komponensben áll össze, mert számot és
        // kihagyás-listát is mutat).
        fetchForbidden: 'A lekéréshez számlázás-kezelő jog szükséges',
        fetchError: 'A használati adatok lekérése nem sikerült',
        rateSaved: 'Árfolyam mentve',
        rateDeleted: 'Árfolyam törölve',
        // A KAPU a számlázás-kezelés (`invoices.manage` + Odin billing-írás) — a „platform-admin" szöveg
        // azóta félrevezető volt (a fal akkor került a billing-írásra).
        rateForbidden: 'Az árfolyam módosításához számlázás-kezelő jog szükséges',
        rateValidation: 'Érvénytelen árfolyam-adat',
        rateError: 'Az árfolyam mentése nem sikerült',
        rateDeleteError: 'Az árfolyam törlése nem sikerült',
      },
      billingProfiles: {
        created: 'Számlázási profil létrehozva',
        detailsCopied: 'Számlázási adatok a vágólapon',
        copyFailed: 'A másolás nem sikerült',
        updated: 'Módosítások mentve',
        deleted: 'Számlázási profil törölve',
        primarySet: 'Elsődleges profil beállítva',
        deletePrimary: 'Az elsődleges profil nem törölhető',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      paymentCards: {
        created: 'Bankkártya létrehozva',
        updated: 'Módosítások mentve',
        deleted: 'Bankkártya törölve',
        duplicate: 'Ez a bankkártya már rögzítve van ennél az ügyfélnél',
        inUse:
          'A kártya nem törölhető: szolgáltatás-hozzárendelés(ek) fizetési módja — előbb állítsd át őket',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      serviceAssignments: {
        created: 'Hozzárendelés létrehozva',
        cloned: 'Hozzárendelés átmásolva',
        updated: 'Módosítások mentve',
        deleted: 'Hozzárendelés törölve',
        closed: 'Hozzárendelés lezárva',
        deleteInvoiced: 'Kiállított számlán szereplő hozzárendelés nem törölhető, csak lezárható',
        conflict: 'Valaki időközben módosította — töltsd újra az oldalt',
        forbidden: 'Nincs jogosultságod ehhez a művelethez',
        validation: 'Ellenőrizd a kiemelt mezőket',
        error: 'A művelet nem sikerült',
      },
      profile: {
        saved: 'Profil mentve',
        saveError: 'A mentés nem sikerült. Töltsd újra az oldalt és próbáld újra.',
        taken: 'Ez az e-mail vagy Discord-azonosító már foglalt.',
        stepUpFailed: 'Hibás jelszó vagy megerősítés — a művelet nem hajtható végre.',
        validation: 'Ellenőrizd a kiemelt mezőket',
        forbidden: 'Ehhez a művelethez nincs jogosultság',
        error: 'A művelet nem sikerült',
        asanaSaved: 'Asana token mentve',
        asanaCleared: 'Asana token törölve',
        personalSaveError: 'Az egyéb adatok mentése nem sikerült — próbáld újra.',
        personalConflict: 'Az egyéb adataidat időközben módosították — töltsd újra az oldalt.',
        passwordChanged: 'Jelszó módosítva',
        passwordRevokeWarning: 'A többi eszközön a biztonság érdekében ki lettél léptetve.',
        rateLimited: 'Túl sok próbálkozás. Várj egy kicsit, majd próbáld újra.',
        twoFactorEnabled: 'Kétlépcsős hitelesítés bekapcsolva',
        twoFactorDisabled: 'Kétlépcsős hitelesítés kikapcsolva',
        twoFactorBadCode: 'Hibás hitelesítő kód',
        twoFactorAlreadyOn: 'A kétlépcsős hitelesítés már be van kapcsolva',
        copied: 'Vágólapra másolva',
        copyFailed: 'A másolás nem sikerült',
        avatarSaved: 'Profilkép frissítve',
        avatarRemoved: 'Profilkép eltávolítva',
        avatarError: 'A profilkép feltöltése nem sikerült. Próbáld újra.',
        avatarTooLarge: 'A kép túl nagy. Válassz kisebb képet.',
        avatarInvalidType: 'Nem támogatott formátum. PNG, JPEG vagy WebP képet tölts fel.',
        avatarRateLimited: 'Túl sok feltöltési kísérlet. Várj egy kicsit, majd próbáld újra.',
      },
    },
  },
  en: {
    translation: {
      drafts: {
        saved: 'Draft saved · {{time}}',
        discard: 'Discard draft',
        resume: 'Saved draft — continue…',
      },
      app: { name: 'Atlas' },
      mo: {
        title: 'Mo',
        subtitle: 'Your ever-present helper',
        close: 'Close',
        empty:
          'Ask away! I answer with sources, and if I don’t have precise data on something, I say so honestly — I don’t guess.',
        noResult: 'No precise data on this in the knowledge base.',
        placeholder: 'Ask Mo…',
        send: 'Send',
        errorNetwork: 'Network error — Mo is unavailable.',
        errorInvalid: 'Invalid request.',
        errorHttp: 'Mo error (HTTP {{code}}).',
        colGroup: 'Group',
        colCount: 'Count',
        colSum: 'Sum',
      },
      // ── Notifications („bell") — see the Hungarian block for the key-shape rationale. ────────────────
      lightbox: {
        title: 'View attachment',
        untitled: 'attachment',
        close: 'Close',
        prev: 'Previous',
        next: 'Next',
        openInNewTab: 'Open in new tab',
      },
      notify: {
        bell: {
          aria: 'Notifications',
          ariaWithCount: 'Notifications — {{unread}} unread',
          liveCount: 'Unread notifications: {{unread}}',
        },
        panel: {
          title: 'Notifications',
          markAll: 'Mark all read',
          unreadMark: 'Unread',
          openMessages: 'Open messages',
          emptyTitle: 'No notifications',
          emptyDescription:
            'Notifications about tickets, meetings, asset requests and due dates show up here.',
          errorTitle: 'Notifications could not be loaded',
          errorDescription: 'Please try again in a moment.',
          loadMore: 'Load more',
          loading: 'Loading…',
        },
        fallback: {
          title: 'Notification',
          body: 'You have a new notification.',
          actorName: 'System',
          ticketNumber: 'unknown',
          accountName: 'unknown',
          subject: 'no subject',
          deliveredCount: 'an unknown number of',
          droppedCount: 'a few',
          meetingTitle: 'untitled',
          startsAtLabel: 'not specified',
          startsInMinutes: 'a few',
          month: 'unknown month',
          count: 'an unknown number of',
          assetName: 'asset',
          assetType: 'asset',
          team: 'unknown',
          netPriceLabel: 'not specified',
          rejectReason: 'not specified',
          cardMasked: 'unknown',
          cardExpiryLabel: 'not specified',
          expiryLabel: 'not specified',
          clientName: 'unknown',
          emoji: 'a',
          serviceName: 'unknown service',
          planName: 'unknown plan',
          startDate: 'not specified',
        },
        types: {
          document: {
            alert: {
              title: 'Document reminder',
              body: 'A document deadline notification — archived notification from the previous CRM (the module was dropped).',
            },
          },
          ticket: {
            open: {
              title: 'Ticket opened',
              body: 'A support ticket was opened — archived notification from the previous CRM.',
            },
            close: {
              title: 'Ticket closed',
              body: 'A support ticket was closed — archived notification from the previous CRM.',
            },
            status_change: {
              title: 'Ticket status changed',
              body: 'A support ticket changed status — archived notification from the previous CRM.',
            },
            assign: {
              title: 'Ticket assigned',
              body: 'A support ticket was assigned to you — archived notification from the previous CRM.',
            },
            opened: {
              title: 'Ticket opened',
              body: 'A new ticket {{ticketNumber}} was opened under the {{accountName}} account: {{subject}}',
            },
            assigned: {
              title: 'New task',
              body: 'The {{ticketNumber}} ticket under the {{accountName}} account has been assigned to you.',
            },
            status_changed: {
              title: 'Ticket status changed',
              body: 'The status of the {{ticketNumber}} ticket under the {{accountName}} account has changed.',
              title_in_progress: 'Ticket in progress',
              body_in_progress:
                'The {{ticketNumber}} ticket under the {{accountName}} account is now being investigated.',
              title_reopened: 'Ticket reopened',
              body_reopened:
                'The {{ticketNumber}} ticket under the {{accountName}} account has been reopened.',
            },
            closed: {
              title: 'Ticket closed',
              body: 'The {{ticketNumber}} ticket under the {{accountName}} account has been closed.',
            },
            resolved: {
              title: 'Ticket resolved',
              body: 'The {{ticketNumber}} ticket under the {{accountName}} account has been resolved.',
            },
            comment: {
              title: 'New ticket comment',
              body: 'A comment was added to a support ticket — archived notification from the previous CRM.',
              external: {
                title: 'New reply',
                body: 'The {{ticketNumber}} ticket under the {{accountName}} account has received a new reply.',
              },
              internal: {
                title: 'New comment',
                body: 'The {{ticketNumber}} ticket under the {{accountName}} account has received a new internal comment.',
              },
              reaction: {
                title: 'New reaction',
                body: '{{actorName}} reacted with {{emoji}} to your comment on ticket {{ticketNumber}}.',
              },
            },
            // The PARTNER-side sharing notification: the recipient could not see this ticket before.
            access_granted: {
              title: 'You were given access to a ticket',
              body: '{{actorName}} shared ticket {{ticketNumber}} with you — subject: {{subject}}',
            },
          },
          message: {
            new: {
              title: 'New message',
              body: 'A message arrived on the message board — archived notification from the previous CRM.',
            },
            received: {
              title: 'New message',
              body: '{{actorName}} sent a message — subject: {{subject}}',
            },
            delivery_truncated: {
              title: 'Recipient list truncated',
              body: 'The message “{{subject}}” was delivered to {{deliveredCount}} recipients, but the recipient list was truncated: {{droppedCount}} were left out.',
              body_recipient_cap:
                'The message “{{subject}}” was delivered to {{deliveredCount}} recipients, but {{droppedCount}} recipients were left out (the audience was too broad). Narrow the audience or send it in batches.',
              body_account_scan_cap:
                'The message “{{subject}}” was delivered to {{deliveredCount}} recipients, but the contacts of {{droppedCount}} target accounts were left out (too many accounts addressed). Send it in batches.',
              body_pivot_page_cap:
                'The message “{{subject}}” was delivered to {{deliveredCount}} recipients, but the recipient list could not be fully re-read for {{droppedCount}} audience group(s), so someone may have been left out. Review the audience and resend to a narrower group if needed.',
            },
          },
          meeting: {
            new: {
              title: 'New meeting',
              body: 'You were invited to a meeting — archived notification from the previous CRM.',
            },
            reschedule: {
              title: 'Meeting rescheduled',
              body: 'A meeting was moved to a new time — archived notification from the previous CRM.',
            },
            delete: {
              title: 'Meeting cancelled',
              body: 'A meeting was deleted — archived notification from the previous CRM.',
            },
            invited: {
              title: 'Meeting scheduled',
              body: 'The {{title}} meeting has been scheduled. Time: {{startsAtLabel}}',
            },
            rescheduled: {
              title: 'Meeting rescheduled',
              body: 'The {{title}} meeting has been rescheduled. New time: {{startsAtLabel}}',
            },
            cancelled: {
              title: 'Meeting deleted',
              body: 'The {{title}} meeting has been deleted.',
            },
            removed: {
              title: 'Removed from a meeting',
              body: 'You have been removed from the participants of the {{title}} meeting.',
            },
            link_changed: {
              title: 'Meeting link changed',
              body: 'The join link of the {{title}} meeting has changed.',
            },
            reminder: {
              title: 'Upcoming meeting',
              body: 'The meeting is starting soon!',
            },
          },
          asset: {
            request: {
              title: 'Asset request',
              body: 'A new asset request arrived — archived notification from the previous CRM.',
            },
            approve: {
              title: 'Asset request approved',
              body: 'An asset request was approved — archived notification from the previous CRM.',
            },
            reject: {
              title: 'Asset request rejected',
              body: 'An asset request was rejected — archived notification from the previous CRM.',
            },
            purchase: {
              title: 'Asset purchased',
              body: 'A requested asset was purchased — archived notification from the previous CRM.',
            },
            requested: {
              title: 'New asset request',
              body: 'A new {{assetType}} purchase request has been submitted for the {{team}} team, with a net value of {{netPriceLabel}}.',
            },
            approved: {
              title: 'Asset request approved',
              body: 'The requested asset {{assetName}} for the {{team}} team, with a net value of {{netPriceLabel}}, has been approved.',
            },
            rejected: {
              title: 'Asset request rejected',
              body: 'The requested asset {{assetName}} for the {{team}} team has been rejected. Reason: {{rejectReason}}',
            },
            purchased: {
              title: 'Asset under purchase',
              body: 'The requested asset {{assetName}} for the {{team}} team has been purchased.',
            },
            approved_info: {
              title: 'Asset request approved',
              body: 'The {{assetName}} ({{assetType}}) requested for the {{team}} team has been approved — procurement will be handled by the buyer.',
            },
            request_updated: {
              title: 'Your asset request was edited',
              body: 'The reviewer edited the details of your {{assetName}} ({{assetType}}) request. Please review the changes.',
            },
            expiring: {
              title: 'Subscription expiring',
              body: '{{assetName}} ({{assetType}}) expires on {{expiryLabel}}.',
              title_month: 'Expires within a month',
              body_month:
                '{{assetName}} ({{assetType}}) expires within 30 days: {{expiryLabel}}. Decide on the renewal in time.',
              title_week: 'Expires within a week',
              body_week: '{{assetName}} ({{assetType}}) expires within 7 days: {{expiryLabel}}.',
              title_due: 'Subscription expired',
              body_due:
                '{{assetName}} ({{assetType}}) has reached its expiry date ({{expiryLabel}}). Renew it or retire it from the inventory.',
            },
          },
          payment_card: {
            expiring: {
              title: 'Payment card expiring',
              body: 'Payment card {{cardMasked}} of client {{clientName}} expires on {{cardExpiryLabel}}',
            },
            expired: {
              title: 'Payment card expired',
              body: 'Payment card {{cardMasked}} of client {{clientName}} has expired ({{cardExpiryLabel}}).',
            },
          },
          service_assignment: {
            pending_start_soon: {
              title: 'Pending assignments starting soon',
              body: '{{count}} pending assignments start soon — earliest: {{firstStartDate}}',
            },
          },
          invoice: {
            generation_finished: {
              title: 'Invoice generation finished',
              body: '{{month}} — {{invoices}} drafts, {{items}} items, {{skipped}} skipped',
            },
          },
        },
      },
      // Rich-text editor toolbar (message body · ticket description · external comment) — GLOBAL component.
      richText: {
        bold: 'Bold',
        italic: 'Italic',
        underline: 'Underline',
        strike: 'Strikethrough',
        highlight: 'Highlight',
        code: 'Inline code',
        codeBlock: 'Code block',
        blockquote: 'Quote',
        horizontalRule: 'Divider',
        bulletList: 'Bullet list',
        orderedList: 'Numbered list',
        undo: 'Undo',
        redo: 'Redo',
        clearFormat: 'Clear formatting',
        blockType: 'Block type',
        paragraph: 'Paragraph',
        heading2: 'Heading 1',
        heading3: 'Heading 2',
        heading4: 'Heading 3',
        alignLeft: 'Align left',
        alignCenter: 'Align center',
        alignRight: 'Align right',
        alignJustify: 'Justify',
        color: 'Text colour',
        colorNone: 'No colour',
        fontSize: 'Font size',
        fontSizeDefault: 'Default',
        link: 'Link',
        unlink: 'Remove link',
        linkUrl: 'Link URL',
        linkPlaceholder: 'https://…',
        linkApply: 'Insert',
        linkRemove: 'Remove',
        linkInvalid: 'Enter a valid URL (http:// or https://).',
        image: 'Insert image',
        pasteNotMedia:
          'Only images or videos can be pasted into the text — attach other files as attachments.',
        video: 'Insert video',
        imageError: {
          type: 'Only JPG, PNG, GIF, WEBP images or MP4, WEBM, MOV videos can be uploaded.',
          size: 'Images may be at most 5 MB, videos at most 25 MB.',
          failed: 'Upload failed.',
        },
      },
      nav: {
        home: 'Go to the home page',
        dashboard: 'Dashboard',
        clients: 'Clients',
        accounts: 'Accounts',
        contacts: 'Contacts',
        services: 'Services',
        finance: 'Revenue / expenses',
        billingProfiles: 'Billing profiles',
        usageData: 'Usage data',
        paymentCards: 'Payment cards',
        invoices: 'Invoices',
        tickets: 'Tickets',
        meetings: 'Meetings',
        messages: 'Messages',
        orgStructure: 'Org structure',
        employees: 'Employees',
        supportTeams: 'Support teams',
        campaignForecast: 'Campaign forecast',
        productUpdates: 'Product updates',
        serviceCatalog: 'Catalog',
        assets: 'Assets',
        accessRights: 'Access rights',
        activityLog: 'Activity log',
        comingSoon: 'Coming soon',
        collapse: 'Collapse',
        menu: 'Menu',
        search: 'Search or jump to…',
        searchPlaceholder: 'Global search…',
        noResults: 'No results.',
        navigation: 'Navigation',
        navigate: 'navigate',
        open: 'open',
        searching: 'Searching…',
        partialResults:
          'You may not be seeing everything — refine your search, or open the relevant list page.',
        results: 'Results',
        resultTypes: {
          client: 'Clients',
          account: 'Accounts',
          contact: 'Contacts',
          ticket: 'Tickets',
          invoice: 'Invoices',
          expense: 'Expenses',
          message: 'Messages',
          meeting: 'Meetings',
          employee: 'Staff',
          supportTeam: 'Support teams',
          asset: 'Assets',
          serviceAssignment: 'Service assignments',
          serviceCatalog: 'Catalog',
          department: 'Departments',
          team: 'Teams',
          position: 'Positions',
        },
        notifications: 'Notifications',
        desc: {
          clients: 'Clients and their data',
          accounts: 'Client accounts',
          contacts: 'External users',
          tickets: 'Support tickets',
          invoices: 'Automatically generated invoices',
          serviceCatalog: 'Service catalog',
          orgStructure: 'Departments, teams and positions',
          employees: 'Internal users',
          supportTeams: 'Support teams and their members',
          campaignForecast: 'Campaigns per account, on a timeline',
          productUpdates: 'Roadmap and changelog — what shipped and what is next',
          assets: 'Internal asset inventory',
          accessRights: 'Per-app roles and permissions of users',
          meetings: 'Scheduled meetings',
          messages: 'Personal messages and system notices',
          activityLog: 'Audit log of the consumer apps built on Odin',
          billingProfiles: 'Client billing details',
          usageData: 'External system usage and exchange rates',
          paymentCards: 'Client cards',
          services: 'Service assignments',
          finance: 'What comes in and what goes out',
        },
        group: {
          clientMgmt: 'Client relations',
          finance: 'Finance',
          support: 'Support & communication',
          organization: 'Organization',
          admin: 'Administration',
        },
        quickActions: 'Actions',
      },
      usageData: {
        billingNote: {
          label: {
            billed: 'Billed from this',
            billed_inactive: 'Inactive — not billed',
            other_variant: 'Billed from another row',
            tracking: 'Tracking-only assignment',
            bound_elsewhere: 'Bound to another ID',
            ended: 'Service ended',
            not_started: 'Service starts later',
            no_assignment: 'Measured, no service',
            unused: 'Service not used',
            no_account: 'Not set on any account',
            no_account_unused: 'Not set on any account',
            not_final: 'Not closed in HotelCMS',
            disabled: 'Switched off in HotelCMS',
            combined_minimum: 'Combined minimum in HotelCMS',
          },
          desc: {
            billed: 'A live service assignment uses this data — the invoice item is made from it.',
            billed_closed:
              'The \u201C{{sourceType}}\u201D service ran until {{date}} and {{inactive}} is inactive since — this month is still invoiced (up to the end date).',
            other_variant:
              'The property\u2019s contract bills from the \u201C{{usedSourceType}}\u201D row; this row is an informational breakdown only, no separate item is made from it.',
            billed_inactive:
              'There is a \u201C{{sourceType}}\u201D service assignment for this month, but {{inactive}} — without an end date nothing is invoiced. If the service ended, set the end date: it is invoiced up to that.',
            ended:
              'The \u201C{{sourceType}}\u201D service of the account with ID {{ref}} ended on {{date}}, so nothing is invoiced from it for this month.',
            not_started:
              'The \u201C{{sourceType}}\u201D service of the account with ID {{ref}} starts on {{date}}, so nothing is invoiced from it for this month yet.',
            tracking:
              'There is an assignment for this ID (\u201C{{sourceType}}\u201D), but it is tracking-only, so no invoice item is made from it. If it should be billed, switch the assignment type to billing.',
            bound_elsewhere:
              'ID {{ref}} belongs to the account, and the account has a \u201C{{sourceType}}\u201D assignment for this month \u2014 but it is bound to ANOTHER ID, so no invoice item is made from this row. Check the assignment\u2019s \u201CSource ID\u201D field.',
            no_assignment:
              'There is a measured value, but the account with ID {{ref}} has no \u201C{{sourceType}}\u201D service assignment for this month, so nothing is invoiced. Add an assignment if it should be billed.',
            unused:
              'The account with ID {{ref}} does not use the \u201C{{sourceType}}\u201D service (the measured value is 0 and there is no assignment) — this is not a gap.',
            no_account:
              'No account carries ID {{ref}}, although there is a measured value — the data cannot be linked to a client. Add the ID on the right account.',
            no_account_unused:
              'No account carries ID {{ref}}; the measured value is 0, so nothing is lost now. If the property is ours, add the ID on its account.',
            not_final:
              'This month is not closed in HotelCMS yet, so the partial amount is not invoiced. Once closed, Atlas picks it up on the next early-morning fetch.',
            disabled:
              'This branch / module is switched off in HotelCMS, so it reports no amount. If the service is live, have it switched on in HotelCMS; if not, the assignment should be closed.',
            combined_minimum:
              'The property closes with a combined minimum in HotelCMS: the direct and inquiry branches are raw amounts here, and the billable amount is the larger branch or the shared minimum — only the "Direct + inquiry" (module total) row carries it. A per-branch assignment therefore produces no invoice: switch the assignment to Direct + inquiry.',
          },
          inactive: {
            assignment: 'the service assignment',
            account: 'the account',
            client: 'the client',
          },
          inactiveState: {
            assignment: 'the service assignment is inactive',
            pending: 'the service assignment is pending (not started yet)',
            account: 'the account is inactive',
            client: 'the client is inactive',
          },
        },
        branch: {
          direct: 'of which direct: {{amount}} {{currency}} · {{count}} bookings',
          inquiry: 'of which request: {{amount}} {{currency}} · {{count}} bookings',
        },
        prevMonth: 'Previous month',
        nextMonth: 'Next month',
        filterType: 'Type',
        filterAssignment: 'Assignment',
        assignmentFilter: {
          missing: 'Missing assignment',
        },
        amountParts: {
          registrants: 'base fee {{base}} + registrants {{measured}}',
          measured: 'base fee {{base}} + usage {{measured}}',
        },
        amountShared: 'registrant fee {{total}} split between {{count}} hotels',
        consumers: {
          title: 'Used by {{count}} assignments',
          unknownOwner: 'unknown account',
        },
        splitMismatch:
          'Splitting the registrant fee is not on for every hotel — the share is split fewer ways.',
        loading: 'loading…',
        countOf: '{{shown}} / {{total}} rows',
        noAccount: 'no resolved account',
        more: '+{{count}}',
        moreItems: 'Show more accounts',
        noMatches: 'No matches',
        noMatchesDesc: 'No usage rows match the filters. Adjust the filters.',
        empty: 'No usage data',
        emptyDesc:
          'No external-system usage data for this month. Usage fetch (external integration) is not active yet — historical (ETL) rows are shown.',
        columns: {
          sourceType: 'Type',
          source: 'Source ID / account',
          period: 'Period',
          measured: 'Received data',
          note: 'Note',
        },
        unit: {
          count: 'pcs',
          person: 'people',
        },
        amountNote: {
          no_rate: 'no rate for this month',
          aggregated: 'aggregated assignment',
          not_due: 'no billing period covers it',
          auto_billing_off: 'automatic invoicing is off',
          owner_inactive: 'client / account is inactive',
        },
        sourceType: {
          newsletter: 'Newsletter',
          loyalty_newsletter: 'Loyalty newsletter',
          rs_loyalty: 'RoomSome Loyalty',
          rs_direct_referral: 'RoomSome commission (direct + referral)',
          rs_direct: 'RoomSome commission (direct)',
          rs_referral: 'RoomSome commission (referral)',
          rs_voucher: 'RoomSome Voucher commission',
          ticket: 'Ticket commission',
        },
        idChip: 'ID',
        sourceSystemChip: {
          mailwizz: 'MailWizz ID',
          hotelcms: 'HotelCMS ID',
          zadircms: 'ZadirCMS ID',
        },
        amountPeriod: {
          quarterly: 'quarterly fee',
          semi_annual: 'semi-annual fee',
          annual: 'annual fee',
        },
        amountProrated: 'prorated',
        amountPeriodRange: 'Billed period: {{start}} – {{end}}',
        fetch: {
          action: 'Fetch',
          running: 'Fetching…',
          title: 'Fetch usage data from the external systems',
          done: '{{month}}: {{written}} rows updated',
          failedRows: '{{count}} rows could not be written — the next fetch retries them',
          empty: '{{month}}: no new data',
          skipped: 'Skipped: {{list}}',
          reason: {
            no_source_api: 'no data source',
            not_configured: 'not configured',
            upstream_error: 'the external system did not respond',
            period_unsupported:
              'only the current or previous month can be fetched (older months no longer give the real data)',
          },
        },
        rates: {
          title: 'Monthly manual rate',
          subtitle: 'EUR/USD → HUF effective on the 1st of the month (used by the generator).',
          none: 'Rate',
          missing: 'not set',
          editRate: 'Edit {{base}} rate',
          deleteRate: 'Delete {{base}} rate',
          addRate: 'Set {{base}} rate',
          cancel: 'Cancel',
          deleting: 'Deleting…',
          invalidHint: 'Numbers only, decimal comma or point (max. 8 decimals).',
          hint: 'Saved on the 1st of the month with source `manual` — the generator reads this.',
          base: 'Currency',
          rate: 'Rate',
          ratePlaceholder: 'e.g. 398.5',
          save: 'Save',
          saving: 'Saving…',
          readOnly: 'Changing a rate requires invoice management (viewing is open).',
        },
      },
      theme: {
        toggle: 'Change theme',
        title: 'Theme',
        light: 'Light',
        dark: 'Dark',
        system: 'Match system',
      },
      money: { net: 'net', vat: 'VAT', gross: 'gross' },
      impersonation: {
        rowAction: 'Sign in as {{name}}',
        noPermission: 'You do not have permission to impersonate',
        bannerTitle: 'You are acting as {{name}}',
        bannerHint:
          'Actions are logged under {{actor}}, and profile settings cannot be changed right now.',
        bannerHintNoActor:
          'Actions are logged under your real name, and profile settings cannot be changed right now.',
        stop: 'Back to my account',
        unknownSubject: 'the selected person',
        fencedHint:
          'These settings cannot be changed while impersonating — they are available again on your own account.',
      },
      portal: {
        brand: 'Client portal',
        nav: {
          aria: 'Portal navigation',
          home: 'Home',
          tickets: 'My tickets',
          messages: 'Messages',
          invoices: 'Services and fees',
          productUpdates: 'Product updates',
          profile: 'Profile',
          imprint: 'Imprint',
          gdpr: 'Privacy',
        },
        productUpdates: {
          subtitle: 'What we shipped and what we are working on',
          changelogTitle: 'What is new',
          changelogEmpty: 'No announced updates yet',
          changelogEmptyDesc: 'As soon as we release something new, you can read about it here.',
          roadmapTitle: 'What we are working on',
          roadmapHelp:
            'The order of planned updates is indicative — we do not promise exact deadlines.',
          roadmapEmpty: 'No shared plans',
          roadmapEmptyDesc: 'As soon as we have plans to announce, they appear here.',
          noDate: 'Date unknown',
          columnEmpty: 'Nothing here right now',
          noHorizon: 'Not scheduled yet',
          unavailableTitle: 'Product updates are unavailable right now',
          unavailableDesc:
            'We cannot load the list for technical reasons. Our colleagues already know — please check back later.',
        },
        brandTag: 'Partner',
        shell: {
          myAccounts: 'My accounts',
          allAccounts: 'All accounts',
          allAccountsHint: '{{count}} accounts combined',
          scopeLabel: 'Filtered: {{name}}',
          primaryContact: 'primary contact',
          switchAccount: 'Switch account',
          helpTitle: 'Need a hand?',
          helpBody: 'Open a ticket and our team replies within 4 working hours.',
          menu: 'Menu',
          breadcrumb: 'Breadcrumb',
          language: 'Language',
          notifications_one: 'Notifications ({{count}} unread)',
          notifications_other: 'Notifications ({{count}} unread)',
          notificationsTitle: 'Notifications',
          markAllRead: 'Mark all read',
          noNotifications: 'No notifications',
          noNotificationsDesc:
            'We let you know here when your ticket gets a reply, or a message or invitation arrives.',
          notificationsErrorTitle: 'We could not load your notifications',
          notificationsErrorDesc:
            'Please try again in a few seconds — your tickets and messages are unaffected.',
          notificationsLoading: 'Loading…',
          openMessages: 'Open messages',
        },
        profile: {
          subtitle: 'Your details, password and notification settings.',
          primaryBadge: 'primary contact',
          primaryShort: 'primary',
          accountCount_one: '{{count}} account',
          accountCount_other: '{{count}} accounts',
          accountsHint: 'To change account access, open a ticket or contact your account manager.',
          legalTitle: 'Legal',
          imprintSub: 'Provider details and contact information',
          gdprSub: 'What data we process and what rights you have',
        },
        // ── LEGAL PAGES (the Hungarian original is the authoritative text; see the note there) ──
        legal: {
          imprint: {
            title: 'Imprint',
            updated: 'Last updated: 2 September 2026',
            blocks: [
              {
                h: 'The operator',
                p: ['The atlas.morgens.hu partner portal is operated by MORGENS Design Kft.'],
              },
              {
                h: 'Company details',
                p: [
                  'Company name: MORGENS Design Marketing Tanácsadó és Informatikai Szolgáltató Kft.',
                  'Short name: MORGENS Design Kft.',
                  'Registered seat: 8800 Nagykanizsa, Magyar utca 79., Hungary',
                  'Authorised representative: Zoltán Katona, managing director',
                  'Statistical code: 23964710-6201-113-20',
                  'VAT number: 23964710-2-20',
                  'Company registration number: 20-09-072782 (registered by the Company Court of the Zalaegerszeg Regional Court)',
                  'Bank account number: 10104985-28145400-01005004',
                  'The account is held at Budapest Bank Zrt.',
                ],
              },
              {
                h: 'Contact',
                p: [
                  'General information: +36 30 648 0047',
                  'E-mail address: sales@morgens.hu',
                  'Electronic contact of the company: sales@morgens.hu',
                ],
              },
              {
                h: 'E-mail delivery',
                p: [
                  'Outgoing e-mails of the partner portal (notifications, ticket replies, invoice notices) are delivered by Amazon SES, a service of Amazon Web Services EMEA SARL, from a European region.',
                ],
              },
            ],
          },
          gdpr: {
            title: 'Privacy',
            updated: 'Last updated: 2 September 2026',
            blocks: [
              {
                h: 'Privacy notice of MORGENS Design Kft.',
                p: [
                  'We store your data only for specified and lawful purposes and do not use it in any other way.',
                  'We process your personal data fairly and lawfully.',
                  'We take appropriate security measures to protect the personal data stored in our records, preventing unlawful access, alteration or disclosure.',
                ],
              },
              {
                h: 'In particular',
                ordered: true,
                p: [
                  'Your personal data may be processed by the operator of the atlas.morgens.hu website (the “Website”), MORGENS Design Kft. (seat: 8800 Nagykanizsa, Magyar utca 79.; reg. no.: 20-09-072782; VAT no.: 23964710-2-20; the “Operator”); we disclose it to third parties only as set out in this notice. Our data processing registration number: NAIH-85268/2015.',
                  'Providing data is voluntary and based on your consent (legal basis). By registering, written consent to the processing of the data is deemed granted. Data is processed and transmitted automatically, through a closed system, without human intervention.',
                  'The personal data you provide in a form (depending on the form: name, company name, phone number, e-mail address) is recorded in our system. We record this data solely for ticketing purposes.',
                  'The purpose of the processing is to keep in touch and provide information in connection with answering enquiries submitted through the Website, and to manage the database used for sending our newsletter and to send it. The personal data you provide is retained until you expressly request that its processing be discontinued.',
                  'We collect no personal data about visitors browsing the Website. While browsing, only anonymous information is recorded about which subpages visitors view and which features they use or attempt to use. This data helps us operate and improve the site and keep it running smoothly.',
                  'You may request free written information at any time about your data, its source, the purpose, legal basis and duration of the processing, the name and address of the data processor and its activities related to the processing, and — in the case of a transfer of your personal data — the legal basis and recipient of the transfer. Our contact details: MORGENS Design Kft. customer service, e-mail: sales@morgens.hu, address: 8800 Nagykanizsa, Magyar utca 79., phone: +36 30 648 0047.',
                  'You may also request the erasure, blocking or rectification of your data at the contact details above, object to the processing of your data, request information about the processing, and object to the processing of personal data. Please note that we fulfil requests for information, erasure, blocking or rectification free of charge and in writing, within the shortest possible time and no later than 10 working days from receipt.',
                  'Please also note that monitoring and promoting the enforcement of the right to the protection of personal data is the task of the Hungarian National Authority for Data Protection and Freedom of Information (1055 Budapest, Falk Miksa utca 9–11.), an independent authority subordinate only to the law.',
                  'If your rights are infringed, you may turn to the National Authority for Data Protection and Freedom of Information (1055 Budapest, Falk Miksa utca 9–11.) or to a court against the Operator or the recipient of the data. The court handles such cases as a priority.',
                  'The registration and processing of personal data is carried out under Act CXII of 2011 on the right of informational self-determination and freedom of information, and Act CXIX of 1995 on the processing of name and address data for the purposes of research and direct marketing.',
                  'This privacy policy may be amended by the Operator at any time; we will inform you in a newsletter or in a pop-up window at sign-in. By continuing to use the service you automatically acknowledge the amended privacy policy; no further consent is required.',
                ],
                links: [
                  {
                    label: 'Act CXII of 2011',
                    url: 'https://net.jogtar.hu/jogszabaly?docid=a1100112.tv',
                  },
                  {
                    label: 'Act CXIX of 1995',
                    url: 'https://net.jogtar.hu/jogszabaly?docid=99500119.tv',
                  },
                ],
              },
              {
                h: 'Data processor — e-mail delivery',
                p: [
                  'Outgoing e-mails of the system (ticket notifications, ticket replies, invoice notices, newsletter) are delivered by Amazon SES, a service of Amazon Web Services EMEA SARL, from a European region. The processor handles the data required for delivery (recipient e-mail address, message content) and uses it for no other purpose.',
                ],
              },
              {
                h: 'Contact',
                p: [
                  'If you have any questions or comments about data processing, please contact our colleagues at sales@morgens.hu.',
                ],
              },
              {
                h: 'Dated',
                p: ['Nagykanizsa, 3 May 2018 · Last updated: 2 September 2026'],
              },
            ],
          },
        },

        tickets: {
          title: 'My tickets',
          subtitle: 'Support tickets for your accounts',
          tabs: { in_progress: 'In progress', closed: 'Closed' },
          columns: {
            ticket: 'Ticket',
            account: 'Account',
            requester: 'Requester',
            assignee: 'Assignee',
            lastActivity: 'Last activity',
            status: 'Status',
            ticketNumber: 'Ticket number',
          },
          submittedAt: 'Submitted',
          closedAt: 'Closed',
          columnsLastActivity: 'Last activity',
          searchPlaceholder: 'Search ticket number, subject, requester…',
          searchLabel: 'Search tickets',
          filters: 'Filters',
          clearFilters: 'Clear',
          allAccounts: 'All accounts',
          unread_one: '{{count}} new reply',
          unread_other: '{{count}} new replies',
          unreadShort_one: '{{count}} new',
          unreadShort_other: '{{count}} new',
          commentCount_one: '{{count}} comment',
          commentCount_other: '{{count}} comments',
          countShort_one: '{{count}} ticket',
          countShort_other: '{{count}} tickets',
          empty: {
            in_progress: {
              title: 'No tickets in progress',
              desc: 'If you have a question or an issue, open a ticket and our team will get back to you.',
            },
            closed: {
              title: 'No closed tickets yet',
              desc: 'Your closed requests will be available here for reference.',
            },
          },
          emptyFiltered: {
            title: 'No results',
            desc: 'Try changing your search or clearing the filters.',
          },
          count: '{{shown}} / {{total}} tickets',
          backToList: 'Back to the list',
          detailFallback: 'Ticket',
          request: 'Request',
          history: 'History',
          onUs: 'we are on it',
          awaitingReply: 'awaiting reply',
          access: 'Ticket access',
          accessDisabled:
            'Only the person who opened the ticket and the account’s primary contact can share access.',
          accessHint:
            'Choose which colleagues may access this ticket — they are notified about it. Revoking access takes effect immediately.',
          accessAlways: 'sees everything',
          accessNoContacts: 'There is no other contact on this account.',
          accessOnlyYou: 'Only you can see this ticket.',
          field: {
            assignee: 'Assignee',
          },
          event: {
            opened: 'Ticket opened',
            reply: 'Reply in the thread',
            status: 'Status changed',
            closed: 'Ticket closed',
          },
          description: 'Description',
          attachments: 'Attachments',
          noAttachments: 'No attachments',
          download: 'Download',
          downloadUnavailable: 'The file is unavailable',
          attachFile: 'Attach file',
          attachUploading: 'Uploading…',
          attachmentsEmpty: 'No attachments yet.',
          links: 'File sharing links',
          linkLabel: {
            googleDrive: 'Google Drive',
            onedrive: 'OneDrive',
            dropbox: 'Dropbox',
          },
          conversation: 'Conversation ({{count}})',
          conversationAria: 'Conversation',
          noComments: 'No replies on this ticket yet.',
          you: 'You',
          newReply: 'new',
          readOnly: 'You only have view access on this account, so you cannot reply.',
          filterFrom: 'Created from',
          filterTo: 'Created to',
          filterClosedFrom: 'Closed from',
          filterClosedTo: 'Closed to',
          comments: 'Comments',
          commentCollapsed: 'Write a comment…',
          commentPlaceholder: 'Write your comment here…',
          commentLabel: 'Comment text',
          commentSend: 'Send comment',
          commentHint: 'Our support team is notified about your comment.',
          visibilityTitle: 'Who can see this ticket',
          visibilityHint:
            'This ticket is visible to you, the account’s primary contact and the colleagues listed here — beyond them, only the MORGENS support team.',
          visibilityUnknown:
            'The list of people with access is visible to the account’s primary contact.',
          accessManage: 'Access',
          noAssignee: 'not assigned yet',
          sending: 'sending…',
          editReply: 'Edit',
          editReplyLabel: 'Edit reply',
          cancel: 'Cancel',
          save: 'Save',
          saving: 'saving…',
          notFound: {
            title: 'Ticket not found',
            desc: 'It may have been deleted, or you do not have access to this ticket.',
          },
        },
        invoices: {
          subtitle: 'Your invoices and active services with next month’s expected fee.',
          tabs: { issued: 'Issued', paid: 'Settled', storno: 'Voided' },
          columns: {
            number: 'Invoice number',
            buyer: 'Billing details',
            amount: 'Amount',
            dates: 'Dates',
            pdf: 'PDF',
          },
          status: {
            draft: 'draft',
            issued: 'issued',
            paid: 'paid',
            overdue: 'overdue',
            cancelled: 'cancelled',
            storno: 'voided',
          },
          issued: 'Issued:',
          due: 'Due:',
          paid: 'Paid:',
          searchPlaceholder: 'Invoice number or buyer…',
          searchLabel: 'Search invoices',
          count_one: '{{count}} invoice',
          count_other: '{{count}} invoices',
          downloadAria: 'Download {{number}} as PDF',
          overdueTitle_one: 'You have {{count}} overdue invoice',
          overdueTitle_other: 'You have {{count}} overdue invoices',
          overdueTotal:
            '— {{amounts}} in total. Please settle it, or tell us if you see a discrepancy.',
          showOverdue: 'View',
          overdueSince_one: '{{count}} day overdue',
          overdueSince_other: '{{count}} days overdue',
          dueIn_one: 'due in {{count}} day',
          dueIn_other: 'due in {{count}} days',
          empty: {
            title: 'No invoices in this view',
            issued: 'When a new invoice is issued it will appear here, and we will email you too.',
            paid: 'No settled invoice in this view yet.',
            storno: 'You had no voided invoices — that is good news.',
          },
          myServices: 'My services',
          itemCount_one: '{{count}} item',
          itemCount_other: '{{count}} items',
          activeAssignments: 'Active assignments',
          pending: 'pending',
          usageBased: 'usage based',
          usageAmount: 'by usage',
          period: { monthly: 'month', yearly: 'year', one_time: 'one-off', recurring: 'month' },
          noServices: {
            title: 'No active services',
            desc: 'When we start a service on your account it will appear here.',
          },
          nextMonth: 'Next month’s expected fee',
          nextMonthOverline: '{{month}} · expected',
          noExpected: 'No fixed-fee item is due next month.',
          variableWarn: 'The amount may vary based on usage — the final value is set at month end.',
          fxNote:
            'Foreign-currency items are shown in forint at the MNB mid rate, for information only.',
        },
        // ── BILLING (monthly overview · my invoices · my services) ──
        billing: {
          title: 'Services and fees',
          subtitle:
            'See month by month what we have already invoiced (actual) and what is still due (plan) — item by item.',
          views: {
            aria: 'Billing views',
            month: 'Monthly overview',
            invoices: 'Invoices',
            services: 'Services',
          },
          month: {
            picker: 'Month picker',
            basis: { actual: 'Actual', plan: 'Plan', mixed: 'Mixed' },
            basisHint: {
              actual: 'Everything in this month has been invoiced.',
              plan: 'There is no invoice for this month yet — the items are planned.',
              mixed: 'This month has invoiced items and items that are still planned.',
            },
            totalsTitle: 'Net total of the month',
            invoicesTitle: 'Invoices',
            planTitle: 'Not yet invoiced (plan)',
            planHint:
              'An invoice is expected for these items in this month — you pay them in the month of invoicing.',
            noInvoices: 'There are no invoices in this month.',
            empty: {
              title: 'No billing in this month',
              desc: 'There is neither an issued invoice nor a due item for this month.',
            },
          },
          invoice: {
            draft: 'Being prepared',
            draftHint:
              'The invoice is still being prepared — the PDF is available once it is issued.',
            showItems: 'Items and note',
            hideItems: 'Hide items',
            noItems: 'This invoice has no items.',
            note: 'Note printed on the invoice',
            itemsAria: 'Invoice items',
            quantity: 'Quantity',
            unitPrice: 'Unit price',
            period: 'Period',
            details: 'Items',
            detailsAria: 'Items of {{number}}',
            sheetTitle: 'Invoice items',
          },
          plan: {
            usageAmount: 'by usage',
            estimate: 'estimate',
            estimateHint: 'based on the previous invoice',
            prorated: 'prorated',
          },
          hiddenShared: {
            notice_one:
              '{{count}} more group item also applies to your account, but it covers accounts you have no fee access to, so you cannot view it.',
            notice_other:
              '{{count}} more group items also apply to your account, but they cover accounts you have no fee access to, so you cannot view them.',
            noticeInvoices_one:
              '{{count}} group invoice also applies to your account, but it covers accounts you have no fee access to, so you cannot view it.',
            noticeInvoices_other:
              '{{count}} group invoices also apply to your account, but they cover accounts you have no fee access to, so you cannot view them.',
            totalsVisible: 'Net total of the items you can view',
            restrictedTitle: 'Billed as a group',
            restrictedAmount: 'Billed as a group',
            restrictedHint:
              'The fee is not shown because the service also covers accounts you have no fee access to.',
          },
          shared: {
            title_one: 'Shared billing — applies to {{count}} accounts together',
            title_other: 'Shared billing — applies to {{count}} accounts together',
            accounts: 'Accounts: {{names}}',
            other_one: '+{{count}} more account',
            other_other: '+{{count}} more accounts',
          },
          frequency: {
            monthly: 'monthly',
            quarterly: 'quarterly',
            semi_annual: 'semi-annual',
            annual: 'annual',
            one_time: 'one-time',
          },
          per: {
            monthly: '/month',
            quarterly: '/quarter',
            semi_annual: '/half-year',
            annual: '/year',
            one_time: 'one-time',
          },
          services: {
            countBadge_one: '{{count}} service',
            countBadge_other: '{{count}} services',
            usageBased: 'usage-based',
            pending: 'pending',
            usageAmount: 'by usage',
            empty: {
              title: 'You have no services',
              desc: 'When we start a service on your account, it will appear here.',
            },
            periodsAria: 'Periods of {{name}}',
          },
        },
        home: {
          greeting: 'Welcome, {{name}}!',
          greetingNoName: 'Welcome!',
          waiting_one: 'You have a new reply on {{count}} ticket.',
          waiting_other: 'You have new replies on {{count}} tickets.',
          allClear:
            'Nothing to do — all your tickets are up to date. If you have a question, open a ticket.',
          tiles: {
            openTickets: 'Tickets in progress',
            unreadTickets: 'New replies on my tickets',
            messages: 'Messages and invitations',
            overdueInvoices: 'Overdue invoices',
          },
          recentTickets: 'My recent tickets',
          viewAll: 'View all',
          meetings: 'Upcoming meetings',
          allAccountsNote: 'all accounts',
          unreadMessages: 'Unread messages',
          feeUnavailable: 'The expected fee is unavailable right now.',
          feeItems_one: '{{count}} item due',
          feeItems_other: '{{count}} items due',
          noOpen: {
            title: 'No tickets in progress',
            desc: 'All your requests are closed.',
          },
          noUnread: {
            title: 'No unread messages',
            desc: 'Earlier announcements stay available on the Messages page.',
          },
          noMeetings: {
            title: 'No upcoming meetings',
            desc: 'When we schedule a meeting with you, it shows up here — and you can answer it here.',
          },
        },
        newTicket: {
          action: 'New ticket',
          title: 'Open a ticket',
          subtitle: 'Three short steps — the more precise the description, the faster we can help.',
          stepsAria: 'Steps',
          steps: { where: 'Where?', what: 'About?', details: 'Details' },
          client: 'Client',
          clientHint: 'The client you represent — the ticket belongs to this client.',
          hasAccess: 'you have access',
          whichAccount: 'Which account is affected?',
          youArePrimary: 'you are the primary contact',
          viewerOnly: 'view only',
          servicesOfAccount: 'My services',
          otherServices: 'Our other services',
          activeService: 'active',
          noServices: 'We found no selectable service for this account.',
          type: 'Ticket type',
          subject: 'Subject',
          subjectPlaceholder: 'A short, descriptive summary',
          description: 'Request',
          descriptionTip:
            'Describe in as much detail as you can what happened, after which steps, and on which device. Attaching a screenshot speeds up the fix a lot.',
          descriptionPlaceholder: 'What happened? After which steps? On which device / browser?',
          descriptionOk: 'Thank you, this is detailed enough.',
          descriptionRemaining_one: '{{count}} more character needed',
          descriptionRemaining_other: '{{count}} more characters needed',
          attachmentsAdd: 'Attach file',
          attachmentsRemove: 'Remove {{name}}',
          linksHint: 'If you share large files, paste the link here.',
          linkPlaceholder: 'https://…',
          prev: 'Back',
          next: 'Next',
          submit: 'Create ticket',
          errors: {
            service: 'Please choose a service.',
            subject: 'The subject is required.',
            subjectMax: 'At most {{max}} characters.',
          },
          readOnly: {
            title: 'You cannot open a ticket',
            desc: 'You only have view access on your accounts. Ask the primary contact of the account to grant you edit rights.',
          },
          unavailable: {
            title: 'Opening a ticket is unavailable right now',
            desc: 'We cannot load your services for technical reasons, so a new ticket cannot be opened at the moment. We are working on it — please try again later or email us.',
          },
        },
        status: {
          open: 'Open',
          in_progress: 'In progress',
          resolved: 'Resolved',
          closed: 'Closed',
          reopened: 'Reopened',
        },
        statusHelp: {
          open: 'We have received your ticket and will look into it soon.',
          in_progress: 'A colleague is working on it.',
          resolved: 'Solved: the bug was fixed or the request fulfilled.',
          closed: 'Closed without a fix (e.g. a duplicate or not reproducible report).',
          reopened: 'The ticket was reopened and we are working on it again.',
        },
        type: {
          support: 'Support',
          bug: 'Bug report',
          feature_request: 'Feature request',
          billing: 'Billing',
          other: 'Other',
        },
        // ── MESSAGES AND INVITATIONS ──
        messages: {
          subtitle: 'Messages we sent you and your upcoming meeting invitations.',
          meetingsTitle: 'My upcoming meetings',
          awaitingAnswer_one: '{{count}} awaiting your answer',
          awaitingAnswer_other: '{{count}} awaiting your answer',
          inboxTitle: 'Inbox',
          unreadCount_one: '{{count}} unread',
          unreadCount_other: '{{count}} unread',
          scope: { broadcast: 'announcement', account: 'to your account', contact: 'to you' },
          organizer: 'Organiser:',
          minutes_one: '{{count}} minute',
          minutes_other: '{{count}} minutes',
          rsvp: { pending: 'awaiting answer', confirmed: 'attending', declined: 'not attending' },
          willAttend: 'I will attend',
          wontAttend: 'I cannot attend',
          changeAnswer: 'Change answer',
          markAllRead: 'Mark all read',
          noMeetings: {
            title: 'No upcoming meetings',
            desc: 'When we schedule a meeting with you, it will show up here.',
          },
          noMessages: {
            title: 'No messages',
            desc: 'The notices we send by e-mail also appear here.',
          },
          archivedView: 'Archived',
          archivedTitle: 'Archived messages',
          archive: 'Archive',
          unarchive: 'Restore',
          noArchived: {
            title: 'No archived messages',
            desc: 'Archived messages appear here and can be restored from here.',
          },
          unavailable: {
            title: 'Messages are unavailable right now',
            desc: 'We cannot load your messages for technical reasons. We are on it — please try again later.',
          },
        },
        loadError: {
          title: 'Could not load',
          desc: 'A temporary connection error occurred. Please try again.',
        },
        noAccess: {
          title: 'You have no portal access',
          desc: "Ask your account's primary contact to grant you access.",
          pageTitle: 'You have no access to this page',
          pageDesc:
            "Your role does not include this feature. Ask your account's primary contact to grant you access.",
          scopeDesc:
            "You have no access to this page on the selected account. Pick another account, or ask the account's primary contact.",
        },
      },
      auth: { logout: 'Log out' },
      common: {
        unsavedChanges: {
          title: 'Discard your changes?',
          desc: 'The form has unsaved changes. They will be lost if you close it now.',
          discard: 'Discard',
          keepEditing: 'Keep editing',
        },
        deletedUser: 'Deleted user',
        deletedAccount: '(deleted account)',
        dangerZone: 'Danger zone',
        pageNotices: {
          title: 'Information',
          count: {
            danger_one: '{{count}} critical',
            danger_other: '{{count}} critical',
            warning_one: '{{count}} warning',
            warning_other: '{{count}} warnings',
            neutral_one: '{{count}} note',
            neutral_other: '{{count}} notes',
          },
        },
        searchPlaceholder: 'Search…',
        appSelector: 'Application',
        error: 'Error',
        notAuthorized: 'Not authorized',
        edit: 'Edit',
        details: 'Details',
        yes: 'Yes',
        no: 'No',
        listTruncated: 'The list is truncated (too many matches) — narrow it with the filters.',
        save: 'Save',
        saving: 'Saving…',
        cancel: 'Cancel',
        delete: 'Delete',
        actions: 'Actions',
        close: 'Close',
        comingSoon: 'Coming soon',
        selected: 'selected',
        all: 'All',
        slugHint: 'Identifier (slug)',
        none: 'None',
        select: 'Select…',
        clear: 'Clear',
        edited: 'Edited',
        editedAt: 'Edited: {{date}}',
        time: 'Time',
        datePlaceholder: 'YYYY-MM-DD',
        timePlaceholder: 'HH:MM',
        passwordPolicyHint:
          'At least 12 characters, with upper and lower case letters and a number.',
        passwordPolicy: {
          too_short: 'The password is too short — at least 12 characters are required.',
          too_long: 'The password is too long (200 characters at most).',
          missing_uppercase: 'At least one upper case letter is required.',
          missing_lowercase: 'At least one lower case letter is required.',
          missing_digit: 'At least one digit is required.',
          invalid: 'The password does not meet the security requirements.',
        },
        calendar: {
          prevMonth: 'Previous month',
          nextMonth: 'Next month',
          prevYear: 'Previous year',
          nextYear: 'Next year',
          prevYears: 'Previous years',
          nextYears: 'Next years',
          pickMonth: 'Pick a month',
          pickYear: 'Pick a year',
          pickDay: 'Pick a day',
        },
        loading: 'Loading…',
        noResults: 'No results',
        noAccessTitle: 'No access',
        noAccessDesc:
          "You don't have permission to view this. If you need access, contact your administrator.",
        loadErrorTitle: "Couldn't load",
        loadErrorDesc: 'Something went wrong. Please try again.',
        retry: 'Try again',
        filters: 'Filters',
        clearFilters: 'Clear filters',
        openFilteredList: 'Open detailed view',
        prevMonth: 'Previous month',
        nextMonth: 'Next month',
        month: 'Month',
        activeFilters: '{{count}} active filter',
        selectedCount: '{{count}} selected',
        noCreatePermission: 'You do not have permission to create.',
        export: {
          label: 'Export',
          xlsx: 'Excel (.xlsx)',
          csv: 'CSV (.csv)',
          json: 'JSON (.json)',
          pending: 'Exporting…',
          pendingHint: 'Export in progress',
          emptyHint: 'Nothing to export',
          done: '{{count}} rows exported',
          partial: 'Only {{count}} rows exported — the list could not be loaded completely',
          empty: 'Nothing to export',
          failed: 'Export failed',
        },
        // The bilingual-field language suffix (sr-only, next to the language flag). hu/en is the FIELD's language.
        langSuffix: { hu: '(Hungarian)', en: '(English)' },
      },
      login: {
        title: 'Welcome back!',
        subtitle: 'Sign in to your Atlas account.',
        email: 'Email address',
        password: 'Password',
        forgot: 'Forgot password?',
        remember: 'Remember me',
        submit: 'Sign in',
        submitting: 'Signing in…',
        errInvalid: 'Invalid email or password',
        errNoAccess:
          'The password is correct, but this account does not have Atlas access yet. Ask an administrator for permissions.',
        errFailed: 'Login failed',
        errThrottled: 'Too many attempts. Please wait a few minutes and try again.',
        errUnavailable:
          'Sign-in is temporarily unavailable (the server is not responding). Try again in a few seconds.',
        mfaLabel: 'Two-factor code',
        mfaPlaceholder: '123456',
        mfaHint: 'Enter the 6-digit code from your authenticator app, or a recovery code.',
        mfaInvalid: 'Invalid or expired code',
        heroEyebrow: 'Internal platform',
        heroLine1: 'One platform for',
        heroLine2: 'every client interaction.',
        heroBody: 'CRM, ticketing, asset inventory, billing and integrations — all in one place.',
      },
      forgotPassword: {
        title: 'Forgot your password?',
        subtitle: 'Enter your email address and we will send you a link to set a new password.',
        email: 'Email address',
        submit: 'Send link',
        submitting: 'Sending…',
        backToLogin: 'Back to sign in',
        sentTitle: 'Check your inbox',
        sentSubtitle: 'We have sent the email.',
        sentBody:
          'If an account exists for the address you entered, we have sent it a link to set a new password. The link is valid for 60 minutes.',
        sentHint:
          'If nothing arrives within a few minutes, check your spam folder and make sure the address was typed correctly.',
        retry: 'Try a different email address',
        errRateLimited: 'Too many attempts. Please wait a few minutes and try again.',
        errUnavailable:
          'Password reset is currently unavailable. Please try again later, or let a colleague know.',
      },
      resetPassword: {
        title: 'Set your password',
        subtitle: 'Choose a new password for your account.',
        newPassword: 'New password',
        confirm: 'Confirm new password',

        mismatch: 'The two passwords do not match.',
        submit: 'Set password',
        submitting: 'Saving…',
        success: 'Your password is set — sign in with your new password.',
        backToLogin: 'Back to sign in',
        invalidTitle: 'This link is no longer valid',
        invalidSubtitle: 'Request a new one — it only takes a moment.',
        invalidBody:
          'The password link is invalid or has expired. This also happens if you have already used it, or if a newer link was requested in the meantime. Request a new email and try again.',
        requestNew: 'Request a new link',
        errRateLimited: 'Too many attempts. Please wait a few minutes and try again.',
        errUnavailable: 'Setting your password is currently unavailable. Please try again later.',
      },
      dashboard: {
        greeting: {
          morning: 'Good morning!',
          afternoon: 'Good afternoon!',
          evening: 'Good evening!',
          morningNamed: 'Good morning, {{name}}!',
          afternoonNamed: 'Good afternoon, {{name}}!',
          eveningNamed: 'Good evening, {{name}}!',
        },
        kpi: {
          clients: 'Clients',
          accounts: 'Accounts',
          openTickets: 'Open tickets',
          mrr: 'Monthly revenue (MRR)',
          mrrChange: '{{pct}} vs. previous month',
        },
        openAll: 'open all',
        pinnedMessage: {
          title: 'Pinned announcement',
          pinnedAt: 'pinned: {{date}}',
          more: '+{{count}} more pinned',
        },
        myWork: {
          title: 'Waiting on you',
          empty: 'Nothing is waiting on you.',
          items: {
            myTickets: 'Open tickets assigned to you',
            rsvp: 'Meetings awaiting your reply',
            assetRequests: 'Asset requests awaiting a decision',
            draftInvoices: 'Drafts awaiting issuing',
          },
        },
        tiles: {
          pinnedMessages: {
            title: 'Pinned announcement',
            description: 'Pinned announcements addressed to me. Mandatory: it can only be moved.',
          },
          kpiClients: {
            title: 'Clients',
            description: 'The number of clients, one click from the list.',
          },
          kpiAccounts: {
            title: 'Accounts',
            description: 'The number of accounts, one click from the list.',
          },
          kpiOpenTickets: {
            title: 'Open tickets',
            description: 'The number of active tickets; opens the “In progress” list.',
          },
          kpiMrr: {
            title: 'Monthly revenue (MRR)',
            description: 'Monthly recurring revenue with the change and a 12-month trend.',
          },
          finance: {
            title: 'Finance',
            description: 'Overdue and awaiting invoices, and this month’s issuing.',
          },
          meetings: {
            title: 'Upcoming meetings',
            description: 'Meetings from today on in an agenda view, with RSVP.',
          },
          celebrations: {
            title: 'Celebrations',
            description: 'Birthdays, name days, anniversaries and a welcome for new colleagues.',
          },
          attention: {
            title: 'Needs attention',
            description:
              'Expiring cards, missing usage data, assignments about to start, generation skips.',
          },
          myTickets: {
            title: 'My tickets',
            description: 'The active tickets assigned to you — which ones are waiting.',
          },
          notes: { title: 'Notes', description: 'A personal text note, saved automatically.' },
        },
        newMenu: {
          label: 'New …',
          items: {
            client: 'New client',
            account: 'New account',
            ticket: 'New ticket',
            meeting: 'New meeting',
            invoice: 'New invoice',
          },
        },
        editor: {
          customize: 'Customize',
          unavailable: 'The layout cannot be loaded right now, so it cannot be customized.',
          hint: 'Drag the tiles by their handle, or use the arrows. Cancel undoes everything.',
          draggable: 'draggable tile',
          dragHandle: 'Move {{tile}}',
          sizeLabel: '{{tile}} size',
          sizeSmall: 'Small',
          sizeMedium: 'Medium',
          sizeWide: 'Wide',
          moveUp: 'Move {{tile}} up',
          moveDown: 'Move {{tile}} down',
          remove: 'Remove {{tile}}',
          mandatoryHint: 'Mandatory tile: it can be moved but not removed.',
          addTile: 'Add tile',
          reset: 'Restore default',
          cancel: 'Cancel',
          save: 'Save',
          moreActions: 'More actions',
          emptyTitle: 'No tiles placed',
          emptyDescription: 'Add a tile with the “Add tile” button.',
          listLabel: 'Dashboard tiles',
          resetTitle: 'Restore the default?',
          resetDescription:
            'Your layout is deleted and the default tiles show again — you also get any new default tiles.',
          resetConfirm: 'Restore',
          leaveTitle: 'Discard your changes?',
          leaveDescription: 'The tile changes have not been saved yet.',
          leaveConfirm: 'Discard and leave',
          leaveStay: 'Stay',
          a11y: {
            instructions:
              'Press space to lift a tile, use the arrow keys to move it, space to drop it, Escape to cancel.',
            lifted: '{{tile}} lifted, position {{position}}.',
            moved: '{{tile}} moved to position {{position}}.',
            dropped: '{{tile}} dropped at position {{position}}.',
            cancelled: '{{tile}} returned to its original position.',
          },
        },
        gallery: {
          title: 'Add tile',
          emptyTitle: 'Every available tile is on the page',
          emptyDescription: 'There are no more tiles you can add.',
          categories: {
            kpi: 'Indicators',
            work: 'Work',
            finance: 'Finance',
            team: 'Team',
            personal: 'Personal',
          },
          new: 'new',
          add: 'Add',
          addTile: 'Add {{tile}}',
        },
        celebrations: {
          window: '{{count}} days',
          windowLabel: 'Look-ahead',
          calendarToday: 'Name day today: {{names}}',
          calendarMore: '+{{count}}',
          empty: 'No celebrations in the next {{count}} days.',
          today: 'Today',
          upcoming: 'Upcoming',
          tomorrow: 'tomorrow',
          showAll: 'Show all {{count}}',
          showLess: 'Show less',
          kind: {
            birthdayToday: 'Happy birthday!',
            birthday: 'Birthday',
            nameDayToday: 'Happy name day!',
            nameDay: 'Name day',
            anniversaryToday_one: '{{count}}-year anniversary — thank you for being with us!',
            anniversaryToday_other: '{{count}}-year anniversary — thank you for being with us!',
            anniversary_one: '{{count}}-year anniversary',
            anniversary_other: '{{count}}-year anniversary',
            newJoinerToday: 'New colleague — welcome!',
            newJoinerPast_one: 'joined {{count}} day ago',
            newJoinerPast_other: 'joined {{count}} days ago',
            childBirthdayToday: '{{child}}’s birthday is today',
            childBirthday: '{{child}}’s birthday',
          },
        },
        myTickets: { empty: 'No active tickets assigned to you.' },
        notes: {
          placeholder: 'Jot something down — only you can see it.',
          unavailableImpersonated: 'The note is not available while impersonating.',
          saving: 'Saving…',
          saved: 'Saved',
          saveFailed: 'Saving failed',
          saveError: 'The note could not be saved — try again.',
          forbidden: 'The note cannot be saved while impersonating.',
          conflict: 'Your note was changed elsewhere.',
          keepMine: 'Keep mine',
          loadTheirs: 'Load the other one',
          counter: '{{count}} / {{max}}',
        },
        attention: {
          title: 'Needs attention',
          expiringCards_one: '{{count}} payment card expires within 60 days',
          expiringCards_other: '{{count}} payment cards expire within 60 days',
          usageMissing: 'No usage data fetched for {{month}}',
          pendingStartingSoon_one: '{{count}} pending assignment starting soon',
          pendingStartingSoon_other: '{{count}} pending assignments starting soon',
          generationSkipsOpen_one: '{{count}} generation skip this month',
          generationSkipsOpen_other: '{{count}} generation skips this month',
        },
        finance: {
          title: 'Finance',
          overdue: 'Overdue invoices',
          awaiting: 'Awaiting payment',
          issuedThisMonth: 'Issued this month',
          count: '{{count}}',
        },
        meetings: {
          title: 'Upcoming meetings',
          new: 'New meeting',
          empty: 'No upcoming meetings.',
          untitled: 'Untitled',
          untitledDay: 'No date',
          today: 'Today',
          tomorrow: 'Tomorrow',
          now: 'now',
          participantCount: '{{count}} participants',
        },
      },
      clients: {
        // Form field labels
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        status: 'Status',
        autoBilling: 'Auto-billing',
        autoBillingHint:
          'Includes this client in monthly invoice generation. Requires a primary billing profile.',
        autoBillingBlocked: {
          create:
            'Can be enabled after the client is created: invoices are issued from a primary billing profile, and a profile can only be attached to an existing client.',
          noPrimaryProfile:
            'No primary billing profile — add one in the “Billing profiles” section below, then you can enable this.',
        },
        statusLabel: { active: 'active', pending: 'pending', inactive: 'inactive' },
        // Column headers (list)
        columns: {
          client: 'Client',
          email: 'Email',
          phone: 'Phone',
          accounts: 'Accounts',
          billingData: 'Billing data',
          autoBilling: 'Auto-billing',
          status: 'Status',
          actions: 'Actions',
        },
        // In-cell dropdown (Accounts / Billing data)
        more: '+{{count}}',
        moreItems: 'Show more items',
        // Toolbar / list
        filterStatus: 'Status',
        loading: 'loading…',
        countOf: '{{shown}} / {{total}} clients',
        // States
        empty: 'No clients',
        emptyDesc: 'No clients have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No clients match your search.',
        // Actions / buttons
        new: 'New client',
        create: 'Create client',
        createSubtitle: 'Add a new client with its basic details.',
        // Related sections, LOCKED while creating (the legacy CRM tabs; documents intentionally dropped)
        pendingAccounts: 'Accounts',
        pendingAccountsDesc: 'Hotels and other accounts that belong to this client.',
        pendingServices: 'Services',
        pendingServicesDesc: 'Services assigned at client level, with their prices.',
        pendingPaymentCards: 'Payment cards',
        pendingPaymentCardsDesc: 'Cards stored for billing (last 4 digits only).',
        pendingBillingProfiles: 'Billing profiles',
        pendingBillingProfilesDesc: 'Billing names and addresses; one of them is primary.',
        pendingHint: 'Available once you create the client.',
        edit: 'Edit',
        editSubtitle: 'Edit the client basic details.',
        view: 'View',
        save: 'Save',
        saving: 'Saving…',
        back: 'Back to clients',
        cancel: 'Cancel',
        delete: 'Delete',
        // Detail / sections
        sectionData: 'Basic details',
        sectionBilling: 'Billing',
        billingPlaceholder: 'Billing settings appear once the billing module lands.',
        // The FOUR related sections on the detail page — same order and titles as the locked sections on
        // the create page (Accounts · Services · Billing profiles · Payment cards).
        accountsTitle: 'Accounts',
        addAccount: 'Add account',
        accountsEmpty: 'This client has no accounts yet.',
        deactivationNotice: {
          title_one: '{{count}} open billing row will be closed as of today.',
          title_other: '{{count}} open billing rows will be closed as of today.',
          postpaid_one:
            'Of these, {{count}} is paid in arrears: its last, already delivered period is still invoiced in the next generation run.',
          postpaid_other:
            'Of these, {{count}} are paid in arrears: their last, already delivered period is still invoiced in the next generation run.',
          prepaidOnly:
            'All are prepaid, so they are invoiced up to the closing month — no further invoice goes out.',
        },
        accountsEmptyDesc: 'This client has no accounts yet.',
        servicesTitle: 'Services',
        addService: 'Add service',
        servicesEmpty: 'This client has no services assigned yet.',
        billingProfilesTitle: 'Billing profiles',
        addBillingProfile: 'Add profile',
        billingProfilesEmpty: 'This client has no billing profiles yet.',
        paymentCardsTitle: 'Payment cards',
        addPaymentCard: 'Add card',
        paymentCardsEmpty: 'This client has no payment cards yet.',
        // Shared error copy for the related sections (all four use the same `RelatedList`).
        relatedError: 'Could not load.',
        relatedForbidden: 'You do not have permission to view this.',
        relatedRetry: 'Retry',
        sectionNavLabel: 'Sections on this page',
        relatedShowAll: 'View all {{count}}',
        relatedTotal: '{{count}} in total — showing the first 8',
        primaryBadge: 'primary',
        notFound: 'Client not found',
        notFoundDesc: 'It may have been deleted, or you may not have permission to view it.',
        // Delete confirmation
        deleteTitle: 'Delete client',
        deleteDesc:
          'Are you sure you want to delete "{{name}}"? Its accounts, billing profiles and service assignments will also be deleted. This action cannot be undone.',
        deleting: 'Deleting…',
        dangerTitle: 'Delete client',
        dangerDesc:
          'Its accounts, billing profiles and service assignments will also be deleted. This action cannot be undone.',
        // Permission-denied tooltip (delete is HIDDEN without the right, so no separate key)
        noManagePermission:
          'You do not have permission to create or edit clients (clients.manage).',
      },
      people: {
        children: {
          title: 'Children',
          empty: 'No children recorded.',
          fullName: 'Full name',
          birthDate: 'Date of birth',
          add: 'Add child',
          remove: 'Remove child',
        },
      },
      employees: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
        status: 'Status',
        // Column headers (list)
        columns: {
          employee: 'Employee',
          position: 'Position / Team',
          supportTeams: 'Support teams',
          assets: 'Assets',
          status: 'Status',
        },
        // Status labels (Odin identity status)
        // `archived` on the employee page means OFFBOARDED — the page uses that word everywhere.
        statusLabel: { active: 'Active', suspended: 'Suspended', archived: 'Inactive' },
        loading: 'loading…',
        countOf: '{{shown}} / {{total}} employees',
        empty: 'No employees',
        emptyDesc: 'No internal employees have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No employees match your search.',
        moreSupportTeams: '+{{count}}',
        moreSupportTeamsAria: 'More teams',
        moreAssets: '+{{count}}',
        moreAssetsAria: 'More assets',
        filterPosition: 'Position',
        filterTeam: 'Team',
        filterDepartment: 'Department',
        filterStatus: 'Status',
        filterAll: 'All {{label}}',
        // Row action + create/edit sheet + delete
        actions: 'Actions',
        edit: 'Edit',
        editTitle: 'Edit employee',
        welcome: 'Send welcome email',
        welcomeConfirmTitle: 'Send welcome email',
        welcomeConfirmDesc:
          'We will send a welcome email to “{{name}}” containing a link, valid for 7 days, that lets them set their own password. We never send passwords by email. The email can be resent if the link expired or was lost.',
        welcomeConfirm: 'Send email',
        new: 'New employee',
        createTitle: 'New employee',
        createDesc:
          'Created by email address in the company-wide directory. If the person already exists, we only refresh their core data (their name is not overwritten). Set their app access and role on the Permissions page.',
        delete: 'Deactivate',
        deleting: 'Deactivating…',
        deleteTitle: 'Deactivate employee',
        deleteDesc:
          'Deactivate “{{name}}” — immediately after you confirm: they can no longer sign in, and all their access and roles are removed in every MORGENS app (not only Atlas). Their data, comments and history remain, and you can still find them with the “Inactive” filter — their personal details (birthday, name day, children) are deleted after 30 days for data-protection reasons. An admin can bring them back on request; their access then has to be granted again.',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        roles: { member: 'Member', admin: 'Administrator', superadmin: 'Superadmin' },
        employmentTypes: { employee: 'Employee', contractor: 'Contractor' },
        employers: { MOR: 'MORGENS Design Kft.', MDIG: 'MORGENS Digital Kft.' },
        fields: {
          email: 'Email address',
          emailHint: 'The login email — the unique identifier of the person in Odin.',
          givenName: 'Given name',
          familyName: 'Family name',
          nickname: 'Nickname',
          nicknameHint: 'How the system addresses them. Left empty, their first name is used.',
          phone: 'Phone number',
          role: 'Atlas role',
          roleHint: 'The initial role in Atlas (changeable later on the Permissions page).',
          position: 'Position',
          positionPlaceholder: 'Select a position…',
          positionOptionWithTeam: '{{name}} – {{team}}',
          birthDate: 'Birthday',
          employmentStartDate: 'Start date',
          employmentEndDate: 'Leaving date',
          nameDay: 'Name day',
          employmentType: 'Employment type',
          employer: 'Employer',
          probation: 'On probation',
          probationStartDate: 'Probation start',
          probationEndDate: 'Probation end',
          password: 'Initial password',
          passwordHint:
            'Optional. If left empty, the colleague sets their own password from the welcome email link.',
        },
      },
      campaignForecast: {
        title: 'Campaign forecast',
        status: {
          planned: 'planned',
          running: 'running',
          closed: 'closed',
        },
        filters: {
          client: 'Client',
          account: 'Account',
          owner: 'Owner',
          status: 'Status',
        },
        zoom: {
          month: 'month',
          quarter: 'quarter',
          quarterWord: 'quarter',
          label: 'Zoom',
          year: 'year',
          today: 'today',
          previous: 'Previous period',
          next: 'Next period',
        },
        new: 'New campaign',
        edit: 'Edit',
        delete: 'Delete',
        deleting: 'Deleting…',
        deleteTitle: 'Delete this campaign?',
        deleteDesc: 'The campaign disappears from the timeline. This cannot be undone.',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        createTitle: 'New campaign',
        createDesc: 'You can pick several accounts — one campaign is created per account.',
        editTitle: 'Edit campaign',
        loading: 'Loading campaigns…',
        countOf: '{{shown}} / {{total}} campaigns',
        refsUnavailable:
          'The client, account and colleague lists are unavailable right now — please try again shortly.',
        timelineEmpty: 'No campaigns in this period',
        timelineEmptyDesc: 'Move to another period or zoom out.',
        partialTitle: 'Some accounts failed',
        partialDesc: 'No campaign was created for {{count}} account(s):',
        fields: {
          client: 'Client',
          clientHint: 'Picking a client narrows the account list.',
          accounts: 'Accounts',
          accountsHint: 'One campaign per account, each with its own code and budget.',
          account: 'Account',
          name: 'Campaign name',
          code: 'Code',
          codeHint: 'Generated by the system, not editable.',
          owner: 'Owner',
          startsOn: 'Starts',
          endsOn: 'Ends',
          description: 'Note / description',
          plannedBudget: 'Planned budget',
          actualBudget: 'Actual budget',
          currency: 'Currency',
          asanaTaskUrl: 'Asana link',
          asanaTaskUrlHint: 'Optional link to a task on app.asana.com.',
        },
      },
      productUpdates: {
        boardTab: 'Board',
        timelineTab: 'Timeline',
        board: {
          emptyColumn: 'Nothing here',
          offBoard: '{{count}} item(s) off the board:',
          offBoardView: 'These items are off the board.',
          backToBoard: 'Back to the board',
        },
        timeline: {
          noDate: 'Date unknown',
          count: '{{count}} item(s)',
        },
        // No Type column: the badge sits under the title, next to the visibility badge. Phase (`stage`) and
        // Status (`status`) are deliberately two columns and two words — they are independent dimensions.
        columns: {
          item: 'Update',
          stage: 'Phase',
          status: 'Status',
          shippedAt: 'Released',
          target: 'Timeframe',
        },
        stage: {
          idea: 'Idea',
          planned: 'Planned',
          in_progress: 'In progress',
          shipped: 'Released',
          on_hold: 'On hold',
          cancelled: 'Cancelled',
        },
        type: {
          feature: 'New feature',
          improvement: 'Improvement',
          fix: 'Fix',
          security: 'Security',
          performance: 'Performance',
        },
        status: { draft: 'Draft', published: 'Announced', archived: 'Archived' },
        // The badge names the FIELD (private/public); the publish dialog keeps its own, more explicit wording.
        visibility: { internal: 'Private', public: 'Public' },
        horizon: { now: 'Now', next: 'Next', later: 'Later' },
        // FILTER ORDER on the page: Type → Visibility → Phase → Status (same order as the columns).
        filters: {
          stage: 'Phase',
          type: 'Type',
          status: 'Status',
          visibility: 'Visibility',
        },
        loading: 'loading…',
        countOf: '{{shown}} / {{total}} updates',
        new: 'New update',
        actions: 'Actions',
        edit: 'Edit',
        delete: 'Delete',
        deleting: 'deleting…',
        deleteTitle: 'Delete update',
        deleteDesc: 'Delete “{{name}}”? Its attachments are deleted as well.',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'saving…',
        createTitle: 'New update',
        createDesc: 'The entry is created as a draft — announcing it is a separate step.',
        editTitle: 'Edit update',
        emptyBoard: 'Nothing on the board yet',
        emptyTimeline: 'No released updates yet',
        emptyDesc: 'Add the first entry with “New update”.',
        noMatches: 'No matches',
        noMatchesDesc: 'No update matches the filters.',
        publish: 'Publish',
        revoke: 'Unpublish',
        unpublish: 'Withdraw announcement',
        publishTitle: 'Announce update',
        publishHelp:
          'An internal announcement is visible to colleagues only. A client-facing announcement also appears in the partner portal, where the summary is shown.',
        publishInternal: 'Internal only',
        publishPublic: 'Clients too',
        publishCurrentInternal: 'Currently: announced, internal only.',
        publishCurrentPublic: 'Currently: announced, clients too.',
        fields: {
          title: 'Title',
          // One concept, one word: the editor says "Phase" too, matching the column and the filter.
          stage: 'Phase',
          type: 'Kind',
          horizon: 'Horizon',
          horizonPlaceholder: 'Not set',
          targetPeriod: 'Target period',
          targetPeriodHint: 'E.g. 2026-Q4 — a coarse, committable period (not a deadline).',
          shippedAt: 'Release date',
          shippedAtHint: 'The changelog is ordered by this.',
          bodyHu: 'Description (Hungarian)',
          bodyEn: 'Description (English)',
          bodyHint: 'Rich text; images can be inserted.',
          bodyEnHint: 'Optional — if empty, clients see the Hungarian text.',
          slug: 'Identifier (slug)',
          slugHint: 'Left empty it is derived from the title — it appears in the client link.',
        },
      },
      supportTeams: {
        columns: {
          // NAME DISAMBIGUATION: "Support team" (not plain "Team") — since the org hierarchy introduced a
          // SEPARATE, colored "team" concept (department → team → position), the two must not be confused.
          team: 'Support team',
          members: 'Members',
          services: 'Services',
        },
        loading: 'loading…',
        countOf: '{{shown}} / {{total}} support teams',
        empty: 'No support teams',
        emptyDesc: 'No support team has been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No support team matches your search.',
        moreMembers: '+{{count}}',
        moreMembersAria: 'More members',
        moreServices: '+{{count}}',
        moreServicesAria: 'More services',
        actions: 'Actions',
        edit: 'Edit',
        editTitle: 'Edit support team',
        new: 'New support team',
        createTitle: 'New support team',
        createDesc: 'Support team — bilingual name, contact email and members.',
        delete: 'Delete',
        deleting: 'Deleting…',
        deleteTitle: 'Delete support team',
        deleteDesc:
          'Delete support team “{{name}}”? Its memberships are removed too. (It cannot be deleted if an active ticket references it.)',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        fields: {
          name: 'Name',
          email: 'Contact email',
          emailHint: "The support team's shared email address.",
          description: 'Description',
          members: 'Members',
          membersPlaceholder: 'Select employees…',
          membersCount: '{{count}} selected',
          services: 'Services',
          servicesPlaceholder: 'Select services…',
          servicesCount: '{{count}} selected',
          servicesNoPermission: "You don't have permission for this.",
        },
      },
      contacts: {
        columns: {
          contact: 'Contact',
          contactInfo: 'Contact info',
          accounts: 'Account(s)',
          clientAdmins: 'Client administrators',
          status: 'Status',
        },
        statusLabel: { active: 'Active', suspended: 'Suspended', archived: 'Offboarded' },
        levelLabel: { admin: 'Admin', member: 'Member', viewer: 'Viewer' },
        filterStatus: 'Status',
        primaryBadge: 'primary',
        portalBadge: 'Portal',
        more: '+{{count}}',
        moreItems: 'Show more items',
        loading: 'loading…',
        countOf: '{{shown}} / {{total}} contacts',
        empty: 'No contacts',
        emptyDesc: 'No client contacts have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No contacts match your search.',
        // Row action + create/edit sheet + delete
        actions: 'Actions',
        new: 'New contact',
        edit: 'Edit',
        editTitle: 'Edit contact',
        welcome: 'Send welcome email',
        welcomeResend: 'Resend welcome email',
        welcomeConfirmTitle: 'Send welcome email',
        welcomeConfirmDesc:
          'We will send a welcome email to “{{name}}”: it introduces the system and includes a link, valid for 7 days, that lets them set their own password. We never send passwords by email. The email can be resent if the link expired or was lost.',
        welcomeConfirm: 'Send email',
        createTitle: 'New contact',
        createDesc:
          'Created by email address. If the person already exists, we only refresh their data; assigning to an account happens on the account page.',
        delete: 'Delete',
        deleting: 'Deleting…',
        deleteTitle: 'Delete contact',
        deleteDesc:
          'Delete “{{name}}”: all of the contact’s account affiliations and portal access are removed and their identity is archived.',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        fields: {
          email: 'Email address',
          emailHint: 'The contact’s unique identifier — their client-portal login email.',
          givenName: 'Given name',
          familyName: 'Family name',
          phone: 'Phone',
          nickname: 'Nickname',
          nicknameHint: 'How the system addresses them. Left empty, their first name is used.',
          jobTitle: 'Job title',
          accountAssignmentHint:
            'Contacts are assigned to accounts on the ACCOUNT page, in the "Contacts" section — that is also where portal access, role and the primary flag are set. A contact can belong to several accounts.',
        },
      },
      tickets: {
        filterStatus: 'Status',
        filterMine: 'Assigned to me',
        filterMineOn: 'assigned to me only',
        filterTeam: 'Support team',
        filterFrom: 'Created from',
        filterTo: 'Created to',
        // a „Lezárt" fül lezárás-dátum tartománya (a `closedAt`-ra)
        filterClosedFrom: 'Closed from',
        filterClosedTo: 'Closed to',
        open: 'Open ticket',
        detailsNumber: 'Ticket number',
        detailsCreated: 'Submitted',
        detailsClosed: 'Closed',
        detailsSupportTeam: 'Support team',
        detailsComments: 'Comments',
        detailsInternalNotes: 'Of which internal notes',
        internalNotesBadge_one: '{{count}} internal note',
        internalNotesBadge_other: '{{count}} internal notes',
        filterType: 'Type',
        filterAll: '{{label}}: all',
        loading: 'loading…',
        columns: {
          ticket: 'Ticket',
          account: 'Account',
          requester: 'Requester',
          assignee: 'Assignee',
          status: 'Status',
          actions: 'Actions',
        },
        edit: 'Edit',
        new: 'New ticket',
        create: 'Open ticket',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        editTitle: 'Edit ticket',
        createTitle: 'New ticket',
        createDesc: 'Ticket details — replies and notifications come after it is opened.',
        fields: {
          status: 'Status',
          closeReason: 'Close reason',
          closeReasonHint: 'Required to set the ticket to “Closed”.',
          shareCloseReason: 'Send the closing reason to the partner by e-mail',
          shareCloseReasonHint:
            'Not sent by default — the closing reason is often an internal note.',
          shareCloseReasonClosedHint:
            'The closing reason can only be sent at the moment of closing — this ticket is already closed.',
          assignee: 'Assignee',
          unassigned: 'Unassigned',
          supportTeam: 'Support team',
          noTeam: 'No team',
          account: 'Account',
          website: 'Website',
          service: 'Service',
          requester: 'Requester (contact)',
          type: 'Type',
          subject: 'Subject',
          description: 'Description',
          descriptionHint: 'At least 25 characters.',
          createdAt: 'Created',
        },
        form: {
          step1: 'Context',
          step1Hint: 'Services and contacts load once an account is selected.',
          step2: 'The ticket',
          step3: 'File sharing links',
          step3Hint: 'Google Drive · OneDrive · Dropbox — optional.',
          chooseAccountFirst: 'Choose an account first',
          chooseService: 'Choose a service…',
          chooseRequester: 'Choose a contact…',
          servicesOfAccount: 'Services of this account',
          otherServices: 'Other service',
          routesTo: 'Routed to: {{team}}',
          routesToUnknown: 'This service has no support team — a ticket cannot be opened for it.',
          noContacts: 'No contacts',
          noContactsHint: 'This account has no contacts — add one first.',
          subjectPlaceholder: 'Short, descriptive summary',
          descriptionPlaceholder:
            'What is the request or the bug? What did you see, what did you expect?',
          descriptionRemaining_one: '{{count}} more character needed',
          descriptionRemaining_other: '{{count}} more characters needed',
          descriptionOk_one: '{{count}} character',
          descriptionOk_other: '{{count}} characters',
        },
        unassigned: 'Unassigned',
        unknownRequester: 'Unknown requester',
        statusLabel: {
          open: 'Open',
          in_progress: 'In progress',
          resolved: 'Resolved',
          closed: 'Closed',
          reopened: 'Reopened',
        },
        statusHelp: {
          open: 'New ticket, nobody has started on it yet.',
          in_progress: 'Someone is working on it.',
          resolved: 'Solved: the bug was fixed or the request fulfilled.',
          closed:
            'Closed without a fix (e.g. duplicate, not reproducible) — always with a closing reason.',
          reopened: 'Reopened after closing — there is work on it again.',
        },
        typeLabel: {
          support: 'Support',
          bug: 'Bug',
          feature_request: 'Feature request',
          billing: 'Billing',
          other: 'Other',
        },
        tab: {
          in_progress: 'In progress',
          closed: 'Closed',
        },
        tabAll: 'All',
        onlyMine: 'Only mine',
        countOf: '{{shown}} / {{total}} tickets',
        empty: 'No tickets',
        emptyDesc: 'No support tickets have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No tickets match your filters.',
        // List: comment-count badge.
        commentCount_one: '{{count}} comment',
        commentCount_other: '{{count}} comments',
        // Detail (full-page) — navigation + permission/error states.
        back: 'Back to tickets',
        notFound: 'Ticket not found',
        notFoundDesc: 'This ticket does not exist or is no longer available.',
        forbidden: 'No access',
        forbiddenDesc: 'You do not have permission to view this ticket.',
        sectionNavLabel: 'Ticket sections',
        sections: {
          data: 'Basic data',
          people: 'Participants',
          links: 'Related links',
        },
        links: {
          title: 'File sharing links',
          googleDrive: 'Google Drive',
          onedrive: 'Microsoft OneDrive',
          dropbox: 'Dropbox',
          asana: 'Asana task',
          placeholder: 'https://…',
        },
        // Asana task from the ticket (the legacy “Asana” button). The wording states what matters to the
        // user: the task is created in THEIR name, and unlinking does NOT delete anything in Asana.
        asana: {
          create: 'Create Asana task',
          creating: 'Creating…',
          confirmTitle: 'Create the Asana task?',
          confirmDescription:
            'The task is created in the central CRM tickets project, in your name, with you as the assignee. The ticket subject, description and the links you added are carried over. This requires your personal Asana access token, set on the My profile page.',
          confirm: 'Create',
          unlink: 'Unlink',
          unlinking: 'Unlinking…',
          unlinkTitle: 'Unlink the reference?',
          unlinkDescription:
            'The Asana task reference disappears from the ticket. THE TASK ITSELF stays in Asana — delete it there if that is also needed.',
        },
        // Close dialog (opened from the header status menu — the close reason is mandatory there).
        close: {
          title: 'Close ticket',
          editTitle: 'Edit close reason',
          confirm: 'Close',
        },
        // Comment thread.
        comments: {
          title: 'Comments',
          empty: 'No comments on this ticket yet.',
          unknownAuthor: 'Unknown author',
          kindLabel: 'Comment type',
          bodyLabel: 'Comment body',
          editBodyLabel: 'Edit comment',
          kind: {
            internal: 'internal note',
            external: 'external reply',
          },
          kindTab: {
            internal: 'Internal note',
            external: 'Comments',
          },
          placeholderExternal: 'Write a comment…',
          placeholderInternal: 'Write a note…',
          reactions: {
            addLabel: 'Add reaction',
            pickLabel: '{{emoji}} reaction',
            toggleLabel_one: '{{emoji}} — {{count}} reaction (click to toggle)',
            toggleLabel_other: '{{emoji}} — {{count}} reactions (click to toggle)',
          },
          composerCollapsedExternal: 'Write a comment…',
          composerCollapsedInternal: 'Write a note…',
          send: 'Send',
          sending: 'Sending…',
          saveNote: 'Save',
          savingNote: 'Saving…',
          edit: 'Edit',
          loadTemplate: 'Load template',
          saveAsTemplate: 'Save as template',
          saveTemplateTitle: 'Save comment template',
          templateTitleLabel: 'Template name',
          templateContentLabel: 'Template content',
        },
        // Best practice (INTERNAL knowledge base).
        // Attachment management.
        attachments: {
          title: 'Attachments',
          empty: 'No attachments on this ticket yet.',
          upload: 'Attach file',
          uploading: 'Attaching…',
          unavailable: 'file content unavailable (migrated attachment)',
          download: 'Download',
          delete: 'Delete',
          deleting: 'Deleting…',
          deleteTitle: 'Delete attachment',
          deleteDesc: 'Are you sure you want to delete “{{name}}”?',
          limitReached: 'You have reached the limit of {{max}} attachments per ticket.',
          add: 'Attach file',
          remove: 'Remove “{{name}}” from the list',
        },
      },
      assets: {
        filterHolder: 'Employee',
        catalog: {
          button: 'Categories',
          title: 'Asset categories and types',
          desc: 'The types shown in the forms and filters. Renaming applies everywhere at once.',
          empty: 'No categories yet.',
          kind: 'What to add',
          kindCategory: 'Category',
          kindType: 'Type',
          name: 'Name',
          numberPrefix: 'Inventory number prefix',
          numberPrefixHint:
            '2–8 uppercase letters or digits — only applies to NEW inventory numbers.',
          isSoftware: 'Software-like',
          isSoftwareHint: 'Has a license key and an expiry date (expiry reminders).',
          category: 'Category',
          add: 'Add',
          saveEdit: 'Save',
          newTitle: 'New category or type',
          editTitle: 'Edit',
          editAria: 'Edit {{name}}',
          deleteAria: 'Delete {{name}}',
          deleteCategoryTitle: 'Delete category',
          deleteTypeTitle: 'Delete type',
          deleteDesc: 'Do you really want to delete "{{name}}"? This cannot be undone.',
          typeInUse: 'Cannot delete: assets or requests still use this type.',
          typeInUseMove: 'A type in use cannot be moved to another category.',
          categoryHasTypes: 'Cannot delete: delete the types of the category first.',
        },
        filterCategory: 'Category',
        filterType: 'Type',
        filterTeam: 'Team',
        filterStatus: 'Status',
        categoryLabel: {
          hardware: 'Hardware',
          software: 'Software',
        },
        filterAll: '{{label}}: all',
        loading: 'loading…',
        name: 'Asset',
        type: 'Type',
        status: 'Status',
        holder: 'Employee',
        usageLocationCol: 'Usage location',
        department: 'Department',
        netPrice: 'Net price',
        purchaseDate: 'Purchased',
        purchaseInfo: 'Purchase details',
        price: 'Price',
        priceNet: 'net',
        priceVat: 'VAT',
        priceGross: 'gross',
        requesterCol: 'Requester',
        tabInventory: 'Owned assets',
        tabRequests: 'Requested assets',
        assetNumber: 'Asset number',
        serialNumber: 'Serial number',
        licenseKey: 'License key',
        purchaseLocation: 'Purchase location',
        purchaseDateLabel: 'Purchase date',
        invoiceNumber: 'Invoice number',
        retirementDate: 'Retirement date',
        requester: 'requester',
        noHolder: 'No holder',
        office: 'office',
        /** The team-select's catch-all group for teams WITHOUT a department (the select's `groups` grouping). */
        otherTeamsGroup: 'Other teams',
        usageLabel: {
          office: 'Office',
          home: 'Home',
          social: 'Social',
          mixed: 'Mixed',
        },
        statusLabel: {
          requested: 'Requested',
          rejected: 'Rejected',
          approved: 'Approved',
          being_procured: 'Being procured',
          purchased: 'Purchased',
          received: 'Received',
          spare: 'Spare',
          in_use: 'In use',
          retired: 'Retired',
        },
        typeLabel: {
          desktop_pc: 'Desktop PC',
          laptop: 'Laptop',
          monitor: 'Monitor',
          mouse: 'Mouse',
          keyboard: 'Keyboard',
          camera: 'Camera',
          headphone: 'Headphone',
          tablet: 'Tablet',
          mobile_phone: 'Mobile phone',
          sim_card: 'SIM card',
          router: 'Router',
          security_camera: 'Security camera',
          other_hardware: 'Other hardware',
          operating_system: 'Operating system',
          antivirus: 'Antivirus',
          document_management: 'Document management',
          design_creative: 'Design / creative',
          ide_code_editor: 'IDE / code editor',
          subscription: 'Subscription (AI, SaaS)',
          other_software: 'Other software',
        },
        countOf: '{{shown}} / {{total}} assets',
        empty: 'No assets',
        emptyDesc: 'No assets have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No assets match your filters.',
        actions: 'Actions',
        new: 'New asset',
        edit: 'Edit',
        editWorkflowDisabled:
          'This asset is in the request workflow — editing is not available here.',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        deleting: 'Deleting…',
        createTitle: 'New asset',
        createDesc: 'The inventory asset details — the asset number is generated automatically.',
        editTitle: 'Edit asset',
        deleteTitle: 'Delete asset',
        deleteDesc: 'Are you sure you want to delete “{{name}}”? This action cannot be undone.',
        fields: {
          officeUse: 'Office (general use)',
          officeUseHint: 'A shared office asset — no person, the location is fixed to communal.',
          usageOfficeFixed: 'Fixed for office assets.',
          teamComputed: 'Team (computed from the holder): {{team}}',
          name: 'Name',
          namePlaceholder: 'Name of the asset',
          type: 'Type',
          status: 'Status',
          serialNumber: 'Serial number',
          licenseKey: 'License key',
          netPrice: 'Net price',
          netPriceHint: 'As a number (e.g. 150000) — the currency is HUF.',
          vatRate: 'VAT rate',
          purchaseDate: 'Purchase date',
          purchaseLocation: 'Place of purchase',
          invoiceNumber: 'Invoice number',
          team: 'Team',
          teamNone: 'Office (no team)',
          holder: 'Holder',
          holderPlaceholder: 'Search employees…',
          usageLocation: 'Usage location',
          usageNone: 'Not specified',
          retirementDate: 'Retirement date',
          retirementDateHint: 'Required for retired status.',
          expiryDate: 'Expiry / renewal',
          expiryDateHint:
            'For subscriptions and licences — a notice goes out 30 and 7 days before, and on the day.',
        },
        countShown: '{{shown}} assets',
        views: {
          inventory: 'Inventory',
          requests: 'Requests',
        },
        request: {
          officeRecipient: 'Office (general use)',
          kinekCol: 'For whom',
          view: 'Open request',
          viewTitle: 'Asset request',
          viewDesc: 'The details of the request — edit it while the request is still open.',
          editTitle: 'Edit request',
          close: 'Close',
          unavailable: 'The request is not available (deleted or no access).',
          new: 'Submit request',
          createTitle: 'New asset request',
          createDesc: 'Details of the requested asset — procurement starts after the decision.',
          submit: 'Submit',
          submitting: 'Submitting…',
          category: {
            hardware: 'Hardware',
            software: 'Software',
          },
          fields: {
            category: 'Category',
            categoryHint: 'Only narrows the type list.',
            namePlaceholder: 'Name of the requested asset',
            vatRateHint: 'Percentage, as a number (e.g. 27).',
            website: 'Website',
            websiteHint: 'Link to the product (optional).',
            requestedFor: 'Requested for',
            requestedForPlaceholder: 'Myself',
            requestedForHint:
              'Leave empty to request it for yourself; on receipt they become the holder.',
            note: 'Note',
            notePlaceholder: 'Reason, configuration, deadline… (optional)',
          },
        },
        workflow: {
          working: 'Saving…',
          approve: {
            action: 'Approve',
            title: 'Approve request',
            desc: 'Approve the “{{name}}” request? Procurement can start after approval.',
            confirm: 'Approve',
          },
          reject: {
            action: 'Reject',
            title: 'Reject request',
            desc: 'Reject the “{{name}}” request? Providing a reason is optional.',
            reasonLabel: 'Rejection reason',
            reasonPlaceholder: 'Why are you rejecting it? (optional)',
            confirm: 'Reject',
          },
          being_procured: {
            action: 'Start procurement',
            title: 'Start procurement',
            desc: 'Start procuring “{{name}}”?',
            confirm: 'Start',
          },
          purchased: {
            action: 'Purchased',
            title: 'Purchased',
            desc: 'Mark “{{name}}” as purchased? The purchase date will be set to today.',
            confirm: 'Mark',
          },
          received: {
            action: 'Received',
            title: 'Received',
            desc: 'Mark “{{name}}” as received? The asset goes to the person it was requested for (otherwise the requester) and moves to the inventory.',
            confirm: 'Mark',
          },
        },
      },
      meetings: {
        from: 'From',
        to: 'To',
        loading: 'loading…',
        columns: {
          meeting: 'Meeting',
          location: 'Location',
          time: 'Time',
          organizer: 'Organizer',
          participants: 'Participants',
          actions: 'Actions',
        },
        new: 'New meeting',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        deleting: 'Deleting…',
        createTitle: 'New meeting',
        createDesc: 'Meeting details and participants — the invite notification later.',
        editTitle: 'Edit meeting',
        weekendLabel: 'weekend',
        hideWeekends: 'hide weekend',
        showWeekends: 'show weekend',
        calendarButtons: {
          today: 'today',
          month: 'month',
          week: 'week',
          day: 'day',
        },
        rescheduleTitle: 'Reschedule meeting',
        rescheduleDesc: 'New time for “{{title}}”: {{start}}. Reschedule it?',
        rescheduleConfirm: 'Reschedule',
        recurrence: {
          label: 'Repeat',
          frequency: {
            none: 'Does not repeat',
            weekly: 'Weekly',
            biweekly: 'Every two weeks',
            monthly: 'Monthly',
            workdays: 'Every workday',
          },
          weekdays: 'Days',
          end: 'Ends',
          endCount: 'after',
          endUntil: 'on date',
          countLabel: 'Number of occurrences',
          countHint: '2–52 occurrences (the first is the start itself).',
          untilLabel: 'Last day',
          preview_one: '{{count}} occurrence: {{first}}',
          preview_other: '{{count}} occurrences: {{first}} – {{last}}',
          count_one: '{{count}} occurrence',
          count_other: '{{count}} occurrences',
          until: 'until {{date}}',
          seriesBadge: 'Recurring',
        },
        seriesScope: {
          edit: {
            label: 'Scope of the change',
            title: 'Edit recurring meeting',
            desc: 'This is an occurrence of a series. What should the change apply to?',
            this: 'This occurrence only',
            following: 'This and following occurrences',
            all: 'All occurrences of the series',
          },
          delete: {
            label: 'Scope of the deletion',
            this: 'This occurrence only',
            following: 'This and following occurrences',
            all: 'All occurrences of the series',
          },
          hint: {
            this: 'The other occurrences of the series stay unchanged.',
            following: 'Earlier occurrences do not change.',
            all: 'Occurrences that have already started do not change.',
          },
          rescheduleOnlyThis:
            'This is an occurrence of a recurring series — dragging moves this occurrence only.',
        },
        participantConflict: {
          title: 'Participant conflict',
          desc_one: 'A participant is already in another meeting at this time. Save anyway?',
          desc_other:
            'In {{count}} cases a participant is already in another meeting at this time. Save anyway?',
          proceed: 'Save anyway',
          back: 'Back',
          listLabel: 'Conflicting meetings',
          untitled: '(untitled)',
        },
        deleteTitle: 'Delete meeting',
        deleteDesc: 'Delete meeting “{{title}}”? This action cannot be undone.',
        fields: {
          title: 'Title',
          titlePlaceholder: 'Meeting title',
          description: 'Description',
          location: 'Location',
          startsAt: 'Starts',
          endsAt: 'Ends',
          zoomLink: 'Zoom link',
          zoomAccount: 'Zoom account',
          zoomAccountNone: 'None',
          googleMeetLink: 'Google Meet link',
          microsoftTeamsLink: 'Microsoft Teams link',
          employees: 'Employees',
          employeesPlaceholder: 'Select employees…',
          employeesUngrouped: 'No team',
          contacts: 'Contacts',
          contactsPlaceholder: 'Select contacts…',
          participantsCount: '{{count}} selected',
        },
        zoomAccountLabel: { zoom_account_1: 'Zoom account 1', zoom_account_2: 'Zoom account 2' },
        online: 'Online',
        noOrganizer: 'No organizer',
        noParticipants: 'No participants',
        more: '+{{count}}',
        moreParticipants: 'More participants',
        participants: {
          title: 'Participants',
        },
        // The calendar's DETAIL card (peek): labels of the panel that opens on an event click.
        peek: {
          join: 'Join',
          notOwnHint: 'You can only edit your own meetings.',
        },
        rsvp: {
          pending: 'Maybe',
          confirmed: 'Attending',
          declined: 'Not attending',
          actionConfirm: "I'll be there",
          actionDecline: "I can't make it",
          myResponse: 'Your attendance:',
        },
        locationLabel: {
          meeting_room: 'Meeting room',
          breakout_room: 'Breakout',
          silent_box_1: 'Silent box 1',
          silent_box_2: 'Silent box 2',
          dining_room: 'Dining room',
          keszthely_office: 'Keszthely office',
          other: 'Other',
        },
        countOf: '{{shown}} / {{total}} meetings',
        countInRange: '{{count}} meetings in view',
        filterLocation: 'Location',
        filterParticipant: 'Colleague',
        busyLegend: 'hatched: the selected colleagues’ busy time',
        empty: 'No meetings',
        emptyDesc: 'No meetings have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No meetings match your filters.',
      },
      messages: {
        filterType: 'Type',
        filterAll: '{{label}}: all',
        loading: 'loading…',
        columns: {
          subject: 'Subject',
          recipients: 'Recipients',
          enclosures: 'Enclosures',
          sender: 'Sender',
          date: 'Sent',
        },
        typeLabel: { internal: 'Internal', external: 'External' },
        statusLabel: { draft: 'Draft', scheduled: 'Scheduled', sent: 'Sent' },
        audience: { everyone: 'everyone', allClients: 'all clients' },
        recipientGroup: {
          account: '{{count}} accounts',
          service: '{{count}} services',
          team: '{{count}} teams',
          department: '{{count}} departments',
        },
        attachmentCount: '{{count}} attachments',
        category: {
          label: 'Category',
          message: 'Message',
          system: 'System notice',
        },
        detailsType: 'Type',
        detailsCategory: 'Category',
        detailsAttachments: 'Attachments',
        detailsLinks: 'Links',
        detailsReads: 'Reads',
        attachments: {
          title: 'Attachments',
          empty: 'No attachments on this message.',
          upload: 'Attach file',
          uploading: 'Attaching…',
          unavailable: 'file content unavailable (migrated attachment)',
          download: 'Download',
          delete: 'Delete',
          deleting: 'Deleting…',
          deleteTitle: 'Delete attachment',
          deleteDesc: 'Are you sure you want to delete “{{name}}”?',
          limitReached: 'You have reached the limit of {{max}} attachments per message.',
          add: 'Attach file',
          remove: 'Remove “{{name}}” from the list',
        },
        linkCount: '{{count}} links',
        readCount: '{{count}} reads',
        unknownSender: 'Unknown sender',
        more: '+{{count}}',
        moreRecipients: 'More recipients',
        countOf: '{{shown}} / {{total}} messages',
        empty: 'No messages',
        emptyDesc: 'No messages have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No messages match your filters.',
        // --- Write vertical (compose/send/schedule/edit/read) ---
        new: 'New message',
        tabIncoming: 'Incoming',
        tabOutgoing: 'Outgoing',
        tabAll: 'All',
        markAllRead: 'Mark all as read',
        unread: 'Unread',
        cancel: 'Cancel',
        fields: {
          type: 'Type',
          audience: 'Audience',
          subject: 'Subject',
          subjectPlaceholder: 'Subject of the message…',
          body: 'Message',
          bodyPlaceholder: 'Write your message here…',
          bodyHint: 'At least {{count}} characters (formatting does not count).',
          links: 'Links',
          googleDriveLink: 'Google Drive link',
          googleMeetLink: 'Google Meet link',
          zoomLink: 'Zoom link',
          scheduledAt: 'Send at',
          scheduledAtHint: 'Local time — must be in the future.',
        },
        audienceKind: {
          everyone: 'Everyone',
          departments: 'Departments',
          teams: 'Teams',
          colleagues: 'Colleagues',
          all_clients: 'All clients',
          accounts: 'Accounts',
          contacts: 'Contacts',
          services: 'Services',
        },
        compose: {
          title: 'New message',
          desc: 'Write an internal or external broadcast message.',
          scheduleToggle: 'Schedule…',
          scheduleCancel: 'Discard schedule',
          submitSend: 'Send',
          submitSchedule: 'Schedule',
          submitting: 'Saving…',
          targetsPlaceholder: 'Select recipients…',
          targetsCount: '{{count}}',
          flagHint: {
            everyone: 'The message will reach every colleague.',
            all_clients: 'The message will reach every client contact.',
          },
          confirmBroad: {
            title: {
              everyone: 'Send to all colleagues?',
              all_clients: 'Send to all clients?',
            },
            desc: {
              everyone: 'Every colleague will receive the message ({{count}} people).',
              all_clients: 'Every client contact will receive the message ({{count}} people).',
            },
            descNoCount: {
              everyone: 'Every colleague will receive the message.',
              all_clients: 'Every client contact will receive the message.',
            },
            back: 'Back',
          },
          /** Explains that targeting a DEPARTMENT reaches everyone through its teams (a department has no
           *  members of its own — the hierarchy is department → team → position). */
          departmentsHint: 'Reaches everyone through the TEAMS of the selected departments.',
          targetsLabel: {
            departments: 'Departments',
            teams: 'Teams',
            colleagues: 'Colleagues',
            accounts: 'Client accounts',
            contacts: 'Contacts',
            services: 'Services',
          },
        },
        edit: {
          title: 'Edit message',
          save: 'Save',
          saving: 'Saving…',
          sentWarning:
            'You are editing a sent message: recipients will NOT be notified about the change, and the message will carry an "edited" marker.',
          loadError: 'The message content could not be loaded.',
        },
        detail: {
          edited: 'edited: {{date}}',
          loadError: 'The message content could not be loaded.',
        },
        actions: {
          label: 'Actions',
          sendNow: 'Send now',
          schedule: 'Schedule',
          reschedule: 'Reschedule',
          unschedule: 'Cancel schedule',
          edit: 'Edit',
          pin: 'Pin to home page',
          unpin: 'Unpin',
          archive: 'Archive',
          unarchive: 'Restore',
        },
        pinned: 'Pinned to the home page',
        archivedView: 'Archived',
        pinnedView: 'Pinned',
        sendNow: {
          title: 'Send now',
          desc: 'Send the message "{{subject}}" now?',
          confirm: 'Send',
          working: 'Sending…',
        },
        schedule: {
          title: 'Schedule message',
          desc: 'The message "{{subject}}" will be sent at the given time.',
          confirm: 'Schedule',
          working: 'Saving…',
        },
        unschedule: {
          title: 'Cancel schedule',
          desc: 'The message "{{subject}}" will not be sent — it stays as a draft.',
          confirm: 'Cancel schedule',
          working: 'Cancelling…',
        },
      },
      invoices: {
        generate: {
          action: 'Generate invoices',
          title: 'Monthly invoice generation',
          description:
            'Produce DRAFT invoices from due recurring assignments for the selected month. Idempotent (an already-invoiced period is skipped).',
          month: 'Billing month',
          prevMonth: 'Previous month',
          nextMonth: 'Next month',
          types: 'Pricing types',
          typesHint: 'Leave empty to run all types.',
          pricingType: {
            flat: 'Flat',
            tiered: 'Tiered',
            base_tiered: 'Base + tiered',
            commission: 'Commission',
          },
          dryRun: 'Simulation (dry-run)',
          dryRunHint: 'Estimate only — does not create invoices on the bus.',
          simulate: 'Simulate',
          generate: 'Generate',
          generating: 'Processing…',
          close: 'Close',
          dryRunResult: 'The simulation would create:',
          liveResult: 'Generation complete:',
          dueAssignments: 'Due',
          groups: 'Groups',
          invoices: 'New invoices',
          appended: 'Appended',
          items: 'Items',
          skipped: 'Skipped ({{count}})',
          skippedCount_one: '{{count}} item',
          skippedCount_other: '{{count}} items',
          skipReason: {
            invalid_period: 'Invalid period',
            already_invoiced: 'Already invoiced',
            no_rate: 'Missing manual rate',
            eu_tax_number_required: 'EU tax number required',
            issuer_missing: 'Issuer not in the catalogue',
            no_price: 'No price',
            no_usage_data: 'No usage data',
            no_usage_source: 'No usage source',
            zero: 'Zero amount',
            write_failed: 'Bus write failed',
            rate_limited: 'Bus throttled (429)',
            currency_mismatch: 'Currency mismatch (no conversion)',
            auto_billing_off: 'Automatic billing is off',
            manually_removed: 'Manually removed item (lock)',
            assignment_not_active: 'Assignment is not active',
            owner_inactive: 'Client / account is inactive',
            usage_not_final: 'Not closed in HotelCMS',
            usage_source_disabled: 'Switched off in HotelCMS',
            usage_combined_minimum: 'Combined minimum in HotelCMS',
            missing_usage_ref: 'No integration ID',
          },
          skippedItem: 'Item: {{itemKey}}',
          skipsHint: 'You can review the skips on the "Generation skips" tab.',
          skipsHintLink: 'Open generation skips',
        },
        tabs: {
          invoices: 'Invoices',
          skips: 'Generation skips',
        },
        skips: {
          columns: {
            occurredAt: 'Time',
            client: 'Client / account',
            service: 'Service',
            item: 'Item',
            reason: 'Reason',
            trigger: 'Trigger',
            state: 'State',
            details: 'Details',
          },
          itemWholeInvoice: 'whole invoice',
          triggerCron: 'scheduled',
          triggerManual: 'manual',
          stateOpen: 'open',
          stateResolved: 'resolved',
          resolvedAt: 'Resolved: {{date}} (run: {{runId}})',
          runIdLabel: 'Run id',
          detailNone: '—',
          detailUnknown: 'unknown',
          detailLabel: {
            pricingType: 'Pricing',
            usageSourceType: 'Usage source',
            currency: 'Currency',
            rateMonth: 'Rate month',
            vatRate: 'VAT rate',
            issuerKey: 'Issuer',
            status: 'Bus response',
            targetCurrency: 'Invoice currency',
            usageCurrencies: 'Measured currencies',
            invoiceNumber: 'Invoice number',
            level: 'Switched off',
            assignmentStatus: 'Assignment status',
            usageMonth: 'Measured month',
            idField: 'Missing ID',
          },
          reasonDesc: {
            invalid_period:
              'The billing period cannot be derived: the billing start date or the frequency is missing or contradictory on the assignment. Check the assignment’s billing settings.',
            already_invoiced:
              'The period is already invoiced — the generator never issues it twice. This is not a problem, so it is not recorded in the log.',
            no_usage_source:
              '{{pricingType}} pricing, but the assignment has no usage source configured, so there is nothing to derive a quantity from. Set the usage source on the assignment.',
            no_usage_data:
              'The usage source ({{usageSourceType}}) is configured, but no measured data has arrived for the period’s month yet. The monthly usage fetch may fill it in; until then the item is skipped.',
            no_rate:
              'The manual {{currency}} rate is missing for {{rateMonth}}, so the tiered price cannot be computed. Add the rate for that month.',
            no_price:
              'The assignment is missing the price row required by {{pricingTypeLower}} pricing (unit price, base fee or passive fee). Add the missing price row.',
            zero: 'The amount computed for the month is zero — a valid business outcome (no traffic), not an error. A zero-amount item never goes on an invoice.',
            eu_tax_number_required:
              'The invoice is reverse-charged ({{vatRate}}), which requires an EU tax number for the customer, but none is on file. Add the EU tax number to the account’s billing details.',
            issuer_missing:
              'The issuer ({{issuerKey}}) is not in the invoice-book catalogue, so there is nothing to issue the document against. Add the issuer to the catalogue.',
            write_failed:
              'The Odin bus rejected the invoice write ({{status}}). It may be transient — the next run retries it.',
            rate_limited:
              'The Odin bus throttled the run (429), so the document was not written. The next run fills it in; if it recurs, review the batch-write pacing.',
            currency_mismatch:
              'The measured usage currency ({{usageCurrencies}}) differs from the invoice’s target currency ({{targetCurrency}}). We do not convert, because the contract provides no rate for it — so the item is skipped.',
            auto_billing_off:
              'The row was due, but automatic billing is switched off ({{level}}). If that is deliberate, resolve the exception; otherwise switch it on and the next run will invoice it.',
            manually_removed:
              'Somebody removed this item by hand from draft {{invoiceNumber}}, so the daily generation will not recreate it. If you do want it, release the lock — the next run will invoice it.',
            assignment_not_active:
              'The assignment\u2019s status is \u201C{{assignmentStatus}}\u201D, so it is not invoiced. If the service is running, set it active; if it ended, close it — closing still invoices the last delivered period.',
            owner_inactive:
              'The row was due, but the owner ({{level}}) is INACTIVE — this is the CRM status, not the automatic-billing switch. If the relationship ended, the assignment should be closed; deactivation now does that automatically, so this row shows the earlier state.',
            usage_not_final:
              'The {{usageSourceType}} statement for {{usageMonth}} is NOT CLOSED in HotelCMS yet, so the partial amount is not invoiced. Have the month closed in HotelCMS — Atlas then picks it up on the next early-morning fetch and the generation fills it in.',
            usage_source_disabled:
              'This branch / module ({{usageSourceType}}) is SWITCHED OFF in HotelCMS, so it reports no amount for {{usageMonth}}. If the service is live, switch it on in HotelCMS; if not, close the assignment.',
            usage_combined_minimum:
              'The property closes with a COMBINED minimum in HotelCMS, so the per-branch item ({{usageSourceType}}) for {{usageMonth}} cannot be priced: the two branches are raw amounts, and the billable amount is the larger branch or the shared minimum. Switch the assignment to Direct + inquiry — the generation then bills the module total.',
            missing_usage_ref:
              'The assignment has no measurable {{idField}}: none is selected on the assignment, the selected one was since removed from the account, or (on a not-yet-bound row) the account has no such ID. So the measured data ({{usageSourceType}}) cannot be linked to it. Add the ID on the account page and select it on the assignment (Source ID) — the next fetch and generation will then fill it in.',
          },
          refField: {
            hotelcmsId: 'HotelCMS ID',
            mailwizzId: 'MailWizz ID',
            hotelcmsOrZadircmsId: 'HotelCMS or ZadirCMS ID',
          },
          autoBillingLevel: { client: 'on the client', account: 'on the account' },
          filterReason: 'Reason',
          filterClient: 'Client',
          filterState: 'State',
          filterStateOpen: 'open',
          filterStateResolved: 'resolved only',
          openAssignment: 'Open assignment',
          release: 'Release lock',
          releaseSuccess: 'Lock released — the next generation run will recreate the item.',
          releaseConflict: 'This row is no longer a releasable lock (it changed in the meantime).',
          loading: 'loading…',
          countOf: '{{shown}} / {{total}} skips',
          empty: 'No skips this month',
          emptyDesc: 'The invoice generator did not log any problems for this month.',
          noMatches: 'No matches',
          noMatchesDesc: 'No skip matches the current filters.',
        },
        filterStatus: 'Status',
        filterType: 'Type',
        filterIssuedFrom: 'Issued from',
        filterIssuedTo: 'Issued to',
        filterDueFrom: 'Due from',
        filterDueTo: 'Due to',
        filterIssuer: 'Issuer',
        filterAll: '{{label}}: all',
        loading: 'loading…',
        columns: {
          invoice: 'Invoice',
          billingParty: 'Billed party',
          issuer: 'Issuer',
          amount: 'Amount',
          dates: 'Dates',
          status: 'Status',
          actions: 'Actions',
        },
        net: 'Net',
        vat: 'VAT',
        gross: 'Gross',
        issueDateLabel: 'Issued',
        dueDateLabel: 'Due',
        paymentDateLabel: 'Paid',
        statusLabel: {
          draft: 'Draft',
          'group:issued': 'issued (incl. overdue)',
          'group:void': 'voided (incl. storno)',
          issued: 'Issued',
          paid: 'Paid',
          overdue: 'Overdue',
          cancelled: 'Cancelled',
          storno: 'Storno',
        },
        typeLabel: {
          invoice: 'Invoice',
          proforma: 'Proforma',
        },
        docKind: {
          e_invoice: 'E-invoice',
          paper_invoice: 'Invoice',
          proforma: 'Proforma',
          storno: 'Storno',
        },
        issuerLabel: {
          none: '—',
          MOR: 'MOR',
          MDIG: 'MDIG',
        },
        more: '+{{count}}',
        moreItems: 'Show more items',
        countOf: '{{shown}} / {{total}} invoices',
        empty: 'No invoices',
        emptyDesc: 'No invoices have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No invoices match your filters.',
        // Draft-CRUD (write vertical)
        new: 'New invoice',
        edit: 'Edit',
        view: 'View',
        delete: 'Delete',
        deleting: 'Deleting…',
        cancel: 'Cancel',
        draftNumberPlaceholder: 'Draft',
        createTitle: 'New invoice',
        createSubtitle:
          'Create an invoice draft — once saved, you can issue it from the invoice page.',
        editTitle: 'Edit invoice',
        editSubtitle: 'The draft header and line items — the list refreshes after saving.',
        notEditableTitle: 'Invoice is not editable',
        notEditableDesc: 'Only draft invoices can be edited.',
        notFound: 'Invoice not found',
        notFoundDesc: 'It may have been deleted, or you may not have access.',
        forbidden: 'No permission',
        forbiddenDesc: 'You do not have permission to create or edit invoices.',
        deleteTitle: 'Delete draft',
        deleteDesc: 'Are you sure you want to delete this invoice draft? This cannot be undone.',
        scope: { account: 'Single account', client: 'Client (combined)' },
        form: {
          sectionScope: 'Recipient',
          sectionMeta: 'Document',
          sectionDates: 'Dates',
          sectionBilling: 'Buyer details',
          sectionItems: 'Line items',
          client: 'Client',
          clientPlaceholder: 'Select a client…',
          scope: 'Billing mode',
          account: 'Account',
          accountPlaceholder: 'Select an account…',
          accounts: 'Accounts',
          accountsPlaceholder: 'Select accounts…',
          accountsCount: '{{count}} selected',
          issuer: 'Issuer',
          type: 'Type',
          deliveryType: 'Invoice type',
          proformaPaperHint: 'A proforma is always paper-based.',
          paymentMethod: 'Payment method',
          currency: 'Currency',
          fulfillmentDate: 'Fulfillment',
          issueDate: 'Issue date',
          dueDate: 'Due date',
          billingProfile: 'Billing profile (prefill)',
          billingName: 'Billing name',
          billingTaxNumber: 'Tax number',
          billingEuTaxNumber: 'EU tax number',
          billingCountryCode: 'Country (code)',
          billingZipCode: 'ZIP code',
          billingCity: 'City',
          billingAddress: 'Address',
          billingEmails: 'Email addresses',
          billingEmailsHint: 'Separate multiple addresses with commas.',
          sectionNote: 'Note',
          note: 'Invoice note',
          noteHint: 'Printed in the note block of the invoice (e.g. VAT exemption clause).',
          addItem: 'Add item',
          removeItem: 'Remove item',
          back: 'Back',
          save: 'Save draft',
          saving: 'Saving…',
        },
        item: {
          servicePlan: 'Service',
          servicePlanPlaceholder: 'Choose from the catalogue…',
          servicePlanLockedHint:
            'Generated item: its service comes from the assignment and cannot be changed. The name and price can be edited.',
          servicePlanUnavailable: 'Service no longer available in the catalogue',
          name: 'Description',
          quantity: 'Quantity',
          quantityUnit: 'Unit',
          unitPrice: 'Unit price',
          vatRate: 'VAT rate',
          note: 'Note',
          net: 'Net',
          vatAmount: 'VAT',
          gross: 'Gross',
        },
        totals: { net: 'Net total', vat: 'VAT total', gross: 'Grand total' },
        quantityUnitLabel: { piece: 'piece', hour: 'hour', day: 'day', other: 'other' },
        vatRateLabel: {
          VAT_27: '27%',
          VAT_18: '18%',
          VAT_5: '5%',
          VAT_0: '0%',
          AAM: 'AAM (VAT-exempt)',
          TAM: 'TAM (exempt subject)',
          FAA: 'FAA (reverse charge)',
          EUFAD37: 'EUFAD37 (EU reverse, §37)',
        },
        deliveryTypeLabel: { electronic: 'E-invoice', paper: 'Paper-based' },
        paymentMethodLabel: {
          bank_transfer: 'Bank transfer',
          cash: 'Cash',
          card: 'Card',
          other: 'Other',
        },
        footerLegal: {
          FAA: 'Reverse charge (VAT Act §142).',
          EUFAD37:
            'The recipient of the service is liable to pay the tax (reverse charge, VAT Act §37).',
        },
        // Read-only invoice detail (/invoices/$id)
        detail: {
          back: 'Back to list',
          sections: {
            document: 'Document details',
            issuer: 'Issuer',
            billing: 'Buyer',
            items: 'Line items',
            summary: 'Summary',
            note: 'Note',
            accounts: 'Billed accounts',
          },
          fields: {
            currency: 'Currency',
            deliveryType: 'Invoice type',
            paymentMethod: 'Payment method',
            fulfillmentDate: 'Fulfillment',
            issueDate: 'Issue date',
            dueDate: 'Due date',
            paymentDate: 'Payment date',
            stornoInvoiceNumber: 'Reverse invoice no.',
            relatedInvoiceNumber: 'Related invoice',
            stornoComment: 'Reverse comment',
            name: 'Name',
            taxNumber: 'Tax number',
            euTaxNumber: 'EU tax number',
            address: 'Address',
            bank: 'Bank',
            emails: 'Email addresses',
            note: 'Note',
          },
          accountsCount: '{{count}} account(s)',
        },
        selectRow: 'Select row',
        selectAll: 'Select all loaded drafts',
        // Invoice issuance lifecycle (Számlázz.hu) actions + confirm dialogs.
        actions: {
          issue: 'Issue',
          bulkIssue: 'Issue ({{count}})',
          bulkIssueTitle: 'Bulk issue',
          bulkIssueDesc:
            'Issue {{count}} drafts through Számlázz.hu. Issuing is IRREVERSIBLE: the provider assigns a number, after which the document can only be voided.',
          bulkIssueConfirm: 'Issue',
          issueTitle: 'Issue invoice',
          issueDesc:
            'Issue this document via Számlázz.hu? Once issued, the invoice can no longer be edited.',
          issuing: 'Issuing…',
          storno: 'Reverse',
          stornoTitle: 'Reverse invoice',
          stornoDesc:
            'Reverse this invoice? Számlázz.hu will generate the reverse (storno) invoice.',
          stornoComment: 'Reverse comment',
          stornoCommentPlaceholder: 'Comment for the reversal (optional)…',
          stornoing: 'Reversing…',
          cancel: 'Void',
          cancelTitle: 'Void proforma',
          cancelDesc:
            'Void this proforma? Számlázz.hu will delete it — the PDF will no longer be available.',
          cancelling: 'Voiding…',
          pdf: 'Download PDF',
          pdfStorno: 'Reverse PDF',
          pdfOriginal: 'Original PDF',
          sync: 'Sync',
        },
      },
      billingProfiles: {
        filterClient: 'Client',
        filterPrimary: 'Primary',
        onlyPrimary: 'Primary only',
        loading: 'loading…',
        columns: {
          billingName: 'Billing name',
          client: 'Client',
          taxNumber: 'Tax number',
          address: 'Address',
          paymentDeadline: 'Payment deadline',
          primary: 'Primary',
          actions: 'Actions',
        },
        primaryBadge: 'primary',
        notPrimaryTooltip: 'Not the primary billing profile',
        directors: {
          label: 'Managing directors',
          sectionHint:
            'Several managing directors can be recorded, each with an optional from–to period. An entry without an end date marks the current managing director.',
          name: 'Name',
          startsOn: 'From',
          endsOn: 'To',
          add: 'Add managing director',
          remove: 'Remove managing director',
          empty: 'No managing director recorded.',
          periodRange: '{{from}} – {{to}}',
          periodFrom: 'since {{from}}',
          periodUntil: 'until {{to}}',
          periodUnknown: 'period unknown',
          current: 'current',
          overlapWarning: 'Overlapping period — another managing director also covers this period.',
          autoClosedHint:
            'The end date of the previous open entry was filled in automatically. You can change it.',
        },
        days: '{{count}} days',
        countOf: '{{shown}} / {{total}} profiles',
        empty: 'No billing profiles',
        emptyDesc: 'No billing profile has been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No billing profile matches the filters.',
        new: 'New profile',
        edit: 'Edit',
        delete: 'Delete',
        deleting: 'Deleting…',
        setPrimary: 'Set as primary',
        copyDetails: {
          action: 'Copy billing details',
          actionFor: 'Copy billing details: {{name}}',
          editFor: 'Edit: {{name}}',
          name: 'Company name',
          seat: 'Registered address',
          taxNumber: 'Tax number',
          euTaxNumber: 'EU VAT number',
          director: 'Representative',
          emails: 'E-invoice email',
        },
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        createTitle: 'New billing profile',
        createDesc: 'A client billing identity — name, tax number, address, defaults.',
        editTitle: 'Edit billing profile',
        deleteTitle: 'Delete billing profile',
        deleteDesc: 'Delete the billing profile “{{name}}”? This action cannot be undone.',
        form: {
          client: 'Client',
          clientPlaceholder: 'Select a client…',
          billingName: 'Billing name',
          taxNumber: 'Tax number',
          euTaxNumber: 'EU tax number',
          countryCode: 'Country',
          countryPlaceholder: 'Select a country…',
          zipCode: 'ZIP code',
          city: 'City',
          address: 'Address',
          billingEmails: 'Billing email addresses',
          billingEmailsHint: 'Separate multiple addresses with commas.',
          paymentDeadline: 'Payment deadline (days)',
          paymentDeadlineHint: 'A number between 1 and 31 (optional).',
          note: 'Default invoice note',
          noteHint: 'Pre-filled into the note block of the invoice (e.g. EU VAT number).',
          isPrimary: 'Primary profile',
          isPrimaryHint:
            "The client's default billing profile — invoices are issued from it. The first profile is automatically primary; switching this on demotes the previous primary.",
          isPrimaryLockedHint:
            'This is the current primary profile. It cannot be switched off (the client would be left without a primary) — set another profile as primary instead, which automatically demotes this one.',
        },
      },
      paymentCards: {
        filterStatus: 'Status',
        loading: 'loading…',
        columns: {
          assignments: 'Assignments',
          card: 'Card',
          bank: 'Bank',
          expiry: 'Expiry',
          status: 'Status',
          client: 'Client',
          actions: 'Actions',
        },
        status: {
          valid: 'Valid',
          expiring: 'Expiring soon',
          expired: 'Expired',
        },
        brands: {
          visa: 'VISA',
          mastercard: 'Mastercard',
          amex: 'American Express',
          maestro: 'Maestro',
          other: 'Other',
        },
        noAssignments: 'no assignments',
        scopeLabel: { client: 'client level', account: 'account level' },
        moreAssignments: '+{{count}} more',
        moreAssignmentsAria: 'More assignments',
        countOf: '{{shown}} / {{total}} cards',
        empty: 'No payment cards',
        emptyDesc: 'No payment card has been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No payment card matches the filters.',
        new: 'New card',
        edit: 'Edit',
        delete: 'Delete',
        deleting: 'Deleting…',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        createTitle: 'New payment card',
        createDesc: 'A client payment card — brand, last 4 digits, expiry, bank.',
        editTitle: 'Edit payment card',
        deleteTitle: 'Delete payment card',
        deleteDesc: 'Delete the payment card “{{card}}”? This action cannot be undone.',
        form: {
          client: 'Client',
          clientPlaceholder: 'Select a client…',
          holderName: 'Cardholder name',
          brand: 'Card brand',
          last4: 'Last 4 digits',
          last4Hint: 'The last 4 digits of the card number.',
          expiryMonth: 'Expiry month',
          expiryMonthHint: 'A number between 1 and 12.',
          expiryYear: 'Expiry year',
          expiryYearHint: 'A four-digit year (e.g. 2030).',
          bankName: 'Bank name',
          note: 'Note',
        },
      },
      finance: {
        forbiddenTitle: 'No permission',
        forbiddenDesc:
          'Viewing the revenue/expenses page needs a finance permission. Ask an administrator if you need it.',
        tabs: {
          summary: 'Overview',
          revenue: 'Revenue',
          expenses: 'Expenses',
        },
        summaryViews: {
          label: 'Overview view',
          overview: 'At a glance',
          revenue: 'Revenue analysis',
          expenses: 'Expense analysis',
        },
        overview: {
          cashflowTitle: 'Revenue and expenses — 12 months',
          revenueActual: 'revenue (invoiced)',
          revenuePlan: 'revenue (plan)',
          expense: 'expenses (booked)',
          balance: 'balance',
          margin: 'margin',
          cashflowNote:
            'Revenue is the invoiced actual for closed months, the plan for open months and before {{month}} (lighter bar). Expenses are the booked net, in HUF.',
          cashflowNoteNoHorizon:
            'Revenue is the invoiced actual for closed months and the plan for open months (lighter bar). Expenses are the booked net, in HUF.',
          foreignNote:
            'Foreign-currency expenses are excluded — the Expense analysis shows them per currency.',
          topRevenue: 'Largest revenue sources (month plan)',
          topRevenueActual: 'Largest revenue sources (month actual)',
          topExpense: 'Largest expense categories (month)',
        },
        kpi: {
          none: 'No data',
          margin: 'Margin',
          marginDesc: 'Balance as a share of revenue (from the cards above)',
          receivables: 'Receivables',
          receivablesDesc: 'Gross of issued, unpaid invoices, as of today',
          receivablesForeign: 'Also in foreign currency: {{list}}',
          overdue_one: 'of which overdue: {{amount}} ({{count}} invoice)',
          overdue_other: 'of which overdue: {{amount}} ({{count}} invoices)',
          concentration: 'Client concentration',
          concentrationDesc: 'Share of the {{count}} largest clients in the month plan revenue',
          concentrationDescActual:
            'Share of the {{count}} largest clients in the month invoiced revenue',
          nextMonth: 'Next month (plan)',
          nextMonthDesc: 'Expected revenue for {{month}}',
        },
        expenseFilters: {
          type: 'Type',
          salary: 'payroll',
          other: 'other',
        },
        expenseAnalytics: {
          byCategory: 'Expenses by category (month)',
          salarySplit: 'Payroll vs other expenses (month)',
          salary: 'payroll',
          nonSalary: 'other expenses',
          uncategorized: 'uncategorized',
          trend: 'Expenses by category — 12 months',
          topSuppliers: 'Largest suppliers (month)',
          other: 'other',
        },
        summary: {
          note: 'Revenue is a forecast, expenses are booked facts — the difference is an indicative balance.',
          noteActual:
            'Closed month: revenue is the invoiced actual, expenses are booked facts — the difference is the balance.',
          unfilteredNote:
            'These three figures always cover the WHOLE month — filtering applies to the breakdown below and to the Revenue tab.',
          revenue: 'Revenue',
          revenueDesc: 'Due this month from active assignments',
          revenueDescActual: 'Invoiced actual for the month, from the invoices',
          expense: 'Expenses',
          expenseDesc: 'Net total of the expenses booked to this month',
          balance: 'Balance',
          balanceDesc: 'Revenue minus expenses (HUF only)',
          foreignNote: 'Foreign-currency expenses, outside the balance:',
          uncategorized:
            '{{count}} expenses still need a category — sort them on the Expenses tab.',
          byCategory: 'Expenses by category',
          byCategoryEmpty: 'No expenses recorded for this month yet.',
          itemCount: '{{count}} items',
        },
      },
      expenses: {
        loading: 'Loading…',
        countOf: '{{shown}} of {{total}} expenses',
        new: 'New expense',
        newForbidden: 'You do not have permission to record an expense.',
        empty: 'No expenses this month',
        emptyDesc:
          'Invoices imported from NAV land here automatically; foreign and receipt-based items can be added manually.',
        noMatches: 'No expenses match the filter',
        noMatchesDesc: 'Try widening the filter or clearing the search term.',
        uncategorized: 'Unclassified',
        employee: 'Colleague',
        employeeHint: 'Who the cost belongs to (salary, benefits) — may be left empty.',
        employeeNone: 'Not set',
        employeeAria: '{{supplier}} — assign colleague',
        employeeNotApplicable: 'not a salary item',
        bulk: {
          selectRow: 'Select row',
          selectAll: 'Select every loaded row',
          button: 'Classify ({{count}})',
          title: 'Classify {{count}} expenses',
          description:
            'A field left on “leave unchanged” is not touched — moving the month will not clear the category, and the other way round.',
          keep: 'leave unchanged',
          monthDerived: 'from the document',
          apply: 'Classify ({{count}})',
        },
        month: {
          label: 'Accounting month',
          unknown: 'no date',
          derived: 'from the document',
          derivedOption: 'From the document',
          aria: 'Accounting month for the expense from {{supplier}}',
          filterAll: 'All months',
        },
        amountUnknown: 'no amount',
        saveRow: 'Save',
        dismiss: 'Dismiss',
        undismiss: 'Restore',
        amountUnknownHint:
          'NAV reported no amount for this document (simplified invoice) — the stored 0 is the gap, not the price.',
        amountManualHint:
          'The amount was filled in by hand — NAV reported none for this document. It can be changed.',
        amountFill: 'Fill in amount',
        copy: 'Copy',
        amountEdit: 'Edit amount',
        amountFillTitle: 'Fill in the missing amount',
        amountEditTitle: 'Change the filled-in amount',
        amountFillDesc:
          'NAV reported no amount for this simplified invoice. Enter the net and the VAT from the document — the gross is calculated.',
        amountNet: 'Net',
        amountVat: 'VAT',
        amountGross: 'Gross (calculated)',
        amountGrossShort: 'Gross',
        issueDateLabel: 'Issued',
        dueDateLabel: 'Due',
        classifyAria: 'Categorise the expense from {{supplier}}',
        deleteTitle: 'Delete expense',
        deleteDesc: 'Delete the item from {{supplier}}? This cannot be undone.',
        columns: {
          invoice: 'Partner',
          invoiceNumber: 'Invoice number',
          taxNumber: 'Tax number',
          originalInvoiceNumber: 'Original invoice number',
          stornoedBy: 'Cancelling document',
          category: 'Category',
          amount: 'Amount',
          dates: 'Dates',
          note: 'Note',
        },
        filters: {
          month: 'Month',
          category: 'Category',
          issuer: 'Issuer',
          source: 'Source',
          status: 'Status',
        },
        dismissedFilter: {
          hidden: 'Active',
          only: 'Dismissed',
        },
        source: {
          nav: 'NAV',
          manual: 'manual',
        },
        navOperation: {
          MODIFY: 'amendment',
          STORNO: 'cancellation',
        },
        stornoed: 'cancelled',
        summary: {
          none: 'No expenses recorded for this month yet.',
          title: 'Expenses this month',
          statusTitle: 'Status',
          netIn: 'Net ({{currency}})',
          grossAndCount: 'gross {{gross}} · {{count}} items',
          uncategorizedLabel: 'Awaiting a category',
          salaryWithoutEmployeeLabel: 'Salary item without a colleague',
          unknownAmountLabel: 'No amount reported',
          navImportLabel: 'Last NAV import',
          dismissedLabel: 'Dismissed',
        },
        form: {
          createTitle: 'New expense',
          createDesc:
            'A manual item: foreign supplier, receipt or an invoice without a tax number — one that never appears in the NAV data service.',
          editTitle: 'Edit expense',
          copyTitle: 'Copy expense',
          copyDesc:
            'A new expense with the details of {{supplier}}. The invoice number is empty — a new document has its own number.',
        },
        fields: {
          supplierName: 'Supplier',
          supplierTaxNumber: 'Tax number',
          supplierTaxNumberHint: 'May be left empty for a foreign supplier.',
          invoiceNumber: 'Invoice number',
          noBill: 'No invoice',
          noBillHint:
            'Receipt or a cost with no document — the invoice number may then be omitted. The date is still required: the accounting month derives from it.',
          noBillValue: 'No invoice',
          issueDate: 'Issue date',
          costDate: 'Date of the cost',
          fulfillmentDate: 'Fulfilment date',
          dueDate: 'Payment due',
          netAmount: 'Net',
          vatAmount: 'VAT',
          grossAmount: 'Gross (calculated)',
          currency: 'Currency',
          issuer: 'Issuer',
          issuerHint:
            'Which of our companies the cost belongs to — on a NAV-imported row the query’s tax number decides it.',
          category: 'Category',
          accountingMonth: 'Accounting month',
          accountingMonthHint:
            'Which month’s result this item affects. “From the document” uses the fulfilment date, or the issue date when there is none.',
          note: 'Note',
        },
        nav: {
          action: 'Fetch from NAV',
          running: 'Fetching…',
          done: '{{count}} new documents arrived',
          nothingNew: 'No new documents arrived',
          reportLine: 'New: {{created}} · already here: {{skipped}}',
          reportFailed: '{{count}} documents could not be written — the next fetch retries them',
          reportDismissed: '{{count}} rows discarded (complete cancellation pairs)',
          failed: 'The NAV fetch failed ({{code}})',
          disabled: 'The NAV import is not wired up in this environment.',
          lastImport: 'last document: {{date}}',
          keyExpiringShort: 'signing key',
          keyExpiring:
            'The NAV signing key expires in {{days}} days ({{date}}) — replace it or the import stops.',
          keyExpired: 'The NAV signing key expired ({{date}}) — request signing will fail.',
        },
        import: {
          button: 'Expense import',
          rowBadge: 'imported',
          title: 'Bulk expense import',
          description:
            'It records exactly what you would enter by hand — only many rows at once. Nothing is written before you confirm.',
          pickHelp:
            'Download the template, fill in the rows and upload it back. The second sheet lists the valid category paths and colleague names to copy from.',
          downloadTemplate: 'Download template',
          chooseFile: 'Choose file',
          sheet: {
            rows: 'Expenses',
            values: 'Valid values',
            categories: 'Category (full path)',
            salary: 'Of these, salary type',
            employees: 'Colleagues',
            issuers: 'Issuer',
            currencies: 'Currency',
            noBill: 'No invoice',
            months: 'Accounting month',
          },
          columns: {
            issuerKey: 'Issuer',
            supplierName: 'Supplier',
            supplierTaxNumber: 'Tax number',
            invoiceNumber: 'Invoice number',
            noBill: 'No invoice',
            issueDate: 'Date',
            fulfillmentDate: 'Fulfilment',
            dueDate: 'Due date',
            netAmount: 'Net',
            vatAmount: 'VAT',
            currency: 'Currency',
            categoryPath: 'Category (full path)',
            employeeName: 'Colleague',
            accountingMonth: 'Accounting month',
            note: 'Note',
          },
          guides: {
            issuerKey: 'Which of our companies paid. Pick from the list: MOR or MDIG.',
            supplierName:
              'Who the cost came from. On a salary row leave it EMPTY — the colleague name fills in.',
            supplierTaxNumber:
              'Only if there is one. Format: 12345678-2-41. Leave empty for a foreign supplier.',
            invoiceNumber:
              'The document number. If there is no invoice (receipt, salary) leave it EMPTY.',
            issueDate:
              'REQUIRED. Format: 2026-09-05 (year-month-day). 2026. 09. 05. and 2026/09/05 also work.',
            fulfillmentDate:
              'If there is one. Format: 2026-09-05. It decides the accounting month before the issue date.',
            dueDate: 'If there is one. Format: 2026-09-30.',
            netAmount:
              'REQUIRED, net amount. Decimal separator: COMMA (1234,56). Thousands may use a space or dot.',
            vatAmount:
              'VAT amount. Zero for salary and loan. The gross is computed — there is no column for it.',
            currency: 'Pick from the list. Left empty it is HUF.',
            categoryPath:
              'Pick from the list — the FULL path is needed (e.g. Advisory bér / Bónusz). A collector category cannot be used.',
            employeeName:
              'Only on a SALARY-type item. Pick from the list; either name order works.',
            accountingMonth:
              'Which month it charges. Format: 2026-09. Left empty we derive it from the date.',
            note: 'Free text, optional.',
          },
          noBill: { missing: 'none', present: 'yes' },
          rowCount: '{{count}} rows',
          errorCount: '{{count}} with errors',
          warningCount: '{{count}} warnings',
          blocked:
            'While a row has an error the import cannot be committed — fix them with the row action.',
          fix: 'Fix',
          fixTitle: 'Fix row',
          fixDesc: 'Row {{row}} — the same form as manual entry.',
          commit: 'Commit ({{count}})',
          another: 'Another file',
          doneTitle: '{{count}} expenses recorded.',
          doneFailed: '{{count}} rows did not go in — the batch can be undone.',
          doneHelp:
            'If it is not right, the batch can be undone in one click. The rows are marked "imported" in the list.',
          undo: 'Undo',
          showBatch: 'Show in the list',
        },
        categories: {
          button: 'Categories',
          title: 'Expense categories',
          desc: 'The tree is at most three levels: category → sub-category → item. No level is mandatory.',
          empty: 'No categories yet — add one below.',
          newTitle: 'New category',
          editTitle: 'Edit category',
          name: 'Name',
          parent: 'Parent category',
          parentHint: 'Left empty it becomes a main category itself. Nothing can go under an item.',
          rootOption: 'None (main category)',
          requiresEmployee: 'Salary-type item',
          requiresEmployeeHint: 'Costs filed here can name the colleague they belong to.',
          employeeBadge: 'Salary-type item',
          add: 'Add',
          saveEdit: 'Save',
          editAria: 'Edit {{name}}',
          deleteAria: 'Delete {{name}}',
          deleteTitle: 'Delete category',
          deleteDesc: 'Delete the category "{{name}}"?',
        },
      },
      revenueForecast: {
        loading: 'Computing…',
        columns: {
          client: 'Client',
          service: 'Service',
          plan: 'Plan',
          category: 'Category',
          amount: 'Amount',
          source: 'Source',
          invoice: 'Document',
          partner: 'Partner',
          billingName: 'Billing name',
          frequency: 'Frequency',
          status: 'Status',
        },
        source: {
          plan: 'plan',
          actual: 'actual',
        },
        amountNet: 'Net',
        amountVat: 'VAT',
        amountGross: 'Gross',
        unclassified: 'Unclassified',
        unclassifiedItem: 'unclassified item',
        list: {
          loading: 'Loading…',
          countOf: '{{shown}} / {{total}} items',
          monthTotal: "This month's revenue",
          statusTitle: 'Status',
          netTotal: 'Net (HUF)',
          itemCount_one: '{{count}} item',
          itemCount_other: '{{count}} items',
          sourceLabel: 'Source',
          rateLabel: 'Exchange rate',
          rateFallback: 'missing',
          listLabel: 'List',
          listTruncated: 'truncated',
          sourceShort: { plan: 'plan', actual: 'actual' },
          beforeHorizon:
            'There is no actual data for this month: Atlas has invoices from {{from}}. The list therefore shows the PLAN computed from the assignments.',
          statusNoEffectOnActual:
            'Closed month: the rows are the invoiced items — the status filter does not apply to them (invoiced revenue does not depend on an assignment’s current status).',
          notClosedYet:
            'The month is still running, or its billing round has not completed yet — so the list shows PLAN data calculated from the assignments.',
        },
        periodRule:
          'Monthly fees count in the month the service runs. Quarterly / semi-annual / annual fees: the full amount at once, in the month the invoice is issued — not spread across months.',
        currencyNote:
          'All amounts in HUF. Plan: foreign currency → MNB prior month-end rate. Actual (closed month): the rate recorded for the month, otherwise the MNB monthly average.',
        statusScopeNote:
          'The status filter applies to the plan-side figures and breakdowns (MRR, PRR, NRR, ARR, AR, the service, client and category breakdowns, the drilldown). It does not apply to the “Revenue this month” and “Expected next month” cards or the plan line of the plan-vs-actual trend — these always show the active portfolio — nor to the actuals: invoiced revenue does not depend on an assignment’s current status.',
        fallbackRateNote:
          'Exchange-rate data is unavailable; foreign-currency items are shown at an estimated/1:1 rate.',
        usageMissingNote:
          '{{count}} tiered/commission item has no usage data for this closed month — those are shown at the base fee, so the real amount may be higher.',
        usageOpenMonthNote:
          'This month is not closed yet: usage data arrives after the close, until then {{count}} tiered/commission item is shown at the base fee.',
        usageMissingShow: 'Which ones?',
        dataHorizon: {
          title: 'Looking back, the calculation cannot be exact — missing data',
          intro:
            'What is not recorded cannot be estimated. In Atlas the data is complete from these months:',
          actuals:
            'Invoices (actuals): from {{month}} — before that there is no actual figure; plan vs actual is meaningful only from then.',
          usage:
            'Usage data: from {{month}} — before that tiered/commission items can only be estimated at the base fee.',
          oneTime:
            'One-off fees: roughly from {{month}} — before that they are recorded incompletely, and the gap cannot be filled.',
          beforeActuals:
            'There is no actual figure for the selected month: Atlas has invoices from {{month}}. The plan figure is computed from the assignments.',
        },
        usageMissingMore: '…and {{count}} more',
        usagePricedNote:
          "{{count}} tiered/commission item is priced from this month's measured usage.",
        prevMonth: 'Previous month',
        nextMonth: {
          title: 'Expected next month revenue',
          desc: 'Revenue of the real next calendar month: the monthly fees, plus the quarterly / semi-annual / annual fees to be invoiced in that month at their full amount (always active status).',
        },
        selectedMonth: {
          title: 'Selected month revenue',
          desc: 'Revenue of the selected month: the monthly fees, plus the quarterly / semi-annual / annual fees invoiced in that month at their full amount (always active status, prorated).',
        },
        counts: {
          label: 'Items by status',
          filterHint: 'Filter to this status (click again to reset)',
          active: 'active',
          pending: 'pending',
          inactive: 'inactive, not closed',
          inactiveHint:
            'Inactive items with an open or future end date (the item itself or its client / account is inactive) — they are not billed. Click for the list.',
        },
        nextMonthAria: 'Next month',
        emptyTitle: 'No match for these filters',
        emptyDesc:
          'No assignment in this month matches the search and filters, so every figure is zero. Try widening the filters, or move to another month.',
        emptyMonthTitle: 'No revenue in this month',
        emptyMonthDesc:
          'There is no valid service assignment for this month. Move to another month, or add the missing assignments.',
        filterStatus: 'Status',
        filterBillingType: 'Charge type',
        billingType: { recurring: 'recurring', one_time: 'one-off' },
        oneTimeOnlyNote:
          'Filtered to one-off charges there is no recurring revenue: MRR, PRR and ARR are therefore 0, and the MRR trend and status counts are hidden.',
        filterMainCategory: 'Main category',
        filterSubCategory: 'Sub-category',
        filterService: 'Service',
        filterPlan: 'Plan',
        filterClient: 'Client',
        filterAccount: 'Account',
        status: { active: 'Active', pending: 'Pending', inactive: 'Inactive' },
        mainCategory: { product: 'Product', advisory: 'Advisory', other: 'Other' },
        subCategory: {
          roomsome: 'RoomSome',
          innovation: 'Innovation',
          digital_marketing: 'Digital marketing',
          revenue_management: 'Revenue management',
          web_development: 'Web development',
          other: 'Other',
        },
        freqShort: { quarterly: 'Q', semi_annual: 'H', annual: 'Y' },
        mrr: {
          title: 'MRR',
          desc: 'Monthly recurring revenue',
          info: 'MONTHLY-frequency recurring revenue earned in the selected month — on an accrual basis: it counts towards the month the service runs in, not the month it is invoiced. Quarterly/semi-annual/annual items belong to PRR. The badge shows the change vs. the previous month.',
          subtitle: {
            current: 'Monthly recurring revenue',
            historical: 'Historical monthly recurring revenue',
            projected: 'Projected monthly recurring revenue',
          },
        },
        prr: {
          title: 'PRR',
          desc: 'Periodic recurring revenue',
          info: 'The FULL fee of NON-monthly (quarterly/semi-annual/annual) recurring items, in the month the invoice is issued — an annual fee therefore appears once, in a single month, and not in the others. Badges show the breakdown by frequency.',
        },
        nrr: {
          title: 'NRR',
          desc: 'Non-recurring revenue',
          info: 'The FULL price of one-off charges STARTING in the selected month — a one-off fee is not spread across months. Tracking-only rows are excluded here too.',
        },
        arr: {
          title: 'ARR',
          desc: 'Annual recurring revenue',
          info: 'Recurring revenue across the ROLLING 12 months ending with the selected month: the monthly fees + the quarterly / semi-annual / annual fees invoiced in the window (an annual fee once, in full). NOT MRR × 12.',
        },
        ar: {
          title: 'AR',
          desc: 'Total annual revenue',
          info: 'Total annual revenue: ARR + the full price of ALL one-off items starting within the SAME rolling 12 months. NOT NRR × 12 — NRR covers a single month.',
        },
        charts: {
          mrrTrend: 'MRR trend (12 months)',
          mrrTrendAria: 'MRR trend over the last 12 months. Latest value: {{value}}.',
          byService: 'Revenue by service',
          fixedFee: 'fixed fee',
          usageBased: 'usage-based',
          byServiceHint: 'Click a service for the plan-level breakdown.',
          byPlanHint: 'Click a plan to jump to its details below.',
          byClient: 'Top 10 clients',
          categoryDistribution: 'Revenue distribution by main category',
          categoryAria: 'Revenue distribution by main category (product, advisory, other).',
          recurring: 'recurring',
          oneTime: 'one-off',
          legendCategory: {
            product: 'Product',
            advisory: 'Advisory',
            other: 'other',
            unclassified: 'unclassified',
          },
          empty: 'No data to display for this month.',
          planVsActual: 'Plan vs actual (12 months)',
          planVsActualAria:
            'Planned and actual revenue over the last 12 months. Actuals appear only for closed months.',
          planSeries: 'plan',
          actualSeries: 'actual',
          actualByService: 'Actual — by service',
          actualByClient: 'Actual — by client',
          actualCategory: 'Actual — by main category',
        },
        actual: {
          planLabel: 'Plan',
          actualLabel: 'Actual',
          open: 'Month not closed yet',
          noData: 'No data',
          carriedNote: 'of which carried over: {{amount}}',
          openHint:
            'The month is still running, or its billing round has not completed yet — so there is no actual figure. The plan figure is unaffected.',
          unclassified: 'Unclassified',
          unknownClient: 'Unknown client',
          skipped_one: '{{count}} item was skipped during invoicing — check the skip log',
          skipped_other: '{{count}} items were skipped during invoicing — check the skip log',
          rateEstimated:
            'No fixed exchange rate for this month, so foreign-currency items use the MNB monthly average (estimated).',
          rateFallback:
            'No exchange rate could be resolved for this month, so foreign-currency items are counted 1:1 — the actual total is UNDERSTATED.',
          truncated:
            'The invoice list hit the paging ceiling, so the actual total may be truncated. Narrow the filters.',
        },
        scope: { client: 'Client', account: 'Account' },
        frequencyLabel: {
          monthly: 'monthly',
          quarterly: 'quarterly',
          semi_annual: 'semi-annual',
          annual: 'annual',
          one_time: 'one-off',
        },
        details: {
          empty: 'No items to display.',
          total: 'Total',
          oneTime: 'one-off',
          notDue: 'no invoice this month',
          dueRevenue: 'revenue this month',
          periodFee: {
            quarterly: 'quarterly fee',
            semi_annual: 'semi-annual fee',
            annual: 'annual fee',
          },
          billedNow: 'invoiced in full this month',
          nextBilling: 'next invoice: {{month}}',
          export: {
            service: 'Service',
            client: 'Client',
            account: 'Account',
            scope: 'Scope',
            frequency: 'Frequency',
            status: 'Status',
            amount: 'Amount this month (HUF)',
            periodFee: 'Full fee (HUF)',
            billedThisMonth: 'Invoiced this month',
            nextInvoice: 'Next invoice',
            components: 'Components',
            yes: 'yes',
            no: 'no',
          },
          nrrTitle: 'NRR — one-off items',
          prrTitle: 'PRR — periodic items',
          pendingTitle: 'Pending items',
          inactiveTitle: 'Inactive, not closed items',
          inactiveDesc:
            'These items are not billed: the item or its client / account is inactive, but the end date is open or in the future. To fix: set the end date (or reactivate the item).',
        },
      },
      services: {
        tabs: { assignments: 'Services', catalog: 'Catalogue' },
        forbiddenTitle: 'You do not have access to this page',
        forbiddenDesc:
          'Viewing service assignments and the catalogue needs a separate permission. Ask your administrator.',
      },
      serviceAssignments: {
        filterClient: 'Client',
        filterAccount: 'Account',
        filterScope: 'Scope',
        filterType: 'Type',
        filterStatus: 'Status',
        filterFrequency: 'Frequency',
        filterAgreement: 'Payment',
        filterPricing: 'Pricing',
        loading: 'loading…',
        columns: {
          service: 'Service',
          owner: 'Client / account',
          type: 'Type',
          status: 'Status',
          quantity: 'Quantity',
          frequency: 'Frequency',
          netAmount: 'Net',
          actions: 'Actions',
        },
        complexService: 'complex service · {{count}} components',
        scopeLabel: { client: 'client level', account: 'account level' },
        moreTargets: '+{{count}} accounts',
        moreTargetsAria: 'More target accounts',
        componentsCount_one: '{{count}} component',
        componentsCount_other: '{{count}} components',
        componentsAria: 'Components and their prices',
        countOf: '{{shown}} / {{total}} assignments',
        empty: 'No service assignments',
        emptyDesc: 'No assignment has been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No assignment matches the filters.',
        new: 'New assignment',
        edit: 'Edit',
        close: 'Close',
        closedBadge: 'Closed',
        reopen: {
          action: 'Reopen',
          success: 'Reopened — the closing date has been cleared.',
        },
        delete: 'Delete',
        deleting: 'Deleting…',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        createTitle: 'New service assignment',
        createDesc:
          'A service plan assigned to a client/account — the snapshot is built from the catalog.',
        editTitle: 'Edit assignment',
        deleteTitle: 'Delete assignment',
        deleteDesc: 'Delete the assignment “{{name}}”? This action cannot be undone.',
        clone: {
          action: 'Duplicate',
          title: 'Duplicate assignment',
          desc: 'A copy of the "{{name}}" assignment — pick where it should go.',
          summaryPlan: 'Plan',
          summaryPricing: 'Pricing',
          summaryPrices: 'Price rows',
          summaryComponents: 'Components',
          summaryPeople: 'Responsibles',
          priceCount_one: '{{count}} price row copied',
          priceCount_other: '{{count}} price rows copied',
          componentCount_one: '{{count}} component copied',
          componentCount_other: '{{count}} components copied',
          peopleAll: 'contact and expert copied',
          peopleExpertOnly: 'only the expert is copied',
          otherClientWarning:
            'You are copying to another client: the contact, the payment card and the selected target accounts are not carried over — set them after copying.',
          startDate: 'Start date',
          startDateHint:
            'The copy starts on this date (the source start and end dates are not inherited).',
          confirm: 'Duplicate',
          saving: 'Copying…',
        },
        closeTitle: 'Close assignment',
        closeDesc: 'Billing of “{{name}}” will end on the given date.',
        closeEndDate: 'Billing end date',
        closeConfirm: 'Close',
        closing: 'Closing…',
        lockedRecurring:
          'Invoiced recurring assignment — only status, quantity, issuer, end/contract dates and the note are editable.',
        lockedOneTime: 'Invoiced one-time assignment — only the note is editable.',
        scope: { client: 'Client', account: 'Account' },
        typeLabel: { tracking: 'tracking', billing: 'billing' },
        statusLabel: { active: 'active', pending: 'pending', inactive: 'inactive' },
        billingTypeLabel: { one_time: 'one-time', recurring: 'recurring' },
        quantityUnitLabel: { piece: 'pcs', hour: 'hour', day: 'day', other: 'other' },
        issuerLabel: { none: '—', MOR: 'MOR', MDIG: 'MDIG' },
        invoiceTypeLabel: { invoice: 'invoice', proforma: 'proforma' },
        deliveryTypeLabel: { electronic: 'e-invoice', paper: 'paper-based' },
        frequencyLabel: {
          monthly: 'monthly',
          quarterly: 'quarterly',
          semi_annual: 'semi-annual',
          annual: 'annual',
          one_time: 'one-off',
        },
        agreementLabel: { prepaid: 'prepaid', postpaid: 'postpaid' },
        invoiceView: {
          label: 'View',
          list: 'List',
          invoices: 'By invoice',
          note: 'Which service lands on which invoice at generation (same rule as invoicing). This view shows no amounts or due dates.',
          recipientClient: 'Shared client invoice · {{name}}',
          recipientAccount: 'Diverted to account · {{name}}',
          vatCurrency: 'VAT {{vat}} · {{currency}}',
          issuerMissing:
            'No valid issuer — generation issues nothing for this invoice until the assignments have an issuer.',
          autoBillingOff: 'Automatic invoicing off — generation skips it',
          clientScope: 'client-level assignment',
          otherCount_one: '{{count}} more item from other accounts is on this invoice.',
          otherCount_other: '{{count}} more items from other accounts are on this invoice.',
          excludedTitle: 'Not invoiced',
          reason: {
            tracking: 'Tracking-only assignment — not invoiced',
            one_time: 'One-time fee — invoiced manually',
            ended: 'Ended — no longer invoiced',
            inactive: 'Inactive — not invoiced',
            incomplete: 'Incomplete setup (pricing, frequency or start date) — generation skips it',
          },
          empty: 'No service would land on an invoice.',
          error: 'The invoice view could not be loaded.',
          forbidden: 'You have no permission for the invoice view.',
          retry: 'Retry',
        },
        attention: {
          bannerTitle_one:
            '{{count}} pending assignment starts within the next {{noticeDays}} days — review it',
          bannerTitle_other:
            '{{count}} pending assignments start within the next {{noticeDays}} days — review them',
          reviewButton: 'Review',
          dialogTitle: 'Pending assignments starting soon',
          dialogDescription:
            'If an assignment is not set to "active" by its start date, the monthly invoice generator will skip it.',
          columns: { client: 'Client / account', service: 'Service', startDate: 'Starts' },
          daysToday: 'today',
          daysIn: '{{count}} days',
          daysOverdue: '{{count}} days overdue',
          footerLink: 'List pending only',
          startBadge: {
            later: 'starts: {{date}}',
            soon: 'starting soon: {{date}}',
            overdue: 'start missed: {{date}}',
            missing: 'no start date',
          },
          startTooltip: {
            later:
              'The billing start date. If it is not set to active by then, the monthly invoice generator skips it.',
            soon: 'Starting soon but still pending: if it is not set to active by then, the monthly invoice generator skips it.',
            overdue:
              'The billing start date has passed but the assignment is still pending: the monthly invoice generator skips it.',
            missing:
              'No billing start date: nothing flags its start, and the monthly invoice generator does not bill it.',
          },
        },
        form: {
          scope: 'Scope',
          client: 'Client',
          clientPlaceholder: 'Select a client…',
          account: 'Account',
          accountPlaceholder: 'Select an account…',
          accountPickClient: 'Select a client first…',
          service: 'Service',
          servicePlaceholder: 'Select a service…',
          plan: 'Plan',
          planPlaceholder: 'Select a plan…',
          planPickService: 'Select a service first…',
          planBillingType: 'Billing type',
          planPricingType: 'Pricing',
          planVatRate: 'VAT',
          type: 'Type',
          status: 'Status',
          quantity: 'Quantity',
          quantityUnit: 'Unit',
          sectionBilling: 'Billing settings',
          issuer: 'Issuer',
          invoiceType: 'Document type',
          invoiceDeliveryType: 'Invoice type',
          billingFrequency: 'Billing frequency',
          frequencyNone: '— none —',
          billingAgreement: 'Billing agreement',
          agreementNone: '— none —',
          billingStartDate: 'Billing start',
          billingEndDate: 'Billing end',
          billingDate: 'Billing date',
          billingDateHint: 'The one-time fee appears in this month of the revenue report.',
          contractStartDate: 'Contract start',
          contractEndDate: 'Contract end',
          firstInvoiceProrated: 'Prorate first invoice',
          firstInvoiceProratedHint: 'Fee prorated to the fraction of the first period.',
          sectionPricing: 'Pricing (override)',
          pricingType: 'Pricing type',
          pricingTypeHint: 'The catalog type or a flat fee (can be downgraded, not upgraded).',
          vatRate: 'VAT rate',
          paymentCard: 'Payment card',
          paymentCardNone: '— none —',
          contact: 'Contact',
          contactPlaceholder: 'Search external contacts…',
          expert: 'Expert',
          expertPlaceholder: 'Search internal colleagues…',
          identityNone: '— none —',
          accountIds: 'Affected accounts',
          accountIdsPlaceholder: 'Select accounts…',
          accountIdsCount: '{{count}} selected',
          accountIdsHint: 'A client-level assignment can affect several accounts.',
          invoiceItemName: 'Custom invoice item name',
          invoiceItemNameHint:
            'If empty, the invoice item name set in the catalogue is used on the invoice.',
          invoiceItemNote: 'Custom invoice item note',
          vatRateOverridden: 'on this row: {{vatRate}}',
          showComponentsOnInvoice: 'Components on the invoice item',
          showComponentsOnInvoiceHint: 'Component names are added to the invoice item note.',
          usageSourceType: 'Usage source',
          usageSourceNone: '— none —',
          usageSourceHint: 'Required for tiered / base+tiered / commission pricing.',
          oneTimeTypeHint:
            'A one-time fee is always tracking: it is invoiced manually and counts in the revenue report in the month of its billing date.',
          usageRefs: 'Source ID',
          usageRefsPlaceholder: 'Which ID’s usage counts?',
          splitSharedUsage: 'Split the registrant fee',
          splitSharedUsageHint:
            'When several hotels are bound to the same Loyalty ID, the registrant-based fee is split evenly between the hotels where this is on. The base fee is not split, and the note keeps the full registrant count.',
          usageRefsCount: '{{count}} IDs',
          usageRefsHint:
            'The item amount is the sum of the usage measured on the selected IDs. IDs are added on the account page, in the Integrations section.',
          usageRefsEmpty:
            'The selected account(s) have no ID matching this source (newsletter → MailWizz, everything else → HotelCMS / ZadirCMS). Add one on the account page, in the Integrations section.',
          note: 'Note',
          sectionComponents: 'Components (package)',
          componentsHint:
            'Complex (package) assignment: the parent price is the sum of the components, on one invoice line. The main plan is a component too (added automatically with the entered base fee on the first add), so the base fee is never lost.',
          pricingFromComponents:
            'The price is the sum of the components: {{amount}} {{currency}} — edit the main plan base fee in the components, first row.',
          addComponent: 'Add component',
          removeComponent: 'Remove',
          componentService: 'Service',
          componentPlan: 'Plan',
          componentContact: 'Component contact',
          componentExpert: 'Component expert',
          componentQuantity: 'Qty',
          componentUnitPrice: 'Unit price',
          componentCurrency: 'Currency',
          componentsTotal: 'Components total',
          componentsCurrencyMismatch: 'The components use different currencies.',
          componentsLocked: 'Components cannot be changed on an invoiced assignment.',
          noComponents: 'No components — the assignment is simple.',
        },
        activeWarning: {
          title: 'An active assignment already exists',
          desc: 'This owner already has an active assignment for the same service (“{{service}}” — {{owner}}). Create another one anyway? Saving can continue.',
          descInComponent:
            'This service (“{{service}}”) is already active for this owner as a component of ANOTHER assignment ({{owner}}). Create a standalone assignment as well? Saving can continue.',
          descFromComponent:
            'The component you added (“{{service}}”) already has an active assignment for this owner ({{owner}}). Save it like this? Saving can continue.',
          proceed: 'Continue saving',
          viewExisting: 'View existing',
          cancel: 'Cancel',
        },
        pricingTypeLabel: {
          flat: 'Flat',
          tiered: 'Tiered',
          base_tiered: 'Base + tiered',
          commission: 'Commission',
        },
        vatRateLabel: {
          VAT_27: '27% VAT',
          VAT_18: '18% VAT',
          VAT_5: '5% VAT',
          VAT_0: '0% VAT',
          AAM: 'AAM (exempt person)',
          TAM: 'TAM (exempt activity)',
          FAA: 'FAA (reverse charge)',
          EUFAD37: 'EUFAD37 (EU reverse)',
        },
        pricing: {
          section: 'Pricing',
          currency: 'Currency',
          primaryLabel: {
            flat: 'Unit price (net)',
            tiered: 'Passive fee (net)',
            base_tiered: 'Base fee (net)',
            commission: 'Minimum fee (net)',
          },
          tiers: 'Tiers',
          addTier: 'Add tier',
          removeTier: 'Delete tier',
          noTiers: 'No tiers yet. Add at least one.',
          tierMin: 'Tier min',
          tierMax: 'Tier max',
          tierMaxUnlimited: 'unlimited',
          netPrice: 'Net price',
          overflowEnabled: 'Overflow (above-tier) fee',
          blockSize: 'Block size',
        },
      },
      serviceCatalog: {
        filterStructure: 'Structure',
        filterCategory: 'Category',
        filterSubCategory: 'Subcategory',
        filterBilling: 'Billing',
        filterPricing: 'Pricing',
        filterStatus: 'Status',
        filterAll: '{{label}}: all',
        loading: 'loading…',
        columns: {
          service: 'Service',
          pricing: 'Pricing',
          supportTeam: 'Support team',
          status: 'Status',
          actions: 'Actions',
        },
        morePlans: '+{{count}}',
        morePlansAria: 'More plans',
        morePrices: '+{{count}} more price row',
        noPrices: 'No price',
        blockUnit: 'pcs',
        priceType: {
          passive: 'passive fee',
          minimum: 'minimum fee',
          base: 'base fee',
          unit: 'unit price',
        },
        new: 'New service',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        deleting: 'Deleting…',
        createTitle: 'New service',
        createDesc: 'Service details, plans and pricing.',
        editTitle: 'Edit service',
        deleteTitle: 'Delete service',
        deleteDesc: 'Delete service “{{name}}”? This action cannot be undone.',
        tabs: {
          basic: 'Details',
          plans: 'Plans',
        },
        fields: {
          name: 'Name',
          description: 'Description',
          structureType: 'Structure',
          structureTypeLockedHint: 'The structure type cannot be changed for an existing service.',
          status: 'Status',
          showInTicketDropdown: 'Show in ticket dropdown',
          showInTicketDropdownHint: 'Whether the service is selectable when opening a new ticket.',
          groupedInCharts: 'Group in charts',
          groupedInChartsHint: 'The service appears as a single item with its siblings in charts.',
          supportTeam: 'Responsible support team',
          supportTeamHint: 'Which support team owns this service (optional).',
          supportTeamNone: 'None',
          supportTeamUnknown: 'Current (unknown) team',
        },
        categoryLabel: {
          product: 'Product',
          advisory: 'Advisory',
          other: 'Other',
        },
        subCategoryLabel: {
          roomsome: 'RoomSome',
          innovation: 'Innovation',
          digital_marketing: 'Digital marketing',
          revenue_management: 'Revenue management',
          web_development: 'Web development',
          other: 'Other',
        },
        billingTypeLabel: {
          recurring: 'Recurring',
          one_time: 'One-time',
        },
        pricingTypeLabel: {
          flat: 'Flat',
          tiered: 'Tiered',
          base_tiered: 'Base + tiered',
          commission: 'Commission',
        },
        vatRateLabel: {
          VAT_27: '27% VAT',
          VAT_18: '18% VAT',
          VAT_5: '5% VAT',
          VAT_0: '0% VAT',
          AAM: 'AAM (exempt person)',
          TAM: 'TAM (exempt activity)',
          FAA: 'FAA (reverse charge)',
          EUFAD37: 'EUFAD37 (EU reverse)',
        },
        structureLabel: {
          simple: 'Simple',
          package: 'Package-based',
        },
        statusLabel: {
          active: 'Active',
          inactive: 'Inactive',
        },
        plan: {
          name: 'Plan name',
          mainCategory: 'Main category',
          subCategory: 'Subcategory',
          billingType: 'Billing type',
          pricingType: 'Pricing type',
          vatRate: 'VAT rate',
          usageSourceType: 'Usage source',
          invoiceItemName: 'Invoice item name',
          invoiceItemNameHint:
            'This name goes on the invoice (it can be overridden per assignment).',
          invoiceItemNote: 'Custom invoice item note',
          oneTimeFlatHint: 'One-time fees are always flat-priced.',
          add: 'Add plan',
          remove: 'Delete plan',
          untitled: 'Plan {{index}}',
        },
        pricing: {
          section: 'Pricing',
          currency: 'Currency',
          usageSourceNone: 'Not set',
          primaryLabel: {
            flat: 'Unit price (net)',
            tiered: 'Passive fee (net)',
            base_tiered: 'Base fee (net)',
            commission: 'Minimum fee (net)',
          },
          tiers: 'Tiers',
          addTier: 'Add tier',
          removeTier: 'Delete tier',
          noTiers: 'No tiers yet. Add at least one.',
          tierMin: 'Tier min',
          tierMax: 'Tier max',
          tierMaxUnlimited: 'unlimited',
          netPrice: 'Net price',
          overflowEnabled: 'Overflow (above-tier) fee',
          blockSize: 'Block size',
        },
        countOf: '{{shown}} / {{total}} services',
        empty: 'No services',
        emptyDesc: 'No services have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No services match your search or filter.',
      },
      orgStructure: {
        loading: 'loading…',
        edit: 'Edit',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving…',
        deleting: 'Deleting…',
        // The „Employees" column is SHARED on all three tabs, with the support-teams design.
        columns: { actions: 'Actions', employees: 'Employees' },
        moreMembers: '+{{count}}',
        moreMembersAria: 'More employees',
        morePositions: '+{{count}}',
        morePositionsAria: 'More positions',
        tabs: {
          departments: 'Departments',
          teams: 'Teams',
          positions: 'Positions',
        },
        fields: {
          name: 'Name',
          color: 'Color',
          department: 'Department',
          departmentPlaceholder: 'Select a department…',
          team: 'Team',
          teamPlaceholder: 'Select a team…',
          lead: 'Team lead',
          description: 'Description',
        },
        colorLabel: {
          burgundy: 'Burgundy',
          coral: 'Coral',
          honey: 'Honey',
          kiwi: 'Kiwi',
          grove: 'Grove',
          mint: 'Mint',
          forest: 'Forest',
          lagoon: 'Lagoon',
          iris: 'Iris',
          midnight: 'Midnight',
          steel: 'Steel',
          gray: 'Gray',
        },
        departments: {
          new: 'New department',
          countOf: '{{shown}} / {{total}} departments',
          empty: 'No departments',
          emptyDesc: 'No departments yet. Create one with the “New department” button.',
          noMatches: 'No matches',
          noMatchesDesc: 'No department matches your search.',
          createTitle: 'New department',
          createDesc:
            'The department’s bilingual name and description. The slug is generated. Colors are set on teams.',
          editTitle: 'Edit department',
          deleteTitle: 'Delete department',
          deleteDesc: 'Delete the department “{{name}}”? This action cannot be undone.',
          columns: {
            name: 'Department',
            teams: 'Teams',
          },
          moreTeams: '+{{count}}',
          moreTeamsAria: 'More teams',
        },
        teams: {
          new: 'New team',
          filterDepartment: 'Department',
          countOf: '{{shown}} / {{total}} teams',
          empty: 'No teams',
          emptyDesc: 'No teams yet. Create one with the “New team” button.',
          noMatches: 'No matches',
          noMatchesDesc: 'No team matches your search.',
          createTitle: 'New team',
          createDesc: 'The team’s bilingual name, color and its department. The slug is generated.',
          editTitle: 'Edit team',
          deleteTitle: 'Delete team',
          deleteDesc: 'Delete the team “{{name}}”? This action cannot be undone.',
          selectDepartment: 'Select a department…',
          noDepartment: 'No department',
          leadPlaceholder: 'Select an employee…',
          leadHint:
            'Receives a notice when an asset request is approved. Optional, but without it nobody gets the lead notice for this team’s requests.',
          noLead: 'No lead',
          noLeadHint: 'No team lead set — no lead notice is sent for this team’s asset requests.',
          columns: {
            name: 'Team',
            lead: 'Lead',
            positions: 'Positions',
          },
        },
        positions: {
          new: 'New position',
          filterDepartment: 'Department',
          filterTeam: 'Team',
          countOf: '{{shown}} / {{total}} positions',
          empty: 'No positions',
          emptyDesc: 'No positions yet. Create one with the “New position” button.',
          noMatches: 'No matches',
          noMatchesDesc: 'No position matches your search.',
          createTitle: 'New position',
          createDesc:
            'The position’s bilingual name and its team. The department follows from the team; the slug is generated.',
          editTitle: 'Edit position',
          deleteTitle: 'Delete position',
          deleteDesc: 'Delete the position “{{name}}”? This action cannot be undone.',
          columns: {
            name: 'Position',
          },
        },
      },
      portalAccess: {
        sectionHint:
          'Who can access the partner portal for THIS account, and in what role. The role may differ per account: the same contact can be a portal admin on one account and a viewer on another.',
        empty: 'No contacts are assigned to this account yet.',
        roles: {
          none: 'no access',
          'portal-admin': 'Portal admin',
          'portal-member': 'Portal member',
          'portal-viewer': 'Portal viewer',
        },
        roleAria: 'Portal role of {{name}}',
        roleLabel: 'Partner portal role',
        roleHint:
          'What this contact can do on the partner portal for THIS account. It may differ per account — the same person can hold another role elsewhere.',
        revokeAria: 'Revoke portal access of {{name}}',
        inviteAria: 'Send portal invitation to {{name}}',
        revokeTitle: 'Revoke portal access',
        revokeBody:
          "{{name}} will no longer be able to access this account on the partner portal. The contact affiliation and the person's data remain UNCHANGED — only the portal access ends, immediately.",
        revokeConfirm: 'Revoke',
        missingPlatformScope:
          'Changing access also requires a platform-level right (iam:portal-access) — ask an access-rights manager.',
      },
      accounts: {
        filterType: 'Type',
        filterCategory: 'Category',
        filterService: 'Service',
        filterStatus: 'Status',
        filterAll: '{{label}}: all',
        loading: 'loading…',
        columns: {
          account: 'Account',
          address: 'Address',
          clientAdmin: 'Client administrator',
          contact: 'Contact',
          services: 'Services',
          domains: 'Domains',
          note: 'Note',
          status: 'Status',
          actions: 'Actions',
        },
        more: '+{{count}}',
        moreItems: 'Show more items',
        typeLabel: {
          hospitality: 'Hospitality',
          accommodation: 'Accommodation',
          spa: 'Spa / Wellness',
          other_tourism: 'Other tourism',
          other_industry: 'Other industry',
        },
        statusLabel: { active: 'Active', pending: 'Pending', inactive: 'Inactive' },
        countOf: '{{shown}} / {{total}} accounts',
        empty: 'No accounts',
        emptyDesc: 'No accounts have been loaded yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No accounts match your filters.',
        // Row action / detail (read-only)
        view: 'View',
        edit: 'Edit',
        back: 'Back to accounts',
        notFound: 'Account not found',
        notFoundDesc: 'It may have been deleted, or you may not have permission to view it.',
        forbidden: 'No access',
        forbiddenDesc: 'You do not have permission to view accounts.',
        // Delete (ConfirmDialog)
        delete: 'Delete',
        cancel: 'Cancel',
        deleting: 'Deleting…',
        deleteTitle: 'Delete account',
        deleteDesc: 'Are you sure you want to delete “{{name}}”? This action cannot be undone.',
        dangerTitle: 'Delete account',
        dangerDesc:
          'The account bindings (contacts, client administrators) and service assignments disappear from the lists too. This action cannot be undone.',
        sections: {
          basics: 'Basics',
          other: 'Other details',
          otherDesc: 'The systems serving this account, its domains and the free-form note.',
          services: 'Services',
          billing: 'Billing',
          billingDesc:
            "How the account is billed: whether it gets its own invoice, and from which profile. Without own billing the client's primary profile applies and the account's items merge into the client invoice.",
          integrations: 'Integrations',
          integrationsDesc: 'External system identifiers — for internal use.',
          contacts: 'Contacts',
          portalAccess: 'Partner portal access',
          admins: 'Client administrators',
        },
        sectionNavLabel: 'Sections on this page',
        relatedError: 'Could not load.',
        relatedForbidden: 'You do not have permission to view this.',
        relatedRetry: 'Retry',
        relatedTotal: '{{count}} in total — showing the first 8',
        contactsEmpty: 'This account has no contacts yet.',
        adminsEmpty: 'This account has no client administrators yet.',
        addContact: 'Add contact',
        addAdmin: 'Add client administrator',
        primaryBadge: 'primary',
        portalBadge: 'portal access',
        pendingHint: 'Available once you have created the account.',
        pendingServicesDesc: 'Service assignments bound to this account.',
        pendingContactsDesc: 'Contacts on the partner side.',
        pendingAdminsDesc: 'Internal colleagues responsible for the account.',
        fields: {
          pms: 'PMS',
          bookingEngine: 'Booking engine',
          channelManager: 'Channel Manager',
          emailWebHosting: 'Email / web hosting',
          asanaName: 'Asana name',
        },
        integrationSystem: { hotelcms: 'HotelCMS', zadircms: 'ZadirCMS', mailwizz: 'MailWizz' },
        integrations: {
          label: 'System IDs',
          hint: 'A system can have several IDs (in HotelCMS e.g. separate loyalty, booking and ticket domains). Use the note to say which is which — on the service assignment you pick by it which one is billed. Removing a row also removes the binding of any assignment that uses it.',
          empty: 'No IDs yet.',
          system: 'System',
          externalId: 'ID',
          note: 'Note',
          notePlaceholder: 'e.g. loyalty.hotel.com',
          add: 'Add ID',
          remove: 'Remove ID',
        },
        // Account "Domains" section (successor of the free-text "Other domains") — the main domain is the Website
        domains: {
          title: 'Domains',
          hint: 'Additional domains of the account (the main domain is the Website). Just type the domain — https:// and the path are dropped.',
          add: 'Add domain',
          empty: 'No domains added',
          domain: 'Domain',
          domainPlaceholder: 'e.g. book.hotel.com',
          statusLabel: 'Status',
          status: { active: 'active', setup: 'in setup', inactive: 'inactive' },
          remove: 'Remove domain',
        },
        // Account affiliation editor (client administrator / contact)
        affiliation: {
          add: 'Add contact',
          addTitle: 'Add contact',
          addDesc:
            'Bind an existing person to the account — as a client administrator or a contact.',
          editTitle: 'Edit contact',
          edit: 'Edit contact',
          editAria: 'Edit the binding of {{name}}',
          removeAria: 'Remove {{name}} from the account',
          remove: 'Remove',
          removeTitle: 'Remove contact',
          removeDesc:
            'Unlink “{{name}}” from this account? The person’s global identity stays INTACT — only the account binding is removed.',
          kind: 'Role',
          kindAdmin: 'Client administrator',
          kindContact: 'Contact',
          identity: 'Person',
          identityPlaceholder: 'Choose a person…',
          identityHintAdmin: 'Internal colleague (client administrator).',
          identityHintContact: 'Partner (contact).',
          managerType: 'Manager type',
          managerNone: '— none —',
          managerDigitalMarketing: 'Digital marketing manager',
          managerRevenue: 'Revenue manager',
          managerLabel: {
            digital_marketing: 'Digital marketing manager',
            revenue_management: 'Revenue manager',
          },
          isPrimary: 'Primary',
          isPrimaryHint:
            'Primary on the account (one per role) — enabling it also opens portal access.',
        },
        // Create / edit form (new / edit route)
        create: 'New account',
        new: 'New account',
        form: {
          sectionParent: 'Parent client',
          servicesEmpty: 'No services are assigned to this account yet.',
          name: 'Name',
          website: 'Website',
          type: 'Type',
          types: 'Type / category',
          category: 'Rating',
          region: 'Region',
          roomCount: 'Room count',
          billingProfile: 'Billing profile',
          billingProfileNone: '— none (client primary) —',
          billingProfilePrimary: '(primary)',
          billingProfileCreateHint: 'Choose a client first to load its profiles.',
          billingProfileDisabledHint: "Without own billing, the client's primary profile is used.",
          overrideBilling: 'Own billing (the account is invoiced separately)',
          overrideBillingHint:
            "When on, the account is invoiced separately using the profile selected below. When off, the client's primary profile applies and the account's services are merged into the CLIENT invoice.",
          parentClient: 'Client',
          parentClientPlaceholder: 'Choose a client…',
          parentClientHint:
            'The account belongs to this client — it cannot be changed after creation.',
          status: 'Status',
          zip: 'Postal code',
          city: 'Town',
          address: 'Address',
          countryCode: 'Country code',
          countryName: 'Country',
          // ── LEGACY-PARITY fields (v4) ──
          latitude: 'Latitude',
          longitude: 'Longitude',
          coordinateHint:
            'Decimal degrees, max 6 decimals (e.g. 47.497912). Both are required together.',
          cloudflare: 'On Cloudflare',
          autoBilling: 'Automatic (monthly) billing',
          autoBillingHint:
            'The monthly biller only bills account-scoped assignments automatically when this is on AND the account status is active.',
          autoBillingBlockedNoPrimary:
            'The client has no primary billing profile — add one on the client page first, or turn on the account’s own billing with a profile.',
          smtps: 'SMTP providers',
          mailings: 'Mailing / DNS',
          exportCampaigns: 'Export campaigns',
          coordinatePairRequired: 'Latitude and longitude must be provided together.',
          note: 'Note',
          none: '— none —',
          create: 'Create',
          save: 'Save',
          saving: 'Saving…',
        },
      },
      accessRights: {
        rolesTab: 'Roles',
        // AUDIENCE switch: the Users tab + the surface selector — internal system vs partner surface.
        usersTab: 'Users',
        audienceLabel: 'User type',
        audienceInternal: 'Staff',
        audienceExternal: 'External',
        countExternal: '{{shown}} / {{total}} external',
        externalEmpty: 'Nobody has portal access yet',
        externalEmptyDesc:
          'This is NOT the contact directory — it lists external users who already have partner portal access. Grant access on the ACCOUNT page (in the "Contacts" section): you pick from the account\'s contacts there, and their role is decided there too (per account).',
        portalNoAccess: 'no portal access',
        portalGrantAria: '{{account}} — {{role}} (go to account)',
        partnerRoleNote:
          'Roles and permissions of the partner surface — here you define WHAT a role means. WHO holds it on WHICH account is decided on the account page (in the "Contacts" section), because a partner\'s authority may differ per account.',
        partnerMembersNote:
          'External users — the role is PER ACCOUNT, so it is read-only here. Set it in the "Contacts" section on the account page (click a role badge).',
        tier: 'Role',
        tierGrants: 'Permissions',
        tierNote:
          'A role grants the base permissions of its assigned staff (cumulative: Member ⊂ Administrator ⊂ Superadmin). You can edit the Member and Administrator base permissions; per-person exceptions (extra/deny) are set on the Staff tab. The Superadmin role is locked (always full access).',
        roleLocked: 'Locked',
        deleteRole: 'Delete role',
        deleteRoleTitle: 'Delete role',
        deleteRoleBody:
          'The role "{{role}}" will be removed from the catalog. Only possible if NOBODY holds it (neither directly nor through an account access). Code-managed roles cannot be deleted here.',
        deleteRoleError: 'Could not delete the role',
        roleDeleted: 'Role deleted',
        roleSourceManifest: 'code-managed',
        roleSourceSeed: 'default, editable',
        roleSourceConsole: 'added here',
        rolePermsSuffix: 'role permissions',
        roleSaved: 'Role permissions saved',
        name: 'Name',
        overrides: 'Per-user overrides',
        tierDefaultOnly: 'Role defaults',
        grantShort: 'extra',
        denyShort: 'denied',
        noRole: '— no role',
        countMembers: '{{shown}} / {{total}} staff',
        filterRole: 'Role',
        filterPermission: 'Permission',
        countRoles: '{{shown}} / {{total}} roles',
        more: '+{{count}}',
        moreItems: 'More permissions',
        rolesEmpty: 'No roles',
        rolesEmptyDesc: 'No roles have been defined for this app yet.',
        membersEmpty: 'No staff',
        membersEmptyDesc: 'No staff have been assigned to this app yet.',
        noMatches: 'No matches',
        noMatchesDesc: 'No users match your search.',
        roleUpdated: 'Role updated',
        overridesSaved: 'Overrides saved',
        overridesSuffix: 'per-user permission overrides',
        overrideHint:
          'Tick what this person should have. Permissions granted by the {{role}} role are inherited; ticking or unticking adds an extra grant or denies a permission relative to that.',
        grantTag: 'extra',
        denyTag: 'denied',
        inheritedTag: 'inherited',
        prereqAdded: 'The view permission was ticked too: {{list}}',
        prereqRemoved: 'Removed along with it: {{list}}',
        prereqMissing: 'Unusable without view: {{list}}',
        overrideSummary: '{{grants}} extra · {{denies}} denied',
        protectedSelfLock:
          'You cannot change a protected permission for yourself — ask another holder',
        protectedNotHeldLock: 'Protected permission: only a holder can change it',
        protectedRoleLock:
          'Protected permission: cannot be bound to a role — it is granted per person',
        // Add member to app — TWO AUDIENCES (staff / external), EXISTING person only + initial role.
        addMember: 'Add member',
        addMemberTitle: 'Add member to app',
        addMemberDesc:
          'Choose who gets access: a staff member or an external user. New people are added on the Employees or the Contacts page.',
        addAudienceInternal: 'Staff member',
        addAudienceExternal: 'External',
        personLabel: 'Person',
        selectPerson: 'Select a person…',
        noInternalCandidates:
          'No staff member left to add — either everyone is a member already, or they must first be added on the Employees page.',
        noPartnerCandidates:
          'No external user left to add — either everyone is a member already, or they must first be added on the Contacts page.',
        initialRole: 'Starting role',
        noAppRoles: 'This app has no roles yet — create one first.',
        addMemberSubmit: 'Add',
        memberAdded: 'Member added to the app',
        addMemberError: 'Failed to add member',
        // Remove member from app (app offboarding)
        removeMember: 'Remove from app',
        removeMemberTitle: 'Remove member from app',
        removeMemberDesc:
          'Remove “{{name}}” from this app: they lose their access and role in it. Their global identity and access in other apps stay INTACT.',
        removeMemberPending: 'Removing…',
        memberRemoved: 'Member removed from the app',
        removeMemberError: 'Failed to remove member',
        selfLockout: 'You cannot revoke your own access — ask another rights manager',
        // Create new role
        newRole: 'New role',
        newRoleTitle: 'Create a new role',
        newRoleDesc:
          'Enter a key (identifier) and a display name. You can set its permissions after creation.',
        roleNameLabel: 'Display name',
        roleNamePlaceholder: 'e.g. Support agent',
        roleKeyLabel: 'Key (identifier)',
        roleKeyPlaceholder: 'e.g. support-agent',
        roleKeyHint: 'Lowercase letters, digits, hyphen and underscore only.',
        roleKeyDup: 'This key is already taken in this app.',
        createRoleSubmit: 'Create',
        roleCreated: 'Role created',
        createRoleError: 'Failed to create role',
      },
      activityLog: {
        actorPlaceholder: 'Actor (name / email)',
        from: 'From',
        to: 'To',
        loading: 'loading…',
        count: '{{count}} events',
        system: 'System',
        impersonatedBadge: 'on behalf of {{name}}',
        impersonatedUnknown: 'another user',
        appLabel: 'Application',
        viewLabel: 'View',
        view: {
          default: 'Default',
          data: 'Data changes',
          admin: 'Permissions',
          auth: 'Sign-ins',
          security: 'Security reads',
          system: 'System',
        },
        columns: {
          actor: 'Actor',
          action: 'Action',
          target: 'Affected',
          time: 'Time',
        },
        ip: 'IP address',
        device: 'Device',
        trace: 'Trace ID',
        roleTarget: '{{role}} role',
        statusLabel: { success: 'Success', failed: 'Failed' },
        empty: 'No events',
        emptyDesc: 'The log is still empty.',
        noMatches: 'No matches',
        noMatchesDesc: 'No events match your filters.',
        changesCount_one: '{{count}} change',
        changesCount_other: '{{count}} changes',
        emptyValue: '∅',
        actionPattern: '{{verb}} {{entity}}',
        verb: {
          create: 'create',
          update: 'update',
          delete: 'delete',
          archive: 'archive',
          recalculate: 'recalculate',
        },
        entity: {
          account: 'Account',
          accounts: 'Account',
          client: 'Client',
          clients: 'Client',
          contact: 'Contact',
          contacts: 'Contact',
          ticket: 'Ticket',
          tickets: 'Ticket',
          invoice: 'Invoice',
          invoices: 'Invoice',
          expense: 'Expense',
          expenses: 'Expense',
          'expense-category': 'Expense category',
          'expense-categories': 'Expense category',
          message: 'Message',
          messages: 'Message',
          meeting: 'Meeting',
          meetings: 'Meeting',
          asset: 'Asset',
          assets: 'Asset',
          department: 'Department',
          departments: 'Department',
          team: 'Team',
          teams: 'Team',
          employee: 'Employee',
          employees: 'Employee',
          'account-domains': 'Account domain',
          'support-teams': 'Support team',
          'service-plans': 'Plan',
          'service-plan-prices': 'Plan price',
          services: 'Service',
          'billing-profiles': 'Billing profile',
          'ticket-comments': 'Ticket comment',
          'ticket-contact-access': 'Ticket access (contact)',
          'ticket-attachments': 'Ticket attachment',
          'ticket-comment-templates': 'Reply template',
          'account-affiliations': 'Account relation',
          'meeting-participants': 'Meeting participant',
          'meeting-series': 'Recurring meeting series',
          widgets: 'Widget',
          // Previously MISSING types written by the BFF — without these the activity log printed the RAW SLUG.
          'payment-cards': 'Payment card',
          positions: 'Position',
          'service-plan-assignments': 'Service assignment',
          'asset-categories': 'Asset category',
          'asset-types': 'Asset type',
          'service-plan-assignment-prices': 'Assignment price',
          'assignment-accounts': 'Assignment account',
          'service-plan-assignment-users': 'Assignment participant',
          'support-team-members': 'Team member',
          'employee-details': 'Employee record',
          'invoice-items': 'Invoice item',
          'service-usage': 'Usage record',
          'invoice-accounts': 'Invoice account',
          'invoice-generation-skips': 'Generation skip',
          'message-reads': 'Read receipt',
          'message-recipient-contacts': 'Message recipient (contact)',
          'message-recipient-accounts': 'Message recipient (account)',
          'message-attachments': 'Message attachment',
          // Second round of missing types (user report) — see the Hungarian block for why the
          // wall did not catch them.
          'ticket-comment-reactions': 'Comment reaction',
          'product-updates': 'Product update',
          campaigns: 'Campaign',
          // Retired type (see the Hungarian note): kept for historical activity-log rows.
          'product-update-attachments': 'Product update attachment',
          'message-revisions': 'Message revision',
          'message-recipient-employees': 'Message recipient (employee)',
          'message-recipient-departments': 'Message recipient (department)',
          'message-recipient-teams': 'Message recipient (team)',
          'message-recipient-services': 'Message recipient (service)',
          'asset-events': 'Asset event',
          'identity-profiles': 'Account setting',
          issuers: 'Invoice issuer',
          comments: 'Comment',
          // Knowledge base (Mo) — the log also shows the cross-app feed.
          spaces: 'Knowledge space',
          categories: 'Category',
          articles: 'Article',
          'article-attachments': 'Article attachment',
          'article-revisions': 'Article revision',
          drafts: 'Draft',
          'knowledge-gaps': 'Knowledge gap',
          // Platform entities (not Atlas resources, but visible in the cross-app feed): the `{type}.{verb}`
          // composition path asks for these keys (`external_app.create`, `file.delete`).
          external_app: 'Application',
          file: 'File',
          // Notification rows are written by Odin (the BFF calls the notify endpoint, not a resource URL),
          // so the wall's resource list has no entry for them — the log shows `notification.create`.
          notification: 'Notification',
          notifications: 'Notification',
        },
        actionLabel: {
          // — Auth / session / impersonation —
          login: 'Sign-in',
          logout: 'Sign-out',
          login_failed: 'Failed sign-in',
          auth_token_reuse: 'Token reuse (revoked)',
          switch_lang: 'Language switch',
          impersonate_start: 'Impersonation started',
          impersonate_leave: 'Impersonation ended',
          iam_impersonate_start: 'Impersonation started',
          iam_impersonate_stop: 'Impersonation ended',
          login_blocked: 'Blocked sign-in',
          login_mfa_failed: 'Failed two-factor check',
          // — Own identity (self-service) + 2FA + password setup —
          identity_2fa_setup: 'Two-factor setup',
          identity_2fa_enabled: 'Two-factor enabled',
          identity_2fa_disabled: 'Two-factor disabled',
          identity_2fa_recovery_regenerated: 'Recovery codes regenerated',
          identity_2fa_recovery_used: 'Recovery code used',
          identity_self_update_profile: 'Own profile updated',
          identity_self_change_password: 'Own password changed',
          identity_self_change_password_failed: 'Failed password change',
          identity_self_asana_pat_set: 'Own Asana token set',
          identity_self_asana_pat_clear: 'Own Asana token cleared',
          identity_self_avatar_set: 'Avatar set',
          identity_self_avatar_clear: 'Avatar cleared',
          identity_service_elevated_scopes_set: 'Service identity elevated scopes',
          workforce_employee_details_self_update: 'Own personal details updated',
          workforce_employee_details_personal_purge: 'Personal details deleted (retention period)',
          platform_audit_log_gdpr_redaction: 'Log redacted (data protection)',
          identity_password_reset_issued: 'Password setup link issued',
          identity_password_reset_issue_skipped: 'Password setup link skipped',
          identity_password_reset_redeemed: 'Password setup link redeemed',
          identity_password_reset_rejected: 'Password setup link rejected',
          // — Admin / permissions —
          admin_role_assign: 'Role assigned',
          admin_role_revoke: 'Role revoked',
          admin_role_upsert: 'Role updated',
          admin_permission_grant: 'Permission granted',
          admin_permission_sync: 'Permissions synced',
          admin_member_add: 'Member added',
          admin_member_remove: 'Member removed',
          admin_permission_revoke: 'Permission revoked',
          admin_rbac_sync: 'RBAC manifest synced',
          admin_grant_create: 'Access granted',
          admin_portal_access_grant: 'Partner portal access granted',
          admin_portal_access_revoke: 'Partner portal access revoked',
          admin_grant_revoke: 'Access revoked',
          admin_identity_provision: 'Identity provisioned',
          admin_identity_core_update: 'Identity core data updated',
          admin_identity_offboard: 'Identity offboarded',
          admin_identity_asana_pat_set: 'Asana token set (as admin)',
          admin_identity_asana_pat_clear: 'Asana token cleared (as admin)',
          admin_app_allowed_senders_set: 'Allowed senders set',
          admin_email_suppression_create: 'Email suppression added',
          admin_email_suppression_delete: 'Email suppression removed',
          admin_app_upsert: 'App updated',
          access_rights_update_action_permissions: 'Action permissions updated',
          access_rights_update_view_permissions: 'View permissions updated',
          // — System / migration / schema —
          schema_register: 'Schema registration',
          reference_upsert: 'Reference data upsert',
          read_see_all: 'Cross-tenant read',
          admin_import_grant_create: 'Migration: access grant',
          admin_import_grant_update: 'Migration: access grant update',
          admin_import_typed: 'Migration: typed data',
          admin_import_audit_log: 'Migration: audit log',
          admin_import_notifications: 'Migration: notifications',
          admin_import_access_grants: 'Migration: access grants',
          admin_import_resource: 'Migration: resource',
          admin_import_identities: 'Migration: identities',
          admin_import_clients: 'Migration: clients',
          admin_import_accounts: 'Migration: accounts',
          // — Client —
          clients_create_client: 'Client created',
          clients_update_client: 'Client updated',
          clients_delete_client: 'Client deleted',
          clients_export_clients: 'Clients exported',
          // — Account —
          accounts_create_account: 'Account created',
          accounts_update_account: 'Account updated',
          accounts_delete_account: 'Account deleted',
          accounts_assign_contact: 'Contact assigned',
          accounts_remove_contact: 'Contact removed',
          accounts_set_as_primary_contact: 'Primary contact set',
          accounts_assign_client_admin: 'Client admin assigned',
          accounts_remove_client_admin: 'Client admin removed',
          accounts_export_accounts: 'Accounts exported',
          // — Contact —
          contacts_create_contact: 'Contact created',
          contacts_update_contact: 'Contact updated',
          contacts_delete_contact: 'Contact deleted',
          contacts_welcome_contact: 'Contact welcomed',
          contacts_export_contacts: 'Contacts exported',
          // — Ticket —
          export_list: 'List exported',
          tickets_open_ticket: 'Ticket opened',
          tickets_update_ticket: 'Ticket updated',
          tickets_status_changed: 'Ticket status changed',
          tickets_send_comment: 'Comment on ticket',
          tickets_create_asana_task: 'Asana task created',
          tickets_unlink_asana_task: 'Asana reference unlinked',
          tickets_contact_access: 'Contact access to ticket',
          tickets_save_comment_as_template: 'Comment saved as template',
          // — Meeting —
          meetings_create_meeting: 'Meeting created',
          meetings_update_meeting: 'Meeting updated',
          meetings_delete_meeting: 'Meeting deleted',
          meetings_change_participation: 'Participation changed',
          meetings_reschedule_meeting: 'Meeting rescheduled',
          // — Message —
          messages_view_message: 'Message viewed',
          messages_send_message: 'Message sent',
          // — Billing —
          invoices_create_invoice: 'Invoice created',
          invoices_create_proforma: 'Proforma created',
          billing_profiles_create_billing_profile: 'Billing profile created',
          billing_profiles_update_billing_profile: 'Billing profile updated',
          billing_profiles_delete_billing_profile: 'Billing profile deleted',
          billing_profiles_set_default_profile: 'Default billing profile set',
          // — Service / plan / price —
          services_create_service: 'Service created',
          services_update_service: 'Service updated',
          services_delete_service: 'Service deleted',
          services_create_plan: 'Plan created',
          services_update_plan: 'Plan updated',
          services_delete_plan: 'Plan deleted',
          services_create_price: 'Price created',
          services_create_assignment: 'Service assignment created',
          services_update_assignment: 'Service assignment updated',
          services_delete_assignment: 'Service assignment deleted',
          // — Employee / position / department —
          employees_create_employee: 'Employee created',
          employees_update_employee: 'Employee updated',
          employees_delete_employee: 'Employee deleted',
          employees_welcome_employee: 'Employee welcomed',
          positions_create_position: 'Position created',
          positions_update_position: 'Position updated',
          positions_delete_position: 'Position deleted',
          departments_create_department: 'Department created',
          departments_update_department: 'Department updated',
          departments_delete_department: 'Department deleted',
          teams_create_team: 'Team created',
          teams_update_team: 'Team updated',
          teams_delete_team: 'Team deleted',
          // — Asset —
          assets_create_asset: 'Asset created',
          assets_update_asset: 'Asset updated',
          assets_submit_asset_request: 'Asset request submitted',
          assets_approve_asset_request: 'Asset request approved',
          assets_reject_asset_request: 'Asset request rejected',
          assets_purchase_asset: 'Asset purchased',
          assets_being_procured_asset: 'Asset being procured',
          assets_received_asset: 'Asset received',
          assets_export_assets: 'Assets exported',
          // — Support / quota / vacation / misc —
          client_supports_create_client_support: 'Client support created',
          client_supports_update_client_support: 'Client support updated',
          account_quotas_create_quota_data: 'Quota data created',
          account_quotas_export_accounts: 'Account quotas exported',
          account_quotas_create_currency_exchange_rate: 'Exchange rate created',
          account_quotas_update_currency_exchange_rate: 'Exchange rate updated',
          vacations_create_vacation: 'Vacation created',
          external_app_create: 'App created',
          external_app_update: 'App updated',
          external_app_delete: 'App deleted',
          feedprocessor_feed_update_field: 'Feed field updated',
          // — Document library (⛔ DISCARDED Atlas module, but LEGACY log rows stay readable) —
          documents_upload_document: 'Document uploaded',
          documents_upload_existing_document: 'Existing document uploaded',
          documents_pre_register_document: 'Document pre-registered',
          documents_update_document: 'Document updated',
          documents_archive_document: 'Document archived',
          documents_delete_document: 'Document deleted',
          document_alerts_create_alert: 'Document reminder created',
          // — Foundation (Odin client/account lifecycle — past-tense form, unlike native `{type}.{verb}`) —
          client_created: 'Client created',
          client_updated: 'Client updated',
          client_deleted: 'Client deleted',
          account_created: 'Account created',
          account_updated: 'Account updated',
          account_deleted: 'Account deleted',
          employees_export_employees: 'Employees exported',
          profile_update_profile_info: 'Profile updated',
          profile_update_password: 'Password changed',
          // — File (native file-flow) —
          file_upload: 'File upload',
          file_download: 'File download',
          file_delete: 'File delete',
          file_commit: 'File commit',
        },
        field: {
          module: 'List',
          format: 'Format',
          rowCount: 'Rows',
          truncated: 'Truncated',
          filters: 'Filters',
          startsAt: 'Starts',
          endsAt: 'Ends',
          notifyEmail: 'E-mail notification',
          locationKey: 'Location',
          zoomAccountKey: 'Zoom account',
          filename: 'File name',
          contentType: 'File type',
          sizeBytes: 'File size',
          fileId: 'File',
          messageId: 'Message',
          euTaxNumber: 'EU tax number',
          supplierName: 'Supplier',
          supplierTaxNumber: 'Supplier tax number',
          issueDate: 'Issue date',
          fulfillmentDate: 'Fulfilment date',
          dueDate: 'Payment due',
          netAmount: 'Net',
          vatAmount: 'VAT',
          grossAmount: 'Gross',
          categoryId: 'Category',
          dismissedAt: 'Dismissed',
          navOperation: 'NAV operation',
          originalInvoiceNumber: 'Original invoice number',
          navInsDate: 'Arrived at NAV',
          readAt: 'Read at',

          name: 'Name',
          email: 'Email',
          phone: 'Phone',
          status: 'Status',
          website: 'Website',
          note: 'Note',
          notes: 'Note',
          title: 'Title',
          subject: 'Subject',
          description: 'Description',
          autoBilling: 'Auto-billing',
          auto_billing: 'Auto-billing',
          channelManager: 'Channel Manager',
          channel_manager: 'Channel Manager',
          bookingEngine: 'Booking engine',
          booking_engine: 'Booking engine',
          countryCode: 'Country code',
          country_code: 'Country code',
          jobTitle: 'Job title',
          job_title: 'Job title',
          // Legacy log diff fields (measured: 114 distinct, 102 of them label-less → raw slug in the panel).
          // Every field appears in both snake_case (legacy MySQL) and camelCase (native Odin payload) form.
          slug: 'Identifier (slug)',
          first_name: 'First name',
          firstName: 'First name',
          last_name: 'Last name',
          lastName: 'Last name',
          nickname: 'Nickname',
          profile_picture: 'Profile picture',
          profilePicture: 'Profile picture',
          name_hu: 'Name (Hungarian)',
          nameHu: 'Name (Hungarian)',
          name_en: 'Name (English)',
          nameEn: 'Name (English)',
          description_hu: 'Description (Hungarian)',
          descriptionHu: 'Description (Hungarian)',
          description_en: 'Description (English)',
          descriptionEn: 'Description (English)',
          type: 'Type',
          category: 'Category',
          main_category: 'Main category',
          mainCategory: 'Main category',
          sub_category: 'Sub-category',
          subCategory: 'Sub-category',
          client_category: 'Client category',
          clientCategory: 'Client category',
          structure_type: 'Legal form',
          structureType: 'Legal form',
          is_primary: 'Primary',
          isPrimary: 'Primary',
          checked: 'Checked',
          roles: 'Roles',
          position: 'Position',
          department: 'Department',
          department_id: 'Department',
          departmentId: 'Department',
          managing_director: 'Managing director',
          managingDirector: 'Managing director',
          managing_directors: 'Managing directors',
          managingDirectors: 'Managing directors',
          address: 'Address',
          city: 'City',
          zip: 'Postal code',
          zip_code: 'Postal code',
          zipCode: 'Postal code',
          latitude: 'Latitude',
          longitude: 'Longitude',
          other_domains: 'Other domains',
          otherDomains: 'Other domains',
          domain: 'Domain',
          email_web_hosting: 'Email / web hosting',
          emailWebHosting: 'Email / web hosting',
          cloudflare: 'Cloudflare',
          pms: 'PMS',
          smtps: 'SMTP settings',
          mailings: 'Mailing lists',
          notifications: 'Notifications',
          email_notifications: 'Email notifications',
          emailNotifications: 'Email notifications',
          export_campaigns: 'Campaign exports',
          exportCampaigns: 'Campaign exports',
          mailwizz_id: 'MailWizz ID',
          mailwizzId: 'MailWizz ID',
          hotelcms_id: 'HotelCMS ID',
          hotelcmsId: 'HotelCMS ID',
          zadircmsId: 'ZadirCMS ID',
          integrations: 'System IDs',
          usageRefs: 'Source ID',
          asana_client_name: 'Asana client name',
          asanaClientName: 'Asana client name',
          start: 'Start',
          end: 'End',
          purchase_date: 'Purchase date',
          purchaseDate: 'Purchase date',
          retirement_date: 'Retirement date',
          retirementDate: 'Retirement date',
          billing_start_date: 'Billing start date',
          billingStartDate: 'Billing start date',
          assigned_to: 'Assignee',
          assignedTo: 'Assignee',
          close_reason: 'Close reason',
          closeReason: 'Close reason',
          reject_reason: 'Rejection reason',
          rejectReason: 'Rejection reason',
          client_support_id: 'Support team',
          clientSupportId: 'Support team',
          show_in_ticket_dropdown: 'Shown in ticket dropdown',
          showInTicketDropdown: 'Shown in ticket dropdown',
          non_primary_contact_accesses: 'Non-primary contact access',
          nonPrimaryContactAccesses: 'Non-primary contact access',
          primary_contact: 'Primary contact',
          primaryContact: 'Primary contact',
          location: 'Location',
          zoom_account: 'Zoom account',
          zoomAccount: 'Zoom account',
          zoom_link: 'Zoom link',
          zoomLink: 'Zoom link',
          google_meet_link: 'Google Meet link',
          googleMeetLink: 'Google Meet link',
          microsoft_teams_link: 'Microsoft Teams link',
          microsoftTeamsLink: 'Microsoft Teams link',
          employee_participants: 'Employee participants',
          employeeParticipants: 'Employee participants',
          contact_participants: 'Contact participants',
          contactParticipants: 'Contact participants',
          serial_number: 'Serial number',
          serialNumber: 'Serial number',
          license_key: 'License key',
          licenseKey: 'License key',
          usage_location: 'Usage location',
          usageLocation: 'Usage location',
          purchase_location: 'Purchase location',
          purchaseLocation: 'Purchase location',
          employee_id: 'Employee',
          employeeId: 'Employee',
          approver_id: 'Approver',
          approverId: 'Approver',
          rejecter_id: 'Rejecter',
          rejecterId: 'Rejecter',
          purchaser_id: 'Purchaser',
          purchaserId: 'Purchaser',
          receiver_id: 'Receiver',
          receiverId: 'Receiver',
          procurement_initiator_id: 'Procurement initiator',
          procurementInitiatorId: 'Procurement initiator',
          services: 'Services',
          clientSupports: 'Support teams',
          net_price: 'Net price',
          netPrice: 'Net price',
          vat_rate: 'VAT rate',
          vatRate: 'VAT rate',
          currency: 'Currency',
          pricing_type: 'Pricing type',
          pricingType: 'Pricing type',
          commission_type: 'Commission type',
          commissionType: 'Commission type',
          payment_methods: 'Payment methods',
          paymentMethods: 'Payment methods',
          billing_type: 'Billing type',
          billingType: 'Billing type',
          billing_name: 'Billing name',
          billingName: 'Billing name',
          billing_email: 'Billing email',
          billingEmail: 'Billing email',
          billing_emails: 'Billing emails',
          billingEmails: 'Billing emails',
          billing_address: 'Billing address',
          billingAddress: 'Billing address',
          billing_city: 'Billing city',
          billingCity: 'Billing city',
          billing_zip: 'Billing postal code',
          billingZip: 'Billing postal code',
          tax_number: 'Tax number',
          taxNumber: 'Tax number',
          vat_id_number: 'EU VAT number',
          vatIdNumber: 'EU VAT number',
          invoice_number: 'Invoice number',
          invoiceNumber: 'Invoice number',
          invoice_notes: 'Invoice notes',
          invoiceNotes: 'Invoice notes',
          invoice_item_name: 'Invoice item name',
          invoiceItemName: 'Invoice item name',
          invoice_item_note: 'Invoice item note',
          invoiceItemNote: 'Invoice item note',
          override_billing: 'Billing override',
          overrideBilling: 'Billing override',
          override_billing_profile_id: 'Overriding billing profile',
          overrideBillingProfileId: 'Overriding billing profile',
          default_invoice_note: 'Default invoice note',
          defaultInvoiceNote: 'Default invoice note',
          // HISTORIC slugs: field names from log entries written before the merge.
          default_invoice_header_note: 'Default invoice note (header, legacy)',
          defaultInvoiceHeaderNote: 'Default invoice note (header, legacy)',
          default_invoice_footer_note: 'Default invoice note (footer, legacy)',
          defaultInvoiceFooterNote: 'Default invoice note (footer, legacy)',
          default_invoice_payment_deadline_days: 'Default payment deadline (days)',
          defaultInvoicePaymentDeadlineDays: 'Default payment deadline (days)',
          parent_document_id: 'Parent document',
          parentDocumentId: 'Parent document',
          old: 'Previous value',
          new: 'New value',
        },
      },
      // Role-tier labels (roleLabel — universal). Permission LABELS/GROUPS now come from Odin (each app provides
      // its own rich metadata — see access-rights.tsx), so perm.resource/action/group/pattern were removed.
      perm: {
        role: {
          superadmin: 'Superadmin',
          admin: 'Administrator',
          member: 'Member',
          support: 'Support',
          contact: 'Contact',
        },
      },
      profile: {
        menu: 'My profile',
        title: 'My profile',
        subtitle: 'Your account details and security settings',
        save: 'Save',
        saving: 'Saving…',
        loadErrorTitle: 'Profile could not be loaded',
        loadErrorDesc: 'An unexpected error occurred. Please reload the page.',
        avatar: {
          edit: 'Change profile picture',
          remove: 'Remove picture',
        },
        basics: {
          title: 'Basics',
          givenName: 'First name',
          familyName: 'Last name',
          nickname: 'Nickname',
          nicknameHint: 'How the system addresses you. Left empty, your first name is used.',
          probationEnds: 'Probation ends: {{date}}',
        },
        contact: {
          title: 'Contact',
          email: 'Email address',
          phone: 'Phone number',
          emailPartnerHint:
            'You also sign in with this address — to change it, open a ticket and our team will do it.',
          stepUpLabel: 'Current password',
          stepUpHint: 'For security, enter your current password to change your email address.',
        },
        personal: {
          title: 'Other details',
          birthDate: 'Birthday',
          nameDay: 'Name day',
          celebrationsOptOut: 'Don’t show me in Celebrations',
          celebrationsOptOutHint:
            'When on, none of your birthday, name day, anniversary or start date appears in the Celebrations tile on your colleagues’ dashboards.',
        },
        integrations: {
          title: 'Integrations',
          discordId: 'Discord ID',
          discordIdHint: 'The numeric Discord user ID (not the username).',
          discordIdPlaceholder: 'e.g. 123456789012345678',
          asanaLabel: 'Asana personal access token (PAT)',
          asanaSave: 'Save token',
          asanaDelete: 'Remove',
          asanaCancel: 'Cancel',
          asanaHintSet:
            'Set. A new token overwrites it; leaving the field empty keeps it unchanged.',
          asanaHintUnset: 'Not set. Enter a token to connect your Asana account.',
          asanaHintClearing: 'Marked for removal — it will be deleted on save.',
        },
        notifications: {
          title: 'Notifications',
          email: 'Email notifications',
          emailHint: 'We also notify you by email about important events.',
          inApp: 'In-app notifications',
          inAppHint: 'Notifications inside the app (bell).',
        },
        password: {
          title: 'Change password',
          policyHint:
            'Required: at least {{count}} characters, lower- and uppercase letters, and a number. A symbol is recommended.',
          current: 'Current password',
          new: 'New password',
          confirm: 'Confirm new password',
          submit: 'Change password',
          saving: 'Saving…',
          rule: {
            length: 'at least {{count}} characters',
            case: 'upper and lower case',
            digit: 'number',
            different: 'different from current',
          },
          ruleMet: 'met',
          ruleMissing: 'still missing',
        },
        twoFactor: {
          title: 'Two-factor authentication (2FA)',
          desc: 'A time-based (TOTP) code from your authenticator app — extra protection for your account.',
          statusOn: 'enabled',
          statusOff: 'disabled',
          statusPending: 'setup in progress',
          setup: 'Set up 2FA',
          scanHint:
            'Scan the QR code with your authenticator app (e.g. Google Authenticator, 1Password), then enter the code it shows.',
          manualKey: 'Manual setup key',
          manualHint: 'If you cannot scan the QR, enter this key manually.',
          copyKey: 'Copy key',
          codeLabel: 'Authenticator code',
          activate: 'Activate',
          regenerate: 'Regenerate recovery codes',
          disable: 'Turn off',
          recovery: {
            title: 'Recovery codes',
            desc: 'Store these somewhere safe. This is the ONLY time you will see them — each works ONCE if you lose access to your authenticator app.',
            copy: 'Copy',
            download: 'Download',
            saved: "I've saved them",
          },
          stepUp: {
            code: 'Authenticator or recovery code',
            or: 'or',
            password: 'Current password',
            cancel: 'Cancel',
          },
          disableDialog: {
            title: 'Turn off two-factor authentication',
            desc: 'To confirm, enter a current authenticator code OR your current password.',
          },
          regenDialog: {
            title: 'Regenerate recovery codes',
            desc: 'New codes are generated and the old ones are invalidated. To confirm, enter an authenticator code OR your password.',
          },
        },
      },
    },
    // Field-error layer (zod issue path → field message). Call: t('validation:clients.email.invalid').
    validation: {
      auth: {
        emailRequired: 'Email address is required',
        emailInvalid: 'Invalid email address',
        passwordRequired: 'Password is required',
        mfaInvalid: 'The code is a 6-digit number or a recovery code',
      },
      expenses: {
        supplierName: 'The supplier name is required',
        invoiceNumber: 'The invoice number is required',
        issueDate: 'The issue date is required (YYYY-MM-DD)',
        amount: 'Invalid amount (at most 2 decimals)',
        currency: 'Pick a currency',
        accountingMonth: 'The accounting month must be in YYYY-MM form',
        issuerKey: 'Pick an issuer',
        categoryName: 'The Hungarian category name is required',
      },
      tickets: {
        description: { min: 'The description must be at least 25 characters.' },
        account: 'Pick an account',
        service: 'Pick a service',
        requester: 'Pick a requester',
        subject: {
          required: 'The subject is required',
          max: 'The subject is at most 255 characters',
        },
        link: { max: 'The link is at most 2083 characters' },
      },
      fallback: {
        required: 'This field is required',
        tooShort: 'At least {{count}} characters',
        tooLong: 'At most {{count}} characters',
        tooSmall: 'At least {{count}}',
        tooBig: 'At most {{count}}',
        tooFewItems: 'Pick at least {{count}} items',
        tooManyItems: 'At most {{count}} items can be picked',
        email: 'Invalid email address',
        url: 'Invalid link',
        date: 'Invalid date',
        option: 'Pick an option from the list',
        invalid: 'Invalid value',
      },
      clients: {
        name: { required: 'Name is required', max: 'At most 255 characters' },
        email: { invalid: 'Invalid email address', max: 'At most 255 characters' },
        phone: { max: 'At most 20 characters' },
        generic: 'Invalid value',
      },
      accounts: {
        name: { required: 'Name is required', max: 'At most 255 characters' },
        roomCount: { invalid: 'Enter a non-negative whole number (e.g. 120)' },
        integrationExternalId: { required: 'Enter the ID or remove the row' },
        coordinate: { invalid: 'Decimal degrees, max 6 decimals (e.g. 47.497912)' },
        tooLong: 'Value is too long',
        affiliation: { identity: 'A person is required' },
        domain: {
          required: 'The domain is required',
          invalid: 'Invalid domain (e.g. example.com)',
          duplicate: 'This domain is listed twice',
        },
      },
      assets: {
        holder: { required: 'Pick an employee or switch on office use' },
        office: { noPerson: 'An office asset cannot have a person' },
        usageLocation: {
          officeSocialOnly: 'The location of an office asset is fixed: communal',
          personal: 'Pick office, home or mixed',
        },
        catalog: {
          name: 'The Hungarian name is required (max 120 characters)',
          prefix: '2–8 uppercase letters or digits (A–Z, 0–9)',
          category: 'Pick a category',
        },
        name: { required: 'Name is required', max: 'At most 255 characters' },
        type: { required: 'Type is required' },
        retirementDate: { required: 'Retirement date is required for retired status' },
        date: { invalid: 'Invalid date (YYYY-MM-DD)' },
        netPrice: {
          required: 'Net price is required',
          format: 'Enter it as a number (e.g. 12000 or 12000.50)',
        },
        vatRate: {
          required: 'VAT rate is required',
          format: 'Enter it as a number (e.g. 27)',
        },
        website: { url: 'Invalid URL (e.g. https://example.com/product)' },
        note: { max: 'At most 2000 characters.' },
        generic: 'Invalid value',
      },
      people: {
        children: {
          fullName: "The child's full name is required",
          birthDate: "The child's date of birth is required",
        },
      },
      employees: {
        email: 'A valid email address is required',
        emailConflict: 'This email is already in use.',
        password: { tooLong: 'The password is too long (200 characters at most)' },
        positionId: { invalid: 'Position is required' },
        familyName: 'Family name is required',
        givenName: 'Given name is required',
        probation_end_before_start: 'The probation end cannot be before its start',
        probation_off: 'Dates can only be set while probation is on',
        generic: 'Invalid value',
      },
      contacts: {
        email: 'A valid email address is required',
        emailConflict: 'This email is already in use.',
        familyName: 'Family name is required',
        givenName: 'Given name is required',
        generic: 'Invalid value',
      },
      supportTeams: {
        name: 'Name (Hungarian and English) is required',
        email: 'Invalid email address',
        generic: 'Invalid value',
      },
      org_structure: {
        name: { required: 'Name is required', max: 'At most 255 characters' },
        department: { required: 'Please select a department' },
        team: { required: 'Please select a team' },
        generic: 'Invalid value',
      },
      messages: {
        pinOnlySentInternal: 'Only a sent internal message can be pinned to the home page.',
        subject: {
          required: 'Subject is required',
          max: 'At most 255 characters',
        },
        body: {
          required: 'Message text is required',
          min: 'At least {{count}} characters of text are required (markup does not count)',
          max: 'The message is too long',
        },
        link: { url: 'Enter a valid http(s) URL (e.g. https://…)' },
        targets: { required: 'Select at least one recipient' },
        audience: { required: 'Choose who the message is for' },
        scheduledAt: {
          required: 'Enter the send time',
          future: 'The time must be in the future',
        },
      },
      meetings: {
        title: 'A meeting title is required',
        location: 'A location is required',
        required: 'This field is required',
        // Cross-meeting conflict (the BFF routes it to the field — locationKey / zoomAccountKey).
        locationConflict: 'This location already has an overlapping meeting.',
        zoomConflict: 'This Zoom account is already booked in this time slot.',
        diningRoomRestricted: 'The dining room cannot host meetings between 11:00 and 13:00.',
        endBeforeStart: 'The end must be later than the start.',
        oneOnlineLink: 'Only one type of online link can be given at a time.',
        recurrence: {
          endRequired: 'Set the end of the series: the number of occurrences or the last day.',
          endAmbiguous: 'Set the end of the series one way only: a count or a date.',
          weekdaysRequired: 'Pick at least one day.',
          startNotInWeekdays:
            'The start day must be one of the selected days — a weekday for a workday series (the first occurrence is the start itself).',
          untilBeforeStart: 'The end of the series cannot be before its start.',
          tooFew: 'A series has at least 2 occurrences.',
          tooMany: 'A series can have at most 52 occurrences.',
          invalidStart: 'The start and end do not form a valid time slot.',
          tooLarge:
            'The series is too large (occurrences × participants) — reduce the occurrences or the participants.',
          dateChange:
            'When editing several occurrences only the time can change, not the day — edit this occurrence alone for that.',
          notEditable: 'The rule of a series cannot be changed afterwards.',
          noFutureOccurrence:
            'The series has no occurrence in this scope that has not started yet.',
        },
        outsideBusinessHours: 'Meetings can be held between {{from}} and {{to}}.',
      },
      invoices: {
        billingName: 'Billing name is required',
        country: 'A two-letter country code is required',
        zip: 'ZIP code is required',
        city: 'City is required',
        address: 'Address is required',
        date: 'Invalid date (YYYY-MM-DD)',
        email: 'Invalid email address in the list',
        itemsRequired: 'At least one line item is required',
        accountRequired: 'Select an account',
        accountsRequired: 'Select at least one account',
        vatMix: 'An invoice may use only one VAT rate',
        euTaxRequired: 'For EU reverse charge (EUFAD37) the EU tax number is required',
        tooLong: 'Value is too long',
        item: {
          servicePlan: 'Choose a service from the catalogue',
          name: 'Description is required',
          quantity: 'Quantity must be at least 0.01',
          format: 'Enter it as a number (e.g. 12000 or 12000.50)',
        },
      },
      billingProfiles: {
        billingName: 'Billing name is required',
        country: 'A two-letter country code is required',
        zip: 'ZIP code is required',
        city: 'City is required',
        address: 'Address is required',
        email: 'Invalid email address in the list',
        deadline: 'Payment deadline must be an integer between 1 and 31',
        client: 'Please select a client',
        directorPeriod: 'The end date cannot be earlier than the start date',
        tooLong: 'Value is too long',
      },
      paymentCards: {
        holderName: 'Cardholder name is required',
        brand: 'Please select a card brand',
        last4: 'Exactly 4 digits are required',
        expiryMonth: 'The month must be a number between 1 and 12',
        expiryYear: 'The year must be a number between 2000 and 2100',
        client: 'Please select a client',
        tooLong: 'Value is too long',
      },
      serviceAssignments: {
        quantityFormat: 'Enter it as a number (e.g. 1 or 1.5)',
        quantityMin: 'Quantity must be at least 0.01',
        planRequired: 'Please select a plan',
        accountRequired: 'Please select an account',
        accountIdsRequired: 'Select at least one affected account for client scope',
        billingStartRequired: 'A billing start date is required for billing',
        billingDateRequired: 'A billing date is required for a one-time fee',
        billingEndBeforeStart: 'The billing end cannot be earlier than the billing start',
        usageSourceRequired:
          'Usage source is required for tiered / base+tiered / commission pricing',
        usageRefsRequired: 'Pick which ID’s usage counts towards the item',
        componentInvalid:
          'Check the components: each needs a plan and a valid price, in a single currency',
        priceFormat: 'Enter a valid net price (e.g. 1000 or 1000.50)',
        tiersRequired: 'At least one tier is required',
        intRequired: 'Enter a whole number',
        tierRange: 'Tier min must be lower than tier max',
        tierOverlap: 'Tiers must not overlap (increasing, non-overlapping order)',
        tierPriceDecrease: 'A tier price cannot be lower than the previous tier',
        blockSize: 'Block size must be a positive whole number',
        tooLong: 'Value is too long',
      },
      serviceCatalog: {
        nameRequired: 'A name is required',
        planNameRequired: 'A plan name is required',
        invoiceItemNameRequired: 'The invoice item name is required',
        oneTimeFlat: 'One-time fees can only use flat pricing',
        priceFormat: 'Enter a valid net price (e.g. 1000 or 1000.50)',
        tiersRequired: 'At least one tier is required',
        intRequired: 'Enter a whole number',
        tierRange: 'Tier min must be lower than tier max',
        tierOverlap: 'Tiers must not overlap (increasing, non-overlapping order)',
        tierPriceDecrease: 'A tier price cannot be lower than the previous tier',
        blockSize: 'Block size must be a positive whole number',
        tooLong: 'Value is too long',
      },
      profile: {
        email: 'Invalid email address',
        discordId: 'The Discord ID may only contain digits',
        stepUpRequired: 'Enter your current password to change your email',
        password: {
          mismatch: 'The two passwords do not match',
          required: 'A password is required',
          too_short: 'At least {{count}} characters required',
          too_long: 'At most {{count}} characters allowed',
          missing_lowercase: 'Include a lowercase letter',
          missing_uppercase: 'Include an uppercase letter',
          missing_digit: 'Include a number',
          same_as_current: 'The new password must differ from the current one',
          wrongCurrent: 'Incorrect current password',
          generic: 'The password does not meet the requirements',
        },
      },
    },
    // Toast / feedback layer. Call: t('toast:clients.saved').
    toast: {
      dashboard: {
        saved: 'Layout saved',
        reset: 'The default layout is back',
        conflict:
          'The layout was changed elsewhere in the meantime — the fresh layout was loaded and your changes were lost.',
        forbidden: 'The layout cannot be saved (not allowed while impersonating).',
        validation: 'The layout is invalid — try again.',
        error: 'Saving the layout failed — your changes are still here, try again.',
        unavailable: 'The layout service is unavailable right now — try again in a minute.',
      },
      expenses: {
        import: {
          templateError: 'The template could not be downloaded — try again.',
          missingColumns: 'Missing column in the file: {{columns}}',
          empty: 'The file has no filled rows.',
          unreadable: 'The file cannot be read. Use the downloaded template (.xlsx or .csv).',
          previewError: 'The preview could not be built — try again.',
          commitError: 'Writing failed — no rows were recorded.',
          duplicateBatch: 'This upload has already been committed once.',
          undoError: 'Undo failed.',
          undone: '{{count}} rows undone',
        },
        created: 'Expense recorded',
        updated: 'Expense updated',
        deleted: 'Expense deleted',
        classified: 'Category saved',
        monthChanged: 'Accounting month saved',
        amountSaved: 'Amount saved',
        bulkClassified: '{{count}} expenses classified',
        bulkPartial: '{{success}} expenses classified — {{failed}} rows failed',
        bulkFailed: 'No row succeeded ({{count}})',
        dismissed: 'Done',
        duplicate: 'This invoice number is already recorded for this supplier',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this',
        validation: 'Check the highlighted fields',
        navReadonly: 'A NAV-imported row cannot be edited — only its category and note',
        error: 'The operation did not succeed',
        unavailable: 'The data is unavailable right now — try again in a few seconds',
        categoryCreated: 'Category created',
        categoryUpdated: 'Category updated',
        categoryDeleted: 'Category deleted',
        categoryInUse: 'The category is in use — move the items pointing at it first',
        categoryDepth: 'The category tree is at most three levels',
      },
      impersonation: {
        started: 'You are now acting as {{name}}',
        stopped: 'Back to your own account',
        sessionExpired: 'Your session expired — please sign in again',
        stopFailed: 'Could not switch back just now — please try again',
        error: 'Impersonation failed',
      },
      portal: {
        replySent: 'Reply sent',
        attachmentDownloadFailed: 'The download link could not be fetched — try again',
        replyUpdated: 'Reply updated',
        replyConflict: 'The reply changed in the meantime — reload the ticket',
        pdfError: 'The invoice PDF cannot be downloaded right now',
        rsvpConfirmed: 'Noted: you will attend',
        rsvpDeclined: 'Noted: you cannot attend',
        rsvpReset: 'Your answer was cleared — you can respond later',
        messagesAllRead_zero: 'No unread messages',
        messageArchived: 'Message archived',
        messageUnarchived: 'Message restored',
        messagesAllRead_one: '{{count}} message marked as read',
        messagesAllRead_other: '{{count}} messages marked as read',
        attachmentLimit: 'At most {{count}} files can be attached',
        attachmentTooLarge: '{{name}} is too large (max {{max}})',
        attachmentInvalidType: 'The type of {{name}} is not allowed',
        attachmentUploaded: 'Attachments uploaded',
        attachmentAddInvalidType: 'This file type is not allowed',
        attachmentAddTooLarge: 'The file is too large (max 50 MB)',
        attachmentAddFailed: 'The file could not be uploaded',
        attachmentPartial: 'Your ticket is open, but these could not be uploaded: {{files}}',
        accessSaved: 'Access list updated',
        ticketCreated: 'Your ticket is open — we will get back to you shortly',
        forbidden: 'You do not have permission for this action',
        validation: 'Please check the highlighted fields',
        error: 'The action failed',
      },
      notifications: {
        markReadError: 'Marking as read failed',
        markAllError: 'The action failed',
        allRead: 'All notifications marked as read',
      },
      clients: {
        created: 'Client created',
        saved: 'Changes saved',
        deleted: 'Client deleted',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
        unavailable: 'That is not possible right now — please try again in a few seconds',
      },
      portalAccess: {
        saved: 'Portal role saved',
        inviteSent: 'Portal invitation started',
        inviteError: 'Could not send the portal invitation',
        revoked: 'Portal access revoked',
        forbidden: 'You do not have permission to change portal access',
        validation: 'Portal access cannot be set with the given data',
        error: 'Could not change portal access',
      },
      accounts: {
        created: 'Account created',
        saved: 'Changes saved',
        deleted: 'Account deleted',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
        affiliationAdded: 'Contact added',
        affiliationSaved: 'Contact saved',
        affiliationRemoved: 'Contact removed',
      },
      meetings: {
        created: 'Meeting created',
        updated: 'Meeting updated',
        rescheduled: 'Meeting rescheduled',
        notOwn: 'You can only modify your own meetings.',
        reschedulePast: 'A meeting cannot be rescheduled into the past',
        deleted: 'Meeting deleted',
        rsvpSaved: 'Attendance saved',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      serviceCatalog: {
        created: 'Service created',
        updated: 'Service updated',
        deleted: 'Service deleted',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      org_structure: {
        departments: {
          created: 'Department created',
          updated: 'Department updated',
          deleted: 'Department deleted',
          inUse: 'The department is in use (it has teams) — remove the references first',
          conflict: 'Someone changed it in the meantime — reload the page',
          forbidden: 'You do not have permission for this action',
          validation: 'Check the highlighted fields',
          error: 'The action failed',
        },
        teams: {
          created: 'Team created',
          updated: 'Team updated',
          deleted: 'Team deleted',
          inUse: 'The team is in use (it has positions or assets) — remove the references first',
          conflict: 'Someone changed it in the meantime — reload the page',
          forbidden: 'You do not have permission for this action',
          validation: 'Check the highlighted fields',
          error: 'The action failed',
        },
        positions: {
          created: 'Position created',
          updated: 'Position updated',
          deleted: 'Position deleted',
          inUse: 'The position is in use (an employee references it) — remove the references first',
          conflict: 'Someone changed it in the meantime — reload the page',
          forbidden: 'You do not have permission for this action',
          validation: 'Check the highlighted fields',
          error: 'The action failed',
        },
      },
      tickets: {
        created: 'Ticket created',
        updated: 'Ticket updated',
        commentCreated: 'Comment added',
        commentUpdated: 'Comment updated',
        attachmentUploaded: 'Attachment uploaded',
        attachmentPartial: 'The ticket was opened, but these could not be uploaded: {{files}}',
        attachmentDeleted: 'Attachment deleted',
        attachmentError: 'The attachment operation failed',
        attachmentDownloadFailed: 'The download link could not be fetched — try again',
        assigneeTeamAmbiguous:
          'The colleague belongs to several support teams — the team was left unchanged',
        attachmentInvalidType: 'File type not allowed',
        attachmentTooLarge: 'The file is too large (max 50 MiB)',
        attachmentLimit: 'A ticket can have at most {{max}} attachments',
        attachmentValidation: 'The file does not meet the attachment requirements',
        template: {
          saved: 'Comment template saved',
        },
        asanaCreated: 'Asana task created',
        asanaAttachmentsIncomplete:
          'The task was created, but not every attachment was copied — attach the missing ones in Asana',
        asanaUnlinked: 'Asana reference unlinked',
        asanaError: 'The Asana operation failed',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      assets: {
        requestUpdated: 'Request updated',
        catalogCreated: 'Added to the catalog',
        catalogUpdated: 'Catalog entry saved',
        catalogDeleted: 'Deleted from the catalog',
        catalogInUse:
          'Cannot delete: an asset or request still uses it (or the category still has types)',
        unavailable: 'The service is temporarily unavailable — try again shortly',
        created: 'Asset created',
        updated: 'Asset updated',
        deleted: 'Asset deleted',
        requestCreated: 'Request submitted',
        requestApproved: 'Request approved',
        requestRejected: 'Request rejected',
        progressSaved: 'Procurement step recorded',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      employees: {
        updated: 'Employee details saved',
        created: 'Employee created',
        createError: 'Failed to create the employee',
        passwordNotSet:
          'The employee was created, but the initial password was not set: an account already exists for this email address — they sign in with their existing password.',
        alreadyEmployee: 'This email address is already an employee in Atlas',
        deleted: 'Employee deactivated',
        deleteError: 'Failed to deactivate the employee',
        welcomeSent: 'Welcome email sent to the employee',
        welcomeNotEligible:
          'This employee cannot receive an invite right now — check their Atlas access on the Permissions page.',
        welcomeError: 'Failed to send the welcome email',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      contacts: {
        created: 'Contact created',
        updated: 'Contact details saved',
        createError: 'Failed to create the contact',
        deleted: 'Contact deleted',
        deleteError: 'Failed to delete the contact',
        welcomeSent: 'Welcome email sent to the contact',
        welcomeNotEligible:
          'This contact cannot receive an invite right now — check their portal access.',
        welcomeError: 'Failed to send the welcome email',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      campaignForecast: {
        created: 'Campaign created',
        createdMany: '{{count}} campaigns created',
        saved: 'Campaign saved',
        deleted: 'Campaign deleted',
        deleteError: 'Could not delete the campaign',
        partial: 'Some accounts failed — check the list',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      productUpdates: {
        created: 'Update created (draft)',
        saved: 'Update saved',
        deleted: 'Update deleted',
        deleteError: 'Could not delete the update',
        published: 'Announcement saved',
        publishForbidden: 'You are not allowed to announce updates',
        publishNeedsSummary: 'A client-facing announcement needs a description',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You are not allowed to do this',
        validation: 'Check the highlighted fields',
        error: 'The operation failed',
        attachmentTooLarge: '{{name}} is too large (max {{max}})',
        attachmentValidation: 'The file does not meet the requirements',
      },
      supportTeams: {
        updated: 'Team saved',
        created: 'Support team created',
        createError: 'Failed to create the support team',
        deleted: 'Support team deleted',
        deleteError: 'Failed to delete the support team',
        inUse: 'The team cannot be deleted: active ticket(s) reference it.',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      messages: {
        sent: 'Message sent',
        scheduled: 'Message scheduled',
        unscheduled: 'Schedule cancelled',
        updated: 'Message saved',
        pinUpdated: 'Pin updated',
        pinValidation: 'Only a sent internal message can be pinned.',
        archiveUpdated: 'Archive updated',
        allRead: 'All messages marked as read',
        attachmentUploaded: 'Attachment uploaded',
        attachmentDeleted: 'Attachment deleted',
        attachmentPartial: 'The message was created, but this could not be uploaded: {{files}}',
        attachmentError: 'The attachment action failed',
        attachmentValidation: 'The file does not meet the requirements',
        attachmentInvalidType: 'File type not allowed',
        attachmentTooLarge: 'The file is too large (max. 50 MiB)',
        attachmentLimit: 'A message can have at most {{max}} attachments',
        attachmentUnavailable: 'Attachment handling is not configured — contact operations',
        conflict: 'The message changed or was sent in the meantime — the list refreshes',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      invoices: {
        created: 'Invoice draft created',
        saved: 'Changes saved',
        deleted: 'Draft deleted',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
        // Invoice issuance lifecycle (Számlázz.hu)
        issued: 'Invoice issued',
        stornoed: 'Invoice reversed',
        cancelled: 'Proforma voided',
        bulkIssued: '{{invoice}} invoices and {{proforma}} proformas issued',
        bulkIssuePartial: '{{invoice}} invoices and {{proforma}} proformas FAILED',
        bulkIssueFailed:
          'No invoice could be issued ({{invoice}} invoices, {{proforma}} proformas)',
        synced: 'Synced — {{paid}} paid, {{overdue}} overdue, {{related}} related',
        syncLocked: 'A sync is already in progress',
        notConfigured: 'Számlázz.hu integration is not configured',
        szamlazzError: 'The Számlázz.hu action failed — the invoice is unchanged',
        generated: 'Generation complete — {{invoices}} invoices, {{items}} items',
        generationStarted:
          'Generation for {{month}} has started — we will let you know when it finishes',
        generationBusy: 'An invoice generation is already running — wait for it to finish',
        generationUnavailable:
          'Background generation is unavailable right now (missing machine credentials)',
      },
      usageData: {
        fetchForbidden: 'Fetching requires invoice management',
        fetchError: 'Failed to fetch usage data',
        rateSaved: 'Rate saved',
        rateDeleted: 'Rate deleted',
        rateForbidden: 'Changing a rate requires invoice management',
        rateValidation: 'Invalid rate data',
        rateError: 'Failed to save the rate',
        rateDeleteError: 'Failed to delete the rate',
      },
      billingProfiles: {
        created: 'Billing profile created',
        detailsCopied: 'Billing details copied to clipboard',
        copyFailed: 'Copy failed',
        updated: 'Changes saved',
        deleted: 'Billing profile deleted',
        primarySet: 'Primary profile set',
        deletePrimary: 'The primary profile cannot be deleted',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      paymentCards: {
        created: 'Payment card created',
        updated: 'Changes saved',
        deleted: 'Payment card deleted',
        duplicate: 'This payment card is already registered for this client',
        inUse: 'The card cannot be deleted: it pays for service assignment(s) — repoint them first',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      serviceAssignments: {
        created: 'Assignment created',
        cloned: 'Assignment duplicated',
        updated: 'Changes saved',
        deleted: 'Assignment deleted',
        closed: 'Assignment closed',
        deleteInvoiced:
          'An assignment that appears on an issued invoice cannot be deleted, only closed',
        conflict: 'Someone changed it in the meantime — reload the page',
        forbidden: 'You do not have permission for this action',
        validation: 'Check the highlighted fields',
        error: 'The action failed',
      },
      profile: {
        saved: 'Profile saved',
        saveError: 'Saving failed. Reload the page and try again.',
        taken: 'This email or Discord ID is already taken.',
        stepUpFailed: 'Wrong password or confirmation — the action could not be completed.',
        validation: 'Check the highlighted fields',
        forbidden: 'You do not have permission for this action',
        error: 'The action failed',
        asanaSaved: 'Asana token saved',
        asanaCleared: 'Asana token removed',
        personalSaveError: 'Saving your other details failed — please try again.',
        personalConflict: 'Your other details were changed in the meantime — reload the page.',
        passwordChanged: 'Password changed',
        passwordRevokeWarning: 'For security, you have been signed out on your other devices.',
        rateLimited: 'Too many attempts. Please wait a moment and try again.',
        twoFactorEnabled: 'Two-factor authentication enabled',
        twoFactorDisabled: 'Two-factor authentication disabled',
        twoFactorBadCode: 'Invalid authenticator code',
        twoFactorAlreadyOn: 'Two-factor authentication is already enabled',
        copied: 'Copied to clipboard',
        copyFailed: 'Copy failed',
        avatarSaved: 'Profile picture updated',
        avatarRemoved: 'Profile picture removed',
        avatarError: 'Uploading the profile picture failed. Please try again.',
        avatarTooLarge: 'The image is too large. Please choose a smaller one.',
        avatarInvalidType: 'Unsupported format. Please upload a PNG, JPEG or WebP image.',
        avatarRateLimited: 'Too many upload attempts. Please wait a moment and try again.',
      },
    },
  },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'hu',
    supportedLngs: ['hu', 'en'],
    // Réteges namespace-ek: a látható feliratok a default `translation`-ben, a mező-hibák a
    // `validation`-ben, a toast-üzenetek a `toast`-ban. A `:` a namespace-szeparátor (i18next default).
    ns: ['translation', 'validation', 'toast'],
    defaultNS: 'translation',
    interpolation: { escapeValue: false },
    detection: { order: ['localStorage', 'navigator'], caches: ['localStorage'] },
  });

// A Zod lokalizált fallback-hibatérképe — itt, mert az i18n-nel EGYÜTT kell élnie (minden belépési pont, a
// tesztek is, ezt a modult töltik be). A `t` a hiba pillanatában fordít, tehát a nyelvváltás is hat.
installFallbackZodErrorMap(i18n.t.bind(i18n));

export default i18n;
